/**
 * Placeholder DTO-types. Wordt vervangen door auto-generated types uit OpenAPI
 * zodra de AppServices-backend een Swagger-endpoint exposeert.
 */

import type {
  InvoiceInventoryResult,
  OrderListLineReservation,
  OrderListOwnStock,
  OrderReceiptInventoryResult,
  OrderReceiptWarehouse,
  OwnReservation,
  OwnStockRow,
  OwnWarehouse,
  PendingOrderReceipt,
} from './inventory.js';

export interface WhoAmIResponse {
  userId: string;
  userName: string;
  displayName: string;
  roles: string[];
  /**
   * Discriminator voor veld-zichtbaarheid per entiteit.
   * Een van: System / RepairerHQ / RepairerLocation / RegularUser.
   */
  userContextType: UserContextType;
  /**
   * S007.9 — Afgeleide hersteller-id voor de huidige user, of `null`.
   *
   * `null` wanneer:
   *   - User-context geen hersteller-rol is, OF
   *   - `Company.CollUA_Repairer_CompanyID.Count > 1` (defensieve default —
   *     refined story #38 / F-13). Geen "eerste pakken".
   *
   * Een non-null GUID is voorwaardelijk voor de "+ Nieuw dossier"-knop op
   * `/dossiers` (#18) en voor toegang tot `GET /api/dossiers/create-options`
   * en `POST /api/dossiers` (#9 / #13).
   */
  repairerId: string | null;

  /**
   * Leesbare bedrijfsnaam (eigen company) — voor het header-sublabel + de
   * Identiteit-tab van "Mijn gegevens" (story S-A). `null` bij geen
   * company-koppeling; de FE valt dan terug op {@link accountType}.
   * Gecached op de BE-sessie (geen DB-roundtrip per whoami-poll).
   */
  companyName?: string | null;

  /**
   * Leesbaar accounttype-label, afgeleid van {@link userContextType}
   * (bv. "Hersteller-vestiging"). Presentatie; `userContextType` blijft de
   * discriminator.
   */
  accountType?: string | null;

  /**
   * Story `fe-be-documentatie-module` (2026-07-09): globaal (dossier-loos)
   * keten-signaal, BE-side afgeleid uit `Company.EuroMotiveCode === KetenHoofdkantoor`
   * (de keten-HK van deze instantie, config-gedreven) en gecached op de sessie
   * (geen DB-roundtrip per whoami-poll, spiegelt {@link companyName}). Voedt de
   * FE-beheer-gate van de Documentatie-module én nieuwsberichten:
   * `canManage = userContextType === 'System' || isKetenUser === true`. Wordt
   * nooit puur client-side vertrouwd — elke schrijf-call her-gate't BE-side.
   *
   * Optioneel getypeerd zodat oudere/gemockte whoami-payloads zonder het veld
   * niet breken; de FE-afleiding coerce't een ontbrekende waarde naar `false`.
   */
  isKetenUser?: boolean;

  /**
   * Story `fe-be-nieuwsberichten` (F-01): het leesbare keten-label van DEZE
   * instantie — de `Name` van het keten-HK (`KetenHoofdkantoor`-config). Elke
   * CARculate-instantie draait voor precies één keten; dit label voedt de
   * doelgroep-badge én de vaste "Keten"-optie in de beheer-dropdown van
   * nieuwsberichten. `null` voor een niet-keten-user zonder beheerrecht.
   */
  ketenLabel?: string | null;

  /**
   * Story `fe-be-switch-user` (S1): switch-recht van de ECHTE (ingelogde) user —
   * `true` wanneer de user mag "inloggen als vestiging" (`GU_User.CanSwitch` OF
   * system-user). De FE toont de switcher-control alléén bij `true`; blijft `true`
   * tijdens acting (zodat de switcher na switch-back weer verschijnt). Nooit als
   * autoriteit vertrouwd — elke switch-call her-gate't BE-side.
   *
   * Optioneel getypeerd zodat oudere/gemockte whoami-payloads zonder het veld niet
   * breken; de FE coerce't een ontbrekende waarde naar `false`.
   */
  canSwitch?: boolean;

  /**
   * Aanwezig (niet-null) zolang de sessie een andere identiteit impersoneert
   * (story `fe-be-switch-user`, S1). Voedt de globale acting-banner + de "Terug
   * naar mijn account"-knop. Op een niet-acting sessie afwezig/`null`.
   */
  actingAs?: ActingAsInfo | null;

  /**
   * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (S-C, 2026-08-21) —
   * de verzekeraar-gebonden-hersteller-capability van DEZE sessie, zoals S-A/S-B
   * hem op de sessie zetten (`RestrictedRepairerCapability`).
   *
   * **Het veld is AFWEZIG** wanneer de capability betrouwbaar "niet beperkt" is —
   * o.a. altijd wanneer de omgevings-schakelaar uit staat (beslissing D5 van de
   * fundering: de uit-situatie is byte-identiek aan vóór S-A/S-B). Daarom
   * optioneel **en** nullable getypeerd: bestaande/gemockte whoami-payloads zonder
   * het veld blijven compileren (precedent: {@link isKetenUser}, {@link canSwitch}).
   *
   * De FE keyt **uitsluitend** op `active === true` — nooit op de
   * omgevings-schakelaar, nooit op de instantie, en nooit op `resolved`. Elke
   * afleiding loopt via de enige gate-module `apps/web/src/auth/restrictedRepairer.ts`.
   */
  restrictedRepairer?: RestrictedRepairer | null;

  /**
   * Story `fe-be-importraamwerk-fase2-controleren` (S2-AC8) — mag DEZE sessie de
   * relatie-import gebruiken? BE: `AppServices\Infrastructure\
   * RelationImportCapability.cs`, gevoed door de tri-state functieschakelaar
   * `GS_SystemParameter.RelationImportEnabled` (S2-AC1/AC2).
   *
   * Optioneel **én** nullable getypeerd zodat bestaande en gemockte
   * whoami-payloads zonder het veld blijven compileren (precedent:
   * {@link isKetenUser}, {@link canSwitch}, {@link restrictedRepairer}).
   *
   * De FE keyt **uitsluitend** op `available === true`, via de enige gate-module
   * `apps/web/src/features/import/relations/relationImportAvailability.ts`. De
   * FE-gate is comfort; het endpoint is de autoriteit en her-evalueert per call
   * (S2-AC6).
   */
  relationImport?: RelationImportCapability | null;

  /**
   * Toont deze instantie de **Meldingen**-tab in de glas-applicatie? Gevoed door
   * de appSetting `Intakes.Enabled` in `AppServices\Web.config`; BE:
   * `AppServices\Dossiers\IntakesFeatureSwitch.cs`.
   *
   * ⚠ Een gewone `boolean`, geen capability-object zoals {@link restrictedRepairer}
   * en {@link relationImport}. Die twee zijn per GEBRUIKER en kennen een derde
   * toestand ("onbekend"); deze is per DEPLOY en **fail-closed**, dus ontbrekend
   * en uit vallen BE-zijde al samen. De FE keyt op `=== true`.
   *
   * Optioneel én nullable getypeerd zodat bestaande en gemockte whoami-payloads
   * zonder het veld blijven compileren (precedent: {@link isKetenUser},
   * {@link canSwitch}, {@link restrictedRepairer}).
   *
   * ⚠ Dit stuurt **alleen het menu-item**. De routes `/intakes` en
   * `/intakes/{nummer}` blijven bereikbaar en de endpoints blijven antwoorden —
   * bewuste keuze, geen omissie. Lees het dus nooit als autorisatie.
   */
  intakesEnabled?: boolean | null;

  /**
   * Story `be-fe-relatiebeheer-herstellers` (HB, F.1) — toont de FE de tab
   * **Herstellers** in Relatiebeheer? `true` voor System en het keten-hoofdkantoor.
   * Alleen een menu-hint: elk `api/repairers`-endpoint beslist zelf (403/404/503).
   * Optioneel getypeerd zodat gemockte whoami-payloads blijven compileren; de FE keyt
   * op `=== true`.
   */
  canManageRepairers?: boolean | null;

  /**
   * Story `be-fe-support-app-behandelen` (S2, AC 2) — toont de FE de app **Support**? `true` voor System en het
   * keten-hoofdkantoor. Alleen een menu-hint: elk `api/support/inbox`-endpoint beslist zelf (403/503). De FE keyt op
   * `=== true`.
   */
  canHandleSupport?: boolean | null;

  /**
   * Story `be-fe-systeem-gebruikers` (AC 4) — toont de FE **Systeem → Gebruikers**? `true` voor System en het
   * keten-hoofdkantoor. Alleen een menu-hint: elk `api/system/users`-endpoint beslist zelf (403/404/503). De FE keyt op
   * `=== true`.
   */
  canManageUsers?: boolean | null;
  /**
   * Story `be-fe-meerprijs-dealer-kortingen` (AC 1.2/11.1): toont de FE Relatiebeheer → Meerprijs dealer? `true` ⇔ de
   * vlag `DealerSurcharge.Enabled` staat aan én de gebruiker is System of het keten-hoofdkantoor. De server stuurt de
   * sleutel altijd mee (ook `false`). Alleen een menu-hint: elk `api/dealer-surcharge`-endpoint beslist zelf. De FE keyt
   * op `=== true`.
   */
  canManageDealerSurcharge?: boolean | null;

  /**
   * EPIC-022 S022.1 (AC 18): staat de voorraad van de hersteller van deze sessie in CARculate? Dan is de tab Inkoop →
   * Voorraad ingeschakeld. De server stuurt de sleutel altijd mee (ook `false`). Alleen een menu-hint: de poort van
   * `api/inventory/warehouses` beslist zelf. De FE keyt op `=== true`.
   */
  inventoryInCarculate?: boolean | null;

  /**
   * Story `be-fe-meerprijs-dealer-berekenen`: staat de meerprijs dealer op deze omgeving aan? Altijd aanwezig. De FE haalt
   * de context van het venster alleen op bij `=== true`; de server beslist.
   */
  dealerSurchargeEnabled?: boolean | null;

  /**
   * Story `fe-be-monteur-s1-basis` (EPIC-021, S021.1) — de monteur-capability van
   * DEZE sessie (BE: `AppServices\Infrastructure\FitterCapability.cs`).
   *
   * **AFWEZIG** wanneer de gebruiker betrouwbaar geen monteur-betekenis heeft (geen
   * koppeling én geen slot): whoami is dan byte-identiek aan vóór deze story. Daarom
   * optioneel én nullable getypeerd (precedent {@link restrictedRepairer}).
   *
   * De FE leest dit veld uitsluitend via de gate-module
   * `apps/web/src/features/fitter/fitterAccess.ts`. `fitterOnly === true` of
   * `resolved === false` → de route-guard stuurt naar `/monteur`. Een
   * system-gebruiker krijgt `resolved: false` nooit (BE-regel, TA-review F-1).
   */
  fitter?: FitterCapability | null;

  /**
   * Story `fe-be-monteur-s4-fotos` (EPIC-021, S021.4), besluit B-4 — staat het
   * watermerk op de foto's van deze monteur aan? Uit de sessie (whoami blijft DB-vrij).
   * **Afwezig = onbekend → de FE neemt de standaard: AAN.** Wijzigen via
   * `PUT /api/me/photo-watermark`.
   */
  photoWatermark?: boolean | null;

  /**
   * Story `fe-be-monteur-s5c-ingelogd-blijven` (S021.5c) §2.5 — tot wanneer dit toestel
   * ingelogd blijft (ISO-UTC). AFWEZIG zonder toestel-token. Uit de sessie (DB-vrij): na
   * intrekken op een ander toestel staat de oude datum er nog tot het einde van deze sessie.
   */
  persistentLoginUntil?: string | null;

  /**
   * S021.5c B-T5 — `true` zolang deze sessie uit een toestel-token komt en nog niet met het
   * wachtwoord is bevestigd: 2FA opnieuw instellen, uitzetten en nieuwe herstelcodes vragen dan
   * eerst het huidige wachtwoord. Anders afwezig.
   */
  stepUpRequired?: boolean | null;

  /**
   * Story `fe-be-cursusmodus-hersteller-mvp` (AC-A1.4 / AC-A3.1) — de trainingsomgeving.
   *
   * **De sleutel ontbreekt helemaal** wanneer de appSetting `Training.Enabled` uit staat
   * (productie): whoami is dan byte-identiek aan vóór deze story. Staat de instelling aan, dan
   * is hij er altijd; `active === true` alleen als de veiligheidscontrole `safe` is.
   *
   * De FE leest dit veld **uitsluitend** via `apps/web/src/auth/trainingMode.ts`
   * (`isTrainingActive`, `canUseCourses`, `isTrainingEnvironment`). Fail-closed: alleen
   * `active === true` telt; `"true"`, `1`, `null` en afwezig zijn uit.
   */
  training?: TrainingFlag | null;

  /**
   * Story `be-fe-menu-aan-uit` (AC 9) — de menu-sleutels die voor deze gebruiker uit staan
   * (`menu.<appId>` of `menu.<appId>.<tabId>`), al opgelost met rol-voorrang. System krijgt een lege
   * lijst. De FE leest dit **uitsluitend** via `apps/web/src/auth/menuVisibility.ts`. Ontbreekt het veld
   * (oudere backend), dan staat alles aan.
   */
  menuHidden?: string[] | null;

  /**
   * Story `be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen` (AC 6/7) — `UA_Repairer.InvoicingActive` van
   * de eigen hersteller. Bij `false` is de app Facturatie verborgen. Afwezig (System, geen hersteller, leesfout) of
   * `true` ⇒ zichtbaar. De FE leest dit **uitsluitend** via `apps/web/src/auth/invoicingActive.ts`.
   */
  invoicingActive?: boolean | null;

  /**
   * Story `be-fe-contactgegevens-bevestigen` (AC 1/7) — `true` zolang deze gebruiker nog geen eigen e-mailadres heeft
   * bevestigd. Dan toont de app (kantoor én monteur) alleen het scherm "Vul je contactgegevens aan", en weigert de
   * backend elke andere call met 403 `contact_verification_required`. **Afwezig bij `false`**, zodat whoami
   * byte-gelijk blijft voor iedereen met een eigen adres. De FE leest dit **uitsluitend** via
   * `apps/web/src/auth/contactVerification.ts` (`isContactVerificationRequired`, fail-open: alleen `=== true` telt).
   */
  contactVerificationRequired?: boolean | null;
}

/** Story `fe-be-cursusmodus-hersteller-mvp` — wire-vorm van whoami `training`. */
export interface TrainingFlag {
  /** `true` alleen als `Training.Enabled` aan staat én de laatste veiligheidscontrole `safe` is. */
  active: boolean;
}

/**
 * Story `fe-be-cursusmodus-hersteller-mvp` (§Contract, AC-B3.2) — 200 van
 * `GET /api/training/practice-dossier`: het eigen oefendossier van de ingelogde cursist.
 *
 * Foutantwoorden: 404 met een lege body (training uit of niet actief), 403
 * `role_not_allowed`, 404 `practice_dossier_not_found`, 409 `practice_dossier_closed`.
 */
export interface PracticeDossierDto {
  /** GUID, lowercase met streepjes (R-19). */
  dossierId: string;
  dossierNumber: number;
  license: string;
  /** De eurocode van de oefen-voorruit (seed-constante). */
  windshieldEurocode: string;
}

/** De totaaluitkomst van de veiligheidscontrole (AC-A2.2). */
export type TrainingSafetyOutcome = 'safe' | 'unsafe' | 'unknown';

/** Het oordeel per item van de checklist (AC-A2.2). */
export type TrainingSafetyVerdict = 'closed' | 'open' | 'unknown';

/**
 * Eén item van de veiligheidscontrole (AC-A2.5): alleen id, label, oordeel, bron en — voor een
 * niet-geheim adres — de host (bij SMTP `host:poort`). Nooit een waarde, pad, query of token.
 */
export interface TrainingSafetyItem {
  id: string;
  /** Nederlands label voor het scherm, bv. "Mail". */
  label: string;
  verdict: TrainingSafetyVerdict;
  /** De bron, bv. "appSettings: smtpserver". */
  source?: string | null;
  /** Alleen bij een niet-geheim adres; bij SMTP `host:poort`. */
  host?: string | null;
  /** Korte Nederlandse toelichting van de BE bij het oordeel (nooit een geheim). De FE toont hem niet. */
  reason?: string | null;
}

/**
 * Story `fe-be-cursusmodus-hersteller-mvp` (AC-A5.1) — 200 van `GET /api/training/safety`
 * (system-only). 404 met een lege body wanneer `Training.Enabled` uit staat.
 */
export interface TrainingSafetyResponse {
  outcome: TrainingSafetyOutcome;
  /** Tijdstip van de controle, lokale tijd (geen `Z`). */
  checkedAt: string;
  /** Starttijdstip van het proces (laatste recycle), lokale tijd (AC-A2.7). */
  processStartedAt: string;
  items: TrainingSafetyItem[];
}

/** Een tarief van een betaalde opvraging: bedrag exclusief btw, met ingangsdatum (`yyyy-MM-dd…`). */
export interface ServiceTariffAmount {
  amountExVat: number;
  validFrom: string;
}

/** Geldend en eerstvolgend tarief; elk `null` als er geen is. */
export interface ServiceTariffEntry {
  current: ServiceTariffAmount | null;
  next: ServiceTariffAmount | null;
}

/** `GET /api/service-tariffs` — voor de bevestigingsvensters (migratie 129). */
export interface ServiceTariffsResponse {
  chassisLookup: ServiceTariffEntry;
  ruitIdRequest: ServiceTariffEntry;
  /** RuitID wordt op deze omgeving als test aangevraagd: geen kosten. */
  ruitIdTestMode: boolean;
}

export type ServiceTariffCode = 'chassis_lookup' | 'ruitid_request';

/** Eén rij in het beheer (system-only). */
export interface ServiceTariffAdminRow {
  id: string;
  code: ServiceTariffCode;
  amountExVat: number;
  validFrom: string;
  state: 'past' | 'current' | 'future';
  canDelete: boolean;
}

export interface ServiceTariffAdminResponse {
  rows: ServiceTariffAdminRow[];
  /** `false` als migratie 129 nog niet gedraaid is. */
  available: boolean;
}

export interface ServiceTariffCreateRequest {
  code: ServiceTariffCode;
  amountExVat: number;
  /** `yyyy-MM-dd`, vandaag of later. */
  validFrom: string;
}

/** Body van `POST /api/training/seed` (system-only, backlog 50). */
export interface TrainingSeedRequest {
  password: string;
  /** 1..50; afwezig = 20. */
  accounts?: number;
}

/** Eén onderdeel van de seed-uitkomst. `status`: created · existing · completed · existing_incomplete · incomplete. */
export interface TrainingSeedPart {
  part: string;
  status: string;
  loginName?: string;
  dossierNumber?: number;
  password?: string;
}

/** 200 van `POST /api/training/seed`. */
export interface TrainingSeedResponse {
  accounts: number;
  accountsNotTouched: number;
  durationMs: number;
  parts: TrainingSeedPart[];
  safety: string;
}

/** Foutbody van de seed (4xx/5xx). `reason` is een Engelse code; de tekst staat in de FE. */
export interface TrainingSeedError {
  reason: string;
  part?: string;
  loginName?: string;
  dossierNumber?: number;
  detail?: string;
  count?: number;
  items?: TrainingSafetyItem[];
  correlationId?: string;
}

/**
 * Story `fe-be-monteur-s1-basis` (S021.1) — wire-vorm van `FitterCapability`.
 */
export interface FitterCapability {
  /** `false` = ONBEKEND: de read bij het inloggen mislukte. Dicht buiten de allowlist. */
  resolved: boolean;
  /** Minstens één monteur-koppeling (PB_FitterUser) binnen de eigen company. */
  isFitter: boolean;
  /** "Alleen monteur-app" staat aan; blijft aan na het wissen van de laatste koppeling (B-7). */
  fitterOnly: boolean;
  /** De gekoppelde monteurs (UA_Fitter-id's) — voor "eigen dossiers" (S021.2). */
  fitterIds: string[];
}

/** Respons van `POST /api/fitter/capability/refresh` (de heel-route, TA-review F-2). */
export interface FitterCapabilityRefreshResponse {
  /** Ontbreekt wanneer de gebruiker betrouwbaar geen monteur-betekenis heeft. */
  fitter?: FitterCapability | null;
}

/**
 * Story `fe-be-monteur-s8a-nieuw-dossier` (S021.8a) — de soort werk bij een nieuw dossier
 * vanuit de monteur-app. `replace` = vervanging, `repair` = reparatie.
 */
export type FitterRepairKind = 'replace' | 'repair';

/** Body van `POST /api/fitter/dossiers` — precies deze drie velden. */
export interface FitterDossierCreateRequest {
  /** Het kenteken zoals ingetikt (de FE stuurt `formatNlLicense`, net als het kantoor). */
  license: string;
  repairKind: FitterRepairKind;
  /**
   * Eén id per aanmaakpoging (UUID). Een herhaling met dezelfde id na een verloren antwoord
   * levert hetzelfde dossier terug (`replayed: true`) in plaats van een tweede.
   */
  clientRequestId: string;
}

/**
 * Antwoord van `POST /api/fitter/dossiers` (201 nieuw, 200 herhaling). De vijf
 * verzekeraar-velden staan er alleen bij een verzekeraar-gebonden hersteller.
 */
export interface FitterDossierCreateResponse {
  id: string;
  number: number;
  replayed: boolean;
  insuranceCompanyId?: string;
  insuranceCompanyName?: string;
  insuranceSort?: string;
  dossierType?: string;
  policyServiceCode?: string;
  /**
   * §13 (besluit 05-10-2026) — de server maakt bij een nieuw dossier meteen een
   * planbord-afspraak (vandaag, vanaf het kwartier, 1 uur, de eigen monteur). `false` = het
   * dossier bestaat, maar staat niet in de agenda. Afwezig bij een herhaling.
   */
  appointmentCreated?: boolean;
}

/**
 * Story `fe-be-monteur-s5a-agenda-en-meer` (S021.5a) — één afspraak in "Mijn agenda"
 * (`GET /api/fitter/agenda`). De contactvelden dragen dezelfde wire-namen als
 * {@link DossierDetail}, zodat de regels in `features/fitter/dossier/dossierInfo.ts`
 * (bellen, route, lease/fleet → bestuurder) ze ongewijzigd lezen. Ze zijn `null` bij een
 * afspraak zonder dossier of met een dossier dat de gebruiker niet mag zien (H-15).
 */
export interface FitterAgendaAppointment {
  id: string;
  /** 1 Declaratie · 2 Werkbon · 3 Los · 4 Afwezig. */
  appointmentType: number;
  /** 1 Reparatie · 2 Vervanging · `null` bij Los/Afwezig. */
  workType: number | null;
  /** 0 gepland · 1 bezig · 2 klaar. */
  status: number;
  /** Lokale wandkloktijd zonder offset (`yyyy-MM-ddTHH:mm:ss`) — nooit door `new Date()` halen. */
  start: string;
  end: string;
  fitterIds: string[];
  title: string | null;
  note: string | null;
  /** 1 eigen werkplaats · 2 bij opdrachtgever · 3 bij klant · 4 op locatie. */
  locationKind: number | null;
  locationText: string | null;
  dossierId: string | null;
  license: string | null;
  vehicle: string | null;
  insuranceSort: string | null;
  customerName: string | null;
  customerPhoneNumber: string | null;
  customerMobile: string | null;
  customerAddress: string | null;
  customerHouseNumber: string | null;
  customerHouseNumberExtension: string | null;
  customerZipCode: string | null;
  customerCity: string | null;
  driverName: string | null;
  driverPhoneNumber: string | null;
  driverMobile: string | null;
  driverAddress: string | null;
  driverHouseNumber: string | null;
  driverHouseNumberExtension: string | null;
  driverZipCode: string | null;
  driverCity: string | null;
  repairLocationStreet: string | null;
  repairLocationHouseNumber: string | null;
  repairLocationHouseNumberExtension: string | null;
  repairLocationZipCode: string | null;
  repairLocationCity: string | null;
  /**
   * S021.V1 (PB-8) — dossiernummer (`UA_Dossier.Number`, = `DossierDetail.number`). `null`
   * zonder (zichtbaar) dossier (H-15). ⚠ Een bewaarde offline-agenda van vóór S021.V1 mist
   * dit veld (`undefined`, PB-11): lees tolerant.
   */
  dossierNumber: number | null;
  /**
   * S021.V1 — werkbonnummer (`UA_Dossier.WorkOrderNumber`), ruw zoals het detail; `0` of
   * kleiner = niet gevuld (R-20). `null` bij een niet-zichtbaar dossier.
   */
  workOrderNumber: number | null;
  /** S021.V1 (PB-7) — opdrachtgever: de naam van de gekoppelde debiteur, zoals de Intake-tab. */
  referenceDebtorName: string | null;
  /**
   * S021.V1 (PB-10) — eurocodes van de ruiten in de calculatie, in calculatievolgorde. Nooit
   * `null` van de server; leeg bij een reparatie of een niet-zichtbaar dossier.
   */
  eurocodes: string[];
}

/**
 * Respons van `GET /api/fitter/branding` (S021.5b §4): de naam, kleur en iconen van de
 * monteur-app voor deze instantie. Anoniem en lock-vrij; ongeldige of lege instellingen zijn
 * aan de serverkant al vervangen door de defaults.
 */
export interface FitterBranding {
  /** Naam onder het icoon en op het inlogscherm. */
  appName: string;
  /** Korte naam op het startscherm. */
  shortName: string;
  /** Merkkleur, `#RRGGBB`. */
  themeColor: string;
  /** Pad op dezelfde site naar het icoon van 192×192. */
  icon192: string;
  /** Pad op dezelfde site naar het icoon van 512×512. */
  icon512: string;
  /** Pad op dezelfde site naar het maskable-icoon (Android). */
  iconMaskable: string;
}

/** Respons van `GET /api/fitter/agenda` (S021.5a). */
export interface FitterAgendaResponse {
  /** De getoonde dag, `yyyy-MM-dd`, gekozen door de server als er geen `date` meeging. */
  date: string;
  /** Is `date` vandaag volgens de server (PB-4)? */
  isToday: boolean;
  fitters: Array<{ id: string; name: string }>;
  /** Op start, eind, id (server-volgorde). */
  appointments: FitterAgendaAppointment[];
}

/**
 * Story `fe-be-importraamwerk-fase2-controleren` (S2-AC8/AC8b) — wire-vorm van
 * `RelationImportCapability`. Exact twee velden, en de vorm spiegelt met opzet
 * {@link RestrictedRepairer}.
 */
export interface RelationImportCapability {
  /**
   * Is de schakelaar betrouwbaar gelezen? `false` = **ONBEKEND** (mislukte
   * read).
   *
   * ⚠ De FE leest dit veld **nergens** (S2-AC8b, geborgd met een scan): zodra er
   * een conditie op staat is `Onbekend` ineens een derde UI-toestand en heeft de
   * gate twee betekenissen. `Onbekend` bereikt de FE daarom als
   * `available: false` — de FE kent het verschil tussen "uit" en "onbekend"
   * niet en hoeft dat niet te kennen; dat onderscheid is BE-diagnostiek.
   */
  resolved: boolean;
  /** Schakelaar staat voor deze sessie op **Aan**. De enige poort-conditie. */
  available: boolean;
}

/**
 * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (S-C) — wire-vorm van
 * `RestrictedRepairerCapability` (BE: `AppServices\Infrastructure\
 * RestrictedRepairerCapability.cs`). Exact vijf velden; bevat **bewust geen**
 * hersteller-id — de FE mag hieruit nooit afleiden *welke* hersteller beperkt is
 * (`repairerId` op {@link WhoAmIResponse} blijft de enige hersteller-identiteit).
 */
export interface RestrictedRepairer {
  /**
   * Is de vlag betrouwbaar vastgesteld? `false` = **ONBEKEND** (een mislukte
   * schakelaar- of per-hersteller-read), niet "beperkt" (fundering D1 + C1).
   * De FE consumeert dit veld **niet**: bij `resolved: false` verandert er niets,
   * anders verdwijnen bij een DB-hikje tabs op instanties waar de regeling niet
   * eens bestaat. De BE-poort doet er wél iets mee (gerichte her-lezing).
   */
  resolved: boolean;
  /** Omgevings-schakelaar AAN **én** `IsRestricted = 1` voor DEZE hersteller. */
  active: boolean;
  /** De verzekeraar waaraan de hersteller gebonden is; `null` bij inrichtingsfout. */
  insuranceCompanyId: string | null;
  /** Leesbare naam van die verzekeraar; `null` bij inrichtingsfout. */
  insuranceCompanyName: string | null;
  /** Mag deze beperkte hersteller het LOSSE kladblok (`/calculatie`) gebruiken? */
  mayUseScratchpad: boolean;
}

/** "Wie acteert als wie"-info voor de acting-banner (story `fe-be-switch-user`). */
export interface ActingAsInfo {
  /** Login-naam van de echte (ingelogde) user. */
  realUserName: string;
  /** Login-naam van de vestiging waar nu als geacteerd wordt. */
  targetUserName: string;
}

/**
 * Eén doel-vestiging in de `GET /api/auth/switch-users`-respons (S1). De switch-
 * sleutel is de `loginName` (uniek + globaal resolvebaar) — bewust GEEN user-id:
 * `cup_GetSwichUsers` levert de hersteller-child-id (niet `GU_User.ID`), en de
 * vestiging-user zit in een andere company, dus een id-resolve scoopt fout.
 */
export interface SwitchUser {
  loginName: string;
  name: string;
}

export type UserContextType =
  | 'System'
  | 'RepairerHQ'
  | 'RepairerLocation'
  | 'RegularUser';

// ─── Mijn gegevens — eigen contact (story fe-mijn-gegevens-scherm S-C) ──────

/**
 * Bewerkbare velden van het eigen `rb_Contact`. Het doelcontact wordt
 * server-side uit de sessie (`GU_User.ContactID`) afgeleid — de FE stuurt
 * nooit een contact-id mee.
 */
export interface ProfileContactDto {
  titleId: string | null;
  firstName: string | null;
  initials: string | null;
  prefix: string | null;
  surName: string | null;
  functionDescription: string | null;
  languageId: string | null;
  email: string | null;
  cellPhone: string | null;
  phone: string | null;
  fax: string | null;
  mailStreet: string | null;
  mailHouseNumber: string | null;
  mailZipCode: string | null;
  mailCity: string | null;
  visitStreet: string | null;
  visitHouseNumber: string | null;
  visitZipCode: string | null;
  visitCity: string | null;
}

/** Dropdown-optie (aanhef / taal). */
export interface ProfileOption {
  id: string;
  label: string;
}

/** Response van `GET /api/profile/contact`: eigen contact + lookup-opties. */
export interface ProfileContactResponse {
  contact: ProfileContactDto;
  titles: ProfileOption[];
  languages: ProfileOption[];
}

// ─── Mijn gegevens — facturatiecontact van het bedrijf (S-G) ────────────────

/**
 * Facturatie-/debiteur-NAW van het BEDRIJF — een ander contact dan het eigen
 * contact (`ProfileContactDto`). De server bepaalt het doelcontact zelf
 * (debiteur-NAW → fallback `_default`); de FE stuurt nooit een contact-id mee.
 */
export interface ProfileBillingDto {
  titleId: string | null;
  firstName: string | null;
  initials: string | null;
  prefix: string | null;
  surName: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  houseNumber: string | null;
  zipCode: string | null;
  city: string | null;
  iban: string | null;
  bic: string | null;
  accountHolder: string | null;
  /** Welk contact server-side geresolved werd: `debiteur` of `default`. Read-only. */
  source: string | null;
}

/** Response van `GET /api/profile/billing`: facturatiecontact + aanhef-lookup. */
export interface ProfileBillingResponse {
  contact: ProfileBillingDto;
  titles: ProfileOption[];
}

// ─── Mijn gegevens — beveiliging (S-D, self-service) ────────────────────────

/** Wachtwoord wijzigen vanuit een ingelogde sessie. Nooit loggen. */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** Self-service 2FA-status: aan/uit + ongebruikte herstelcodes-teller. */
export interface TwoFactorStatusResponse {
  enabled: boolean;
  remainingRecoveryCodes: number;
}

/** Respons van self-service `2fa/enroll-begin`: alleen de otpauth-URI (gevoelig). */
export interface SelfEnrollBeginResponse {
  otpauthUri: string;
}

// ─── Mijn gegevens — bedrijf (S-E) ──────────────────────────────────────────

/** Algemene bedrijfsvelden (`RB_Company`). */
export interface ProfileCompanyDto {
  name: string | null;
  website: string | null;
  vatAccountable: boolean | null;
  vatNumber: string | null;
  kvkNumber: string | null;
}

/** Hersteller-config (`UA_Repairer`). Het eXchange-wachtwoord wordt nooit teruggegeven. */
export interface ProfileRepairerDto {
  hourlyRate: number | null;
  bovagNumber: string | null;
  dispatchRelationNumber: string | null; // read-only
  exchangeLoginCode: string | null;
  exchangeRelationNumber: number | null;
  hasExchangePassword: boolean;
  exchangePriceListId: string | null;
  templateText1: string | null;
  templateText2: string | null;
}

/** Response van `GET /api/profile/company`. */
export interface ProfileCompanyResponse {
  isRepairer: boolean;
  isMonteur: boolean;
  /** Server-autoritatieve edit-gate: `false` voor monteurs. */
  canEdit: boolean;
  company: ProfileCompanyDto;
  repairer: ProfileRepairerDto | null;
  priceLists: ProfileOption[];
  /**
   * Server-side bepaald (story fe-factuur-online-keten): config gezet ÉN het lid
   * heeft ≥ 1 keten-factuur. De FE rendert de keten-tab uitsluitend op deze vlag.
   */
  ketenInvoicesApplicable: boolean;
  /**
   * Story `fe-be-iclaim-poliscontrole-en-aanbieden` fase 2 — volledigheid van de
   * vijf iClaim-instellingen. `null` voor een niet-hersteller.
   */
  iClaimStatus: IClaimStatusResponse | null;
}

/**
 * Eén iClaim-instelling. ⚠ Draagt NOOIT de waarde — alleen of hij gevuld is en
 * waar hij vandaan komt. Het zijn inloggegevens (wachtwoord, client secret).
 */
export interface IClaimFieldStatus {
  /** De `UF_CompanyInvoiceSetting`-sleutelnaam, bv. `iClaimShopCode`. */
  key: string;
  /** Nederlands label voor het scherm, bv. "Client secret". */
  label: string;
  /**
   * Gevuld bij de hersteller, of — indien toegestaan — bij het
   * keten-hoofdkantoor of in de gedeelde standaardinstelling.
   */
  filled: boolean;
  /**
   * `'repairer'` | `'keten'` | `'systeem'`, of `null` wanneer het veld ontbreekt.
   *
   * ⚠ `'systeem'` is er sinds story `be-fe-iclaim-verzendlaag-en-aanbieden`
   * (AC-23): de derde trap van de terugvalketen, de gedeelde
   * EuroMotive-standaardinstelling uit `Web.config`. Elke consument hoort er een
   * **eigen** tekst voor te hebben — valt hij in een `else`-tak samen met
   * `'repairer'`, dan leest de hersteller "dit heb ik zelf ingevuld" terwijl hij
   * juist op de gedeelde set draait.
   *
   * ⚠ `'systeem'` komt NOOIT voor bij `iClaimShopCode`: die erft van niets
   * (U3/K2), en er bestaat ook geen systeem-appSetting voor.
   */
  source: 'repairer' | 'keten' | 'systeem' | null;
  /**
   * Mag dit veld van het keten-hoofdkantoor komen? Onwaar voor `iClaimShopCode`:
   * die is herstellerspecifiek — een geërfde shopcode zet de claim stil op naam
   * van een andere hersteller.
   */
  chainFallbackAllowed: boolean;
}

/**
 * Volledigheid van de iClaim-koppeling (`GET /api/profile/company`).
 * "Volledig" = alle vijf gevuld — dat is nadrukkelijk niet hetzelfde als
 * "werkt": er is geen verbindingstest (K1).
 */
export interface IClaimStatusResponse {
  complete: boolean;
  fields: IClaimFieldStatus[];
  /** De sleutelnamen die ontbreken — zonder deze is "niet volledig" een dood signaal. */
  missingFields: string[];
}

/** Bewerkbare set voor `PUT /api/profile/company`. eXchange-wachtwoord leeg = ongemoeid. */
export interface ProfileCompanyUpdateDto {
  name: string | null;
  website: string | null;
  vatAccountable: boolean | null;
  vatNumber: string | null;
  kvkNumber: string | null;
  hourlyRate: number | null;
  bovagNumber: string | null;
  exchangeLoginCode: string | null;
  exchangeRelationNumber: number | null;
  exchangePassword: string | null;
  exchangePriceListId: string | null;
  templateText1: string | null;
  templateText2: string | null;
}

/** Response van `GET /api/profile/company/exchange-status`. */
export interface ExchangeStatusResponse {
  checked: boolean;
  connected: boolean;
}

// ─── Mijn gegevens — Betalen (Mollie) (story fe-be-mollie-config-mijn-gegevens) ─

/**
 * Response van `GET /api/profile/mollie-settings`. Bevat NOOIT de key of de
 * ciphertext — alleen afgeleide, veilige velden (BE-borg). `keyMode` volgt
 * uitsluitend uit de (exact-lowercase) prefix van de opgeslagen key.
 */
export interface MollieSettingsResponse {
  enabled: boolean;
  /** Er staat een (versleutelde) key in de DB. Nooit de waarde zelf. */
  keyIsSet: boolean;
  /** `••••…<laatste 4>` of `null` als er geen key is. */
  keyMasked: string | null;
  /** `"test"` / `"live"` (uit de prefix) of `null`. */
  keyMode: 'test' | 'live' | null;
  /** Redirect-URL (geen secret) of `null`. Op het scherm: "Pagina na betalen". */
  redirectUrl: string | null;
  /**
   * Laatst bewaarde uitkomst van "Verbinding testen" voor de huidige key, of `null`
   * (nooit getest, key vervangen, of migratie 128 nog niet gedraaid). Story
   * `be-fe-mollie-inrichten-uitleg` (PB-3). Optioneel voor een oudere backend.
   */
  lastTest?: MollieLastTest | null;
}

/** Bewaarde testuitkomst — niets van de key, alleen wanneer + uitkomst + iDEAL. */
export interface MollieLastTest {
  result: 'valid' | 'invalid' | 'unreachable';
  /** `true`/`false`, of `null` = niet vast te stellen (alleen gevuld bij `valid`). */
  idealActive: boolean | null;
  /** Tijdstip van de test (lokale servertijd, ISO zonder zone). */
  testedAt: string;
}

/**
 * Body voor `PUT /api/profile/mollie-settings`. `apiKey` leeg/weggelaten =
 * bestaande key ongemoeid laten; gevuld = getrimd + prefix-gevalideerd +
 * versleuteld opgeslagen door de BE. De `RepairerID` komt uit de sessie.
 */
export interface MollieSettingsUpdateRequest {
  enabled: boolean;
  redirectUrl: string | null;
  /** Leeg/null = bestaande key behouden. */
  apiKey: string | null;
}

/** Uitkomst van `POST /api/profile/mollie-settings/test` — altijd HTTP 200. */
export interface MollieTestResponse {
  result: 'valid' | 'invalid' | 'unreachable';
  /**
   * Staat iDEAL aan? Alleen bij `valid`; `null` = niet vast te stellen (dan geen
   * iDEAL-claim). Optioneel voor een oudere backend. Story `be-fe-mollie-inrichten-uitleg`.
   */
  idealActive?: boolean | null;
  /** Tijdstip van deze test (lokale servertijd). */
  testedAt?: string;
  /** Bewaard? `false` zonder migratie 128: dan kent alleen deze sessie de uitkomst. */
  saved?: boolean;
}

// ─── Mijn gegevens — Factuur online (story fe-factuur-online) ───────────────

export interface ProfileInvoice {
  invoiceNumber: number;
  period: number; // YYYYMM (0 = onbekend)
  subTotal: number;
  vatTotal: number;
  total: number;
}

/** `configured:false` → geen EuroMotiveCode / geen API-key → "niet geconfigureerd". */
export interface ProfileInvoicesResponse {
  configured: boolean;
  invoices: ProfileInvoice[];
}

export interface ProfileInvoicePdfResponse {
  pdfBase64: string;
}

// ─── Mijn gegevens — Factuur online (keten), Bron 2 ─────────────────────────

/**
 * Eén keten-factuurregel. Vorm = bron 1 (`ProfileInvoice`) + de `id`
 * (`IN_Invoice.ID`, Guid-string) als download-sleutel: de PDF wordt via deze Guid
 * opgehaald, niet via het (per-keten-reeks niet-unieke) `invoiceNumber`.
 */
export interface KetenInvoice extends ProfileInvoice {
  id: string;
}

/**
 * Response van `GET /api/profile/company/keten-invoices` (story
 * fe-factuur-online-keten). `applicable:false` → config leeg of geen keten-rijen
 * → de FE rendert de keten-tab niet.
 */
export interface KetenInvoicesResponse {
  applicable: boolean;
  invoices: KetenInvoice[];
}

export interface LoginRequest {
  userName: string;
  password: string;
  /**
   * Story `fe-be-monteur-s5c-ingelogd-blijven` (S021.5c) §2.2 — het schuifje "Ingelogd blijven
   * op dit toestel". Alleen het monteur-inlogscherm stuurt het; weggelaten = de sleutel staat
   * niet in de body (het kantoor-inlogscherm). Alleen een echte `true` levert een toestel-token
   * op, en alleen voor een alleen-monteur (B-4).
   */
  rememberDevice?: boolean;
}

// ─── Epic veilige-authenticatie — multi-step login-flow ────────────────────

/**
 * De gesloten set flow-statussen (BE `LoginFlowStatus` — exacte string-match,
 * story F-9). `authenticated` is de eindstaat (sessie-cookie gezet).
 */
export type LoginFlowStatus =
  | 'needPasswordReset'
  | 'need2faEnroll'
  | 'need2faCode'
  | 'authenticated';

/** Respons van `auth/flow/login` + vervolgstappen (behalve enroll-verify). */
export interface FlowResponse {
  status: LoginFlowStatus;
  /** Aanwezig bij elke status behalve `authenticated`. Nooit loggen. */
  stepToken?: string | null;
  /** Alleen bij `authenticated`. */
  identity?: WhoAmIResponse | null;
}

export interface SetNewPasswordRequest {
  stepToken: string;
  newPassword: string;
  confirmPassword: string;
  /** S021.5c §2.2 — zie {@link LoginRequest.rememberDevice}. */
  rememberDevice?: boolean;
}

export interface EnrollBeginResponse {
  stepToken: string;
  /** otpauth://-URI; bevat het secret → behandel als gevoelig, nooit loggen. */
  otpauthUri: string;
}

export interface EnrollVerifyResponse {
  status: LoginFlowStatus;
  /** Eenmalig getoonde recovery-codes — nooit loggen, niet in URL. */
  recoveryCodes: string[];
  identity?: WhoAmIResponse | null;
}

export interface RecoveryRegenerateResponse {
  /** Eenmalig getoonde nieuwe recovery-codes — nooit loggen, niet in URL. */
  recoveryCodes: string[];
  /** "Nog N over"-teller direct na regenereren. */
  remainingCount: number;
}

export interface DossierListItem {
  id: string;
  number: number | null;
  workOrderNumber: number | null;
  license: string | null;
  customerName: string | null;
  damageDate: string | null;
  repairDate: string | null;
  status: string | null;
  insuranceCompanyName: string | null;
  isArchive: boolean;
  /**
   * Story `fe-be-dossier-indicatoren` (Fase B): de voor de caller zichtbare
   * indicatoren van dit dossier (eigen bedrijf / VisibleToAll / system —
   * dezelfde regel als detail). Gebatcht per pagina meegeleverd in één extra
   * query (geen N+1). Lege array bij 0 zichtbare indicatoren. Optioneel zodat
   * oudere list-responses (van vóór Fase B) zonder breuk passen.
   */
  indicators?: DossierIndicator[];
  /**
   * Story `fe-calculatie-kolom-zoeklijst` (2026-06-12): `true` wanneer er een
   * filiaal-calculatie bestaat voor dit dossier (zelfde definitie als de
   * Calculatie-tab). Gebatcht per pagina meegeleverd door de BE (geen N+1).
   * Optioneel zodat oudere list-responses zonder breuk passen.
   */
  hasCalculation?: boolean;
  /**
   * Besluit eigenaar 19-10-2026: het kenmerk "Te verzenden" (géén status). `true` wanneer
   * het dossier in de set van de dashboardtegel en het filter `handling=to_send` valt (het
   * gedeelde BE-predicaat), anders `false`. Gebatcht per pagina meegeleverd. Ontbreekt
   * wanneer de BE het niet kon lezen (of bij `view=fitter`) — dan geen kenmerk tonen.
   */
  sendPending?: boolean;
  /**
   * Story `fe-be-monteur-s2-mijn-werk` (S021.2) — de kaartvelden van Mijn werk. Alleen
   * aanwezig bij `view=fitter` (dan alle acht, eventueel `null`); anders ontbreken ze.
   */
  /** `UA_Dossier.FitterIsReady` (`NULL` → `false`). */
  fitterIsReady?: boolean;
  /** Naam van de bestuurder, ruw. */
  driverName?: string | null;
  /** Lease- of fleet-verzekeraar. */
  isLeaseOrFleet?: boolean;
  /** Soort werk: `UA_RepairType.Description`. */
  repairTypeName?: string | null;
  /** Ruwe `CarPosition`-CSV; vertalen met `formatDamageZones`. */
  carPosition?: string | null;
  repairLocationStreet?: string | null;
  repairLocationHouseNumber?: string | null;
  repairLocationCity?: string | null;
  /**
   * Story `fe-be-dossieroverzicht-hersteller-kolom-filter` (2026-08-05) — de naam
   * van de uitvoerende hersteller (`RB_Company.Name` via
   * `UA_Dossier.RepairerID → UA_Repairer.CompanyID`).
   *
   * **Twee-delig contract (BE `ShouldSerializeRepairerName()`):**
   *  - **key afwezig** (`undefined`) ⇒ de caller is niet geprivilegieerd
   *    (`userContextType ∉ {System, RepairerHQ}`) — de kolom wordt dan ook niet
   *    gerenderd;
   *  - **key aanwezig met `null`** ⇒ wél rechten, maar het dossier heeft geen
   *    hersteller → de cel toont `'—'`.
   */
  repairerName?: string | null;
}

/**
 * Story `fe-be-dossieroverzicht-hersteller-kolom-filter` (2026-08-05) — één optie
 * voor de multi-select hersteller-filter (`GET /api/dossiers/repairer-options`).
 *
 * `name` (uit `RB_Company`) mag `null` zijn — `UA_Repairer` heeft zelf geen naam.
 * De FE valt dan terug op `repairerCode` en daarna op `'—'`; de optie blijft
 * selecteerbaar. `repairerCode` onderscheidt twee herstellers die naar DEZELFDE
 * `RB_Company` wijzen en dus een identieke naam hebben.
 */
export interface DossierRepairerOption {
  id: string;
  name: string | null;
  repairerCode: string | null;
}

/** Respons van `GET /api/dossiers/repairer-options`. */
export interface DossierRepairerOptionsResponse {
  options: DossierRepairerOption[];
  /**
   * `true` wanneer de BE meer herstellers vond dan hij teruggeeft (harde cap van
   * 500) — de lijst is dan afgekapt. De FE MOET dat tonen; stil afkappen maakt
   * hersteller nr. 501 onvindbaar zonder dat de gebruiker doorheeft dat er iets
   * mist (PO-acceptance 2026-08-05: "geen stille afkapping, nergens").
   *
   * Optioneel getypeerd zodat een oudere respons zonder de vlag niet breekt;
   * ontbrekend → behandelen als `false`.
   */
  truncated?: boolean;
}

/**
 * Story `fe-be-dossier-indicatoren` (2026-06-08) — één catalogus-rij
 * (`GET /api/dossiers/indicator-types`). Alleen actieve types, oplopend op
 * `sortOrder`. `color` is een tint-token (`amber`/`red`/`green`/`blue`/`slate`)
 * of `null`; `lucideIcon` is een lucide-icoonnaam die de FE icon-registry mapt
 * (onbekend → `Flag`-fallback).
 */
export interface DossierIndicatorType {
  id: string;
  code: string;
  description: string;
  lucideIcon: string;
  color: string | null;
  sortOrder: number;
}

/**
 * Story `fe-be-dossier-indicatoren` (2026-06-08) — de canonieke indicator-DTO
 * (hergebruikt voor POST-201, PATCH-200, GET-detail én lijst-batch). De
 * catalogus-velden zijn inline meegeleverd (denormalised) zodat een later
 * gedeactiveerd type blijft renderen. `message: null` → tooltip valt terug op
 * `description` (message-boven-description-regel).
 */
export interface DossierIndicator {
  id: string;
  indicatorTypeId: string;
  code: string;
  description: string;
  lucideIcon: string;
  /**
   * De EFFECTIEVE kleur (per-dossier-override ?? type-default ?? slate),
   * server-side gecoalesce'd. De rail + zoekresultaten renderen op dít veld
   * (via `resolveIndicatorTone`). Een onbekende string valt FE-side terug op
   * `slate` (story `fe-be-dossier-indicator-kleur-override`).
   */
  color: string | null;
  /**
   * De RUWE per-dossier kleur-override (`UA_DossierIndicator.Color`) 1-op-1;
   * `null` = geen override → de dialog selecteert de type-default-swatch. Alleen
   * de dialog gebruikt dit veld; de rendering gebruikt `color`.
   */
  colorOverride: string | null;
  sortOrder: number;
  message: string | null;
  visibleToAll: boolean;
}

/** Body voor `POST /api/dossiers/{id}/indicators` (toevoegen). */
export interface DossierIndicatorCreateRequest {
  indicatorTypeId: string;
  /** Optionele toelichting; whitespace-only wordt server-side naar null genormaliseerd. */
  message?: string | null;
  /** "Zichtbaar voor iedereen"; default false (privé). */
  visibleToAll?: boolean;
  /**
   * Optionele per-dossier kleur-override (tint-token uit
   * {amber,red,green,blue,slate}). Weggelaten of `null` = geen override →
   * type-default. Een onbekende string → 400 `invalid_color`.
   */
  color?: string | null;
}

/**
 * Body voor `PATCH /api/dossiers/{id}/indicators/{indicatorId}` (bewerken).
 * Partiële merge: een AFWEZIGE key blijft ongewijzigd; een aanwezige
 * `message: null`/whitespace → null; aanwezige `visibleToAll` overschrijft.
 * `color` volgt dezelfde null-vs-absent-semantiek als `message`: absent =
 * ongewijzigd; `null` = terug naar type-default; token = override.
 */
export interface DossierIndicatorUpdateRequest {
  message?: string | null;
  visibleToAll?: boolean;
  color?: string | null;
}

/**
 * Toegestane sorteersleutels voor `GET /api/dossiers` (story
 * `fe-dossier-infinite-scroll-search`). Deze waardes worden 1-op-1 als
 * `sort`-query-param naar de BE gestuurd en moeten **exact** matchen met de
 * allowlist-keys in `DossierListQuery.SortColumnSql` (camelCase,
 * `StringComparer.Ordinal`). Een onbekende sleutel levert BE-side 400
 * `invalid_sort`. De FE-kolommen indexeren rechtstreeks op deze union.
 */
export type DossierSortKey =
  | 'number'
  | 'license'
  | 'customerName'
  | 'insuranceCompanyName'
  | 'damageDate'
  | 'repairDate'
  | 'status'
  /**
   * Story `fe-be-dossieroverzicht-hersteller-kolom-filter` (2026-08-05) —
   * mapt BE-zijde op `rc.Name` (`DossierListQuery.SortColumnSql`, constante
   * `DossierListQuery.RepairerSortKey`). Alleen bruikbaar voor
   * geprivilegieerde rollen (System/RepairerHQ); een niet-geprivilegieerde
   * caller krijgt op deze sleutel `400 invalid_sort`, wat de FE via de
   * sort-guards in `DossiersPage` opvangt.
   */
  | 'repairerName';

/** Sorteerrichting voor `GET /api/dossiers` (`dir`-query-param). */
export type DossierSortDir = 'asc' | 'desc';

/**
 * Status-filter-stand voor `GET /api/dossiers` (`status`-query-param, story
 * `fe-dossier-openstaand-filter-frame`). Enum (geen boolean) zodat een latere
 * `archived`-stand zonder contract-breuk past:
 *   - `'open'`  — alleen openstaande dossiers (default-stand; de FE stuurt 'm
 *                 expliciet mee). Server-clause:
 *                 `(cs.StatusValue <> 300 OR cs.StatusValue IS NULL) AND
 *                  ISNULL(d.IsArchive,0) = 0`.
 *   - `'all'`   — open + archief (geen status/archief-clause).
 *
 * BE is lenient: een ontbrekende/onbekende waarde degradeert server-side naar
 * `open`. De FE stuurt nooit iets anders dan `open`/`all`.
 */
export type DossierStatusFilter = 'open' | 'all';

/**
 * Query-parameters voor de keyset-gepagineerde `GET /api/dossiers` (story
 * `fe-dossier-infinite-scroll-search`, bouwt op het BE-contract van
 * `be-dossier-keyset-paging-search`).
 *
 * `cursor` is een **opaque** string die uit de vorige `nextCursor` komt;
 * weglaten/`undefined`/`null` = eerste pagina. `q` = vrije zoekterm (debounced
 * FE-side). `pageSize` default 50 (= BE-default, gecapt op 100). `isExchange`
 * `true` → `?isExchange=true`; `false`/`undefined` → niet meegestuurd (BE ziet
 * ze als identiek aan ontbrekend). `status` → `'open'`/`'all'`, alleen
 * meegestuurd als gezet (story `fe-dossier-openstaand-filter-frame`).
 */
export interface GetDossiersParams {
  cursor?: string | null;
  pageSize?: number;
  q?: string;
  sort?: DossierSortKey;
  dir?: DossierSortDir;
  isExchange?: boolean;
  status?: DossierStatusFilter;
  /**
   * Vier extra filter-condities (story `fe-dossier-filter-condities-uitbreiding`,
   * 2026-06-07). Alle booleans worden alleen meegestuurd wanneer `true`
   * (`?isOffer=true` etc.); `false`/`undefined` → niet op de wire (BE ziet ze
   * als identiek aan ontbrekend). `statusValue` is een specifieke status (int),
   * los van de open/archief-modus `status`; alleen meegestuurd wanneer gezet.
   */
  /** Offerte-filter → `ISNULL(d.IsOffer,0)=1`. */
  isOffer?: boolean;
  /** Monteur-gereed-filter → `ISNULL(d.FitterIsReady,0)=1`. */
  fitterReady?: boolean;
  /** Hersteldatum-vandaag-filter → server-tijd half-open range op `RepairDate`. */
  repairToday?: boolean;
  /** Specifieke status → `cs.StatusValue = @statusValue`. */
  statusValue?: number;
  /**
   * Multi-value filter op indicator-TYPE via de stabiele `Code` (story
   * `fe-be-dossier-indicator-filter`, 2026-06-08). Een dossier matcht als het —
   * voor de caller zichtbaar — minstens één indicator van een van deze codes
   * heeft (OR / any-of). Op de wire als **herhaalde** `indicatorCodes`-param
   * (codes mogen spaties/komma's bevatten → geen CSV). Lege array → niet op de
   * wire (no-op).
   */
  indicatorCodes?: string[];
  /**
   * "In behandeling"-statusset-filter (story `dashboard-fase1-hersteller`,
   * 2026-06-12). Doorklik-doel van de gelijknamige KPI-tegel op het dashboard.
   * Enige geldige waarde: `'in_progress'` → BE-clause `cs.StatusValue IN (…)`
   * (alles tussen Invoeren en Afgerond, excl. 241/242/243 en archief). Count en
   * lijst delen dezelfde definitie zodat de tegel-count = lijst-totaal. Alleen
   * meegestuurd wanneer gezet; afwezig → geen filter.
   */
  statusSet?: 'in_progress';
  /**
   * Multi-value filter op de **uitvoerende hersteller** (story
   * `fe-be-dossieroverzicht-hersteller-kolom-filter`, 2026-08-05). Een dossier
   * matcht als z'n `RepairerID` in deze set zit (OR / any-of) → BE-clause
   * `d.RepairerID IN (@rep0, …)`. Op de wire als **herhaalde** `repairerIds`-param
   * (spiegelt `indicatorCodes`; één conventie, geen CSV). Lege array → niet op de
   * wire (no-op).
   *
   * **Rol-gated**: alleen `System`/`RepairerHQ`. Voor andere rollen negeert de BE de
   * param volledig (respons identiek aan de baseline) en strip't de FE hem uit de
   * state. Cap 50 unieke ids — daarboven antwoordt de BE
   * `400 { error: "invalid_repairer_ids", reason: "invalid_repairer_ids" }` (géén
   * stille clamp); een malformed GUID levert dezelfde 400.
   */
  repairerIds?: string[];
  /**
   * Filter op de **verzekeraar** (story
   * `fe-be-verzekeraar-gebonden-hersteller-dashboard`, EPIC-019 S-D, 2026-08-21).
   * Doorklik-doel van de vijf tegels van de verzekeraar-gebonden variant →
   * BE-clause `d.InsuranceCompanyID = @insurerId`: één simpele gelijkheid op de
   * directe FK-kolom, dus **geen** join/CTE/subquery in de lijst-query.
   *
   * Een **filter, geen poort**: beschikbaar voor élke gebruiker en weg te halen in
   * de filterbalk (concept Deel 5 — "de poorten zitten op aanmaken en muteren,
   * niet op inzage"), zodat een verzekeraar-gebonden hersteller z'n oude dossiers
   * bij een andere verzekeraar blijft zien. De lijst-scoping blijft uitsluitend de
   * bestaande access-scoping.
   *
   * Strict: een malformed GUID **of** het lege GUID levert
   * `400 { error: "invalid_insurance_company_id", reason: "invalid_insurance_company_id" }`
   * (stil negeren zou de lijst BREDER maken dan bedoeld). Leeg/whitespace → niet
   * op de wire (no-op).
   */
  insuranceCompanyId?: string;
  /**
   * "Actie vereist"-statusset (story
   * `fe-be-verzekeraar-gebonden-hersteller-dashboard`, EPIC-019 S-D, beslissing
   * E1). Doorklik-doel van de **vijfde** tegel: de statussen waar de hersteller
   * **zelf** moet handelen — **243** (afgekeurd door hoofdkantoor; alleen de
   * hersteller kan heropenen) en **245** (teruggegeven voor aanpassing).
   * Bewust **niet** 241/242/290: daar kan de hersteller niets.
   *
   * → BE-clause `cs.StatusValue IN (@stReturned, @stAdjust)` uit dezelfde
   * `DashboardBucketMap`-builder als de tegel-count, zodat de doorklik exact
   * dezelfde set selecteert. Alleen meegestuurd wanneer `true`.
   */
  actionRequired?: boolean;
  /**
   * Story `fe-be-monteur-s2-mijn-werk` (EPIC-021, S021.2) — "Alleen eigen dossiers".
   * De BE neemt de monteur-set uit de **sessie** (`whoami.fitter.fitterIds`), nooit uit
   * de request; zonder monteur-id's is het resultaat leeg. Alleen meegestuurd wanneer `true`.
   */
  mine?: boolean;
  /**
   * S021.2 — hersteldatum morgen (servertijd, spiegel van `repairToday`). Alleen
   * meegestuurd wanneer `true`.
   */
  repairTomorrow?: boolean;
  /**
   * S021.2 — kenteken zoeken zonder streepjes/spaties (`licenseSearch`). De BE
   * normaliseert naar `A-Z0-9`; alleen meegestuurd bij een niet-lege waarde. Los van
   * `q`, zodat de zoekterm van de kantoorlijst ongewijzigd blijft.
   */
  licenseSearch?: string;
  /**
   * S021.2 — `'fitter'` zet de kaartvelden van Mijn werk aan
   * ({@link DossierListItem.fitterIsReady} e.a.). Zonder `view` ontbreken die keys.
   */
  view?: 'fitter';
  /**
   * Story `be-fe-dashboard-te-verzenden` (story E, AC 10/13) — `'to_send'` filtert op de
   * dossiers die de dashboardtegel "Te verzenden" telt (gefactureerd, stap 3 aan de beurt of
   * mislukt). Eén gedeelde BE-definitie met de teller. Alleen meegestuurd wanneer gezet; de
   * tegel stuurt `status=all` mee, anders vallen de gearchiveerde alleen-mailen-dossiers
   * eruit (doorsnede met "Openstaand").
   */
  handling?: 'to_send';
}

/**
 * Eén optie van de gecureerde glas-status-lookup
 * (`GET /api/dossiers/status-options`, story
 * `fe-dossier-filter-condities-uitbreiding`). Gededupliceerd op `statusValue`,
 * oplopend gesorteerd. `description` is typisch `"NNN. Label"` (louter
 * weergave). Stabiel/cachebaar (lange client-staleTime).
 */
export interface DossierStatusOption {
  statusValue: number;
  description: string;
}

/**
 * Response van `GET /api/dossiers` (keyset-paging). Bevat **geen** `total` —
 * het overzicht toont het aantal nu-geladen rijen, niet een exact totaal
 * (story-beslissing F-2). `nextCursor` is de opaque cursor voor de volgende
 * batch (`null` = einde lijst); `hasMore` is `false` op de laatste batch.
 */
export interface DossierListResponse {
  items: DossierListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

/**
 * Story `fe-be-verzekeraar-rol-scope-worklist` (2026-06-29) — één rij in de
 * verzekeraar-worklist "Te beoordelen" (`GET /api/insurer/worklist`). Read-only
 * inzage; geen muterende velden.
 */
export interface InsurerWorklistItem {
  id: string;
  number: number | null;
  license: string | null;
  /** Hersteller-naam (via UA_Repairer → RB_Company.Name). */
  repairerName: string | null;
  /** Schadedatum (ISO-8601 `yyyy-MM-dd`) of `null`. */
  damageDate: string | null;
  /** "Bedrag incl." = persisted DamageAmount + DamageVatAmount (geen herberekening). */
  amountInclusive: number;
  /**
   * Dekking-indicatie (read-only): `true` = wel dekking, `false` = geen dekking,
   * `null` = nog niet beoordeeld (concept-chips ✓/✗/?).
   */
  hasCoverage: boolean | null;
  /** Status-waarde (222 dekkingscontrole / 240 expertise). */
  statusValue: number | null;
  /** Status-omschrijving (WF_CaseState.Description). */
  status: string | null;
}

/** Respons van `GET /api/insurer/worklist`. */
export interface InsurerWorklistResponse {
  items: InsurerWorklistItem[];
}

/**
 * Snelfilter voor de worklist. `'all'` → 222+240+630; `'222'`/`'240'`/`'630'` →
 * die status; `'parked'` → "Geparkeerd" (placeholder, BE levert leeg tot
 * legacy-verificatie).
 *
 * `'630'` ("Aangeboden, wacht op behandeling maatschappij") is toegevoegd door
 * story `fe-be-dossier-aanbieden-maatschappij` (2026-08-24, beslissing B2): die
 * status betekent letterlijk "wacht op behandeling maatschappij" en is dus haar
 * wachtrij. ⚠ Zichtbaar is hier níet hetzelfde als bewerkbaar — de verzekeraar
 * ziet deze dossiers wél, maar mag ze niet aanraken.
 */
export type InsurerWorklistFilter = 'all' | '222' | '240' | '630' | 'parked';

/**
 * Story `fe-be-verzekeraar-dossiers-zoeken` (2026-06-30) — één rij in het
 * verzekeraar-"Dossiers"-zoekscherm (`GET /api/insurer/dossiers`). Read-only;
 * spiegelt `InsurerWorklistItem` maar met een extra `isArchive`-bit (archief-
 * badge) en ZONDER statusrestrictie (alle statussen + archief).
 */
export interface InsurerDossierItem {
  id: string;
  number: number | null;
  license: string | null;
  /** Hersteller-naam (via UA_Repairer → RB_Company.Name). */
  repairerName: string | null;
  /** Schadedatum (ISO-8601 `yyyy-MM-dd`) of `null`. */
  damageDate: string | null;
  /** "Bedrag incl." = persisted DamageAmount + DamageVatAmount (geen herberekening). */
  amountInclusive: number;
  /** Dekking-indicatie (read-only): `true`/`false`/`null`. */
  hasCoverage: boolean | null;
  /** Status-waarde — ALLE statussen (geen {222,240}-restrictie). */
  statusValue: number | null;
  /** Status-omschrijving (WF_CaseState.Description). */
  status: string | null;
  /** Archief-vlag (UA_Dossier.IsArchive) — `true` → toon archief-badge. */
  isArchive: boolean;
}

/** Respons van `GET /api/insurer/dossiers`. */
export interface InsurerDossierListResponse {
  items: InsurerDossierItem[];
}

/**
 * Story `dashboard-fase1-hersteller` (2026-06-12) — de respons van het
 * rol-aware aggregaat-endpoint `GET /api/dashboard/summary`. Eén call levert
 * de 5 KPI-counts, de 3 werkrij-previews en de omzet-reeks; het summary-blok
 * degradeert als één eenheid (één call → alles-of-niets).
 */
export interface DashboardSummary {
  kpis: DashboardKpis;
  workRows: DashboardWorkRows;
  revenue: DashboardRevenueYear[];
  /**
   * Story `dashboard-hersteller-statusoverzicht` (2026-06-13) — het
   * per-status-aantal-blok. Eén item per in-gebruik glas-status binnen de
   * access-scope, **exclusief Archief (300)** (openstaand), oplopend op
   * `statusValue`. Optioneel op de wire (oudere BE → afwezig → FE toont geen blok).
   */
  statusBreakdown?: DashboardStatusBreakdownItem[];
  /**
   * Story `be-fe-dashboard-te-verzenden` (story E, AC 4) — de tegel "Te verzenden":
   * gefactureerde dossiers waarvan stap 3 (verzenden) aan de beurt of mislukt is.
   *
   * - `null` → geen tegel (de schakelaar is niet actief én er is niets te verzenden, of de
   *   gebruiker is geen vestiging);
   * - `{ count: n }` → tegel met het getal (ook `0` bij een actieve schakelaar);
   * - `{ count: null }` → tegel met "–": het aantal is niet vast te stellen.
   *
   * Optioneel op de wire: een oudere backend stuurt het veld niet; dat leest als `null`.
   * De FE leest de schakelaar zelf nooit.
   */
  toSend?: DashboardToSend | null;
}

/** Story E — de tegel "Te verzenden"; `count: null` = niet vast te stellen. */
export interface DashboardToSend {
  count: number | null;
}

/**
 * Eén status-rij in het per-status-aantal-blok. `total` matcht de doorklik-lijst
 * `?status=all&statusValue={statusValue}`; `fitterReadyTotal` matcht diezelfde
 * lijst `+ &fitterReady=true` (zelfde monteur-gereed-clause → count↔lijst-consistent).
 */
export interface DashboardStatusBreakdownItem {
  statusValue: number;
  /** `WF_CaseState.Description` ("NNN. Label"). */
  description: string;
  total: number;
  /** Subset waarvan "monteur gereed" aanstaat (alleen relevant voor 210). */
  fitterReadyTotal: number;
}

/**
 * Story `dashboard-hersteller-werklijsten` (2026-06-20) — de operationele
 * werklijsten van het Hersteller-dashboard, los van de summary opgehaald via
 * `GET /api/dashboard/worklists` (company-gescoopt, gevoed door de legacy-
 * `Interaction`-SP's). Apart endpoint zodat de lijstjes progressief renderen
 * en zelfstandig degraderen (faalt los van de summary).
 */
export interface DashboardWorklists {
  /** Open aanbiedingen/offertes. */
  offers: DashboardOffer[];
  /** Dossiers zonder voorraad-reservering (leeg bij niet-voorraad-herstellers). */
  noReservations: DashboardNoReservation[];
  /** Openstaande eXchange-filiaal-dossiers, met aantal dagen. */
  exchange: DashboardExchangeItem[];
}

export interface DashboardOffer {
  id: string;
  number: number | null;
  description: string | null;
  license: string | null;
}

export interface DashboardNoReservation {
  id: string;
  number: number | null;
  /** Hersteldatum zoals de SP 'm formatteert. */
  repairDate: string | null;
  eurocode: string | null;
}

export interface DashboardExchangeItem {
  id: string;
  /** De SP levert het dossiernummer als string. */
  number: string | null;
  license: string | null;
  /** "Monteur gereed" zoals de SP 'm levert. */
  fitterReady: string | null;
  status: string | null;
  /** Aantal dagen openstaand (`null` als niet-numeriek). */
  days: number | null;
}

/** De 5 KPI-counts (op de access-scope gefilterd). */
export interface DashboardKpis {
  invoeren: number;
  inProgress: number;
  waitHeadquarters: number;
  /** Context-only (niet-klikbaar in fase 1). */
  completedThisWeek: number;
  exchangeOpen: number;
}

/** De drie werkrij-buckets met hun top-N previews. */
export interface DashboardWorkRows {
  /** "Afmaken — Invoeren" (status 210). */
  finish: DashboardWorkRow;
  /** "Actie vereist" — alleen teruggestuurd (243), mét reden. */
  actionRequired: DashboardWorkRow;
  /** "Polis te controleren / velden ontbreken". */
  policyToCheck: DashboardWorkRow;
}

/** Eén werkrij: het totaal (voor "Alle bekijken") + de top-N previews. */
export interface DashboardWorkRow {
  total: number;
  items: DashboardPreview[];
}

/** Eén preview-rij in een werkrij. */
export interface DashboardPreview {
  id: string;
  number: number | null;
  customerName: string | null;
  /** Afkeur-reden (alleen "Actie vereist"; server-side gejoind). */
  reason: string | null;
  /** Korte aanduiding van wat ontbreekt (alleen "Polis te controleren"). */
  missingHint: string | null;
  /** Numerieke workflow-status (`WF_CaseState.StatusValue`) → status-chip. */
  statusValue: number | null;
  /** Merk (`UA_Dossier.ManufacturerText`). */
  manufacturer: string | null;
  /** Model (`UA_Dossier.ModelSetText`). */
  modelSet: string | null;
  /** Kenteken (`UA_Dossier.License`). */
  license: string | null;
  /** Laatste activiteit (`UA_Dossier.Modified`), ISO-8601 of `null`. */
  lastActivity: string | null;
  /**
   * Schadedatum (`UA_Dossier.DamageDate`) als ISO-8601, of `null` (story
   * `fe-be-verzekeraar-gebonden-hersteller-dashboard`, EPIC-019 S-D, beslissing
   * D5/E8). **Additief** — daarom optioneel getypeerd: bestaande/gemockte
   * summary-fixtures zonder het veld blijven compileren, en een oudere BE levert
   * 'm simpelweg niet (de FE toont dan een neutraal streepje).
   *
   * Voedt de kolom "Schadedatum" van de werkrij "Nog afmaken" van de
   * verzekeraar-gebonden variant. Het normale dashboard krijgt het veld als
   * verrijking mee maar toont er bewust GEEN extra kolom voor — dat zou de
   * bestaande drie werkrijen wijzigen (AC 31).
   */
  damageDate?: string | null;
}

/** Eén jaar-reeks in de omzet-grafiek. */
export interface DashboardRevenueYear {
  year: number;
  /**
   * 12 maand-punten (jan..dec). Een verstreken maand zonder omzet = `0`; een
   * toekomstige maand van het huidige jaar = `null` (geen datapunt, year-to-date).
   */
  months: (number | null)[];
}

/**
 * Story `fe-be-facturatie-dashboard` (2026-07-15) — de respons van
 * `GET /api/facturatie/dashboard`. Eén call levert de omzet-reeks (3 jaar × 12
 * maandpunten, het bestaande {@link DashboardRevenueYear}-contract) plus het
 * aantal facturen van het lopende jaar.
 *
 * De FE leidt KPI 1 (omzet YTD), KPI 2 (omzet vorig jaar) en KPI 4 (gemiddeld
 * per maand) AF uit `series` (per definitie consistent met de grafiek-staven) en
 * leest KPI 3 uit `invoiceCountCurrentYear` — er komt bewust GEEN apart
 * KPI-bedragen-object mee (dat zou een tweede aggregatie-bron zijn die uit de
 * pas kan lopen met de grafiek).
 */
export interface FacturatieDashboard {
  /** 3 jaar (huidig + 2 vorige, oudste eerst) × 12 maandpunten. */
  series: DashboardRevenueYear[];
  /** Het aantal facturen van het lopende jaar (owner-only + archive-clausule). */
  invoiceCountCurrentYear: number;
}

// ─── HK-dashboard (story dashboard-fase2-hoofdkantoor 2026-06-13) ───────────

/**
 * De respons van de Hoofdkantoor-tak van `GET /api/dashboard/summary`. De
 * BE kiest deze vorm op `session.UserContextType === RepairerHQ` (nooit uit
 * query/body); de FE discrimineert óók op `userContextType` (uit de
 * auth-context). `variant: 'hk'` maakt het wire-contract zelf-beschrijvend.
 * De bestaande {@link DashboardSummary} (Hersteller) blijft ONGEWIJZIGD.
 */
export interface DashboardHqSummary {
  variant: 'hk';
  kpis: DashboardHqKpis;
  workRows: DashboardHqWorkRows;
  charts: DashboardHqCharts;
  /**
   * Story `fe-be-hk-dashboard-meldingen-sectie` (2026-07-05) — de read-only
   * sectie "Meldingen & instroom": openstaande meldingen per status (blok 1),
   * per kanaal (blok 2) en de recent afgewezen meldingen (blok 3). Optioneel op
   * de wire (oudere BE → afwezig → FE toont de sectie niet).
   */
  intake?: DashboardHqIntake;
  /**
   * Story `fe-be-hk-dashboard-meldingen-sectie` (2026-07-05, blok 4) — de
   * HK-brede dossier-status-breakdown. Hergebruikt het bestaande
   * {@link DashboardStatusBreakdownItem}-contract (via `StatusBreakdownCard`).
   * Optioneel op de wire.
   */
  dossierStatusBreakdown?: DashboardStatusBreakdownItem[];
}

/** Story `fe-be-hk-dashboard-meldingen-sectie` — de sectie "Meldingen & instroom" (blok 1-3). */
export interface DashboardHqIntake {
  /** Blok 1 — openstaande meldingen per melding-workflow-status (oplopend). */
  perStatus: DashboardHqIntakeStatus[];
  /** Blok 2 — de openstaande meldingen per instroomkanaal. */
  perChannel: DashboardHqIntakeChannels;
  /** Blok 3 — de recent afgewezen meldingen (top-5, recentst eerst). */
  rejected: DashboardHqRejectedIntake[];
}

/** Eén status-rij in blok 1. */
export interface DashboardHqIntakeStatus {
  statusValue: number;
  /** `WF_CaseState.Description` ("NNN. Label"). */
  description: string;
  total: number;
}

/**
 * Blok 2 — de kanaal-tellingen. Binaire split (website = complement van
 * telefoon): `phone + website === totalOpen` (count-invariant).
 */
export interface DashboardHqIntakeChannels {
  /** Telefoon (callcenter). */
  phone: number;
  /**
   * Website (webformulier) = het binaire complement van telefoon
   * (`CallCenterIntake` leeg). Er is geen aparte website-kolom op de tabel.
   */
  website: number;
  /** Totaal openstaand = phone + website. */
  totalOpen: number;
}

/** Eén rij in blok 3 (afgewezen meldingen). */
export interface DashboardHqRejectedIntake {
  /** Melding-nummer (`UA_Intake.DossierNumber`) → `<Link to="/intakes/{number}">`. */
  number: number | null;
  /** Moment van afwijzing (`UA_Intake.Modified`), ISO-8601 of `null`. */
  moment: string | null;
  /** Hersteller-display-naam (`RB_Company.Name`). */
  repairerName: string | null;
  /**
   * Afwijs-reden. `UA_Intake` heeft geen scalar reden-kolom: de reden wordt als
   * `GD_Communication.Message` gepersisteerd. De BE levert de meest recente
   * afwijs-communicatie, bron-getrouw gespiegeld op de derived Domain-accessor
   * `UA_Intake.RejectReason`. `null` bij ontbreken.
   */
  reason: string | null;
}

/** De 5 HK-KPI-tegels (op de HK-access-scope gefilterd). */
export interface DashboardHqKpis {
  /** Te controleren = 241 EXCL. geparkeerd. */
  toReview: number;
  /** Geparkeerd = 241 + ExpertiseResult-code "03". */
  parked: number;
  /** Goedgekeurd = COUNT 242 (zonder datum-filter; label "Goedgekeurd"). */
  approved: number;
  /** Teruggestuurd = COUNT 243 (zonder datum-filter; label "Teruggestuurd"). */
  returned: number;
  /**
   * "Oudste wachtende": ISO-8601-`Modified` van het oudste te-controleren
   * dossier, of `null` bij een lege set. Context-only — vervangt
   * "Gem. doorlooptijd controle" (er is geen betrouwbare sinds-in-controle-
   * timestamp).
   */
  oldestWaiting: string | null;
}

/** De HK-werkrijen (GEEN SLA-werkrij — scope-af). */
export interface DashboardHqWorkRows {
  /** Controle-wachtrij (241 excl. geparkeerd) — top-N previews. */
  reviewQueue: DashboardHqWorkRow;
  /** Recent beoordeeld (laatste 242/243) — top-N previews. */
  recentReviewed: DashboardHqWorkRow;
  /** Per hersteller (openstaand-te-controleren) — telling, GEEN doorklik. */
  perRepairer: DashboardHqRepairerCount[];
}

/** Eén HK-werkrij: totaal (voor "Alle bekijken") + top-N previews. */
export interface DashboardHqWorkRow {
  total: number;
  items: DashboardHqPreview[];
}

/** Eén preview-rij in een HK-werkrij. */
export interface DashboardHqPreview {
  id: string;
  number: number | null;
  /** Hersteller-display-naam (`RB_Company.Name`). */
  repairerName: string | null;
  customerName: string | null;
  /** Numerieke workflow-status (`WF_CaseState.StatusValue`) → akkoord/terug-chip. */
  statusValue: number | null;
  /** Calculatie-totaal = `SUM(UA_DossierRow.Amount)`; `null` → FE toont "—". */
  amount: number | null;
  /** Laatste activiteit (`UA_Dossier.Modified`), ISO-8601 of `null`. */
  lastActivity: string | null;
}

/** Eén hersteller-rij in de "Per hersteller"-werkrij/grafiek. */
export interface DashboardHqRepairerCount {
  repairerId: string | null;
  repairerName: string | null;
  /** Aantal openstaand-te-controleren (241 excl. geparkeerd) voor deze hersteller. */
  count: number;
}

/** De twee HK-grafieken (huidige totalen — geen nep-maand-trend). */
export interface DashboardHqCharts {
  /** Volume per hersteller (huidige openstaande/te-controleren-verdeling). */
  volumePerRepairer: DashboardHqRepairerCount[];
  /** Goedgekeurd vs. teruggestuurd: huidige totalen (242 vs 243). */
  approved: number;
  returned: number;
}

// ─── Verzekeraar-gebonden hersteller (EPIC-019 S-D, 2026-08-21) ─────────────

/**
 * De respons van de **vierde** variant van `GET /api/dashboard/summary`: het
 * eenvoudige dashboard van een verzekeraar-gebonden hersteller (story
 * `fe-be-verzekeraar-gebonden-hersteller-dashboard`).
 *
 * De BE kiest deze vorm UITSLUITEND op de sessie-capability
 * (`SessionRecord.RestrictedRepairer.Active`), nooit uit query/body; de FE mount
 * de variant op `isRestrictedRepairerActive(user) === true`. `variant:
 * 'restricted'` maakt het wire-contract zelf-beschrijvend — en de FE valt terug
 * op de fout-tegel wanneer hij deze variant mountte maar een andere `variant`
 * terugkrijgt (beter een zichtbare herstelactie dan stille, verkeerde cijfers).
 *
 * De bestaande {@link DashboardSummary} (Hersteller), {@link DashboardHqSummary}
 * (HK) en {@link DashboardSystemSummary} blijven ONGEWIJZIGD: op één instantie
 * werken beperkte én gewone herstellers naast elkaar, en die laatste houden
 * exact hun volledige dashboard.
 *
 * **Geen verzekeraarsnaam op deze DTO**: de kop-subregel "Dossiers voor «X»"
 * leest de naam uit de sessie-capability die al op whoami meerijdt
 * (`restrictedInsurerName`). Hem hier nóg eens meesturen zou een tweede waarheid
 * zijn.
 */
export interface DashboardRestrictedSummary {
  variant: 'restricted';
  /** De vijf verzekeraar-gescoopte tegels. */
  kpis: DashboardRestrictedKpis;
  /**
   * De werkrijen — bewust de **bestaande** {@link DashboardWorkRows}-vorm.
   * Alleen `finish` ("Nog afmaken", status 210) is gevuld; `actionRequired` en
   * `policyToCheck` zijn LEGE rijen (`total: 0`, `items: []`).
   *
   * Die volledige vorm is opzet: zou een BE-deploy zonder FE-deploy deze respons
   * aan het normale hersteller-dashboard voeren, dan leest dat onvoorwaardelijk
   * `workRows.actionRequired.total` + `workRows.policyToCheck.total` — met lege
   * rijen degradeert dat naar nullen in plaats van een wit scherm.
   */
  workRows: DashboardWorkRows;
}

/**
 * De vijf tegel-counts, alle vijf **strikt** gescoopt op de vaste verzekeraar
 * (`d.InsuranceCompanyID = <vaste verzekeraar>`, geen `OR IS NULL`) en met
 * gearchiveerde dossiers eruit (`IsArchive = 0 OR IS NULL`).
 *
 * Bewust een EIGEN type en niet {@link DashboardKpis}: die velden zijn NIET
 * verzekeraar-gescoopt en zouden dus een ander getal tonen dan de tegel belooft.
 */
export interface DashboardRestrictedKpis {
  /** Tegel 1 — Invoeren (210): nog niet ingestuurd. */
  invoeren: number;
  /** Tegel 2 — Dekkingscontrole (222): ligt bij de verzekeraar. */
  coverageCheck: number;
  /** Tegel 3 — Expertise (240): in expertise. */
  expertise: number;
  /** Tegel 4 — Afgehandeld (280): goedgekeurd, **exclusief archief**. */
  settled: number;
  /**
   * Tegel 5 — Actie vereist: 243 (afgekeurd door hoofdkantoor) + 245
   * (teruggegeven voor aanpassing) — alles waar de hersteller **zelf** moet
   * handelen. Bewust niet 241/242/290.
   */
  actionRequired: number;
}

// ─── System-dashboard (story dashboard-fase3-system 2026-06-13) ─────────────

/**
 * De respons van de System-tak van `GET /api/dashboard/summary`. De BE kiest
 * deze vorm op `session.UserContextType === System` (nooit uit query/body); de
 * FE discrimineert óók op `userContextType` (uit de auth-context).
 * `variant: 'system'` maakt het wire-contract zelf-beschrijvend. De bestaande
 * {@link DashboardSummary} (Hersteller) én {@link DashboardHqSummary} (HK)
 * blijven ONGEWIJZIGD.
 *
 * Systeembreed: KPI's + signalen + volume tellen alle tenants
 * (`SystemAccessFromSql` + `1 = 1`, géén company-/WF_CaseUser-filter).
 */
export interface DashboardSystemSummary {
  variant: 'system';
  kpis: DashboardSystemKpis;
  signals: DashboardSystemSignals;
  volume: DashboardSystemVolume;
  health: DashboardSystemHealth;
}

/** De platform-KPI-tegels (systeembreed). eXchange-push-gefaald is bewust afwezig. */
export interface DashboardSystemKpis {
  /** Dossiers actief (totaal) = systeembreed niet-gearchiveerd. Klikbaar. */
  activeDossiers: number;
  /** Actieve herstellers = distinct RepairerID over de niet-gearchiveerde set. */
  activeRepairers: number;
  /** Fouten (24u) = `GS_ErrorLog`-rijen met `Created >= now-24u`. Niet-klikbaar. */
  errors24h: number;
}

/** De platform-signalen. 0/0 op de auth-counts is een geldige neutrale staat. */
export interface DashboardSystemSignals {
  /** Aantal fouten in de laatste 24u (signaal vraagt aandacht bij > 0). */
  errors24h: number;
  /** Aantal naar de sterke hash gemigreerde users (`Password LIKE '$pbkdf2-%'`). */
  authMigratedUsers: number;
  /** Aantal users met 2FA actief (`APP_UserMfa.Enabled = 1`). */
  authMfaEnabled: number;
}

/** Het systeembrede volume per status — dezelfde buckets als fase 1/2. */
export interface DashboardSystemVolume {
  /** Invoeren (210). Klikbaar naar de 210-gefilterde lijst. */
  invoeren: number;
  /** In behandeling (de gedeelde `in_progress`-set). Klikbaar. */
  inProgress: number;
  /** Controle hoofdkantoor (241). Klikbaar naar de 241-lijst. */
  controle: number;
  /** Afgerond (242 + week-venster). Niet-klikbaar (datum-bereik-mini-vervolgstory). */
  afgerond: number;
}

/** Het afgeslankte health-blok — alleen "Database OK" (impliciet). */
export interface DashboardSystemHealth {
  /** Altijd `true` wanneer de summary gebouwd is (geslaagde aggregatie = DB OK). */
  databaseOk: boolean;
}

/**
 * Server-side afgeleid dossier-type (S007.8 A1-A5, eerste-match-wint).
 * Union met `(string & {})`-fallback zodat onverwachte server-waardes
 * compile-time getypeerd blijven en de FE failsafe-pad geraakt wordt
 * (zie S007.8 C7 / F-09).
 */
export type DossierType =
  | 'Offerte'
  | 'B2B'
  | 'LeaseFleet'
  | 'Verzekerd'
  | 'Onverzekerd'
  | (string & {});

/**
 * De ENE eerlijke dekkingstoestand (story
 * `be-fe-dekkingstoestand-ua-dossiercoverage`, 01-10-2026). Gesloten set van
 * zeven; `(string & {})` houdt een onbekende waarde uit de lijn leesbaar zonder
 * dat TypeScript de union platslaat.
 *
 * ⚠ `pending_async`, `pending_external` en `no_response` zijn **géén**
 * `not_covered`: "niet vastgesteld" en "vastgesteld dat er niets is" zijn
 * verschillende dingen. Een gate die dekking betekent toetst dus op
 * `=== 'covered'`, nooit op `!== 'not_covered'` — en een onbekende waarde leest
 * daardoor nooit als dekking.
 */
export type CoverageState =
  | 'covered'
  | 'not_covered'
  | 'no_policy'
  | 'pending_async'
  | 'pending_external'
  | 'not_checked'
  | 'no_response'
  | (string & {});

/**
 * **Wie** de dekkingstoestand heeft vastgesteld — de `Source` van de nieuwste
 * `UA_DossierCoverage`-rij (story `be-fe-dekkingstoestand-ua-dossiercoverage`).
 *
 * ⚠ Dit is **niet** hetzelfde als {@link PolicySource} in
 * `features/dossiers/policy/types.ts`. Die laatste is een FE-afleiding uit
 * `policyService` ("welke polisservice hoort bij dit dossier"); dit veld zegt
 * wie de laatste dekkings-**gebeurtenis** heeft vastgelegd. Op een
 * Dispatch-dossier is `policyService` altijd Dispatch, terwijl deze bron
 * `own_entry` kan zijn — en juist dát verschil bepaalt of de hersteller het
 * dekking-blok nog mag bewerken.
 *
 * ⚠ `own_entry` = de hersteller vulde het zelf aan. `manual` is voorbehouden
 * aan de **beslissing van de verzekeraar** (status 222 → 220/221); die twee
 * hebben heel verschillend gewicht en horen niet op één hoop.
 */
export type CoverageSourceValue =
  | 'exchange'
  | 'dispatch'
  | 'ubench'
  | 'localdatabase'
  | 'mail'
  | 'manual'
  | 'own_entry'
  | 'carculate'
  | (string & {});

/**
 * Story `be-fe-dekking-d1-popup-en-slot` — de beschikbaarheid van de popup
 * "Dekking zelf opgeven", zoals de server haar bepaalt
 * (`OwnCoverageAvailability.Decide`).
 */
export interface OwnCoverageAvailability {
  /** Mag de popup open? */
  available: boolean;
  /** Nederlandse uitleg wanneer hij niet beschikbaar is; `null` als hij dat wel is. */
  reason: string | null;
  /** `insured` = volledige popup; `lease` = alleen ja/nee. */
  variant: 'insured' | 'lease' | (string & {});
  /** Dispatch: "Dekking afgegeven" vraagt contactpersoon + datum/tijd contact. */
  requiresContact: boolean;
  /** Er is al een eigen opgave: knoptekst "Zelf opgegeven dekking wijzigen…". */
  isEdit: boolean;
}

/**
 * Story `be-fe-dekking-d2-aanbieden-vereist-dekking` (D2) — de uitkomst van de aanbied-poort voor
 * het scherm (`policy.submitCoverage`, detail en delta's).
 */
export interface SubmitCoverageAvailability {
  /** Mag het dossier wat de dekking betreft worden aangeboden? */
  allowed: boolean;
  /** De Nederlandse reden op de knop; `null` als het mag. */
  reason: string | null;
  /** `coverage_not_confirmed` / `lease_coverage_not_confirmed`; `null` als het mag. */
  reasonCode: string | null;
}

/**
 * Story D3 (`be-fe-dekking-d3-vervolg-220-221`, D3-V2) — staat "Controleer polis" dicht omdat de
 * verzekeraar heeft beslist (220/221)? `policy.policyCheckLock` op detail en delta's; dezelfde regel als
 * de 403 `insurer_has_decided` van `POST /policy/check`.
 *
 * Story D4 (`be-fe-dekking-d4-wissel-kopie-portal`, D4-4) — hetzelfde veld draagt ook de Dispatch-regel:
 * Dispatch definitief zonder portal-link ⇒ dicht ("Dispatch heeft al definitief geantwoord."); mét portal-link
 * maar zonder schadenummer ⇒ dicht ("Haal eerst via de link het schadenummer op en vul het in."); mét
 * schadenummer ⇒ open (opnieuw controleren). 403-codes `dispatch_already_definitive` /
 * `dispatch_damage_number_required`. De verzekeraar (220/221) gaat voor.
 */
export interface PolicyCheckLock {
  /** Staat de knop dicht? */
  locked: boolean;
  /** De reden op de knop (van de server); `null` als hij open is. */
  reason: string | null;
}

/** Story `be-fe-dekking-d1-popup-en-slot` — body van `POST /api/dossiers/{id}/own-coverage`. */
export interface OwnCoverageRequest {
  version: number;
  /** "Dekking afgegeven" (`true`) of "Geen dekking" (`false`). */
  isCovered: boolean;
  /**
   * Alleen bij verzekerd + "Dekking afgegeven" de vijf dekkingsvelden; bij
   * Dispatch daarnaast contactpersoon + datum/tijd contact. Bij "Geen dekking"
   * en bij lease/fleet blijven de dekkingsvelden weg.
   */
  fields: {
    insuranceTypeId?: string | null;
    policyValidUntil?: string | null;
    excessReplacement?: number | null;
    excessRepair?: number | null;
    vatIncluded?: boolean | null;
    insuranceContactPerson?: string | null;
    contactMomentInsurer?: string | null;
  };
}

/**
 * Polis-sub-object. Velden zijn allemaal nullable; volledig null is mogelijk
 * (bv. B2B-dossier). FE beslist tab-zichtbaarheid op `dossierType`, niet op
 * null-checks van dit object.
 */
export interface DossierPolicy {
  /**
   * `UA_Dossier.HasCoverage`.
   *
   * ⚠⚠ **Dit veld is sinds 01-10-2026 GEEN dekkings-SIGNAAL meer** (story
   * `be-fe-dekkingstoestand-ua-dossiercoverage`). Het is geen opgeslagen feit
   * maar een afgeleide vlag: hij staat op `true` bij 82.008 gemeten dossiers
   * zonder énige poliscontrole, en voor lease/fleet geeft de Domain-getter 'm
   * onvoorwaardelijk. Lees {@link DossierPolicy.coverageState} voor de toestand.
   *
   * Het veld blijft bestaan als **invoer** — de verzekeraar verstuurt haar
   * beslissing als `isCovered` naar `POST /coverage-check` — en voor
   * wire-compat. Een scan (`coverageSignal.scan.test.ts`) houdt nieuwe
   * lezingen in `apps/web` tegen.
   */
  isCovered: boolean | null;
  /**
   * De vastgestelde dekkingstoestand — de nieuwste rij uit
   * `UA_DossierCoverage`, met terugval op de `PolicyHistory`-blob en de twee
   * handmatige beoordelings-vlaggen. `not_checked` = er is nooit gecontroleerd.
   *
   * Optioneel zodat responses van vóór deze story blijven valideren; een
   * ontbrekende waarde leest als "nog niets vastgesteld", nooit als dekking.
   */
  coverageState?: CoverageState | null;
  /**
   * Wanneer die toestand is vastgesteld, als ISO-string of `null`.
   *
   * ⚠ `null` betekent NIET "niet gecontroleerd": een handmatige beoordeling
   * zonder rij valt terug op `UA_Dossier.Modified`, en een onvolledige
   * audit-blob levert geen bruikbare datum terwijl de toestand wél geldt. Lees
   * daarvoor {@link DossierPolicy.coverageState}.
   */
  coverageCheckedAt?: string | null;
  /**
   * **Wie** de toestand heeft vastgesteld — de bron van de nieuwste
   * `UA_DossierCoverage`-rij.
   *
   * ⚠⚠ `null` is een **echt onderscheid** en geen default: dan komt de toestand
   * uit de terugval (er is geen rij) en weten we niet wie 'm vaststelde. De
   * Polis-tab vergrendelt daarop fail-veilig — alleen een aantoonbaar
   * `own_entry` houdt het dekking-blok open.
   *
   * **Waarom dit veld bestaat** (01-10-2026, gemeten op dossier 202651614): de
   * bron stuurt hier **gedrag** en niet alleen tekst. Een Dispatch-dossier
   * waarop de hersteller de dekking zelf had aangevuld ging opnieuw op slot met
   * "Dispatch heeft de dekking geleverd" — terwijl Dispatch niets geleverd had.
   * `policyService` is in béide gevallen Dispatch, dus daarmee is het verschil
   * niet te zien; de tabel weet het wél.
   */
  coverageSource?: CoverageSourceValue | null;
  /**
   * Story `be-fe-dekking-d1-popup-en-slot` (D1-4) — mag de hersteller de dekking
   * zelf opgeven (popup "Dekking zelf opgeven"), en zo niet: waarom. De server
   * bepaalt dit met één beslisregel; de FE leest het en leidt niets zelf af.
   * Ontbreekt het (oudere backend), dan is de popup niet beschikbaar.
   */
  ownCoverage?: OwnCoverageAvailability | null;
  /**
   * Story `be-fe-dekking-d2-aanbieden-vereist-dekking` (D2) — mag dit dossier naar het
   * hoofdkantoor (of naar eXchange) wat de dekking betreft, en zo niet: de reden op de knop.
   * Hetzelfde beslispunt als de server-poort (gate 3f); de FE leest dit en leidt niets zelf af.
   * Ontbreekt het (oudere backend), dan valt de FE terug op de oude lease/eXchange-regel.
   */
  submitCoverage?: SubmitCoverageAvailability | null;
  /**
   * Vervolg D2 (D2-10, 22-10-2026) — mag "Voorexpertise aanvragen" wat de dekking betreft, en zo
   * niet: de reden op de knop. Alleen bij een eXchange-maatschappij/-volmacht een eis (een
   * eXchange-dossier mét dekking van eXchange). Zelfde beslispunt als de 422
   * `exchange_coverage_not_confirmed` van `POST /voorexpertise`; de FE leidt niets zelf af.
   * Ontbreekt het (oudere backend), dan geldt er geen dekkingseis op de knop.
   */
  preExpertiseCoverage?: SubmitCoverageAvailability | null;
  /**
   * Story D3 (D3-V2) — "Controleer polis" dicht na de beslissing van de verzekeraar (220/221). Server-
   * oordeel; de FE leidt het niet zelf af uit de status. Ontbreekt het (oudere backend): geen slot.
   */
  policyCheckLock?: PolicyCheckLock | null;
  /**
   * `UA_Dossier.PolicyNumber` — ontsloten in het `policy`-sub-object door
   * story `fe-be-verzekeraar-dekkingscontrole` (2026-06-29). Stond voorheen
   * alleen op de DTO-root (`DossierDetail.policyNumber`, niet verwijderd —
   * back-compat). Het dekkingscontrole-formulier leest het veld uit
   * `dossier.policy.*`; de coverage-check-delta levert 'm onder
   * `policy.policyNumber`. Optioneel zodat responses van vóór deze story
   * (zonder het sub-object-veld) blijven valideren.
   */
  policyNumber?: string | null;
  /**
   * "geldig van"-datum van de polis, als ISO-string of `null`. Opgeslagen in de
   * GD_FieldValue-sidecar (`policyvalidfrom`), NIET als `UA_Dossier`-kolom —
   * `Server\` is read-only, dus geen Domain-regen. Toegevoegd door story
   * `fe-be-verzekeraar-dekkingscontrole` (2026-06-29) als eerste-klas
   * polisveld naast het bestaande `policyValidUntil` (`PolicyValidto`).
   * Optioneel voor wire-compat met responses van vóór deze story.
   */
  policyValidFrom?: string | null;
  /**
   * FK naar `UA_InsuranceType` (3 vaste codes: WA / WA_Casco / WA_Plus).
   * Bron-kolom `UA_Dossier.InsuranceTypeID`. Wordt door
   * `UA_Dossier.FillFromPolicy` automatisch gezet bij policy-check en kan
   * via `PATCH /policy` handmatig overschreven worden. Vervangt op termijn
   * het V1-tekstvak `coverageType` (story
   * `appservices-policy-insurance-type-field`, 2026-05-25).
   */
  insuranceTypeId?: string | null;
  /**
   * Soort dekking (vrij-tekst). Opgeslagen in `GD_FieldValue` sidecar
   * (GD_FieldGroup.Name='UE_DossierExtra',
   * GD_FieldGroupField.Name='coveragetype') sinds S007.14c. Server doet geen
   * enum-validatie; FE-state-machine dicteert toegestane waardes.
   *
   * V2-deprecated zodra de `insuranceTypeId`-dropdown live is — laten staan
   * voor wire-compat tot de deprecation-story.
   */
  coverageType: string | null;
  /** `UA_Dossier.PolicyValidto` — ISO-string. */
  policyValidUntil: string | null;
  /** `UA_Dossier.VATInsured` — true = BTW inclusief. */
  vatIncluded: boolean | null;
  /** EUR — `UA_Dossier.OwnriskRepair`. */
  excessRepair: number | null;
  /** EUR — `UA_Dossier.OwnriskReplace`. */
  excessReplacement: number | null;
  /** EUR — `UA_Dossier.OwnRiskDiscountAmount`. */
  excessDiscount: number | null;
  /**
   * Aangepast eigen risico (EUR). Read-only afgeleide waarde:
   * `(actief ER) − excessDiscount`, waar "actief ER" door
   * `activeExcessKind` wordt bepaald (repair → excessRepair,
   * replacement → excessReplacement). De FE berekent 'm live; het
   * sidecar-veld blijft als historische opslag bestaan maar wordt niet
   * meer als input gebruikt.
   */
  adjustedExcess?: number | null;
  /**
   * Welk eigen risico actief is voor de Aangepast-ER-berekening,
   * server-afgeleid uit `UA_Dossier.RepairTypeID` → `UA_RepairType`
   * (legacy PolisV2.aspx.cs:966: Reparatie → "repair", anders →
   * "replacement"). `null`/undefined tot BE 'm ontsluit → FE valt
   * defensief terug op "replacement" (legacy else-tak).
   */
  activeExcessKind?: 'repair' | 'replacement' | null;
  /** Contactpersoon bij verzekeraar/volmacht — `UA_Dossier.InsuranceContactPerson` (S007.8 v2). */
  insuranceContactPerson: string | null;
  /** Datum/tijd contact met verzekeraar — `UA_Dossier.ContactMomentInsurer` (ISO-string, S007.8 v2). */
  contactMomentInsurer: string | null;
}

/**
 * Schade-sub-object — gewijzigd door story-A `fe-schade-tab-editable`
 * (2026-05-29) + story-B `fe-schade-tab-schadelocatie-selector` (2026-05-30).
 * De legacy `repairReady` (= `FitterIsReady`) en de losse tekstvelden
 * `vehicleCondition` / `repairType` zijn geschrapt; in plaats daarvan
 * exposen we nu FK-Guid + display-naam-paren voor de twee reference-
 * tabellen, plus de Roest-vlag uit `UA_DossierExtra` en het conditionele
 * `repairCompletedDate` (= `UA_Dossier.CalculationDate`).
 *
 * Story B schrapt de read-only `glassParts`-string en vervangt 'm door
 * de twee CSV-velden `carPosition` (numerieke auto-zone-codes) +
 * `windshieldPosition` (alfanumerieke voorruit-vlakken). De display-
 * mapping naar labels gebeurt FE-side via `formatDamageZones(carPosition)`.
 */
export interface DossierDamage {
  /**
   * `UA_Dossier.CalculationDate` — datetime "Herstel gereed". Alleen
   * zichtbaar (en bewerkbaar) wanneer `repairCompletedDateVisible === true`.
   * Wire-formaat: ISO datetime `YYYY-MM-DDTHH:mm` (geen timezone-suffix).
   */
  repairCompletedDate: string | null;
  /**
   * `GS_SystemParameter.Exchange_SendRepairReady` — wanneer `false` wordt
   * het "Herstel gereed"-veld helemaal niet gerenderd (DOM-out, niet
   * disabled). FE leest deze flag eenmaal uit het detail-DTO.
   */
  repairCompletedDateVisible: boolean;
  /**
   * `UA_DossierExtra.Rust` — "Roest in sponningen"-vlag. Default `false`
   * wanneer geen `UA_DossierExtra`-rij voor het dossier bestaat. Eigen
   * persist-pad via legacy `Interaction.UA_DossierExtraAdd` (eerste write)
   * resp. `UA_DossierExtraUpdate` (vervolg).
   */
  rustInRabbets: boolean;
  /**
   * FK naar `UA_RepairType` — "Soort herstel".
   *
   * Note: `carConditionId`/`carConditionName` zijn door Story V
   * (`fe-voertuig-tab-editable`, 2026-05-30) verhuisd naar
   * {@link DossierVehicle}. Lees ze daar (`dossier.vehicle.carConditionId`).
   */
  repairTypeId: string | null;
  /** Display-naam van de gekozen `UA_RepairType` (cascade-veld). */
  repairTypeName: string | null;
  /**
   * Story B (2026-05-30) — komma-gescheiden numerieke codes voor de
   * auto-zone-selectie (bv. `"1,16"`). Bron: `UA_Dossier.CarPosition`.
   * Lege string = niets geselecteerd (BE-contract: lege string, niet
   * null). Codes leven 1-op-1 in de SVG-`data-val`-attributen — zie
   * `FRONTEND-SCHADELOCATIE.md` §2 + `SchadelocatieSelector.tsx`.
   */
  carPosition: string;
  /**
   * Story B (2026-05-30) — komma-gescheiden alfanumerieke codes voor de
   * voorruit-detail-vlakken (bv. `"B2,C2"`). Bron:
   * `UA_Dossier.WindshieldPosition`. Wordt door BE stil opgeschoond naar
   * `""` wanneer `carPosition` geen `"1"` bevat (geen 400 — forgiving
   * contract, zie story §"Deel F").
   */
  windshieldPosition: string;
  /**
   * Story `fe-schade-soort-reparatie-startype` (2026-06-14) — "Soort
   * reparatie" (`UA_Dossier.StarType`). Conditioneel veld onder "Soort
   * herstel"; alleen relevant wanneer de gekozen `repairTypeId` de
   * Reparatie-rij is. Drie waarden: `""` (niet gezet), `"1"` (Enkelvoudig),
   * `"2"` (Meervoudig). Altijd een string (nooit `null` — string-contract,
   * spiegelt `carPosition`/`windshieldPosition`). De BE maakt 'm stil leeg
   * (`""`) zodra "Soort herstel" naar een ander hersteltype gaat (cleanup-
   * cascade); de FE verbergt het veld dan al direct op de lokaal-gekozen
   * repairType (live toggle).
   */
  starType: string;
}

/**
 * Voertuig-sub-object — story `fe-voertuig-tab-editable` (2026-05-30).
 * 13 editable velden + 3 read-only RDW-datums. De zeven cross-tab-velden
 * (License/ManufacturerText/ModelSetText/ModelText/ModelYear/ChassisNumber/
 * NumberOfKilometers) zijn óók beschikbaar op de DossierDetail-root voor
 * backward-compat met Intake/Overview-tabs — BE schrijft beide locaties uit
 * dezelfde DB-kolom.
 *
 * `carConditionId`/`carConditionName` zijn in deze story verhuisd uit
 * `DossierDamage`; PATCH gaat nu via `/api/dossiers/{id}/vehicle`. RDW-datums
 * (firstRegistration/nameRegistration/dutchRegistration) zijn read-only
 * display (ISO-string of `null`). Bron: `UA_Dossier.cs` extension (regels
 * 8625/8672/8719); BE leest uit kolommen 87/88/89 van `LoadDossierDetail`-
 * SELECT. PATCH op deze velden levert 403 `field_not_editable` (niet in
 * `VehicleFieldCodes`-set). Auto-vulling via OVI/RDW-flow volgt in een
 * follow-up-story.
 */
export interface DossierVehicle {
  // Cross-tab gedeeld met Intake (gespiegeld vanuit DTO-root).
  license: string | null;
  manufacturerText: string | null;
  modelSetText: string | null;
  modelText: string | null;
  modelYear: number | null;
  chassisNumber: string | null;
  numberOfKilometers: number | null;

  // Voertuig-only editable.
  /** FK naar `UA_VehicleBody` — "Carrosserievorm". */
  vehicleBodyId: string | null;
  /** Display-naam van de gekozen `UA_VehicleBody` (cascade-veld). */
  vehicleBodyName: string | null;
  /**
   * `UA_Dossier.IsIntegral` — alleen relevant wanneer
   * `vehicleBodyName === "Kampeerwagen"` (case-insensitive). Tri-state
   * (null/true/false). Bij wisseling naar niet-Kampeerwagen wordt BE-side
   * stil opgeschoond naar `null` (zie story §"Deel E").
   */
  isIntegral: boolean | null;
  /** `UA_Dossier.Doors` — int select {1..5} of null. */
  doors: number | null;
  /** `UA_Dossier.MeldCode` — meldcode/ruitcode. */
  meldCode: string | null;
  /**
   * FK naar `UA_CarCondition` — "Toestand voertuig". Verhuisd uit
   * `DossierDamage` (Story V, 2026-05-30).
   */
  carConditionId: string | null;
  /** Display-naam van de gekozen `UA_CarCondition` (cascade-veld). */
  carConditionName: string | null;

  // ── Herkomst van een automatisch ingevulde kilometerstand ──────────────
  // Story `fe-be-kilometerstand-uit-foto` (2026-09-11), C14.
  //
  // ⚠⚠ TWEE LOSSE VELDEN, BEWUST — geen samengesteld object. D3 eist dat na
  //    een handmatige wijziging de MARKERING vervalt maar het ICOON blijft.
  //    Met één object laat je óf het hele object vervallen (icoon weg) óf het
  //    hele object staan (markering blijft), en beide varianten passeren een
  //    test die maar één van de twee asserteert. Twee velden, twee levensduren.
  //
  // ⚠ De FE kent het ABONNEMENT niet: ze leest de periode niet, kent het veld
  //   niet en heeft er geen prop voor. Zichtbaarheid volgt UITSLUITEND deze
  //   twee signalen (D5/D6 — "het slot leest de poorten niet").
  //
  // Beide optioneel voor backward-compat met detail-responses van vóór deze
  // story; ontbrekend gedraagt zich als `null` (= niets tonen).

  /**
   * Datum waarop de kilometerstand automatisch is ingevuld (ISO-string) —
   * **voedt de markering**. Vervalt zodra een mens `numberOfKilometers`
   * handmatig wijzigt (C15).
   */
  kilometerstandAutomatischIngevuldOp?: string | null;

  /**
   * Id van de bronfoto — **voedt het icoontje** dat de bijlage-viewer opent.
   * Blijft staan ná een handmatige wijziging (D3) en is leeg wanneer de
   * bronbijlage verwijderd is (D8) — zodat er nooit een kapotte viewer, een
   * 404-toast of een dode link ontstaat.
   */
  kilometerstandBronBijlageId?: string | null;

  /**
   * Id van de nieuwste Kilometerstand-foto op het dossier — **los van of er
   * ooit iets is uitgelezen**. Beantwoordt een andere vraag dan de twee velden
   * hierboven: niet "waar komt deze waarde vandaan" maar "waar kan ik hem
   * controleren".
   *
   * ⚠ Draagt géén bewering dat de waarde automatisch is ingevuld. De markering
   * hangt uitsluitend aan `kilometerstandAutomatischIngevuldOp`.
   */
  kilometerstandFotoBijlageId?: string | null;

  // Read-only RDW-datums (V1 altijd null — geen DB-kolom).
  /** 1e toelating — read-only display, ISO-string of null. */
  firstRegistration: string | null;
  /** Tenaamstelling — read-only display, ISO-string of null. */
  nameRegistration: string | null;
  /** Afgifte NL — read-only display, ISO-string of null. */
  dutchRegistration: string | null;
}

/**
 * Respons van `GET /api/dossiers/{id}/odometer-read-status` — waarmee het
 * scherm kan zien dat de achtergrondtaak de kilometerstand heeft ingevuld.
 *
 * De job schrijft buiten elk request om; zonder deze status zou de waarde pas
 * ná een harde verversing in beeld komen.
 */
export interface OdometerReadStatus {
  /**
   * Er loopt op dit moment een uitlezing voor dit dossier. Zolang dit `true`
   * is kan de waarde nog veranderen; wordt het `false`, dan is de uitkomst
   * definitief — óók wanneer er niets geschreven is (een normale afloop).
   */
  pending: boolean;
  /** De kilometerstand zoals die nu op het dossier staat. */
  numberOfKilometers: number | null;
  /** Zie `DossierVehicle.kilometerstandAutomatischIngevuldOp`. */
  kilometerstandAutomatischIngevuldOp: string | null;
  /** Zie `DossierVehicle.kilometerstandBronBijlageId`. */
  kilometerstandBronBijlageId: string | null;
  /** Zie `DossierVehicle.kilometerstandFotoBijlageId`. */
  kilometerstandFotoBijlageId: string | null;
}

/**
 * De uitkomst-codes van een groene-kaart-uitlezing. Spiegelt
 * `GreenCardReadOutcomes` in de backend.
 *
 * ⚠ Er bestaat **géén** `Geschreven`: deze functie schrijft niets automatisch
 * weg. De popup toont, en de gebruiker zet de schakelaar om.
 */
export type GreenCardReadOutcome =
  /** Een bruikbaar antwoord van een verzekeringsbewijs — de popup toont het resultaat. */
  | 'Gelezen'
  /** `documentsoort` viel buiten de positieve lijst; géén formulier, géén vergelijkingen. */
  | 'GeenVerzekeringsbewijs'
  /** Het model meldt zelf `leesbaarheid = onleesbaar`; géén formulier. */
  | 'Onleesbaar'
  /** Er wás een antwoord, maar het is niet te lezen. */
  | 'OnbruikbaarAntwoord';

/**
 * Eén uitgelezen verzekeringsbewijs. Alle velden zijn platte strings met
 * "leeg = niet gevonden" — exact de vorm van het antwoordschema, zodat er
 * onderweg niets te interpreteren valt.
 *
 * ⚠ `geldigVan` / `geldigTot` zijn strikt `jjjj-mm-dd` of `null`. Er wordt niet
 * geraden en niet omgezet: `03-04-2026` is 3 april of 4 maart afhankelijk van
 * wie het leest, en één maand verschuiving kantelt de geldigheidswaarschuwing
 * naar de tegenovergestelde mededeling.
 */
/**
 * Story `fe-be-verzekeraar-zoeken-op-groenekaartcode` (13-09-2026) — één
 * maatschappij die de code van deze groene kaart draagt.
 *
 * ⚠ Dezelfde vorm wordt gebruikt voor `sharedWith` in het antwoord van
 * `PUT /api/insurers/{id}` (E5): "welke ANDERE maatschappijen dragen deze code
 * ook?". Eén vorm, want het is dezelfde vraag vanuit twee richtingen.
 */
export interface GreenCardInsurerMatch {
  /** De GUID van de maatschappij. ⚠ Dít is de waarde die `PATCH /policy` krijgt, nooit de naam. */
  id: string;
  /** De naam zoals die in het dossier komt te staan; `null` als er geen bedrijf aan hangt. */
  name: string | null;
}

export interface GreenCardReadResult {
  /** De bijlage waaruit dit gelezen is. */
  attachmentId: string;
  outcome: GreenCardReadOutcome;
  /**
   * De maatschappijen die de code van DEZE kaart dragen, op naam gesorteerd.
   *
   * ⚠⚠ **Per resultaat-object, niet op het topniveau van de status-respons.**
   * Een dossier kan meer dan één groene kaart hebben; op het topniveau zouden de
   * treffers van twee kaarten met verschillende codes over elkaar heen vallen en
   * wist de popup niet meer welke maatschappij bij welke kaart hoort.
   *
   * ⚠ **Altijd een array, nooit `null`.** Geen code, geen normaliseerbare code
   * en nul treffers leveren alle drie `[]` — een normale afloop, geen fout, dus
   * ook geen reden-code.
   *
   * ⚠ De RUWE code zelf komt bewust **niet** op de wire: een regel
   * "Code verzekeraar · NL/118" zegt de hersteller niets en suggereert een
   * overneembaar veld dat er niet is.
   */
  insuranceCompanyMatches: GreenCardInsurerMatch[];
  /** Lokale tijd waarop de uitlezing afgerond is. */
  completedAt: string | null;
  /**
   * `false` wanneer de bronbijlage inmiddels verwijderd is. Het aangrijppunt
   * verdwijnt dan — nooit een kapotte viewer, een 404-toast of een dode link.
   */
  attachmentExists: boolean;

  /** `groene kaart` | `polisblad` | `verzekeringsbewijs` | `anders`. */
  documentsoort: string | null;
  /** `goed` | `matig` | `onleesbaar`. Bij `matig` toont de popup een kanttekening. */
  leesbaarheid: string | null;

  kenteken: string | null;
  polisnummer: string | null;
  geldigVan: string | null;
  geldigTot: string | null;
  merk: string | null;
  merkEnType: string | null;
  verzekeraar: string | null;
  soortMotorrijtuig: string | null;
  verzekeringnemerNaam: string | null;
  verzekeringnemerAdres: string | null;
  verzekeringnemerPostcode: string | null;
  verzekeringnemerPlaats: string | null;
  tussenpersoon: string | null;
  /** Het BEDRAG bij een verhoogd eigen risico elders; leeg als het er niet staat. */
  eigenRisicoAndereHersteller: string | null;
  /** De beperking op de herstellerkeuze, in één zin; leeg als er geen beperking staat. */
  beperkingHerstellerkeuze: string | null;
}

/**
 * Respons van `GET /api/dossiers/{id}/greencard-read-status` — waarmee de popup
 * kan zien dat de achtergrondtaak het verzekeringsbewijs gelezen heeft.
 *
 * ⚠ De respons draagt geen intern spoor: geen modelnaam, geen tokenverbruik,
 * geen ruwe modelrespons, geen prompt en geen API-sleutel.
 */
export interface GreenCardReadStatus {
  /**
   * Er loopt op dit moment een uitlezing voor dit dossier. Zolang dit `true` is
   * kan er nog een resultaat bijkomen; wordt het `false`, dan is de uitkomst
   * definitief — óók wanneer die uitkomst "er viel niets te lezen" is.
   *
   * ⚠ Een nog niet opgepakte taak telt óók mee. Vlak na de upload bestaat er nog
   * geen claim-rij, en een poller die alleen daarop keyt ziet "niets te doen",
   * stopt meteen, en de gebruiker zit precies in het gat dat deze route moest
   * dichten.
   */
  pending: boolean;
  /** De terminale resultaten; een lopende uitlezing staat hier nog niet in. */
  results: GreenCardReadResult[];
}

/** Eén rij in de calculatie-tabel. */
export interface DossierCalculationRow {
  /** `UA_DossierRow.EuroCode`. */
  code: string | null;
  quantity: number | null;
  amount: number | null;
}

/**
 * DTO voor één rij in de Calculatie-tab (story calculatie-tab-readonly).
 *
 * Geleverd door `GET /api/dossiers/{dossierId}/calculation-rows?calculationCode=filiaal`.
 * Bevat zowel de DB-velden van `UA_DossierRow` als de server-side berekende
 * waarden (`netto`, `bedrag`, `vatAmount`, `inclusief`) zodat de FE géén
 * berekening doet (AC 4.4).
 *
 * **Server-side regel voor `rowType`** (`CalculationRowTypeRule`, besluit 23-10-2026; dossier én kladblok):
 *  1. `EuroCode` gevuld + artikel van artikeltype "Eurocode" → `"eurocode"`
 *  2. `EuroCode` gevuld + artikel van een ander/onbekend artikeltype → `"item"` (legacy vult die kolom óók
 *     voor montage, kit, primer enz.)
 *  3. `EuroCode` gevuld zonder artikel → legacy-tekentoets (eerste vier tekens een getal ≠ 0 → `"eurocode"`,
 *     anders `"item"`)
 *  4. geen `EuroCode`: `ItemID` met `Item.Code` → `"item"`, anders `"vt"` (vrije tekst).
 */
export interface CalculationRowDto {
  /** `UA_DossierRow.ID` — lowercase-dashed Guid-string. */
  id: string;
  /** `UA_DossierRow.RowNumber` — nullable int. */
  rowNumber: number | null;
  /**
   * `UA_DossierRow.ChildFrom`. FE rendert child-indent + `└`-prefix alleen
   * als de parent ook in de result-set zit (AC 3.3 + M-cal.11).
   */
  childFrom: number | null;
  /** Server-side bepaald — zie priority-keten boven. */
  rowType: 'eurocode' | 'item' | 'vt';
  /**
   * `EuroCode` indien gevuld, anders `Item.Code` via `ItemID`, anders lege
   * string. Bij data-anomalie logt de server een warning.
   */
  code: string;
  /** `UA_DossierRow.Description`. */
  description: string | null;
  /** `UA_DossierRow.Quantity`. */
  quantity: number | null;
  /** `UA_DossierRow.UnitPrice`. */
  unitPrice: number | null;
  /**
   * `UnitPriceDiscountPercentage` (gecast naar decimal) als > 0, anders
   * `UnitPriceDiscountAmount`. Bij beide null → 0 (AC 3.5).
   */
  discountValue: number | null;
  /**
   * `"%"` als `UnitPriceDiscountPercentage > 0`, anders `"€"`. Bij anomalie
   * (beide > 0) wint `"%"` (story-noot bij AC 3.6 / F-cal.04).
   */
  discountType: '€' | '%';
  /** Server-side berekend (geen DB-kolom). */
  netto: number | null;
  /**
   * `UA_DossierRow.Amount` indien niet null, anders server-side berekend
   * via `netto × quantity` (AC 4.2).
   */
  bedrag: number | null;
  /** `UA_DossierRow.VatPercentage`. */
  vatPercentage: number | null;
  /**
   * KB-08a — het gekozen btw-TARIEF (`UA_DossierRow.VatID`, lowercase-dashed Guid).
   * Het percentage alleen wijst het tarief niet aan: er bestaan meerdere tarieven
   * met hetzelfde percentage (drie van 0%). De keuzelijst selecteert op dit id en
   * valt alleen zonder id terug op het percentage. Ontbreekt op een oudere backend.
   */
  vatId?: string | null;
  /**
   * `UA_DossierRow.VatAmount` indien niet null, anders server-side berekend
   * via `bedrag × vatPercentage / 100` (AC 4.2).
   */
  vatAmount: number | null;
  /** Server-side berekend (geen DB-kolom): `bedrag + vatAmount`. */
  inclusief: number | null;
  /**
   * Story `be-fe-brutoprijs-bij-calculeren` — hoe vers de brutoprijs van deze
   * eurocode is.
   *
   * ⚠ Ontbreekt (`undefined`) of is `null` op elke NIET-eurocode-regel, en op
   * een oudere backend. De regel toont dan **niets**; het wordt nadrukkelijk
   * niet stil toestand 4, want dan zou elke tekstregel "brutoprijs nog niet
   * opgehaald" tonen (AC 5.6 / 5.6b).
   */
  grossPrice?: CalculationRowGrossPriceDto | null;
}

/**
 * De brutoprijs-toestand van één eurocode-regel.
 *
 * ⚠⚠ **Neutraal.** Geen `sourceCode`, geen `unavailableReason` en geen enkele
 * veldnaam of waarde die naar de leverancier verwijst — de bron wordt naar
 * buiten nooit benoemd (CLAUDE.md; er staat een scan op).
 *
 * ⚠ Ook geen `fetchFailed`: de regel toont hoe **oud de prijs** is, niet of de
 * laatste poging lukte. Dat zijn twee verschillende vragen en ze horen niet in
 * één merkteken; een storing is uitsluitend zichtbaar als voortgangsmelding.
 */
export interface CalculationRowGrossPriceDto {
  /**
   * - `confirmed` — er is een bedrag en het is recent bevestigd (het normale geval);
   * - `stale` — er is een bedrag, maar het is te lang geleden bevestigd;
   * - `unavailable` — bevraagd, en er is geen brutoprijs beschikbaar. ⚠ De
   *   NORMALE, PERMANENTE toestand van campers en vrachtwagens: rustig grijs,
   *   nooit amber, nooit rood;
   * - `never` — nog nooit opgehaald; kortstondig.
   *
   * ⚠ De drempel tussen `confirmed` en `stale` wordt **server-side** bepaald
   * (`GrossPrice.StaleAfterDays`) zodat de FE de instelling niet dupliceert.
   */
  state: 'confirmed' | 'stale' | 'unavailable' | 'never';
  /** Het bedrag, of `null` bij `unavailable`/`never`. */
  amount: number | null;
  /** ISO-datum (`yyyy-MM-dd`), lokale tijd. `null` bij `never`. */
  confirmedAt: string | null;
  /**
   * Mag deze gebruiker een verse ophaling forceren (support / system /
   * keten-HK)? ⚠ Dit is **uitsluitend de rolpoort** — of er iets te verversen
   * válht is een serverantwoord, geen verborgen knop. De FE leest de
   * zichtbaarheid hier en redeneert zelf **niet** over rollen.
   */
  canRefresh: boolean;
}

/** Verzoek om een brutoprijs binnen dezelfde dag opnieuw op te halen. */
export interface GrossPriceRefreshRequestDto {
  /** De genormaliseerde eurocode. ⚠ Geen dossier-id: een brutoprijs geldt
   * landelijk en hangt niet aan een dossier. */
  euroCode: string;
}

/** Antwoord op een verse ophaling. Neutraal — geen bron, geen interne reden. */
export interface GrossPriceRefreshResponseDto {
  state?: 'confirmed' | 'stale' | 'unavailable' | 'never';
  amount?: number | null;
  /** Het bedrag van vóór de ophaling, zodat de melding het verschil kan tonen. */
  previousAmount?: number | null;
  confirmedAt?: string | null;
  /** De ophaling liep stuk; er is gerekend met wat er stond. */
  fetchFailed?: boolean;
  /** De bruto-prijslijst is bijgewerkt. */
  priceListUpdated?: boolean;
  canRefresh?: boolean;
  /** `gross_price_nothing_to_refresh` wanneer er nog geen historie-rij was. */
  reason?: string;
}

/**
 * Eén historische calculatie-snapshot (story `fe-historische-calculaties`).
 *
 * Geleverd door `GET /api/dossiers/{dossierId}/calculation-history`. Een
 * snapshot bundelt de `UA_DossierRowHistory`-rijen die één `HistoryIndex`
 * delen — legacy schrijft bij elke "bewaar calculatie" de oude rijen weg met
 * een opgehoogde index. De rijen hebben exact dezelfde shape (en server-side
 * berekening) als de actieve `CalculationRowDto`, zodat de read-only popup
 * 1-op-1 dezelfde getallen toont. Het totaal berekent de FE zelf met
 * `computeTotals`.
 */
export interface CalculationHistorySnapshotDto {
  /** `HistoryIndex` — versie binnen het dossier; hoger = nieuwer. */
  historyIndex: number;
  /**
   * Opslag-moment (lokale tijd, ISO-8601 zonder offset
   * `yyyy-MM-ddTHH:mm:ss`). `null` wanneer onbekend. FE toont 'm
   * ongeconverteerd.
   */
  savedAt: string | null;
  /** Naam van de gebruiker die deze versie wegschreef. `null` indien onbekend. */
  savedByName: string | null;
  /** Naam van de gebruikte prijslijst. `null` indien geen/onbekend. */
  pricelistName: string | null;
  /** De regels van deze snapshot, op `rowNumber` gesorteerd. */
  rows: CalculationRowDto[];
}

/**
 * De inkoopherleiding van één eurocode — antwoord van
 * `GET /api/dossiers/{id}/purchase-price-origin` (story
 * `fe-be-inkoopherleiding-popup`, concept `inkoopherleiding-popup-v1.html`).
 *
 * Laat zien wáár de prijs vandaan komt op een prijslijst die op inkoop rekent:
 * wat elke leverancier bood, wie won, en hoe de opslag tot de uitkomst leidde.
 *
 * ⚠ `branch` is een SLEUTEL en geen schermtekst; de Nederlandse woorden staan in
 * de FE. Mogelijke waarden:
 * - `Stock` — minstens één leverancier had voorraad;
 * - `GrossFallback` — niemand had voorraad, teruggevallen op de brutoprijs;
 * - `NoneNoStock` — niemand op voorraad én geen brutoprijs ⇒ 0,00;
 * - `NoneZeroPrice` — wél antwoorden maar geen bruikbare prijs ⇒ 0,00;
 * - `Unknown` — de prijs kwam uit de cache en de opbouw was niet meer beschikbaar;
 * - `null` — er is voor deze eurocode niets vastgelegd (alleen een storing).
 */
export interface PurchasePriceOriginItemDto {
  euroCode: string;
  /** Moment van de calculatie. `null` ⇒ geen vastlegging, alleen een storing. */
  priceDate: string | null;
  branch: string | null;
  fromCache: boolean;
  /**
   * Was de opbouw nog beschikbaar? `false` is een **verwachte** toestand (de
   * cacheregel met de opbouw was opgeruimd), géén storing — alle opslag- en
   * plafondvelden zijn dan `null`.
   */
  breakdownAvailable: boolean;
  resultAmount: number | null;
  purchaseAmount: number | null;
  /** ⚠ Leeg bij een cachetreffer: er is dan geen leverancier bevraagd. De winnaar
   *  is dan af te lezen aan `isWinner` in `suppliers`. */
  winningSupplierCode: string | null;
  breakpointAmount: number | null;
  markupFactor: number | null;
  bonusFactor: number | null;
  provisionFactor: number | null;
  /** Het BEDRAG van de brutoprijs. ⚠ De bron erachter wordt nooit benoemd. */
  grossPriceUsed: number | null;
  grossNetPercentage: number | null;
  isLeaseDossier: boolean | null;
  /** Heeft het plafond de uitkomst bepaald? Dan bepaalde de inkoopprijs hem juist niet. */
  capApplied: boolean;
  suppliers: PurchasePriceOriginSupplierDto[];
  /**
   * Het begin van de prijs**periode** van deze antwoorden.
   *
   * ⚠⚠ Níet het moment van bevragen. Een leverancier die al uren hetzelfde
   * bedrag noemt houdt zijn oude periodestart, ook wanneer hij bij deze
   * calculatie gewoon opnieuw geantwoord heeft. Of er bij déze calculatie
   * bevraagd is zegt uitsluitend `fromCache`.
   */
  suppliersObservedAt: string | null;
  /** Het laatste dat voor deze eurocode gebeurde was een mislukte bevraging. */
  failedAt: string | null;
  /**
   * Deze herleiding is **overgenomen** van een ander dossier (dossier-kopie):
   * op dít dossier is nooit bevraagd. De bedragen en de opbouw zijn die van de
   * oorspronkelijke calculatie, en `priceDate` is het moment daarvan.
   *
   * ⚠ Losstaande vlag en niet "nummer is gevuld": een verdwenen brondossier
   * laat het nummer leeg terwijl het nog steeds een overname is.
   */
  isCopied: boolean;
  /** Het dossiernúmmer waar de bevraging werkelijk plaatsvond. Bij een kopie van
   *  een kopie wijst dit naar de OORSPRONG, niet naar de tussenstap. */
  copiedFromDossierNumber: number | null;
}

/** Wat één leverancier bood op het moment van de bevraging. */
export interface PurchasePriceOriginSupplierDto {
  supplierCode: string | null;
  /**
   * ⚠⚠ Een leverancier **zonder** voorraad levert hier `0` op en niet `null` —
   * dat is wat de bron zegt. Toon dit bedrag dus NIET wanneer `countsForMinimum`
   * onwaar is; het is geen aanbod.
   */
  amount: number | null;
  /** De brontekst, letterlijk (bv. `In voorraad`, `Geen`). */
  availability: string | null;
  /** Telde dit antwoord mee voor het minimum? Alleen `In voorraad`/`Gedeeltelijk`. */
  countsForMinimum: boolean;
  isWinner: boolean;
}

/** Het volledige antwoord: één blok per eurocode, alfabetisch. */
export interface PurchasePriceOriginDto {
  /** Is er iets te tonen? Voedt de knop-state. ⚠ Los veld, zodat de FE geen
   *  betekenis hoeft af te leiden uit een lege lijst. */
  hasData: boolean;
  items: PurchasePriceOriginItemDto[];
}

/**
 * Sub-object voor de verplichte prijslijst van een dossier — onderdeel van
 * `CalculationPricelistDto.fixedPricelist`. Bevat alleen `id` + `name`. `null`
 * op het bovenliggende veld wanneer de dossier-tuple niet verplicht-zegt
 * (`Item2 != true`) of geen `Item1` heeft.
 */
export interface CalculationFixedPricelistDto {
  id: string;
  name: string | null;
}

/**
 * S009.5 — Eén suggestie in het autocomplete-paneel voor eurocode-search.
 *
 * Wire-shape uit `GET /api/items/search`: lijst van objecten met `id`, `code`,
 * `description` en optioneel `itemTypeDescription`. FE rendert geen item-type-
 * pilletje wanneer `itemTypeDescription` leeg of null is.
 */
export interface ItemSuggestionDto {
  /** `UE_Item.ID` als lowercase-dashed Guid-string. */
  id: string;
  /** `UE_Item.Code` (de eurocode). */
  code: string;
  /** `UE_Item.Description` — kan leeg zijn, zelden null. */
  description: string;
  /**
   * `UE_ItemType.Description` via `UE_Item.ItemTypeID`. Nullable —
   * FE toont geen pil wanneer leeg.
   */
  itemTypeDescription: string | null;
}

// ─── Artikelbeheer (story fe-artikelbeheer-zoek-detail-scoping) ─────────────

/**
 * Eén treffer in de Artikelbeheer-Zoek-tab
 * (`GET /api/items/manage-search?q=`). Tenant-gescopet server-side via
 * `ItemListID` + `fUE_ItemListConnectionRecords` (System-/eigen-lijsten +
 * vrije artikelen) — NOOIT kale CompanyID.
 */
export interface ItemSearchHit {
  /** `UE_Item.ID` (lowercase-dashed Guid-string) — route-param. */
  id: string;
  code: string | null;
  description: string | null;
  /** `UE_ItemType.Description`, nullable. */
  itemTypeName: string | null;
}

/**
 * Artikel-detail (`GET /api/items/{id}`). De tien kernvelden + twee
 * BE-berekende scoping-flags. De FE rekent NOOIT zelf company-vergelijking:
 * - `canEdit` bepaalt of de Artikel-tab bewerkbaar is (System altijd; anders
 *   alleen eigen-lijst-artikelen).
 * - `isSystemArticle` toont de read-only-banner.
 */
export interface ItemDetail {
  id: string;
  code: string | null;
  description: string | null;
  itemTypeId: string | null;
  itemTypeName: string | null;
  itemListId: string | null;
  itemListName: string | null;
  supplierId: string | null;
  supplierName: string | null;
  procTypeId: string | null;
  procTypeName: string | null;
  vatId: string | null;
  vatName: string | null;
  parentItemId: string | null;
  parentItemCode: string | null;
  internComment: string | null;
  variant: string | null;
  canEdit: boolean;
  isSystemArticle: boolean;
  /**
   * Server-side bepaald (canonieke `UE_ItemType`-natural-key "Eurocode"): of dit
   * artikel een eurocode is. Bepaalt of de Glasdefinitie-tab verschijnt — de FE
   * rekent dit niet langer zelf op de typenaam (Story 4, stabiele key).
   */
  isEurocode: boolean;
}

// ─── Glasdefinitie (Story 4) ──────────────────────────────────────────────

/**
 * Glasdefinitie van een artikel = de 9 FK-velden op het `UA_Eurocode`-record
 * (natural key = item.Code). `exists=false` → nog geen record; FE toont "Maak definities".
 */
export interface GlassDefinitionDto {
  exists: boolean;
  code: string | null;
  matrixId: string | null;
  glassTypeId: string | null;
  glassTintId: string | null;
  topTintId: string | null;
  vehicleBodyId: string | null;
  glassPositionId: string | null;
  glassAccessoryId: string | null;
  accessoryTypeId: string | null;
  carculatePositionId: string | null;
}

/** Request-body voor `PUT …/glass-definition` — de 9 FK-velden. */
export interface GlassDefinitionWritePayload {
  matrixId: string | null;
  glassTypeId: string | null;
  glassTintId: string | null;
  topTintId: string | null;
  vehicleBodyId: string | null;
  glassPositionId: string | null;
  glassAccessoryId: string | null;
  accessoryTypeId: string | null;
  carculatePositionId: string | null;
}

/** Eén dropdown-optie (id + omschrijving) voor de Glasdefinitie-lookups. */
export interface GlassLookupOption {
  id: string;
  description: string;
}

/**
 * `UA_CarculatePosition`-optie incl. cascade-data: bij selectie (of SVG-klik op
 * `imagePosition`) vult de FE `glassPositionId` + `glassTypeId`.
 */
export interface CarculatePositionOption {
  id: string;
  description: string;
  imagePosition: number | null;
  glassPositionId: string | null;
  glassTypeId: string | null;
}

/** De 9 lookup-lijsten voor de Glasdefinitie-dropdowns. */
export interface GlassLookups {
  matrices: GlassLookupOption[];
  glassTypes: GlassLookupOption[];
  glassTints: GlassLookupOption[];
  topTints: GlassLookupOption[];
  vehicleBodies: GlassLookupOption[];
  glassPositions: GlassLookupOption[];
  accessoryTypes: GlassLookupOption[];
  accessories: GlassLookupOption[];
  carculatePositions: CarculatePositionOption[];
}

// ─── Interchangeables (Story 4, System-only) ──────────────────────────────

/** Eén lid van een uitwisselgroep (parent of gekoppeld artikel). */
export interface InterchangeableMemberDto {
  id: string;
  code: string | null;
  description: string | null;
  isParent: boolean;
}

/**
 * De uitwisselgroep van een artikel. `inGroup=false` → het artikel zit (nog) niet
 * in een groep; de FE toont dan "Maak uitwisselgroep".
 */
export interface InterchangeablesDto {
  inGroup: boolean;
  parentId: string | null;
  parentCode: string | null;
  members: InterchangeableMemberDto[] | null;
}

/**
 * FK-dropdownbronnen voor de Artikel-tab (Leverancier owner-gescopet; BTW +
 * Procestype globale referentie). Parent gaat via artikel-zoeken.
 */
export interface ItemFkLookups {
  suppliers: LookupOption[];
  vatCodes: LookupOption[];
  procTypes: LookupOption[];
}

/**
 * Body voor `POST /api/items` (create) en `PUT /api/items/{id}` (update). De
 * tien kernvelden; OwnerID/CompanyID/audit-velden worden server-side gezet via
 * `Interaction.UE_ItemAdd/UE_ItemUpdate`.
 */
export interface ItemWritePayload {
  code: string;
  description: string;
  itemTypeId: string;
  itemListId: string;
  supplierId?: string | null;
  procTypeId?: string | null;
  vatId?: string | null;
  parentItemId?: string | null;
  internComment?: string | null;
  variant?: string | null;
}

// ─── Artikelbeheer — Prijzen-tab (story fe-artikelbeheer-prijzen-tab) ────────

/**
 * Eén rij in de Prijzen-tab van het artikel-detail.
 *
 * De BE levert per zichtbare prijslijst (gefilterd via `HasAccess`) de
 * DB-prijs (`unitPrice`), de afgeleide-weergave (`derivedDisplay`) en de
 * twee flags. De FE rekent GEEN rol/owner zelf.
 *
 * Velden:
 * - `priceListId`    — UUID van de prijslijst (opaque; wordt teruggestuurd
 *                      bij de write).
 * - `priceListName`  — leesbare naam, bv. `"System — Standaard"`.
 * - `unitPrice`      — de eigen DB-prijs (`UE_Price.UnitPrice`), of `null`
 *                      wanneer er geen eigen prijs is opgeslagen.
 * - `derivedDisplay` — "berekend via System.Standaard" / datum / "read-only"
 *                      of `null` wanneer er niets afgeleid is.
 * - `canEdit`        — `true` als de sessie-company deze prijs mag bewerken
 *                      (`OwnerID == session.CompanyId`; System: altijd `true`).
 * - `isSystemBase`   — `true` voor de System.Standaard-basisrij (read-only
 *                      ter referentie voor niet-system).
 * - `isCalculated`   — `true` als `UE_PriceList.PriceIsCalculated = true`
 *                      (schrijven niet mogelijk via `SavePriceForItem`).
 * - `tag`            — `"own"` / `"base"` / `"view"` (spiegel van het
 *                      concept-legenda; puur visueel, de flags zijn leidend).
 */
export interface ItemPriceRowDto {
  priceListId: string;
  priceListName: string;
  /** De eigen DB-prijs (`UE_Price.UnitPrice`), of `null` bij geen eigen prijs. */
  unitPrice: number | null;
  /**
   * Leesbare afgeleide-weergave, bv. `"40,90 — berekend via System.Standaard"`,
   * of `"read-only"` voor de basisrij vanuit niet-system-perspectief.
   * `null` wanneer er niets afgeleid is.
   */
  derivedDisplay: string | null;
  /** `true` als de sessie-company deze prijs mag bewerken. */
  canEdit: boolean;
  /** `true` voor de System.Standaard-basisrij (read-only voor niet-system). */
  isSystemBase: boolean;
  /** `true` als `UE_PriceList.PriceIsCalculated`; `SavePriceForItem` weigert writes. */
  isCalculated: boolean;
  /** Visueel tag-type voor de legenda (`"own"` / `"base"` / `"view"`). */
  tag: 'own' | 'base' | 'view';
  /**
   * Alleen in het antwoord op een geslaagde PUT: `"price_list_not_linked"` wanneer de
   * prijs wel is opgeslagen maar de prijslijst geen koppelrij voor het bedrijf heeft (dan
   * ziet de prijsbepaling hem niet). Anders `null`/afwezig.
   */
  warning?: 'price_list_not_linked' | null;
}

/**
 * Request-body voor `PUT /api/items/{id}/prices/{priceListId}`.
 *
 * Bevat uitsluitend `price` (de nieuwe `UE_Price.UnitPrice`). De BE schrijft
 * via `act.SavePriceForItem` (CLAUDE.md hard-rule — geen raw SQL).
 * `null` → prijs verwijderen / terugvallen op de basis-prijslijst (geen
 * `IsNullPrice`-ondersteuning in story 2; dat is een followup).
 */
export interface ItemPriceWriteRequest {
  price: number | null;
}

// ─── Sets-tab (story 3 artikelbeheer) ───────────────────────────────────────

/**
 * Wire-DTO voor één set-component, zoals geretourneerd door
 * `GET /api/items/{id}/sets` en `POST/PUT /api/items/{id}/sets/{setId}/components`.
 */
export interface ItemSetComponentDto {
  componentId: string;
  rowId: number;
  subItemId: string;
  itemCode: string;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
}

/**
 * Wire-DTO voor één set (inclusief componenten), zoals geretourneerd door
 * `GET /api/items/{id}/sets` en `POST/PUT /api/items/{id}/sets/{setId}`.
 */
export interface ItemSetDto {
  setId: string;
  priceListId: string;
  /** Sectie-kop in de FE (F-01: alleen prijslijstnaam tonen). */
  priceListName: string;
  /** "Aantal in set" — bewerkbaar wanneer `canEdit=true`. */
  quantity: number | null;
  /** `true` als de sessie-company deze set mag bewerken. */
  canEdit: boolean;
  components: ItemSetComponentDto[];
}

/**
 * Request-body voor `POST /api/items/{id}/sets` — nieuwe set aanmaken.
 */
export interface ItemSetCreateRequest {
  priceListId: string;
}

/**
 * Request-body voor `PUT /api/items/{id}/sets/{setId}` — set-header bijwerken.
 */
export interface ItemSetUpdateRequest {
  quantity: number | null;
}

/**
 * Request-body voor `POST /api/items/{id}/sets/{setId}/components` — component toevoegen.
 */
export interface ItemSetComponentCreateRequest {
  subItemId: string;
  quantity?: number | null;
  unitPrice?: number | null;
  description?: string;
}

/**
 * Request-body voor `PUT /api/items/{id}/sets/{setId}/components/{rowId}` — component bijwerken.
 */
export interface ItemSetComponentUpdateRequest {
  quantity: number | null;
  unitPrice: number | null;
  description: string;
}

/**
 * Prijslijst-optie voor de "Set aanmaken"-dropdown.
 */
export interface ItemSetPriceListOptionDto {
  priceListId: string;
  priceListName: string;
}

/**
 * S015.5 (EPIC-015) — Eén resultaat-rij uit `GET /api/eurocodes/search`
 * (de AutoQraft-glascatalogus). De 10 velden komen exact uit het BE-DTO
 * (`EurocodeSearch.cs` — `DtoColumns` + de lokaal-afgeleide `inPricelist`).
 *
 * `inPricelist` wordt FE-side **alleen functioneel** gebruikt (bepaalt of
 * een klik op "+ Toevoegen" een byCode-add doet of een nette "volgt
 * binnenkort"-melding toont) — **nooit** als badge gerenderd (beslissing
 * 2026-06-14). `sameas1..3` komen al binnen maar worden pas in S015.8 als
 * kruisverwijzing-chips getoond.
 */
export interface EurocodeSearchItemDto {
  /** De eurocode (bv. `"2444AGSMVZ1B"`). Natural key voor de byCode-add. */
  eurocodeNumber: string;
  /** Merk-omschrijving. Kan `null` zijn (ontbrekend veld upstream). */
  merk: string | null;
  /** Model-omschrijving. Kan `null` zijn. */
  model: string | null;
  /** Vrije omschrijving van de ruit. Kan `null` zijn. */
  beschrijving: string | null;
  /** Glas-type (bv. `"Laminated"`). Kan `null` zijn. */
  glassType: string | null;
  /** Glas-positie (bv. `"Front"`). Kan `null` zijn. */
  glassPosition: string | null;
  /** Kruisverwijzing 1 (S015.8). Kan `null` zijn. */
  sameas1: string | null;
  /** Kruisverwijzing 2 (S015.8). Kan `null` zijn. */
  sameas2: string | null;
  /** Kruisverwijzing 3 (S015.8). Kan `null` zijn. */
  sameas3: string | null;
  /**
   * Lokaal-afgeleid: bestaat deze eurocode al als toegankelijk `UE_Item.Code`?
   * `true` → byCode-add werkt; `false` → "toevoegen volgt binnenkort"-melding
   * (de echte create komt in S015.7). Nooit als badge gerenderd.
   */
  inPricelist: boolean;
}

/**
 * S015.5 (EPIC-015) — Response-body van `GET /api/eurocodes/search`.
 *
 * `reason` is **optioneel** en alleen aanwezig op een **200** met een
 * fail-soft-conditie — primair `"not_configured"` (geen API-key ingericht):
 * dan komen `items:[]` + `total:0` binnen **zonder** throw, en de FE toont een
 * rustige "nog niet beschikbaar"-staat (géén rode error-toast). Een echte
 * upstream-fout (502 `eurocode_unreachable`/`rate_limited`) komt als typed
 * throw binnen (geen body hier), niet via dit veld.
 */
export interface EurocodeSearchResponseDto {
  items: EurocodeSearchItemDto[];
  total: number;
  page: number;
  pageSize: number;
  /** Alleen gezet bij een fail-soft 200 (bv. `"not_configured"`). */
  reason?: string;
}

/**
 * S015.6 (EPIC-015) — Eén resultaat-rij uit `GET /api/eurocodes/suggest`
 * (chassis-voorstel). Exact-spiegel van het getrimde BE-DTO
 * (`EurocodeSuggestMapper.TrimResponse` in `C:\CARculate\AppServices\Eurocode\
 * EurocodeSuggest.cs`): per resultaat alléén `eurocode` + `aantal` + de
 * lokaal-afgeleide `inPricelist` (S015.4-grens, gedeeld met search).
 *
 * `aantal` is door de BE geclampt naar een niet-negatieve int (ontbrekend/
 * negatief/float → 0). Bij `aantal === 0` laat de FE de "Nx gefactureerd"-
 * subtekst weg (de code blijft kiesbaar). `inPricelist` wordt — net als bij
 * search — **alleen functioneel** gebruikt (byCode-add vs. "volgt binnenkort"),
 * nooit als badge gerenderd.
 */
export interface EurocodeSuggestResultDto {
  /** De eurocode (natural key voor de byCode-add). Nooit leeg (BE skipt code-loze rijen). */
  eurocode: string;
  /** Hoe vaak deze eurocode historisch gefactureerd is (≥ 0, BE-geclampt). */
  aantal: number;
  /**
   * Lokaal-afgeleid: bestaat deze eurocode al als toegankelijk `UE_Item.Code`?
   * `true` → byCode-add werkt; `false` → "toevoegen volgt binnenkort"-melding.
   * Nooit als badge gerenderd.
   */
  inPricelist: boolean;
  /**
   * W6 (KB-05) — korte omschrijving van de ruit, zodat het verschil tussen de
   * voorgestelde ruiten zichtbaar is (met/zonder sensor, verwarming, kleur …).
   * Bron: de catalogus (`beschrijving`, dezelfde als de zoekresultaten), met
   * terugval op de omschrijving van het artikel; `null` als geen van beide iets
   * heeft. Optioneel zodat een oudere backend zonder het veld gewoon werkt.
   */
  description?: string | null;
}

/**
 * S015.6 (EPIC-015) — Response-body van `GET /api/eurocodes/suggest`.
 *
 * Exact-spiegel van `EurocodeSuggestMapper.TrimResponse`:
 *  - `resultaten` — meest waarschijnlijk eerst (`resultaten[0]`); de
 *    upstream-volgorde wordt **behouden** (niet hersorteren).
 *  - `prefixLengte` — `9` (brede match op merk/model) of `11` (exact dit
 *    voertuig) of `null` (onbekend). Voedt de brede-match-hint.
 *  - `aantalResultaten` — top-level telling (kan afwijken van
 *    `resultaten.length`); een **echte** 0-match is
 *    `{ aantalResultaten: 0, resultaten: [], reason: undefined }`.
 *  - `positie.label` kan `null` zijn → de FE valt terug op een lokaal
 *    `positieLabel(id)`.
 *  - `reason` is **alleen aanwezig** bij een fail-soft 200 (`"not_configured"`);
 *    een echte 5xx komt als typed throw binnen. Branch op de **presence** van
 *    `reason`, niet op een token (er is geen `no_match`-token).
 */
export interface EurocodeSuggestResponseDto {
  resultaten: EurocodeSuggestResultDto[];
  prefixLengte: number | null;
  aantalResultaten: number;
  positie: { id: number; label: string | null };
  /** Alleen gezet bij een fail-soft 200 (bv. `"not_configured"`). */
  reason?: string;
}

/**
 * S009.5 — Request-body voor `POST /api/dossiers/{id}/calculation-rows`.
 *
 * Discriminator-veld `kind`:
 *   - `"byItemId"` — `itemId` verplicht.
 *   - `"byCode"` — `code` verplicht.
 *   - `"vt"` — geen item-velden nodig; server resolved `Item.Code="VT"`.
 *   - `"byNewEurocode"` — story S015.7: ensure-the-eurocode-item-exists (maakt
 *     het artikel aan als het nog niet bestaat) + daarna de byCode-add. `code`
 *     verplicht; **geen** `description` in de body — de BE haalt 'm uit de
 *     catalogus.
 *
 * Server doet strict-lowercase validatie (geen `"ByItemId"`).
 */
export type AddCalculationRowKind = 'byItemId' | 'byCode' | 'vt' | 'byNewEurocode';

export interface AddCalculationRowRequestDto {
  kind: AddCalculationRowKind;
  itemId?: string | null;
  code?: string | null;
  pricelistId: string;
  calculationCode: string;
}

/**
 * S009.5 — Response-body voor `POST /api/dossiers/{id}/calculation-rows`.
 *
 * `addedRowNumbers` kan meerdere ints bevatten wanneer een eurocode child-
 * rijen genereert (bv. ruit + plakset). V1 schrijft altijd één rij maar het
 * contract laat ruimte voor uitbreiding zonder breaking change.
 */
export interface AddCalculationRowResponseDto {
  addedRowNumbers: number[];
  totalRowsAfter: number;
}

// ─── Losse calculatie ("kladblok") — story fe-calculatie-kladblok-glas ────
//
// Het kladblok rekent ZONDER dossier via een dun AppServices-endpoint dat
// uitsluitend `Interaction.GetCalculationForItem(...)` wrapt (geen
// `UA_DossierSaveCalculation`, geen raw SQL). Response = exact dezelfde
// row-DTO-vorm als `GET /api/dossiers/{id}/calculation-rows` (inclusief
// set-/child-componenten met hun `ChildFrom`-relatie). De FE kent zelf een
// client-side row-key toe; het endpoint kent GEEN persist-`id`/`rowNumber` toe.

/**
 * Discriminator-`kind` voor een losse calculatie-regel. Spiegelt
 * {@link AddCalculationRowKind} maar dossier-vrij:
 *   - `"byItemId"` — `itemId` verplicht.
 *   - `"byCode"` — `code` verplicht (natural-key resolve).
 *   - `"byNewEurocode"` — ensure-the-eurocode-item-exists + byCode-resolve.
 *   - `"vt"` — vrije-tekstregel (`text` optioneel; lokaal gerekend FE-side,
 *     maar het contract laat een server-VT toe voor pariteit).
 *   - `"byAdditiveItemId"` — een EXTRA ARTIKEL uit "Extra artikelen…": `itemId`
 *     draagt een `UA_AdditiveItem.ID` (géén `UE_Item.ID`). De BE vertaalt het
 *     exact zoals het dossier-bulk-add (IDOR-set → Carculate-ItemCode →
 *     artikel). Alleen op `calculate`/`calculate-batch`, niet op `create-dossier`.
 *     ⚠ Nooit zo'n id als `byItemId` sturen: dat gaf altijd 404 `ItemNotFound`
 *     (15-10-2026).
 */
export type CalculateKind = 'byItemId' | 'byCode' | 'byNewEurocode' | 'vt' | 'byAdditiveItemId';

/**
 * Kinds die `create-dossier` accepteert. Een extra artikel reist bij de promotie
 * als `byCode` (de code van de berekende regel) — de BE weigert `byAdditiveItemId`
 * daar met 400 `InvalidKind`.
 */
export type CreateDossierFromKladblokKind = Exclude<CalculateKind, 'byAdditiveItemId'>;

/**
 * Body voor `POST /api/calculation/calculate` (single). Dossier-vrij.
 * `pricelistId` + `vatId` zijn verplicht; `insuranceCompanyId` optioneel
 * (een glasdossier heeft niet altijd een verzekeraar — AC-3.3).
 */
export interface CalculateRequestDto {
  kind: CalculateKind;
  itemId?: string | null;
  code?: string | null;
  /** Vrije-tekst-regel-omschrijving bij `kind: "vt"`. */
  text?: string | null;
  pricelistId: string;
  /** Strict-lowercase `"filiaal"` in V1 (spiegelt het dossier-add-endpoint). */
  calculationCode: string;
  vatId?: string | null;
  insuranceCompanyId?: string | null;
}

/**
 * Body voor `POST /api/calculation/calculate-batch` (bulk — extra/eigen
 * artikelen): een array van EXACT dezelfde single-items, en niets daarbuiten.
 *
 * ⚠⚠ **GECORRIGEERD 09-09-2026 — dit type deed het tegenovergestelde van wat
 * zijn eigen commentaar beloofde.** Het zette de discriminators per item en
 * `pricelistId`/`calculationCode`/`vatId` op de ENVELOPPE. De backend
 * (`CalculateBatchRequestDto` in `Calculation\CalculateRequestDtos.cs`) heeft
 * uitsluitend `Items` en valideert **per item**; die envelope-velden werden dus
 * genegeerd en elk item viel op de eerste controle. Uitkomst: eigen artikelen
 * calculeren vanuit het kladblok gaf
 * `{"error":"Only 'filiaal' is supported in this version."}`.
 *
 * ⚠ Dat is precies de faalvorm die de hard-rule *"bulk mirrors single shape"*
 * moet voorkomen: de bulk-body veld-voor-veld tegen de single-DTO leggen. Het
 * commentaar beriep zich op die regel terwijl de vorm haar overtrad — en de
 * test heette "met de single-body-shape" maar pinde de enveloppe vast, dus
 * hij was groen op de verkeerde vorm.
 */
export interface CalculateBatchRequestDto {
  items: CalculateRequestDto[];
}

/**
 * Eén te persisteren kladblok-regel voor `POST /api/calculation/create-dossier`
 * (story `fe-calculatie-kladblok-glas`, Story 2 / AC-8). De `kind`-discriminator
 * spiegelt {@link CalculateKind}; `quantity`/`discountValue`/`discountType`/
 * `description` zijn de inline-edits die op de gepersisteerde regel behouden
 * blijven. Set-/child-componenten worden NIET meegestuurd — die leidt de BE af
 * via `GetCalculationForItem`.
 */
export interface CreateDossierFromKladblokLineDto {
  kind: CreateDossierFromKladblokKind;
  itemId?: string | null;
  code?: string | null;
  quantity?: number | null;
  discountValue?: number | null;
  discountType?: string | null;
  description?: string | null;
  /**
   * KB-08a — het btw-tarief van de hoofdregel zoals het in het kladblok stond. Zonder
   * dit veld kreeg de regel na "Maak dossier"/"Afdrukken" het tarief uit de calculatie.
   */
  vatId?: string | null;
  /**
   * KB-08a — het btw-tarief van de componenten (montage, kit, …) van deze regel, per
   * artikelcode. De BE leidt de componenten opnieuw af en koppelt op code.
   */
  componentVats?: KladblokComponentVatDto[] | null;
  /**
   * De met de hand aangepaste eenheidsprijs van de hoofdregel, of het bedrag van een
   * tekstregel (besluit eigenaar 19-10-2026, "calculeren is calculeren"). Alleen
   * meesturen als de gebruiker de prijs aanpaste: zonder dit veld blijft de prijs uit
   * de calculatie (legacy) staan.
   */
  unitPrice?: number | null;
  /** De aangepaste eenheidsprijzen van de componenten van deze regel, per artikelcode. */
  componentPrices?: KladblokComponentPriceDto[] | null;
}

/** KB-08a — het kladblok-tarief van één component (child-regel), op artikelcode. */
export interface KladblokComponentVatDto {
  code: string;
  vatId: string;
}

/** De in het kladblok aangepaste eenheidsprijs van één component, op artikelcode. */
export interface KladblokComponentPriceDto {
  code: string;
  unitPrice: number;
}

/**
 * Uitkomst van `calculateBatchDetailed`: de berekende regels plus de aangevraagde
 * id's die niets opleverden (geen artikelcode of geen prijs in de gekozen
 * prijslijst). Komt uit de response-header `X-Calculation-Skipped-Items` (KB-06).
 */
export interface CalculateBatchResultDto {
  rows: CalculationRowDto[];
  skippedItemIds: string[];
}

/**
 * Body voor `POST /api/calculation/create-dossier` — promoveert een
 * wegwerp-calculatie-kladblok tot één echt regulier `UA_Dossier` (status 210)
 * met de verzamelde regels. `pricelistId` verplicht; `insuranceCompanyId`
 * optioneel (OV-1 — een glasdossier hoeft geen verzekeraar, `null` als leeg).
 */
export interface CreateDossierFromKladblokRequestDto {
  pricelistId: string;
  vatId?: string | null;
  license?: string | null;
  chassisNumber?: string | null;
  insuranceCompanyId?: string | null;
  calculationCode: string;
  lines: CreateDossierFromKladblokLineDto[];
}

/**
 * Respons op een succesvolle promotie: het id van het nieuwe dossier (de FE
 * navigeert naar `/dossiers/{id}`) + het aantal gepersisteerde regels (parent +
 * child/set-componenten).
 */
export interface CreateDossierFromKladblokResponseDto {
  id: string;
  rowsPersisted: number;
  /**
   * Story `be-fe-meerprijs-dealer-berekenen` (R-2 gedicht): het aantal MD-regels dat niet is meegenomen omdat het
   * nieuwe dossier onder het MD-slot valt. Afwezig = alles meegenomen.
   */
  mdLinesSkipped?: number;
  /** `md_locked_central` / `md_locked_central_unknown` bij `mdLinesSkipped`. */
  mdLockReason?: string;
}

/**
 * Body voor `POST /api/calculation/print` — de losse calculatie uit het
 * kladblok afdrukken (story `be-fe-kladblok-afdrukken`). Dezelfde vorm als
 * {@link CreateDossierFromKladblokRequestDto}, maar zonder verzekeraar: de BE
 * maakt onderwater een calculatie-dossier aan (eigen nummerreeks, geen
 * workflow) en geeft alleen de PDF terug.
 */
export type KladblokPrintRequestDto = Omit<CreateDossierFromKladblokRequestDto, 'insuranceCompanyId'>;

/**
 * Reason-codes van `POST /api/calculation/print` (wire-contract, gespiegeld op
 * `KladblokPrintReasons` in de BE).
 */
export const KLADBLOK_PRINT_REASONS = {
  printReportNotConfigured: 'print_report_not_configured',
  calculationTypeNotFound: 'calculation_type_not_found',
  calculationDossierCreateFailed: 'calculation_dossier_create_failed',
  printFailed: 'print_failed',
} as const;

/**
 * Status-discriminator voor de dossier-vrije chassis-lookup
 * (`POST /api/vehicles/lookup-chassis`). Spiegelt {@link ChassisLookupStatus}
 * maar zonder dossier-version/-delta — alleen het VIN terug.
 */
export type ChassisLookupNoDossierStatus = 'found' | 'not_found' | 'error';

export interface ChassisLookupNoDossierRequest {
  /** NL-kenteken zoals de user heeft getypt (met of zonder streepjes). */
  license: string;
}

export interface ChassisLookupNoDossierResponse {
  status: ChassisLookupNoDossierStatus;
  /** Aanwezig bij `status='found'`. */
  chassisNumber?: string | null;
  /** Aanwezig bij `status='error'` — exception-message voor de toast. */
  error?: string | null;
  /**
   * `true` op de trainingsomgeving: het chassisnummer is het vaste voorbeeld
   * (`Training.DummyChassisNumber`), er is geen echte opvraag gedaan.
   */
  isTrainingExample?: boolean;
}

/**
 * Request-body voor `POST /api/dossiers/{dossierId}/ruitid` — een RuitID-
 * aanvraag aanmaken in Bindrix (EPIC-014, story S014.2). De FE bepaalt de
 * glaspositie (uit de bevestig-popup, S014.3); de voertuigvelden zijn
 * optionele overrides — ontbrekende waarden vult de server uit het dossier.
 */
export interface RuitIdCreateRequest {
  /** Glaspositie-ID (1..19), verplicht. */
  rglasspositionId: number;
  /** Optionele kenteken-override; anders uit het dossier. */
  license?: string;
  /** Optionele VIN-override; anders uit het dossier. */
  vin?: string;
  /** Optionele merk-override; anders uit het dossier. */
  brand?: string;
  /** Optionele model-override; anders uit het dossier. */
  model?: string;
  /** Optionele modeljaar-override; anders uit het dossier. */
  modelYear?: number;
}

/**
 * Response van `POST /api/dossiers/{dossierId}/ruitid` — het Bindrix-record-ID
 * + de initiële workflow-status (1 = Nieuw; de BE stuurt 'm niet zelf mee).
 */
export interface RuitIdCreateResponse {
  ruitId: string;
  status: number;
}

/**
 * Response van `GET /api/dossiers/{dossierId}/ruitid/{ruitId}` (poll) — de
 * huidige status (1 Nieuw · 2 Wordt verwerkt · 3 Afgerond · 4 Fout) + de
 * resultaatvelden. Resultaatvelden zijn alleen gevuld bij status 3; bij
 * status 4 is `errorMessage` gevuld. `grossPrice` is door de BE defensief van
 * de Bindrix-string naar een number geparsed (`null` bij leeg/onparsebaar).
 */
export interface RuitIdPollResponse {
  ruitId: string;
  status: number;
  eurocode: string | null;
  oemcode: string | null;
  grossPrice: number | null;
  calibration: string | null;
  calibrationMinutes: number | null;
  errorMessage: string | null;
  /**
   * S014.13 — de door RuitID/Bindrix gevonden VIN (chassisnummer), server-side
   * getrimd. Alleen gevuld bij status 3; anders `null`. Voedt de
   * chassisnummer-auto-fill bij een live-afronding.
   */
  vin: string | null;
}

/**
 * Eén server-side RuitID-registratie (story S014.5, EPIC-014) uit
 * `GET /api/dossiers/{dossierId}/ruitid`. De FE gebruikt de lijst om bij
 * popup-open de lock-staat per glaspositie te kennen (idempotentie over
 * sessies/gebruikers/ronden — de in-memory poll-registry overleeft geen
 * refresh) en om het resultaat-paneel na refresh te herstellen.
 *
 * `status`: 0 Gereserveerd · 1 In behandeling · 2 · 3 Afgerond · 4 Fout.
 */
export interface RuitIdRegistration {
  /** De Bindrix-glaspositie (1..19) — de idempotentie-sleutel-dimensie. */
  glasspositionId: number;
  /** Het Bindrix-record-ID; `null` zolang de rij Gereserveerd (0) is. */
  ruitId: string | null;
  status: number;
  /** Eurocode bij een afgeronde (3) aanvraag; anders `null`. */
  eurocode: string | null;
  /** Foutmelding bij een mislukte (4) aanvraag; anders `null`. */
  errorMessage: string | null;
  /** Naam/e-mail van de aanvrager (telemetrie). */
  requestedByName: string | null;
  /** Aanvraag-tijdstip (ISO/lokaal). */
  requestedAt: string;
  /**
   * S014.11 — bewaarde resultaatvelden bij een afgeronde (3) aanvraag, zodat het
   * resultaat-paneel ze na refresh kan herstellen. `null` bij lopend/fout en bij
   * vóór-037-afgeronde rijen (geen backfill). `grossPrice` komt als JSON-number.
   */
  oemcode: string | null;
  grossPrice: number | null;
  calibration: string | null;
  calibrationMinutes: number | null;
  /**
   * S014.13 — de bewaarde VIN (chassisnummer) bij een afgeronde (3) aanvraag;
   * anders `null` (lopend/fout en vóór-038-afgeronde rijen — geen backfill).
   * De FE vult hiermee een leeg dossier-chassisnummer éénmalig automatisch in.
   */
  vin: string | null;
}

/** Response van `GET /api/dossiers/{dossierId}/ruitid` (story S014.5 D-3). */
export interface RuitIdListResponse {
  items: RuitIdRegistration[];
}

/**
 * Eén melding uit het server-side meldingencentrum (story S014.8). Company-
 * breed zichtbaar; `isRead` is de afgeleide leesstatus voor de huidige user.
 * `deepLinkState` komt als rauwe JSON-string van de BE en wordt door de FE
 * geparsed + ongewijzigd doorgegeven aan `<Link state>` (D-7).
 */
export interface AppNotification {
  id: string;
  /** Type-code (uitbreidbaar). V1: `ruitid_done` / `ruitid_error`. */
  type: string;
  title: string;
  /** Secret-vrije body; bij `ruitid_error` = de fout-melding. `null` mogelijk. */
  body: string | null;
  /** Relatieve deep-link-route (bv. `/dossiers/{id}`); `null` wanneer afwezig. */
  deepLinkPath: string | null;
  /**
   * Optionele react-router-`state` als JSON-string (bv.
   * `{"activeTab":"calculation"}`); `null` wanneer de melding geen state heeft.
   */
  deepLinkState: string | null;
  /** Afgeleide leesstatus voor de huidige user. */
  isRead: boolean;
  /** Aanmaak-tijdstip (ISO/lokaal). */
  createdAt: string;
}

/** Response van `GET /api/notifications` (story S014.8 D-8). */
export interface NotificationListResponse {
  items: AppNotification[];
}

/** Response van `GET /api/notifications/unread-count` (story S014.8 D-8). */
export interface UnreadCountResponse {
  count: number;
}

/**
 * DTO voor het read-only prijslijst-endpoint
 * `GET /api/dossiers/{dossierId}/calculation-pricelist?calculationCode=filiaal`
 * (story S009.4 — V1 read-only display).
 *
 * **TA-fix F-pl.01 (`isFixed`)**: alleen `true` wanneer de dossier-tuple
 * verplicht is (`Item2 == true`) ÉN `selectedPricelistId` exact matcht. Bij
 * mismatch is `isFixed = false` — de balk (`PricelistMismatchBanner`) dekt
 * het mismatch-signaal af, niet het slot-icoon op de toolbar.
 *
 * **TA-fix F-pl.03**: de mismatch-balk-conditie is
 * `fixedPricelist != null && fixedPricelist.id !== selectedPricelistId`. Dat
 * dekt expliciet ook het geval `selectedPricelistId == null` met een
 * verplichte `fixedPricelist` (null !== id → balk verschijnt met
 * "geen prijslijst toegewezen"-tekstvariant).
 */
export interface CalculationPricelistDto {
  /** Resolver-uitkomst, of `null` als geen prijslijst toewijsbaar is. */
  selectedPricelistId: string | null;
  /** `UE_PriceList.Description` voor `selectedPricelistId`, of `null`. */
  selectedPricelistName: string | null;
  /**
   * Haalt de geselecteerde prijslijst inkoop op (prijslijstservice `stockapisza`)?
   * Alleen dan staat de knop "Inkoopherleiding" er (besluit eigenaar 12-10-2026).
   * Optioneel: een oudere backend stuurt hem niet, en dan is er geen knop.
   */
  selectedPricelistRecordsPurchasePrice?: boolean;
  /** F-pl.01 — true alleen bij Item2=true ÉN Item1==selectedPricelistId. */
  isFixed: boolean;
  /**
   * Debug-label voor support — story `calc-prijslijst-default-en-lock`
   * vervangt de oude `"row1" | "row0" | "dossier" | "none"`-enum door een
   * uitgebreide set die zowel de rijen-tak (Bug-A fix) als de 10-stappen-
   * default-waterfall (Bug-B fix) onderscheidt:
   *
   *   - `"row0"` — rijen-tak, row[0] wint (Bug-A fix).
   *   - `"rowFallback"` — rijen-tak, row[0] cross-tenant, row[i>=1] wint.
   *     Eén generieke string (geen index in source).
   *   - `"companyDefault"`, `"companyInsured"`, `"companySales"` —
   *     waterfall-bronnen 1/2/3 (Company.GetInvoiceSetting).
   *   - `"customDossierType"` — bron 4 (Dossier.CustomDossierType.PricelistID).
   *   - `"genrelDebtor"`, `"insurerDebtor"`, `"agentDebtor"` — bronnen 5/6/7
   *     (Debtor.DefaultPricelistID).
   *   - `"insurerDefault"`, `"agentDefault"` — bronnen 8/9 (PO-2: GEEN lock).
   *   - `"companyCompany"` — bron 10 (RB_CompanyCompany — DE ENIGE lockende).
   *   - `"exchangeRepairer"` — bron 9.5 (story S009.7): de eXchange-prijslijst
   *     van de hersteller, gated op `IsExchangeDossier`. **Lockt** sinds het
   *     S009.7-vervolgbesluit. Ontbrak tot EPIC-019 S-F in deze union terwijl
   *     de resolver 'm al emitte — pre-existente drift, nu rechtgezet.
   *   - `"fallbackStandaard"` — bron 11 (laatste-redmiddel, "Standaard"-named
   *     PL wanneer 1-10 geen winnaar opleveren). Geen lock.
   *   - `"restrictedInsurer"` — **stap 0** (EPIC-019 S-F): de prijslijst van de
   *     verzekeraar van dít dossier, geforceerd en gelockt voor een
   *     verzekeraar-gebonden hersteller. Wint van élke andere bron.
   *   - `"none"` — geen kandidaat zichtbaar / niets gevuld.
   *
   * Verwijderde waardes: `"row1"` (vervangen door `"rowFallback"`),
   * `"dossier"` (waterfall verving 't), `"fixed"` (was al weg).
   * Niet gebruikt door FE-render-logica.
   */
  source:
    | 'row0'
    | 'rowFallback'
    | 'companyDefault'
    | 'companyInsured'
    | 'companySales'
    | 'customDossierType'
    | 'genrelDebtor'
    | 'insurerDebtor'
    | 'agentDebtor'
    | 'insurerDefault'
    | 'agentDefault'
    | 'exchangeRepairer'
    | 'companyCompany'
    | 'fallbackStandaard'
    | 'restrictedInsurer'
    | 'none';
  /**
   * Verplichte prijslijst voor dit dossier — `null` wanneer
   * `dossierTuple.Item2 != true`, `Item1` ontbreekt, of (AC 2.B.security)
   * de tuple-ID niet zichtbaar is voor de huidige session-company. Anders
   * een `{id, name}`-object dat de balk-conditie en -tekst aanstuurt.
   */
  fixedPricelist: CalculationFixedPricelistDto | null;
  /**
   * **Dropdown-iter AC 1.1** — `true` wanneer `totalExclusive == 0` ÉN
   * `isFixed == false` ÉN `availablePricelists.length >= 2`. FE rendert dan
   * een `<select>`; in alle andere gevallen een read-only label met tooltip
   * per `disableReason`.
   *
   * **TA-fix F1**: FE rendert dropdown UITSLUITEND bij
   * `data.editable === true` — geen client-side herevaluatie van de drie
   * booleans.
   */
  editable: boolean;
  /**
   * Disable-reden voor de tooltip op het read-only label, `null` wanneer
   * `editable === true`.
   *
   * Story `calc-prijslijst-default-en-lock` voegt `"calcLocked"` toe — de
   * rijen-tak emit altijd deze waarde wanneer `CalcRows.Count > 0`, ongeacht
   * bedrag-totaal of fixed-status (Bug-A fix). `"totalNotZero"` blijft in de
   * union voor consumer-compat maar wordt door de nieuwe resolver
   * **never-emitted** (bedrag-tak is verwijderd).
   *
   * Prio bij meerdere true-condities:
   *   - Rijen-tak: altijd `"calcLocked"` (overschrijft `"fixed"` /
   *     `"tooFewOptions"`).
   *   - Waterfall-tak: `"fixed"` (bron 10 wint) > `"tooFewOptions"` > `null`.
   *
   * EPIC-019 S-F voegt `"insurerPricelistMissing"` toe: een verzekeraar-gebonden
   * hersteller waarvoor stap 0 géén prijslijst oplevert. Dan is `editable`
   * **altijd** `false` — ook bij twee of meer zichtbare prijslijsten — en is
   * `selectedPricelistId` `null`. Bewust een eigen waarde en niet
   * `"tooFewOptions"`: die tooltip ("Slechts één prijslijst beschikbaar") is in
   * deze situatie feitelijk onjuist en misleidend.
   */
  disableReason:
    | 'fixed'
    | 'calcLocked'
    | 'totalNotZero'
    | 'tooFewOptions'
    | 'insurerPricelistMissing'
    | null;
  /**
   * **Dropdown-iter Scenario 3** — alle prijslijsten die de huidige user
   * mag zien voor deze company, gesorteerd op `name` (NL-locale, case-
   * insensitive). Bron: `Interaction.UE_PriceList_SelectList()`
   * (company-scoped + per-user `CanRead`). Lijst kan leeg zijn.
   */
  availablePricelists: CalculationFixedPricelistDto[];
  /**
   * Story `calculatie-inline-edit-en-delete` AC 4.1 — mag de huidige user
   * het `UnitPrice`-veld editten? Berekend BE-side via
   * `EditPermissionsResolver` uit de actieve prijslijst-vlag + drie
   * user-/session-overrides (zie story §"Achtergrond").
   *
   * **UI-hint, geen security-grens**: F1/MR-1 — BE re-evalueert exact dezelfde
   * resolver bij elke PATCH-request, dus een stale DTO-cache kan geen
   * edit-recht "stelen". Backwards-compat: optioneel om server-versies van
   * vóór deze story te ondersteunen (default `false` in consumers).
   */
  canEditPrice?: boolean;
  /**
   * Story `calculatie-inline-edit-en-delete` AC 4.1 — mag de huidige user
   * het `Quantity`-veld editten? Zelfde resolver-output als
   * {@link canEditPrice}, andere lock-vlag.
   */
  canEditQuantity?: boolean;
}

/**
 * Story `calculatie-inline-edit-en-delete` — sparse PATCH-body voor
 * `PATCH /api/dossiers/{id}/calculation-rows/{rowId}`. Velden die `undefined`
 * zijn worden niet aangeraakt. Voor `description` is een lege string óók
 * een geldige update (= leegmaken).
 */
export interface PartialCalculationRowUpdate {
  description?: string;
  quantity?: number;
  unitPrice?: number;
  discountValue?: number;
  discountType?: '€' | '%';
  vatId?: string;
}

/**
 * Story `calculatie-inline-edit-en-delete` Scenario 5 — één BTW-tarief
 * uit `GET /api/lookups/vat-rates`. Wire-shape `{ id, label, percentage }`.
 *
 * - `id`: GUID-string van `UE_Vat.ID`.
 * - `label`: display-tekst (bv. `"NL Hoog 21%"`). FE rendert dit als
 *   option-label in de BTW-dropdown.
 * - `percentage`: numerieke waarde voor sortering en `vatAmount`-fallback-display.
 */
export interface VatRateDto {
  id: string;
  label: string;
  percentage: number;
}

/**
 * Calculatie-sub-object. Lege batch: `rows = []`, totalen = 0 (geen fallback
 * naar oudere batch — S007.8 A9b).
 */
export interface DossierCalculation {
  code: string | null;
  /** ISO-string. */
  timestamp: string | null;
  rows: DossierCalculationRow[];
  totalExcl: number;
  vatAmount: number;
  totalIncl: number;
}

/** Bijlage-metadata. Geen blob-content, geen download-URL in V1. */
export interface DossierAttachment {
  id: string;
  fileName: string | null;
  /** ISO-string. */
  createdDate: string | null;
  /** Display-string uit `GD_AttachmentType.Description`. */
  type: string | null;
  /** Bytes — display met dynamische B/KB/MB-format. */
  sizeBytes: number | null;
}

/** Eén opmerking. Body kan HTML zijn; drawer rendert via `sanitizeHtml`. */
export interface DossierComment {
  id: string;
  /** ISO-string. */
  createdDate: string;
  createdByName: string | null;
  /** GD_Communication heeft geen subject-kolom in V1 — altijd null. */
  subject: string | null;
  body: string;
  /**
   * Optionele description van `GD_Communication.CommunicationType`
   * (lookup-naam, bv. "Email", "Telefoon", "Bezoek"). `null`/ontbrekend =
   * type onbekend; FE rendert dan niets. BE-uitbreiding 2026-05-29 —
   * forward-compatible tot AppServices `LoadComments` 'm meelevert.
   */
  communicationType?: string | null;
}

/** Opmerkingen-historie — count gelijk aan `items.length`. */
export interface DossierComments {
  count: number;
  items: DossierComment[];
}

/**
 * Eén rol-gefilterd, actief communicatietype voor de aanmaak-dropdown
 * (`GET /api/dossiers/{id}/communication-types`, story
 * `fe-be-opmerkingen-aanmaken`). De BE levert al een gefilterde lijst: alleen
 * types die de huidige rol-set mag kiezen.
 */
export interface DossierCommunicationType {
  id: string;
  description: string;
}

/**
 * Body voor `POST /api/dossiers/{id}/comments` (opmerking aanmaken). Het
 * communicatietype is verplicht; de tekst is optioneel (whitespace-only wordt
 * server-side naar null genormaliseerd).
 */
export interface DossierCommentCreateRequest {
  communicationTypeId: string;
  message?: string | null;
}

/**
 * Eén regel uit legacy `UA_Dossier.CanCheckPolicyByMail()`
 * (`MailPolicyCheckRules`). `category === '1'` = blokkerend: de mail-
 * poliscontrole mag pas wanneer al deze meldingen zijn opgelost.
 * Andere categorieën zijn informatief en blokkeren niet.
 */
export interface MailPolicyCheckRule {
  category: string;
  reason: string;
}

/** Generieke lookup-optie (company-types, titles). */
export interface LookupOption {
  id: string;
  description: string;
}

/**
 * Eén contact-sectie (Relatie of Bestuurder) voor de
 * `PUT /api/dossiers/{id}/contacts`-call. `null` voor een sectie betekent
 * "niet aanraken" (verkoop/offerte → driver=null; lease/fleet → customer=null).
 * De privacy/companyType/title-velden gelden alleen voor de Relatie.
 */
export interface DossierContactInput {
  name: string | null;
  address: string | null;
  houseNumber: string | null;
  houseNumberExtension: string | null;
  zipCode: string | null;
  city: string | null;
  country?: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  doesNotProvidePhone?: boolean;
  doesNotProvideEmail?: boolean;
  companyTypeId?: string | null;
  titleId?: string | null;
}

/** Body voor `PUT /api/dossiers/{id}/contacts`. */
export interface UpdateContactsRequest {
  customer: DossierContactInput | null;
  driver: DossierContactInput | null;
}

/**
 * Eén beschikbare afdruklijst (rapportlijst) uit
 * `GET /api/dossiers/{id}/print/report-lists`. Story
 * `appservices-documenten-printen-mailen` (Iter A).
 */
export interface PrintReportList {
  id: string;
  name: string;
}

/**
 * Response van `GET /api/dossiers/{id}/print/report-lists`: de (op type
 * `UA_ReportListType_Default` gefilterde) afdruklijsten + de standaard-
 * geselecteerde lijst-id voor de dropdown (`null` = geen geldige default).
 */
export interface PrintReportListsResponse {
  reportLists: PrintReportList[];
  defaultReportListId: string | null;
}

/**
 * Eén rapport binnen een gekozen afdruklijst uit
 * `GET /api/dossiers/{id}/print/reports?reportListId=…`. Naast id + naam de
 * Fase-1-verrijking uit `UF_InvoiceDocument`: wanneer een rapport eerder
 * gegenereerd/gemaild is (`lastCreated`/`mailedTo`), of het opnieuw gegenereerd
 * kan worden (`canReprint`), het laatst gegenereerde document
 * (`lastAttachmentId`, voor download via
 * `GET /print/documents/{attachmentId}`), en of het standaard aangevinkt staat
 * (`selectedByDefault`).
 */
export interface PrintReport {
  reportId: string;
  name: string;
  /** ISO-timestamp van de laatste generatie, of `null`. */
  lastCreated: string | null;
  /** E-mailadres waarnaar laatst gemaild, of `""`. */
  mailedTo: string;
  /** `true` zodra er een eerder gegenereerd document is. */
  canReprint: boolean;
  /** Attachment-id van het laatst gegenereerde document, of `null`. */
  lastAttachmentId: string | null;
  /** `true` als het rapport standaard aangevinkt moet staan. */
  selectedByDefault: boolean;
}

/**
 * Eén recipient-suggestie voor de mail-popup
 * (`GET /api/dossiers/{id}/print/mail-recipients`, Fase 2). `role` =
 * `customer | driver | repairLocation | agent | repairer | insurer`; `label`
 * is de toon-tekst ("Klant: x@y.nl"), `email` de waarde.
 */
export interface MailRecipient {
  role: string;
  label: string;
  email: string;
}

/** Response van `GET /api/dossiers/{id}/print/mail-recipients`. */
export interface MailRecipientsResponse {
  recipients: MailRecipient[];
  /** Voorgesteld eerste ontvangstadres (eerste klant-adres), of `""`. */
  defaultTo: string;
  /**
   * B2B-12 (16-10-2026): voorgestelde CC. Alleen gevuld bij de bedrijfsinstelling
   * `DefaultMailByAdminstratie` (dan is `defaultTo` het administratie-adres van de klant en dit
   * het CC-adres). Ontbreekt bij oudere backends en bij de factuur-mailpopup.
   */
  defaultCc?: string;
}

/**
 * B2B-12 (16-10-2026) — `GET`/`PUT /api/dossiers/{id}/relation-administration`: de
 * administratiegegevens van de GEKOPPELDE KLANT van het dossier. Ze horen bij de klant en gelden
 * voor alle dossiers van die klant (`appliesToAllDossiersOfCustomer`).
 *
 * - `linked: false` ⇒ geen klant gekoppeld (geen fout; de velden zijn leeg).
 * - `linked: true, accessible: false` ⇒ klant van een ander bedrijf; geen velden.
 */
export interface RelationAdministration {
  linked: boolean;
  accessible: boolean;
  relationId: string | null;
  /** Soort = Bedrijf? Alleen dan is het BTW-nummer van toepassing. */
  isCompany: boolean;
  administrationEmail: string | null;
  administrationContactPerson: string | null;
  administrationCcEmail: string | null;
  debtorGroupId: string | null;
  /** Zonder debiteur valt er geen debiteurgroep te kiezen. */
  hasDebtor: boolean;
  vatNumber: string | null;
  appliesToAllDossiersOfCustomer: boolean;
}

/**
 * Body van `PUT /api/dossiers/{id}/relation-administration`. Partiële merge: een afwezige key
 * raakt niets, `""`/`null` maakt leeg. Reason-codes bij een fout: `no_linked_relation` (409),
 * `relation_not_accessible` (403), `invalid_email` / `invalid_vat_number` /
 * `vat_number_not_applicable` / `debtor_group_unknown` (400, met `field`), `debtor_not_found`
 * (409), `relation_write_rejected` (422), en het bestaande edit-slot (403).
 */
export interface RelationAdministrationWritePayload {
  administrationEmail?: string | null;
  administrationContactPerson?: string | null;
  administrationCcEmail?: string | null;
  debtorGroupId?: string | null;
  vatNumber?: string | null;
}

/** Eén mail-template uit `GET /api/dossiers/{id}/print/mail-templates` (Fase 3). */
export interface MailTemplate {
  id: string;
  name: string;
}

/**
 * Render-resultaat van `GET .../print/mail-templates/{templateId}/render`
 * (Fase 3): voorgevuld onderwerp + (HTML-)body voor de mail.
 */
export interface MailTemplateRender {
  subject: string;
  body: string;
}

/**
 * Response van `GET /api/facturatie/invoices/{id}/mail-templates` (story
 * `fe-be-factuur-mailen-popup`). De company-mailtemplates + de per-hersteller
 * default — `defaultTemplateId` is `null` als er geen default is óf de opgeslagen
 * default niet (meer) in de zichtbare lijst voorkomt (F-04).
 */
export interface InvoiceMailTemplatesResponse {
  templates: MailTemplate[];
  defaultTemplateId: string | null;
}

/**
 * Body van `POST /api/facturatie/invoices/{id}/mail` (rijke popup, story
 * `fe-be-factuur-mailen-popup`). Alle velden behalve `to` optioneel;
 * `attachmentIds`/`combine*` zijn alleen zinvol bij een aan een dossier
 * gekoppelde factuur.
 */
export interface InvoiceMailSendRequest {
  to: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  /** Informatief/audit — de BE her-rendert NIET uit dit id (F-09). */
  templateId?: string;
  /** Extra dossier-bijlagen (GD_Attachment-id's). */
  attachmentIds?: string[];
  /** Voeg de factuur-PDF + geselecteerde PDF-bijlagen samen tot één bestand. */
  combinePdfs?: boolean;
  /** Voeg geselecteerde foto's samen in één PDF. */
  combineImages?: boolean;
}

/**
 * Response van `GET/PUT /api/profile/invoice-mail-template` — de per-hersteller
 * standaard factuur-mail-template (`defaultTemplateId` = `null` → "Geen
 * standaard") plus de company-mailtemplates zodat "Mijn gegevens" de dropdown kan
 * vullen zonder een factuur-context (GET vult `templates`; PUT laat 'm leeg).
 */
export interface InvoiceMailDefaultTemplateResponse {
  defaultTemplateId: string | null;
  templates: MailTemplate[];
}

/**
 * Body voor `POST /api/dossiers/{id}/print/send-mail` (Fase 2). De
 * geselecteerde rapporten (+ reprint-subset) worden als bijlagen gemaild.
 */
export interface SendMailRequest {
  reportIds: string[];
  reprint?: string[];
  to: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body?: string;
  /**
   * Het gekozen mailtemplate. De BE gebruikt dit om de bijlage-rapporten op te
   * halen die eraan gekoppeld zijn (UA_ReportAttachment) - bijvoorbeeld een
   * ICS-agenda-afspraak - en die als extra bijlage mee te sturen.
   */
  templateId?: string;
  /** Extra dossier-bijlagen (GD_Attachment-id's) om mee te sturen. */
  attachmentIds?: string[];
  /** Voeg alle PDF-bijlagen samen tot één bestand. */
  combinePdfs?: boolean;
  /** Bed de geselecteerde foto's in één PDF in (i.p.v. als losse bestanden). */
  combineImages?: boolean;
}

/**
 * Result van `GET` (cached) of `POST` (verse run) op
 * `/api/dossiers/{id}/rules-check`. Errors = `expertise` + `infoexception`-
 * meldingen uit de verzekeraar-SP; warnings = `info`-meldingen.
 * Story: `appservices-dossier-validatie`.
 */
export interface DossierRulesCheckResult {
  ranAt: string;
  ranByUserId: string;
  ranByUserName: string;
  procedureName: string;
  errors: string[];
  warnings: string[];
}

/**
 * Discriminated union voor de GET-uitkomst: ofwel een cache-hit (`cached`),
 * ofwel een 404 die de FE laat onderscheiden tussen "nog niet gedraaid"
 * (`hasProcedure: true`) en "geen validatie ingericht voor deze
 * verzekeraar" (`hasProcedure: false`).
 */
export type DossierRulesCheckGetResult =
  | { kind: 'cached'; data: DossierRulesCheckResult }
  | { kind: 'no-cache'; hasProcedure: boolean };

/**
 * Story `fe-be-status-transitie-aanbieden-hoofdkantoor` (2026-06-01) — één
 * entry uit de BE-authoritatieve transitie-map
 * (`GET /api/dossiers/status-transitions`). Dossier-onafhankelijk; de FE
 * spiegelt 'm voor knop-zichtbaarheid (`primaryLabel`) + disabled-reden
 * (`precondition`).
 */
export interface StatusTransitionEntry {
  action: string;
  fromStatusValue: number;
  toStatusValue: number;
  toStatusLabel: string;
  precondition: string;
  primaryLabel: string;
  /**
   * Story `fe-be-hoofdkantoor-controle-...` (2026-06-02) — `true` voor de drie
   * hoofdkantoor-acties (approve/reject/park-headquarters), `false` voor
   * `submit-to-headquarters`. Laat de FE de rol-gebonden acties onderscheiden.
   */
  requiresHeadquartersRole: boolean;

  /**
   * Story `fe-be-afgekeurd-dossier-heropenen` (2026-06-06) — `true` voor
   * `resume-after-rejection` (243→210), `false`/afwezig voor andere acties.
   * Symmetrisch met `requiresHeadquartersRole`; de heropen-knop zelf hangt
   * FE-side op `dossier.isRepairerUser` (niet op deze map-vlag). Optioneel
   * zodat een oudere lookup-respons zonder dit veld niet breekt.
   */
  requiresRepairerRole?: boolean;

  /**
   * Story `fe-exchange-precheck-alleen-submit` (2026-06-08) — `true` voor
   * `submit-to-headquarters` (210→241; de enige actie die de eXchange-precheck-
   * hervalidatie draait), `false`/afwezig voor de HK-acties op 241
   * (approve/reject/park) en `resume-after-rejection`. De FE gate't de
   * eXchange-precheck-popup-interceptie hierop (i.p.v. op een kale
   * `isExchange`-vlag) zodat de "Aanbieden aan hoofdkantoor"-popup niet ten
   * onrechte verschijnt bij het goedkeuren van een eXchange-dossier. Optioneel
   * zodat een oudere lookup-respons zonder dit veld niet breekt.
   */
  requiresExchangePrecheck?: boolean;

  /**
   * Story `fe-be-verplichte-fotos-vinkje-en-aanbieden` (07-10-2026) — `true` voor de
   * twee aanbied-acties naar 241 (`submit-to-headquarters` en
   * `submit-to-headquarters-preexpertise`): die vereisen alle verplichte foto's van
   * het fotoprotocol (BE gate 4c). De FE toont daarop de reden "Nog niet alle
   * verplichte foto's zijn toegevoegd". Optioneel zodat een oudere lookup-respons
   * zonder dit veld niet breekt (afwezig = geen foto-eis in de FE; de BE handhaaft).
   */
  requiresRequiredPhotos?: boolean;

  /**
   * Story D2 (22-10-2026) — `true` voor de twee aanbied-acties naar 241 en de twee
   * "Verstuur naar eXchange"-acties: alleen met vastgestelde dekking (BE gate 3f, 422
   * `coverage_not_confirmed` / `lease_coverage_not_confirmed`). De reden komt uit
   * `policy.submitCoverage`. Optioneel: afwezig = kijk naar de alias hieronder.
   */
  requiresCoverage?: boolean;

  /**
   * Alias van `requiresCoverage` uit de backend van vóór D2 (besluit 19-10-2026, toen
   * alleen lease/fleet). Lees via `entryRequiresCoverage`, nooit los.
   */
  requiresLeaseFleetCoverage?: boolean;

  /**
   * Story D2 (D2-2) — extra bronstatussen naast `fromStatusValue` (vandaag alleen 220 op
   * "Aanbieden aan hoofdkantoor"). Afwezig of leeg = alleen `fromStatusValue`.
   */
  additionalFromStatusValues?: number[];
}

/** Response-body van `GET /api/dossiers/status-transitions`. */
export interface StatusTransitionLookupResult {
  transitions: StatusTransitionEntry[];
}

/**
 * 200-body van `POST /api/dossiers/{id}/status-transition` (AC #1).
 * `auditTraceWritten` is altijd aanwezig: `true` bij geslaagde WF_Trace,
 * `false` bij een gefaalde best-effort-trace (AC #14).
 */
export interface StatusTransitionResult {
  action: string;
  fromStatusValue: number;
  toStatusValue: number;
  /** Nieuwe DTO-status-string, zelfde vorm als `dossier.status`. */
  status: string;
  toStatusLabel: string;
  transitionedAt: string;
  transitionedByUserId: string;
  transitionedByUserName: string;
  auditTraceWritten: boolean;
  /**
   * Story `fe-be-hoofdkantoor-controle-...` (2026-06-02) — veld-effect-uitkomst:
   * `true` wanneer `ExpertiseResultID` is leeggemaakt (approve/reject).
   */
  expertiseResultCleared?: boolean;
  /**
   * Story `fe-be-hoofdkantoor-controle-...` (2026-06-02) — `true` wanneer dit
   * een park-actie was (status blijft 241, `ExpertiseResultID` op de "03"-rij).
   */
  parked?: boolean;
  /**
   * Story `be-hoofdkantoor-controle-redenen-vastleggen` (2026-06-05, story 1, B11)
   * — exact het aantal aangemaakte reden-koppelrijen (= aantal unieke, geldige
   * redenen in de body; 0 bij approve/park-zonder-reden of submit-to-headquarters).
   */
  reasonsWritten?: number;
  /**
   * Story `be-fe-centraal-alleen-binnen-keten` (29-09-2026) — alleen bij
   * `send-to-exchange`(`-preexpertise`): het oordeel van eXchange. Afwezig bij
   * alle andere acties.
   */
  exchangeOffer?: ExchangeOfferOutcome;
  /**
   * Story `fe-be-verplichte-fotos-vinkje-en-aanbieden` (07-10-2026) — waarschuwingen
   * bij een geslaagde overgang, als Engelse codes. Vandaag alleen
   * `required_photos_unverified`: de BE kon de verplichte foto's niet controleren en
   * liet het aanbieden door (fail-open). Afwezig wanneer er niets te melden is.
   */
  warnings?: string[];
}

/**
 * Story `be-fe-centraal-alleen-binnen-keten` (29-09-2026) — de oordelen die
 * eXchange bij het aanbieden kan teruggeven, als Engelse wire-keys. De FE
 * vertaalt ze naar Nederlandse tekst.
 */
export type ExchangeOfferVerdict =
  | 'approved'
  | 'to_expertise'
  | 'rates_rejected'
  | 'calculation_rejected'
  | 'not_accepted';

/**
 * Het oordeel van eXchange op de wire (200-body, en in de 422/502/500-body van
 * `send-to-exchange`). `verdict` is `null` bij een technische fout;
 * `explanation` is de toelichting van eXchange, ongewijzigd (als tekst renderen).
 */
export interface ExchangeOfferOutcome {
  verdict: ExchangeOfferVerdict | null;
  explanation: string | null;
}

// ─── HK-controle redenen (story `be-hoofdkantoor-controle-redenen-vastleggen`) ─

/**
 * Eén meegestuurde reden bij een HK-actie (afkeuren vereist ≥1; goedkeuren/
 * parkeren optioneel). `comment` is verplicht (niet-leeg) wanneer de reden
 * `requiresComment` heeft.
 */
export interface HeadquartersReasonSelection {
  reasonId: string;
  comment?: string;
}

/** Eén reden in de lookup `GET /api/dossiers/headquarters-reasons` (B12). */
export interface HeadquartersReason {
  id: string;
  code: string;
  description: string;
  appliesToApprove: boolean;
  appliesToReject: boolean;
  appliesToPark: boolean;
  requiresComment: boolean;
  sortOrder: number;
}

/** Welke HK-actie de lookup filtert (`?applies=`). */
export type HeadquartersReasonApplies = 'approve' | 'reject' | 'park';

/**
 * Eén historie-rij van `GET /api/dossiers/{id}/headquarters-reasons` (B13a).
 * Gesorteerd op `createdAt` desc (laatste ronde bovenaan).
 */
export interface DossierHeadquartersReason {
  id: string;
  reasonId: string;
  reasonDescription: string;
  comment: string | null;
  /** `Approve` | `Reject` | `Park`. */
  result: string;
  createdByUserName: string;
  createdAt: string;
}

// ─── eXchange-precheck (story `fe-be-exchange-aanbieden-precheck`) ──────────

/**
 * Mogelijke blokkerende redenen van de eXchange-precheck. `null` = niet
 * geblokkeerd. Precies één waarde tegelijk (BE-prioriteit, BIJGEWERKT
 * 2026-08-05: `exchange_unreachable` > `exchange_no_coverage` >
 * `own_risk_mismatch` > `progress_incomplete` > `null`).
 *
 * `progress_incomplete` (PB-10, vervangt PB-2): ≥1 voortgang-categorie (incl.
 * polis) is niet Compleet → aanbieden geblokkeerd.
 *
 * `exchange_no_coverage` (PO 2026-08-05): eXchange heeft nog geen dekking
 * afgegeven — zowel "nog niet vastgesteld" als "expliciet geen dekking". Deze
 * literal ontbrak eerder in het type terwijl `derivePopupState` er al op
 * matchte; `tsc` merkte die vergelijking terecht aan als onbereikbaar.
 */
export type ExchangePrecheckBlockReason =
  | 'own_risk_mismatch'
  | 'exchange_unreachable'
  | 'exchange_no_coverage'
  | 'progress_incomplete';

/** eigen-risico-sub-object van de precheck-DTO (`null` bij niet-eXchange/onbereikbaar). */
export interface ExchangePrecheckOwnRisk {
  repairDossier: number;
  repairExchange: number;
  replaceDossier: number;
  replaceExchange: number;
  /** `repairDossier !== repairExchange` OF `replaceDossier !== replaceExchange`. */
  mismatch: boolean;
  /** Er bestaat een "negeer eigen risico"-notitie op het dossier. */
  overridden: boolean;
}

/** voortgang-sub-object van de precheck-DTO (`null` wanneer voortgang-object ontbrak). */
export interface ExchangePrecheckProgress {
  calculatie: boolean;
  fotos: boolean;
  persoonsgegevens: boolean;
  polis: boolean;
  schade: boolean;
  voertuig: boolean;
  /** Wire-keys van de onvolledige onderdelen (volgorde-stabiel). */
  incompleteFields: string[];
}

/**
 * 200-body van `GET /api/dossiers/{id}/exchange-precheck` (en de verse DTO uit
 * `POST .../ignore-own-risk` + de `precheck`-payload van een 422
 * `exchange_precheck_failed` op de transitie). `canProceed` is BE-autoritatief.
 */
export interface ExchangePrecheckResult {
  isExchangeDossier: boolean;
  soapReachable: boolean;
  canProceed: boolean;
  blockReason: ExchangePrecheckBlockReason | null;
  /** `null` bij niet-eXchange of onbereikbaar. */
  ownRisk: ExchangePrecheckOwnRisk | null;
  /** `null` bij niet-eXchange, onbereikbaar, of ontbrekend voortgang-object. */
  progress: ExchangePrecheckProgress | null;
  /**
   * PB-8 (2026-06-04) — vrije-tekst-details van de calculatie-afwijking tussen
   * eXchange en lokaal, of `null` wanneer er geen afwijking is (of
   * niet-eXchange/onbereikbaar). **Niet-blokkerend**: de popup toont 'm als rode
   * waarschuwing maar "Aanbieden" blijft enabled mits `canProceed`.
   */
  calcMismatch?: string | null;
  /**
   * PB-9 (2026-06-05) — lokaal (filiaal-)calculatie-totaal
   * (`dossier.GetDamageAmount("filiaal")`), of `null` bij read-fout /
   * niet-eXchange / onbereikbaar. **Let op:** `0` is een geldige "geen
   * calc-regels"-waarde (toon als `€ 0,00`), NIET hetzelfde als `null`
   * ("geen vergelijking mogelijk"). **Niet-blokkerend**.
   */
  calcTotalLocal?: number | null;
  /**
   * PB-9 (2026-06-05) — eXchange-calculatie-totaal (read-only parse van het
   * `Totaalbedrag`), of `null` bij parse-/read-fout / geen calc /
   * niet-eXchange / onbereikbaar (→ FE toont "geen eXchange-calculatie").
   * **Niet-blokkerend**.
   */
  calcTotalExchange?: number | null;
}

// ─── eXchange-precheck UITBREIDING — auto-voltooien (PB-6/PB-7, F1/G1) ──────

/**
 * De vijf **pushbare** voortgang-categorieën van het per-categorie
 * update-endpoint (`POST /api/dossiers/{id}/exchange-update/{category}`).
 * `polis` ontbreekt bewust — `updateExchangePolicyData` is een uitgeschakelde
 * no-op-stub en wordt nooit auto-geprobeerd (PB-7).
 */
export type ExchangeUpdateCategory =
  | 'calculatie'
  | 'fotos'
  | 'voertuig'
  | 'schade'
  | 'persoonsgegevens';

/**
 * 200-body van `POST /api/dossiers/{id}/exchange-update/{category}`.
 * `success` is faal-veilig per categorie: `true` tenzij de push gooide of
 * eXchange offline is. Niet gegarandeerd "eXchange volledig bijgewerkt" — de
 * her-precheck is de bron van waarheid voor echte volledigheid.
 */
export interface ExchangeUpdateResult {
  category: string;
  success: boolean;
  messages: string[];
}

// ─── Facturen aanmaken (story `fe-be-aanmaken-facturen`, 2026-06-07) ────────

/**
 * Machine-code voor de invoice-gate (BE = source of truth, AC 6). De FE mapt
 * 'm naar een NL-tooltip-tekst; `null` = geen blokkade (`canCreate=true`).
 */
export type InvoiceBlockReason =
  | 'no_accessible_debtor'
  | 'no_calculation'
  | 'already_invoiced';

/**
 * Eén aan te maken factuur in het preview-grid (AC 12). `debiteurnummer` is
 * nullable → ontbrekend toont FE-side een lege cel (geen "0", C-1). De
 * `debiteur` is zoals de legacy-order die bepaalt (kan afwijken van de
 * klant-debiteur, bv. een maatschappij-factuur).
 */
export interface InvoicePreviewLine {
  /** `InvoiceType.Description`. */
  soort: string | null;
  /** `DebtorOrCreditorName`. */
  debiteur: string | null;
  /** `DebtorNumber` — `null`/ontbrekend → lege cel. */
  debiteurnummer: number | null;
  /** `InvoiceTotalDisplayOnly`. */
  factuurtotaal: number;
}

/** Eén partij-kolom (6 rijen) van het betaaloverzicht (AC 15). */
export interface PaymentOverviewColumn {
  damage: number;
  vat: number;
  ownRisk: number;
  discountOwnRisk: number;
  provision: number;
  total: number;
}

/**
 * Betaaloverzicht-matrix: 4 partijen × 6 rijen (AC 15-17). Bedragen komen uit
 * de bestaande dossier-methods; de FE rendert ze (rekent niet zelf).
 */
export interface PaymentOverview {
  /** Verzekerde. */
  customer: PaymentOverviewColumn;
  /** Maatschappij. */
  insurance: PaymentOverviewColumn;
  /** Hersteller. */
  repairer: PaymentOverviewColumn;
  /** Opdrachtgever. */
  referenceDebtor: PaymentOverviewColumn;
}

/**
 * 200-body van `GET /api/dossiers/{id}/invoice-preview`. Read-only; voedt zowel
 * de knop-gate (`canCreate`/`blockReason`) als de popup-inhoud (preview-grid +
 * betaaloverzicht). Intern consistent: `canCreate=true` ⇒ `invoices.length ≥ 1`
 * (AC 23b).
 */
export interface InvoicePreviewResult {
  invoices: InvoicePreviewLine[];
  paymentOverview: PaymentOverview;
  canCreate: boolean;
  blockReason: InvoiceBlockReason | null;
  /**
   * Verzekerd dossier (`!(IsSalesDossier || IsOffer)`). De popup toont het
   * betaaloverzicht alleen wanneer `true`; bij een niet-verzekerd dossier
   * (verkoop/offerte) is de Verzekerde/Maatschappij/Eigen-risico-matrix zinloos
   * en wordt 'm verborgen. Optioneel voor backwards-compat met oudere responses.
   */
  isInsured?: boolean;

  /**
   * Story 3a — read-only documenten-overzicht per partij (verzendregels-runtime,
   * concept Blok D). Optioneel voor backwards-compat met oudere responses.
   */
  deliveryOverview?: DeliveryOverview;
}

// ─── Story 3a — verzendregels-runtime overzicht (read-only, concept Blok D) ────

/** Het documenten-overzicht per partij bij "Facturen aanmaken". */
export interface DeliveryOverview {
  parties: DeliveryOverviewParty[];
  /** Centraal dossier → Maatschappij (partij 1+2) wordt niet door de hersteller gemaild. */
  isCentralCustomer: boolean;
}

export interface DeliveryOverviewParty {
  /** 1=Verzekeraar/Volmacht, 2=Lease/Fleet, 3=Verzekerde/klant, 4=Opdrachtgever. */
  party: number;
  partyLabel: string;
  /** De betrokken relatie (bv. "Allianz"); mag null zijn. */
  relationName: string | null;
  /** Partij-adres uit de relatiegraaf; null = onbekend ("handmatig versturen"). */
  email: string | null;
  /** Maatschappij (1+2) op een centraal dossier → HK-melding prevaleert boven de mail-chip. */
  suppressedByHq: boolean;
  documents: DeliveryOverviewDocument[];
  /**
   * Story K7-a (PB-1) — waar de documenten vandaan komen: de keteninstellingen (centraal,
   * maatschappij), de verzendregels, of `unknown` (centraal niet vast te stellen ⇒ geblokkeerd).
   */
  documentSource?: DeliveryDocumentSource | null;
  /** K7-a (PB-16) — de verzendroute van een keten-partij; weggelaten bij verzendregels. */
  route?: string | null;
  /** K7-a (PB-20) — de partij is geblokkeerd (reden-code); weggelaten wanneer niet. */
  blockedReason?: string | null;
  /** K7-a (PB-21) — niet-blokkerende route-reden waarom de partij leeg is (`route_no_address`, …). */
  notice?: string | null;
  /**
   * K7-a (PB-12) — "Kopie naar …" bij een keten-partij. Story `be-fe-afhandeling-administratie-adres`: bij klant (3) en
   * opdrachtgever (4) het CC-adres administratie van de relatie, als dat ingevuld is.
   */
  cc?: string | null;
  /**
   * Story `be-fe-afhandeling-administratie-adres` (PB-3) — `administration` wanneer `email` het e-mailadres
   * administratie van klant/opdrachtgever is; anders weggelaten.
   */
  addressSource?: 'administration' | null;
  /**
   * Gebruiker 07-10-2026 — een beschikbaar document staat op "Mailen" maar de partij heeft geen
   * mailtemplate in de verzendregels: er wordt NIET gemaild. Zelfde regel als de verwerking.
   */
  mailTemplateMissing?: boolean;
  /**
   * Story `be-fe-geen-documenten-voorstel` (G1, PB-G1) — waarom deze partij geen documenten krijgt terwijl ze die
   * wel hoort te krijgen. Alleen de BE zet dit veld; weggelaten wanneer de partij terecht leeg is of documenten
   * heeft. De FE leidt het nooit zelf af.
   */
  emptyReason?: DeliveryEmptyReason;
}

/** G1 — de reden waarom een partij in stap 2 geen documenten krijgt. */
export type DeliveryEmptyReason = 'no_rules_configured' | 'no_rule_matches';

/** G1 — de voorwaarden van een verzendregel; `null` = n.v.t. */
export interface DeliveryProposalConditions {
  condInsured: number | null;
  condRepairType: number | null;
  condOwnRisk: number | null;
  condOwnRiskDiscount: number | null;
  condVatInsured: number | null;
  condCentral: number | null;
}

/** G1 — de bron van (een document in) het voorstel. */
export type DeliveryProposalSource = 'own_history' | 'chain_history' | 'default' | 'none';

/** G1 — één document in het voorstel. */
export interface DeliveryProposalDocument {
  code: string;
  label: string;
  mailen: boolean;
  source: DeliveryProposalSource;
  /** Alleen bij `own_history`: bij hoeveel vergelijkbare dossiers. */
  count?: number;
  /** Alleen bij `own_history`: van hoeveel vergelijkbare dossiers. */
  total?: number;
}

/** G1 — 200-body van `GET /api/dossiers/{id}/delivery-proposal?party=n`. */
export interface DeliveryProposal {
  party: number;
  partyLabel: string;
  emptyReason: DeliveryEmptyReason;
  situation: DeliveryProposalConditions;
  source: DeliveryProposalSource;
  /** Alleen bij bron 1 (eigen geschiedenis). */
  comparableCount?: number;
  /** Alleen bij bron 1: de naam van de eigen hersteller. */
  repairerName?: string;
  documents: DeliveryProposalDocument[];
  /** De documentkiezer: catalogus van de hersteller, gefilterd op zichtbaarheid voor dit dossier. */
  documentOptions: DeliveryDocumentOption[];
  /** De partij heeft geen standaard mailtemplate: bij mailen moet er één gekozen worden. */
  mailTemplateRequired: boolean;
  mailTemplateOptions: DeliveryTemplateOption[];
  suggestedMailTemplateId?: string;
  /** Alleen de vestiging van het dossier mag overnemen (System en hoofdkantoor niet). */
  canAccept: boolean;
}

/** G1 — body van `POST /api/dossiers/{id}/delivery-proposal/accept`. */
export interface DeliveryProposalAcceptRequest {
  party: number;
  conditions: DeliveryProposalConditions;
  documents: Array<{ code: string; mailen: boolean }>;
  /** Alleen als `mailTemplateRequired` en er gemaild wordt. */
  mailTemplateId?: string | null;
}

/** G1 — 200-body van `accept`. */
export interface DeliveryProposalAcceptResult {
  /** De regel(s) bestonden al; er is niets geschreven. */
  alreadyExists: boolean;
  rulesWritten: number;
  mailTemplateSet: boolean;
}

/** Story K7-a — de documentbron van een partij. */
export type DeliveryDocumentSource = 'chain_settings' | 'delivery_rules' | 'unknown';

export interface DeliveryOverviewDocument {
  /** Effectieve document-code (`ParentReportID ?? ID`). */
  code: string;
  /** Label uit de catalogus; bij een verweesd id de code zelf. */
  label: string;
  /** Effectieve mail-intentie (opt-in wint bij dedupe). */
  mailen: boolean;
  /** Mail-adres (== partij-`email` wanneer `mailen`); null → "handmatig versturen". */
  ontvanger: string | null;
  /** `false` = niet (meer) in de catalogus → "niet meer beschikbaar" (in 3b overgeslagen). */
  available: boolean;
  /** Maatschappij (1+2) op een centraal dossier → wél genereren/opslaan, niet mailen. */
  suppressedByHq: boolean;
}

/** 200-body van `POST /api/dossiers/{id}/invoices` bij succes (AC 18/11b). */
export interface CreateInvoicesResult {
  /** Aantal aangemaakte facturen — voor de succes-state "✓ N facturen aangemaakt". */
  createdCount: number;
  correlationId?: string;
  /**
   * Story 3b — het per-partij/-document eindresultaat van de documentverwerking
   * (genereren/mailen). Optioneel (best-effort; kan ontbreken bij oudere responses).
   */
  documentResult?: DeliveryProcessResult;
  /**
   * EPIC-022 S022.5 (AC 20) — alleen bij een CARculate-hersteller met minstens één reservering. `pending > 0` ⇒ de
   * voorraad is nog niet afgeboekt (de achtergrondtaak probeert opnieuw). Geen sleutel ⇒ niets te melden.
   */
  inventory?: InvoiceInventoryResult;
}

// ─── Story 3b — documentverwerking-resultaat (per partij/document) ─────────────

/** Eind-status van één document (AC 24). */
export type DeliveryDocumentStatus = 'generated' | 'mailed' | 'manual' | 'skipped' | 'failed';

export interface DeliveryProcessResult {
  /**
   * `false` = de verwerking is niet (volledig) uitgevoerd door een compose-/
   * evaluatiefout (zie `reason`/`correlationId`) — onderscheiden van een legitieme
   * lege uitkomst (`attempted=true`, geen documenten). De facturen zijn NIET
   * teruggedraaid.
   */
  attempted: boolean;
  correlationId: string | null;
  reason: string | null;
  parties: DeliveryPartyResult[];
}

export interface DeliveryPartyResult {
  party: number;
  partyLabel: string;
  documents: DeliveryDocumentResult[];
  /** Story K7-a (PB-20) — de partij is geblokkeerd (reden-code); weggelaten wanneer niet. */
  blockedReason?: string | null;
  /** Story K7-a (PB-21) — niet-blokkerende route-reden (`route_no_address`, `route_exchange`, …). */
  notice?: string | null;
}

export interface DeliveryDocumentResult {
  code: string;
  label: string;
  status: DeliveryDocumentStatus;
  reason: string | null;
  /** Adres bij `mailed`; anders null. */
  ontvanger: string | null;
}

// ─── Mollie-betaallinks (story `fe-be-mollie-betaallink-genereren`, EPIC-018) ──

/**
 * Eén Mollie-betaallink (POST-201 + elk item van de GET-lijst). Bevat NOOIT de
 * key/secret. `qrCodeUrl` wordt server-side opgeslagen en direct gerenderd
 * (`<img src>`, geen client-side afleiding). `isExpired` is server-berekend
 * (`ExpiresAt < nu`) zodat de FE niet zelf klok-rekent.
 */
export interface PaymentLink {
  id: string;
  amount: number;
  currency: string;
  method: string;
  molliePaymentId: string | null;
  paymentLinkUrl: string;
  qrCodeUrl: string;
  /** ISO-datum (of `null`) — verloopmoment (= aanmaak + 2 maanden). */
  expiresAt: string | null;
  status: string;
  /** Server-berekend: `true` wanneer de link verlopen is (`ExpiresAt < now`). */
  isExpired: boolean;
  /**
   * De factuur waaraan deze link is gekoppeld (`UF_Invoice.ID`), of `null` voor de
   * DOSSIER-BREDE klant-link — story `fe-be-meerdere-betaallinks-tonen`. Voedt het
   * label ("Dossier-breed" vs "Factuur <nr>").
   */
  invoiceId: string | null;
  /** Factuurnummer van de gekoppelde factuur, of `null` (dossier-breed / onbekend). */
  invoiceNumber: number | null;
  /**
   * Betaalmoment (ISO of `null`) — story `fe-be-mollie-betaalstatus-ophalen`
   * (S-G). Alleen gevuld bij `status === "paid"`.
   */
  paidAt: string | null;
  /** Server-afgeleid: `status === "paid"` (S-G). `isPaid` wint van "verlopen". */
  isPaid: boolean;
  /**
   * Server-afgeleid: `isPaid || (ExpiresAt < now)` — de link vraagt niet meer
   * door (betaald óf tijd-verlopen). Voedt de badge + de knop-/auto-refresh-gate.
   */
  isTerminal: boolean;
}

/**
 * 200-body van `POST /api/dossiers/{id}/payment-links/refresh` (S-G) — vaste
 * envelope. `link` is gevuld zodra er een link is (terminaal-ongewijzigd óf
 * ververst); `refreshed` = `true` iff er daadwerkelijk bij Mollie is gepolst;
 * `reason` alleen bij een no-action (`"no_link"` / `"no_mollie_id"`).
 */
export interface PaymentLinkRefreshResult {
  link: PaymentLink | null;
  refreshed: boolean;
  reason?: string;
}

/** 200-body van `GET /api/dossiers/{id}/payment-links` — links op `Created` desc. */
export interface PaymentLinkListResult {
  links: PaymentLink[];
  /**
   * EPIC-019 S-G (AC 31) — waarom er GEEN NIEUWE betaallink gegenereerd mag
   * worden, of `null`. Vandaag exact één waarde: `"restricted_repairer"` (deze
   * vestiging maakt geen facturen aan). Een machine-CODE: de FE beslist erop en
   * toont hem nooit.
   *
   * ⚠ Dit haalt alleen de GENEREER-knop weg. De bestaande link(s) blijven volledig
   * zichtbaar — bedrag, status, QR, kopieer-knop — en `refresh` blijft werken; dat
   * is inzage, en inzage blijft (AC 24/35). Additief: `links` en `customerTotal`
   * zijn ongewijzigd.
   */
  createBlockedReason?: string | null;
  /**
   * Server-berekende Klant-total (het bedrag dat de klant zelf moet betalen),
   * afgeleid via `GetTopayCustomerTotal("filiaal")` + afronding — exact zoals de
   * POST. Zo kan de betaallink-sectie z'n bedrag lichtgewicht meekrijgen zonder
   * de zware `invoice-preview`-keten opnieuw te draaien.
   */
  customerTotal: number;
}

// ─── Facturen tonen (story `fe-be-facturen-tonen`, 2026-06-07) ────────────────

/**
 * Debiteur-adres van een factuur (read-only). Alle velden mogen `null` zijn → de
 * FE rendert "—".
 */
export interface InvoiceDebtor {
  naam: string | null;
  adres: string | null;
  postcode: string | null;
  plaats: string | null;
}

/**
 * Eén factuurregel (8 kolommen, 1-op-1 uit de domain). Bedragen mogen `null`
 * zijn → de FE rendert "—"; er wordt NIET FE-side herberekend (F6/A5).
 */
export interface InvoiceLine {
  /** Artikelnummer (`UF_InvoiceLines.ItemCode`). */
  artikelnummer: string | null;
  omschrijving: string | null;
  aantal: number | null;
  stukprijs: number | null;
  korting: number | null;
  /** Btw-bedrag (`UF_InvoiceLines.VatAmount`). */
  btwBedrag: number | null;
  /** Btw-percentage (`UF_InvoiceLines.VatPercentage`). */
  btwPercentage: number | null;
  totaal: number | null;
}

/**
 * Eén factuur in de read-only lijst. `polisnummer`/`type`/`eigenRisico`/
 * `btwNummer` zijn `null` bij een niet-verzekerde factuur (A2); de FE rendert ze
 * alleen wanneer non-null (geen lege "null"-labels). `attachmentId` is `null`
 * wanneer er geen gekoppelde factuur-PDF is.
 */
export interface InvoiceListItem {
  invoiceId: string;
  /** Soort-label (`InvoiceType.Description`, 1-op-1). */
  soort: string | null;
  factuurnummer: number | null;
  /** ISO-datum-string (of `null`). */
  factuurdatum: string | null;
  debiteurnummer: number | null;
  debiteur: InvoiceDebtor;
  /** Alleen verzekerde facturen (A2). */
  polisnummer: string | null;
  /** Alleen verzekerde facturen (A2). */
  type: string | null;
  /** Alleen verzekerde facturen (A2). */
  eigenRisico: number | null;
  /** Alleen verzekerde facturen (A2). */
  btwNummer: string | null;
  regels: InvoiceLine[];
  totaal: number;
  btw: number;
  totaalIncl: number;
  /** `AttachmentID` van het meest-recente factuurdocument, of `null` (A4). */
  attachmentId: string | null;
  /** De factuur is zelf een creditfactuur — FE verbergt/disable't "Crediteren"
   * (story `fe-be-factuur-crediteren`, AC8). */
  isCredit: boolean;
  /** De factuur mag gecrediteerd worden (guard-set G2-G5) — knop-gate (AC8). */
  canCredit: boolean;
  /** Precieze reden waarom niet crediteerbaar (AC11), of `null` als crediteerbaar. */
  creditDisabledReason: CreditDisabledReason | null;
  /** Op het ORIGINEEL: id van de (meest recente) creditfactuur, of `null` — FE toont
   * "Gecrediteerd: <nr>" als <Link> (story `fe-be-factuur-credit-verwijzing`). */
  creditedByInvoiceId: string | null;
  /** Op het ORIGINEEL: nummer van de creditfactuur, of `null`. */
  creditedByInvoiceNumber: number | null;
  /** Op de CREDIT: id van de originele factuur, of `null` — FE toont "Credit bij: <nr>". */
  creditOfInvoiceId: string | null;
  /** Op de CREDIT: nummer van de originele factuur, of `null`. */
  creditOfInvoiceNumber: number | null;
}

/** 200-body van `GET /api/dossiers/{id}/invoices` (read-only, single-call A1). */
export interface InvoiceListResult {
  /** De facturen in BE-volgorde (FE sorteert niet). Lege lijst bij geen facturen. */
  invoices: InvoiceListItem[];
}

// ─── Facturen-overzicht + journaalregels (story fe-be-facturen-overzicht-journaalregels) ──

/** Sorteerbare kolommen v1 voor `GET /api/facturatie/invoices` (allowlist). */
export type FacturenSortKey = 'invoiceNumber' | 'invoiceDate' | 'debtorName';
export type FacturenSortDir = 'asc' | 'desc';

/** Query-params voor de keyset-gepagineerde facturenlijst (mirror `GetDossiersParams`). */
export interface GetFacturenParams {
  cursor?: string | null;
  pageSize?: number;
  q?: string;
  sort?: FacturenSortKey;
  dir?: FacturenSortDir;
}

/** Eén factuur-rij in het facturen-overzicht. */
export interface FacturenListItem {
  invoiceId: string;
  invoiceNumber: number | null;
  /** ISO-datum-string of `null`. */
  invoiceDate: string | null;
  debtorName: string | null;
  debtorNumber: number | null;
  dossierNumber: number | null;
  license: string | null;
  /** Soort-label (`InvoiceType.Description`). */
  soort: string | null;
  /** "Totaal incl." (credit-teken server-side berekend). */
  totaalIncl: number;
}

/** 200-body van `GET /api/facturatie/invoices` (keyset-paging, geen `total`). */
export interface FacturenListResponse {
  items: FacturenListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Debiteur op de factuur-detailpagina. */
export interface FactuurDebtor {
  naam: string | null;
  nummer: number | null;
}

/** Eén factuurregel op de detailpagina (8 kolommen, read-only). */
export interface FactuurRegel {
  artikelnummer: string | null;
  omschrijving: string | null;
  aantal: number | null;
  stukprijs: number | null;
  korting: number | null;
  btwPercentage: number | null;
  btwBedrag: number | null;
  totaal: number | null;
}

/** 200-body van `GET /api/facturatie/invoices/{id}` (header + factuurregels). */
export interface FactuurDetail {
  invoiceId: string;
  invoiceNumber: number | null;
  invoiceDate: string | null;
  debtor: FactuurDebtor;
  dossierNumber: number | null;
  license: string | null;
  soort: string | null;
  totaal: number;
  btw: number;
  totaalIncl: number;
  regels: FactuurRegel[];
  /** GUID van het gekoppelde dossier (`UF_Invoice.SubjectID`), of `null`.
   * Voedt de klikbare dossier-link + de mail-popup-bijlagen. */
  dossierId: string | null;
  /** De factuur is zelf een creditfactuur (story `fe-be-factuur-crediteren`, AC8). */
  isCredit: boolean;
  /** De factuur mag gecrediteerd worden (guard-set G2-G5) — knop-gate (AC8). */
  canCredit: boolean;
  /** Precieze reden waarom niet crediteerbaar (AC11), of `null` als crediteerbaar. */
  creditDisabledReason: CreditDisabledReason | null;
  /** Op het ORIGINEEL: id van de (meest recente) creditfactuur, of `null` — FE toont
   * "Gecrediteerd: <nr>" als <Link> (story `fe-be-factuur-credit-verwijzing`). */
  creditedByInvoiceId: string | null;
  /** Op het ORIGINEEL: nummer van de creditfactuur, of `null`. */
  creditedByInvoiceNumber: number | null;
  /** Op de CREDIT: id van de originele factuur, of `null` — FE toont "Credit bij: <nr>". */
  creditOfInvoiceId: string | null;
  /** Op de CREDIT: nummer van de originele factuur, of `null`. */
  creditOfInvoiceNumber: number | null;
}

/** Reden waarom een factuur niet gecrediteerd kan worden (AC11) — server-afgeleid,
 * spiegelt de guard-set: `is_credit` → `credit_function_disabled` → `no_dossier`
 * → `already_credited`. */
export type CreditDisabledReason =
  | 'is_credit'
  | 'credit_function_disabled'
  | 'no_dossier'
  | 'already_credited'
  /**
   * EPIC-019 S-G — deze VESTIGING maakt in CARculate geen facturen aan, dus
   * crediteren is er nooit mogelijk. Anders dan de vier redenen hierboven is dit
   * een PERMANENTE blokkade; de FE rendert de knop dan niet
   * (`shouldShowCreditButton`) in plaats van hem uit te schakelen (beslissing K5).
   * Additief union-lid: geen bestaande waarde hernoemd of verwijderd.
   */
  | 'restricted_repairer';

/** 200-body van `POST /api/facturatie/invoices/{id}/credit` (story
 * `fe-be-factuur-crediteren`). `creditInvoiceNumber` is best-effort (AC2). */
export interface CrediteerFactuurResult {
  ok: boolean;
  /** Toegekend creditfactuurnummer, of afwezig als niet bepaalbaar (AC2). */
  creditInvoiceNumber?: number;
}

/** Eén journaalregel (grootboek-tab). */
export interface FactuurJournalLine {
  lineId: string;
  version: number;
  ledgerNumber: number | null;
  omschrijving: string | null;
  debet: number | null;
  credit: number | null;
  /** "Nieuw" / "Geëxporteerd". */
  status: string;
  isExported: boolean;
}

/** 200-body van de journaalregel-read/mutatie (tab-refresh zonder refetch). */
export interface FactuurJournalLines {
  lines: FactuurJournalLine[];
  /** ≥ 1 geëxporteerde regel → "Alle regels vrijgeven" zichtbaar/actief. */
  hasExported: boolean;
}

/** Eén grootboek-edit in de batch-PATCH. */
export interface JournalLedgerEdit {
  lineId: string;
  /** Ruwe grootboek-invoer (string) — server valideert int &gt; 0. */
  ledgerNumber: string;
}

/**
 * Body van `POST /api/facturatie/invoices/{id}/mail` (story
 * `factuur-default-template-afdrukken`). Alle velden optioneel: leeg `to` →
 * de debiteur-e-mail van de factuur (server-side); geen adres → 400.
 */
export interface FactuurMailRequest {
  /** Ontvanger-override; leeg → debiteur-e-mail. */
  to?: string;
  /** Onderwerp-override; leeg → "Factuur <nr>". */
  subject?: string;
  /** Body-override. */
  body?: string;
}

// ─── Grootboeken inrichten (story `grootboeken-inrichten`, fase 1) ────────────

/** De sectie waarin een grootboek-rij valt. */
export type LedgerSection = 'invoicing' | 'itemType' | 'item';

/**
 * Eén debiteurengroep-tab. `id` is `null` voor de fallback-groep "Geen"
 * (`DebtorGroupID = null`) — die bestaat niet als DB-rij maar is wél bewerkbaar.
 */
export interface LedgerGroup {
  /** GUID-string; `null` voor "Geen". */
  id: string | null;
  code: string;
  name: string;
  /** True voor de fallback-groep "Geen". */
  isNone: boolean;
  /** True wanneer deze groep in deze bootstrap zojuist is aangemaakt. */
  isNew: boolean;
}

/** Eén bewerkbare grootboek-rij (groep × soort). Geen inkoopveld. */
export interface LedgerRow {
  id: string;
  /** `null` = de "Geen"-groep. */
  groupId: string | null;
  section: LedgerSection;
  code: string;
  /** Verkoop-grootboeknummer (mag leeg zijn; geen validatie). */
  salesLedgerNumber: string | null;
}

/** 200-body van `GET /api/facturatie/ledgers` + van elke mutatie-respons. */
export interface LedgersBootstrap {
  groups: LedgerGroup[];
  rows: LedgerRow[];
}

// ─── Exportinstellingen (story `exportinstellingen`) ──────────────────────────

/**
 * Eén key/value-instelling uit `UF_CompanyInvoiceSetting` incl. de optimistic-
 * lock-`version` — die round-trippt bij de save zodat een concurrente wijziging
 * een 409 `version_conflict` geeft (geen silent overwrite).
 */
export interface ExportSetting {
  keyName: string;
  keyValue: string;
  version: number | null;
}

/** Eén exportroutine-optie (instantie-gefilterd: Glasgarage-only opties zijn
 *  afwezig op een niet-Glasgarage-instantie). */
export interface ExportRoutineOption {
  id: string;
  code: string;
  label: string;
}

/** Eén btw-tarief-rij: de `settingKey` (`export{VatCode}`) waarvan waarde/versie
 *  in `ExportSettingsBootstrap.settings` staat, plus het label. */
export interface ExportVatRate {
  settingKey: string;
  vatCode: string;
  label: string;
}

/** 200-body van `GET /api/facturatie/export-settings` + van de save-respons. */
export interface ExportSettingsBootstrap {
  /** Alle door dit scherm beheerde key/value-instellingen. */
  settings: ExportSetting[];
  /** De gekozen exportroutine (GUID-string) of `null` (niet gezet). */
  invoiceRoutineId: string | null;
  /** Optimistic-lock-versie van de `UA_RepairerInvoiceSettings`-rij (of `null`). */
  invoiceRoutineVersion: number | null;
  /** De beschikbare routine-opties (instantie-gefilterd). */
  routineOptions: ExportRoutineOption[];
  /** De btw-tarieven → hun `export{VatCode}`-instellingskeys. */
  vatRates: ExportVatRate[];
}

/** PUT-body voor `saveExportSettings`. CompanyID/RepairerID staan bewust NIET in
 *  de body — die leidt de BE server-side af uit de sessie (A2/A14a). */
export interface ExportSettingsSaveRequest {
  settings: ExportSetting[];
  invoiceRoutineId: string | null;
  invoiceRoutineVersion: number | null;
}

// ─── Nummering — factuurreeks per jaar (story nummering-factuurreeks-per-jaar) ─

/** Eén bewerkbare factuur-reeks (start-/volgnummer + optimistic-lock-`version`). */
export interface NumberSerie {
  id: string;
  code: string;
  /** Display-label (legacy: Description afgekapt op "_" + hoofdletter). */
  label: string;
  firstNumber: number | null;
  nextNumber: number | null;
  version: number | null;
}

/** 200-body van `GET /api/facturatie/numbering` + van de save-respons. */
export interface NumberingBootstrap {
  /** Globale kill-switch (`GS_SystemParameter.YearlyNumbersActive`). Uit → de
   *  jaar-sectie wordt inert getoond met uitleg. */
  yearlyGloballyActive: boolean;
  /** Opt-in van deze company (`InvoiceNumberYearly == "true"`). */
  yearlyEnabled: boolean;
  /** Formaatkeuze "2"/"4" (default "4"). */
  yearDigits: string;
  /** Optimistic-lock-versie van de `InvoiceNumberYearly`-key. */
  yearlyVersion: number | null;
  /** Optimistic-lock-versie van de `InvoiceNumberYearDigits`-key. */
  yearDigitsVersion: number | null;
  /** Of een formaatwissel 4→2 nu geblokkeerd is (er bestaat al een 4-cijferige
   *  reeks) — de FE disabelt dan de 2-cijferig-keuze (A1). */
  formatDowngradeBlocked: boolean;
  /** Huidig jaar (server-klok) — voor de preview. */
  currentYear: number;
  /** De bestaande factuur-reeksen (Level-access, company-gescoped). */
  series: NumberSerie[];
}

/** Eén te bewerken reeks in de save-body (start-/volgnummer als string zodat de BE
 *  niet-numeriek/decimaal/overloop kan afkeuren met 400). */
export interface NumberSerieSave {
  id: string;
  firstNumber: string;
  nextNumber: string;
  version: number | null;
}

/** PUT-body voor `saveNumbering`. CompanyID/RepairerID staan bewust NIET in de body
 *  — server-side afgeleid uit de sessie (A2/AC-16). */
export interface NumberingSaveRequest {
  yearlyEnabled: boolean;
  yearDigits: string;
  yearlyVersion: number | null;
  yearDigitsVersion: number | null;
  series: NumberSerieSave[];
}

// ─── Factuurdocumenten-matrix (story fe-be-factuurdocumenten-story1, READ) ────

/**
 * Eén document in een matrix-cel. `label` = het rapport uit `UA_Report` (1-op-1),
 * of `"(onbekend rapport)"` bij een orphan-`reportId`. `autoMail` is in Story 1
 * ALTIJD `false` (er is nog geen `AutoMail`-kolom) — een read-only mail-indicatie;
 * de data-gedreven standen + interactie volgen in Story 2.
 */
export interface InvoiceDocument {
  /**
   * GUID-string van de `UF_InvoiceReport.ID` (de rij zelf) — de stabiele
   * mutatie-sleutel voor toggle/verwijderen/reorder (Story 2). Uniek per regel,
   * óók bij exacte duplicaten zodat gericht opschonen mogelijk is.
   */
  rowId: string;
  /** GUID-string; `null` bij een rij zonder (of een orphan) rapport-verwijzing. */
  reportId: string | null;
  label: string;
  /** Story 2: de echte `UF_InvoiceReport.AutoMail`-kolomwaarde (muteerbaar). */
  autoMail: boolean;
}

/** Eén selecteerbaar rapport voor de "+ document toevoegen"-picker (Story 2). */
export interface InvoiceReportOption {
  reportId: string;
  name: string;
}

/** 200-body van `GET /api/facturatie/invoice-documents/reports`. */
export interface InvoiceReportOptions {
  reports: InvoiceReportOption[];
}

/**
 * Eén factuurtype uit de ALGEMENE (System-owned) `UF_InvoiceType`-set voor de
 * "+ factuurtype"-picker (story 2b). Company-onafhankelijk: `UF_InvoiceType` is
 * voor iedereen zichtbaar. De picker toont hieruit de types die het bedrijf nog
 * niet heeft.
 */
export interface InvoiceTypeOption {
  invoiceTypeId: string;
  /** `UF_InvoiceType.Code` (bv. "VF1"). */
  code: string;
  /** `UF_InvoiceType.Description` (het factuurtype-label). */
  description: string;
}

/** 200-body van `GET /api/facturatie/invoice-documents/types` (de algemene set). */
export interface InvoiceTypeOptions {
  types: InvoiceTypeOption[];
}

/** Body van `POST /api/facturatie/invoice-documents` (document toevoegen). */
export interface AddInvoiceDocumentRequest {
  invoiceTypeId: string;
  isInsured: boolean;
  isCredit: boolean;
  reportId: string;
}

/** Body van `PATCH /api/facturatie/invoice-documents/reorder` (volgorde in één cel). */
export interface ReorderInvoiceDocumentsRequest {
  invoiceTypeId: string;
  isInsured: boolean;
  isCredit: boolean;
  orderedRowIds: string[];
}

/** 200-body van de reorder: de nieuwe geordende cel (documenten in `Ordinal`-volgorde). */
export interface InvoiceDocumentCell {
  documents: InvoiceDocument[];
}

/**
 * De vier cellen van de 2×2-matrix (`IsInsured` × `IsCredit`). Elke
 * `UF_InvoiceReport`-rij valt in precies één cel (NULL-bit → 0). Binnen een cel
 * gesorteerd op `UA_Report.Name`, dan `UF_InvoiceReport.ID`; duplicaten worden
 * NIET samengevouwen (multipliciteit is het doel).
 */
export interface InvoiceDocumentCells {
  /** `IsInsured=0/NULL`, `IsCredit=0/NULL`. */
  uninsuredNormal: InvoiceDocument[];
  /** `IsInsured=0/NULL`, `IsCredit=1`. */
  uninsuredCredit: InvoiceDocument[];
  /** `IsInsured=1`, `IsCredit=0/NULL`. */
  insuredNormal: InvoiceDocument[];
  /** `IsInsured=1`, `IsCredit=1`. */
  insuredCredit: InvoiceDocument[];
}

/** Eén factuurtype-blok: label uit `UF_InvoiceType` + vier cellen + telling. */
export interface InvoiceTypeMatrix {
  invoiceTypeId: string;
  /** `UF_InvoiceType.Code` (bv. "VF1"). */
  code: string;
  /** `UF_InvoiceType.Description` (bv. "Totale factuur"). */
  label: string;
  /** Totaal over de vier cellen (incl. duplicaten + orphans; geen dedup). */
  documentCount: number;
  cells: InvoiceDocumentCells;
}

/**
 * 200-body van `GET /api/facturatie/invoice-documents` (company-gescoped, read).
 * `invoiceTypes` bevat ALLEEN factuurtypes met ≥1 rij, gesorteerd op `code`.
 * `usesDefaultTemplate = true` → de company heeft niets ingericht en de factuur
 * valt terug op de bestaande default-template (lege/fallback-staat).
 */
export interface InvoiceDocumentsMatrix {
  invoiceTypes: InvoiceTypeMatrix[];
  usesDefaultTemplate: boolean;
}

// ─── Inrichting / Dossierdocumenten-verzendregels (DD_*) ──────────────────
//
// Story `fe-be-dossierdocumenten-story1-beheerscherm`. Per partij een
// beslistabel (regels met tri-state-condities → 0..n documenten + opt-in mail)
// + één standaard mailtemplate. Company-gescoped (BE dwingt de sessie-company af).
//
// Tri-state-conditie (F-01): `null` = nvt (wildcard), `1`/`2` = de twee concrete
// opties; `0` is verboden (BE → 400 `unknown_condition_value`).

/** Partij-enum: 1=Verzekeraar/Volmacht, 2=Lease/Fleet, 3=Verzekerde/klant, 4=Opdrachtgever. */
export type DeliveryParty = 1 | 2 | 3 | 4;

/** Een tri-state conditie-waarde: `null` = nvt, `1`/`2` = concrete opties. */
export type DeliveryConditionValue = 1 | 2 | null;

/** Discriminator van een verzendregel: 1 = hoofdfactuur (default), 2 = aanvullend. */
export type DeliveryRuleKind = 1 | 2;

/** Factuursoort van een aanvullende regel: 1 = Provisienota (VP1), 2 = Verkoopfactuur (VF5). */
export type DeliveryInvoiceSort = 1 | 2;

/** Eén gekoppeld document van een regel (chip in de UI). */
export interface DeliveryRuleDocument {
  documentCode: string;
  /** Label uit de bron; bij een verweesd id de code zelf. */
  label: string;
  /** `false` = de bron kent dit id niet (meer) → "niet meer beschikbaar". */
  available: boolean;
  sortOrder: number;
}

/** Eén beslistabel-regel. */
export interface DeliveryRule {
  id: string;
  party: DeliveryParty;
  condInsured: DeliveryConditionValue;
  condRepairType: DeliveryConditionValue;
  condOwnRisk: DeliveryConditionValue;
  condOwnRiskDiscount: DeliveryConditionValue;
  condVatInsured: DeliveryConditionValue;
  mailOptIn: boolean;
  sortOrder: number;
  isActive: boolean;
  /** Optimistic-concurrency-token; mee terug bij een update (F-08). */
  version: number;
  documents: DeliveryRuleDocument[];
  /** Discriminator (1=hoofdfactuur, 2=aanvullend). */
  ruleKind: DeliveryRuleKind;
  /** Factuursoort op een aanvullende regel (1=Provisienota, 2=Verkoopfactuur); `null` op hoofdfactuur-regels. */
  invoiceSort: DeliveryInvoiceSort | null;
  /**
   * Story `be-fe-verzendregel-conditie-centraal` — conditie "Centraal dossier" (1 = Ja, 2 = Nee). Alleen op
   * partij 3 (verzekerde/klant), hoofdfactuur. De BE laat het veld WEG bij n.v.t.; lees altijd
   * `rule.condCentral ?? null` (PB-14, R-05).
   */
  condCentral?: DeliveryConditionValue;
}

/** De regelset + standaard mailtemplate van één partij. */
export interface DeliveryPartyConfig {
  party: DeliveryParty;
  /** De standaard mailtemplate-id; `null` = "— geen —". */
  mailTemplateId: string | null;
  rules: DeliveryRule[];
  /** De AANVULLENDE-factuur-regels (nieuw blok, per factuursoort); geen dossier-condities. */
  aanvullendeRules: DeliveryRule[];
}

/** Optie in de document-picker (A0-C, voorlopige bron). */
export interface DeliveryDocumentOption {
  code: string;
  label: string;
}

/** Optie in de mailtemplate-picker (A0-A, factuur-mailtemplates). */
export interface DeliveryTemplateOption {
  id: string;
  name: string;
}

/** Volledige beheerscherm-config van de sessie-company (één GET). */
export interface DeliveryConfig {
  parties: DeliveryPartyConfig[];
  documentOptions: DeliveryDocumentOption[];
  templateOptions: DeliveryTemplateOption[];
  /**
   * Story `be-fe-verzendregel-conditie-centraal` (PB-10) — `true` wanneer de database-aanpassing (migratie 121)
   * nog niet is uitgevoerd; dan is de kolom "Centraal dossier" uitgeschakeld. Weggelaten zodra de kolom bestaat.
   */
  centralConditionUnavailable?: boolean;
}

/** Body voor create/update van één regel (per-regel-save). */
export interface DeliveryRuleWriteRequest {
  party: DeliveryParty;
  condInsured: DeliveryConditionValue;
  condRepairType: DeliveryConditionValue;
  condOwnRisk: DeliveryConditionValue;
  condOwnRiskDiscount: DeliveryConditionValue;
  condVatInsured: DeliveryConditionValue;
  mailOptIn: boolean;
  documentCodes: string[];
  /** Verplicht bij update (optimistic concurrency); weg bij create. */
  version?: number;
  /** Discriminator: `undefined`/1 = hoofdfactuur (default), 2 = aanvullende-factuur-regel. */
  ruleKind?: DeliveryRuleKind;
  /** Factuursoort (1=Provisienota, 2=Verkoopfactuur); verplicht + partij-consistent bij `ruleKind` = 2. */
  invoiceSort?: DeliveryInvoiceSort;
  /** Conditie "Centraal dossier" — alleen op partij 3; op 1/2/4 weglaten of `null` (anders 400). */
  condCentral?: DeliveryConditionValue;
}

/** Body voor het zetten van de standaard mailtemplate per partij. */
export interface PartyMailTemplateWriteRequest {
  party: DeliveryParty;
  /** UA_Report.ID; `null`/leeg = "— geen —". */
  mailTemplateId: string | null;
}

// ─── Inrichting / Standaarddocumenten (story be-fe-standaarddocumenten, G2) ──
//
// Eén lijst per instantie, alleen voor System. Zelfde vorm als een verzendregel; de documenten staan als
// rapport-code (getoond op naam), de mailtemplate op naam (templates zijn per bedrijf).

/** Optie in de documentkiezer van de standaardlijst. */
export interface DefaultDeliveryDocumentOption {
  code: string;
  /** De rapportnaam, zoals de verzendregels hem tonen (`lijst.naam`). */
  label: string;
  /** AC 5: bij hoeveel herstellers dit rapport niet in de catalogus staat. */
  missingCount: number;
}

export interface DefaultDeliveryRuleDocument {
  documentCode: string;
  label: string;
  /** `false` = geen enkele hersteller heeft dit rapport (meer). */
  available: boolean;
  sortOrder: number;
  /** AC 5: bij hoeveel herstellers dit rapport niet in de catalogus staat. */
  missingCount: number;
}

/** Eén regel van de standaardlijst (altijd hoofdfactuur). */
export interface DefaultDeliveryRule {
  id: string;
  party: DeliveryParty;
  condInsured: DeliveryConditionValue;
  condRepairType: DeliveryConditionValue;
  condOwnRisk: DeliveryConditionValue;
  condOwnRiskDiscount: DeliveryConditionValue;
  condVatInsured: DeliveryConditionValue;
  /** Weggelaten bij n.v.t.; lees `rule.condCentral ?? null`. */
  condCentral?: DeliveryConditionValue;
  mailOptIn: boolean;
  sortOrder: number;
  isActive: boolean;
  version: number;
  ruleKind: DeliveryRuleKind;
  /** De naam van de mailtemplate, of `null` (geen voorkeur). */
  mailTemplateCode: string | null;
  documents: DefaultDeliveryRuleDocument[];
}

export interface DefaultDeliveryPartyConfig {
  party: DeliveryParty;
  rules: DefaultDeliveryRule[];
}

/** `GET /api/inrichting/default-delivery-rules/config`. */
export interface DefaultDeliveryConfig {
  /** `false` = de database-aanpassing (migratie 126) is nog niet uitgevoerd; de lijst is leeg en alleen-lezen. */
  available: boolean;
  /** Het aantal herstellers van de instantie (de noemer van `missingCount`). */
  repairerCount: number;
  parties: DefaultDeliveryPartyConfig[];
  documentOptions: DefaultDeliveryDocumentOption[];
  /** De namen van de factuur-mailtemplates over de instantie. */
  templateNames: string[];
}

/** Body voor create/update van een standaardregel. */
export interface DefaultDeliveryRuleWriteRequest {
  party: DeliveryParty;
  condInsured: DeliveryConditionValue;
  condRepairType: DeliveryConditionValue;
  condOwnRisk: DeliveryConditionValue;
  condOwnRiskDiscount: DeliveryConditionValue;
  condVatInsured: DeliveryConditionValue;
  condCentral?: DeliveryConditionValue;
  mailOptIn: boolean;
  documentCodes: string[];
  /** Verplicht bij update. */
  version?: number;
  /** Naam van de mailtemplate; `null` = geen voorkeur. Alleen bewaard bij `mailOptIn`. */
  mailTemplateCode?: string | null;
}

/** `POST …/seed-from-history`. */
export interface DefaultDeliverySeedResult {
  rulesWritten: number;
}

// ─── Audit-historie (story `fe-be-wf-trace-audit`, item 11, 2026-06-09) ───────

/**
 * Eén audit-regel uit `GET /api/dossiers/{id}/audit` (read-only WF_Trace).
 * `category` is het RUWE token (`StatusTransition` / `Archive` / `HQApproved` /
 * `InvoiceCreation` / onbekend) — de FE mapt 'm naar een NL-label. `userName`
 * is "Onbekend" wanneer de user niet (meer) resolvet. `statusLabel` mag `null`
 * zijn (FE toont "—"). `traceDateTime` is een ISO-8601-string of `null`.
 */
export interface AuditTraceEntry {
  traceDateTime: string | null;
  userName: string;
  category: string | null;
  description: string | null;
  statusLabel: string | null;
}

/**
 * 200-body van `GET /api/dossiers/{id}/audit` — de audit-historie, nieuwste
 * boven (BE sorteert), met een cap van 200 regels. `truncated` is `true`
 * wanneer er méér dan de cap zijn → de drawer toont een "oudere regels niet
 * getoond"-hint. Geen traces → `items: []` (geen 404).
 */
export interface DossierAudit {
  items: AuditTraceEntry[];
  truncated: boolean;
}

// ─── Eerdere schades op dit voertuig (story `fe-be-eerdere-schades-voertuig`) ──

/**
 * Eén regel uit `GET /api/dossiers/{id}/vehicle-damage-history` — een ander
 * schadedossier op hetzelfde voertuig (kenteken) binnen de caller-scope.
 *
 * - `damageDate` is een ISO date-only-string (`yyyy-MM-dd`) of `null`.
 * - `amountInclVat` is het bedrag incl. BTW (persisted legacy-totaal) of `null`
 *   wanneer het dossier geen bedrag heeft → FE toont "—".
 * - `statusLabel`/`repairerName` mogen `null` zijn.
 * - bij `isArchive: true` toont de FE een "Archief"-badge i.p.v. de status-chip.
 * - v1.1: `insuranceCompanyName` (alleen gevuld als bekend; anders `null` → FE
 *   laat de Verzekeraar-detailregel weg) en `customerName` (`null` → FE toont "—").
 */
export interface VehicleDamageHistoryItem {
  dossierId: string;
  number: number | null;
  damageDate: string | null;
  statusLabel: string | null;
  repairerName: string | null;
  amountInclVat: number | null;
  isArchive: boolean;
  insuranceCompanyName: string | null;
  customerName: string | null;
}

/**
 * 200-body van `GET /api/dossiers/{id}/vehicle-damage-history` — read-only lijst
 * + telling van ANDERE dossiers op hetzelfde (genormaliseerde) kenteken binnen
 * de caller-scope. `license` = het genormaliseerde kenteken (leeg wanneer het
 * dossier geen kenteken heeft); lijst is schadedatum aflopend gesorteerd (BE
 * sorteert). Geen kenteken / geen matches → `count: 0`, `items: []`. Cross-tenant
 * / onbekend → 404.
 *
 * v1.1: `count` = het WERKELIJKE totaal binnen scope; `items` is gecapt op
 * maximaal 10 (de meest recente). `count` is dus NIET meer per definitie gelijk
 * aan `items.length` — bij `count > items.length` wijst de FE naar het
 * dossier-zoekscherm voor het volledige overzicht.
 */
export interface VehicleDamageHistory {
  license: string;
  count: number;
  items: VehicleDamageHistoryItem[];
}

/**
 * Story `fe-be-hk-eurocode-vergelijking` (2026-07-03) — één bron-dossier in de
 * HK-eurocode-vergelijking: kop-metadata + de VOLLEDIGE calculatie van dat
 * dossier (`CalculationRowDto[]`, dezelfde wire-shape als de Calculatie-tab) +
 * de id('s) van de calc-rij(en) die op de geselecteerde eurocode matchen.
 * `invoiceDate` = ISO date-only van `MAX(UF_Invoice.InvoiceDate)` (of `null`).
 * Bij `isArchive` toont de FE een "gearchiveerd"-badge i.p.v. de status-chip;
 * `amountInclVat` (persisted excl + BTW) `null` → FE toont "—".
 */
export interface HqEurocodeHistorySourceDossier {
  dossierId: string;
  number: number | null;
  invoiceDate: string | null;
  repairerName: string | null;
  insuranceCompanyName: string | null;
  statusValue: number | null;
  statusLabel: string | null;
  isArchive: boolean;
  amountInclVat: number | null;
  rows: CalculationRowDto[];
  matchedRowIds: string[];
}

/**
 * Story `fe-be-hk-eurocode-vergelijking` — één eurocode-groep uit de huidige
 * calculatie van het bron-dossier. `count` = het WERKELIJKE totaal binnen
 * HK-toegang (kan groter zijn dan `dossiers.length`: gecapt op max 20 nieuwste,
 * factuurdatum aflopend); bij `count === 0` toont het rechterpaneel de lege
 * staat. `description` = representatieve omschrijving (of `null`).
 */
export interface HqEurocodeHistoryEurocode {
  eurocode: string;
  description: string | null;
  count: number;
  dossiers: HqEurocodeHistorySourceDossier[];
}

/**
 * 200-body van `GET /api/dossiers/{id}/hq-eurocode-history` — per eurocode uit
 * de HUIDIGE calculatie van dossier `{id}` de eerdere volledige calculaties van
 * andere dossiers (met facturen, factuurdatum ≤ 2 jaar, huidig dossier
 * uitgesloten, access-scoped, gearchiveerd meegenomen, max 20 nieuwste).
 * Gate (BE, defense-in-depth): geen-toegang / onbekend → 404;
 * niet-HK → 403 `not_headquarters`; HK maar status ≠ 241 → 403
 * `not_in_hq_review`. Huidige calc zonder eurocode-regel → `{ eurocodes: [] }`.
 */
export interface HqEurocodeHistory {
  eurocodes: HqEurocodeHistoryEurocode[];
}

/**
 * Story `fe-relatiebeheer-fase1-A` (2026-06-23) — één rij van de klantenlijst
 * (`GET /api/relations`). Mirror van `DossierListItem` maar voor relaties.
 * `companyType` is `null` wanneer het company-type niet geresolved kon worden
 * (de FE toont dan geen chip). `status` is een soft-deactivate-vlag (geen hard
 * delete).
 */
export interface RelationListItem {
  relationId: string;
  name: string;
  companyType: 'Persoon' | 'Bedrijf' | null;
  debtorNumber: number | null;
  city: string | null;
  status: 'active' | 'inactive';
}

/**
 * Toegestane sorteersleutels voor `GET /api/relations` (klantenlijst). Worden
 * 1-op-1 als `sort`-query-param naar de BE gestuurd; de BE valt voor een
 * onbekende waarde terug op `name` (geen 400). Alleen Naam, Debiteurnr. en
 * Plaats zijn sorteerbaar (Type/Status niet — F-A6).
 */
export type RelationSortKey = 'name' | 'debtorNumber' | 'city';

/** Sorteerrichting voor `GET /api/relations` (`dir`-query-param). */
export type RelationSortDir = 'asc' | 'desc';

/**
 * Query-parameters voor de gepagineerde `GET /api/relations` (klantenlijst).
 * Mirror van `GetDossiersParams`: `cursor` is een opaque offset-string uit de
 * vorige `nextCursor` (weglaten = eerste pagina); `q` = vrije zoekterm
 * (debounced FE-side); `activeOnly` = "Alleen actieve"-toggle (default AAN);
 * `pageSize` default 50.
 */
export interface GetRelationsParams {
  cursor?: string | null;
  pageSize?: number;
  q?: string;
  activeOnly?: boolean;
  sort?: RelationSortKey;
  dir?: RelationSortDir;
}

/**
 * Response van `GET /api/relations` (offset-paging). Bevat — net als de
 * dossier-lijst — bewust GEEN `total`. `nextCursor` is de opaque cursor voor de
 * volgende batch (`null` = einde); `hasMore` is `false` op de laatste batch.
 */
export interface RelationListResponse {
  items: RelationListItem[];
  nextCursor: string | null;
  hasMore: boolean;
}

// ─── Verzekeraars (story fe-relatiebeheer-verzekeraars-fase-a1, 2026-06-24) ──

/**
 * Eén rij uit de verzekeraarslijst (`GET /api/insurers`). Een verzekeraar =
 * `UA_InsuranceCompany` ⊕ `RB_Company` (NIET `RB_GenericRelation`). Type-vlaggen
 * los i.p.v. één enum: een verzekeraar kan tegelijk volmacht/lease/fleetowner zijn
 * (alle gezette chips worden getoond — F-06).
 */
export interface InsurerListItem {
  insurerId: string;
  name: string;
  wamCode: string | null;
  porCode: string | null;
  isAuthorizedAgent: boolean;
  isLeaseCompany: boolean;
  isFleetOwner: boolean;
  debtorNumber: number | null;
  status: 'active' | 'inactive';
}

/** Toegestane sorteersleutels voor `GET /api/insurers` (alleen Naam in A1). */
export type InsurerSortKey = 'name';

/** Sorteerrichting voor `GET /api/insurers`. */
export type InsurerSortDir = 'asc' | 'desc';

/** Type-filter voor `GET /api/insurers` (volmacht/lease/fleetowner; leeg = alles). */
export type InsurerTypeFilter = 'volmacht' | 'lease' | 'fleetowner';

/**
 * Query-parameters voor de gepagineerde `GET /api/insurers`. Mirror van
 * {@link GetRelationsParams}: `cursor` opaque offset-string (weglaten = eerste
 * pagina); `q` vrije zoekterm (debounced FE-side, OR over naam/Wam/Por/debiteurnr.);
 * `activeOnly` "Alleen actieve"-toggle (default AAN); `type` type-filter.
 */
export interface GetInsurersParams {
  cursor?: string | null;
  pageSize?: number;
  q?: string;
  activeOnly?: boolean;
  type?: InsurerTypeFilter;
  sort?: InsurerSortKey;
  dir?: InsurerSortDir;
  /**
   * Filter-chip "Centrale klant" (story `fe-verzekeraars-lijst-filter-chips`,
   * 2026-06-25). Onafhankelijke toggle (NIET in de single-select `type`-groep).
   * `true` → alleen verzekeraars die centrale klant zijn t.o.v. de keten
   * (betekenis-gelijk aan de detail-badge "Centrale klant: Ja/Nee"). LET OP de
   * `true`-only-conventie: alleen `true` komt op de wire (`?centralOnly=true`);
   * `false`/`undefined` → key weggelaten — anders dan `activeOnly` (dat juist
   * `=false` meestuurt). Combineerbaar met `exchangeOnly`/`type`/`activeOnly`
   * (alles AND).
   */
  centralOnly?: boolean;
  /**
   * Filter-chip "eXchange" (story `fe-verzekeraars-lijst-filter-chips`,
   * 2026-06-25). Onafhankelijke toggle. `true` → alleen eXchange-maatschappijen
   * (polis-service-naam bevat "exchange", case-insensitive — dezelfde betekenis
   * als `isExchangePolicyService` op het detail). `true`-only-conventie
   * (`?exchangeOnly=true`; `false`/`undefined` → weggelaten).
   */
  exchangeOnly?: boolean;
}

/** Response van `GET /api/insurers` (offset-paging; bewust geen `total`). */
export interface InsurerListResponse {
  items: InsurerListItem[];
  nextCursor: string | null;
  hasMore: boolean;
  /**
   * Story `be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen` (AC 2) — mag deze gebruiker een verzekeraar
   * aanmaken (System of hoofdkantoor)? Server-afgeleid; de FE toont "+ Nieuwe verzekeraar" alleen bij `true`.
   * Optioneel getypeerd voor oudere mocks/backends (afwezig = geen knop).
   */
  canManageInsurer?: boolean;
}

/** Wire-sleutels van de bewerkbare debiteur-velden (`PUT /api/insurers/{id}/debtor`). */
export type InsurerDebtorField =
  | 'debtorNumber'
  | 'pricelistId'
  | 'debtorGroupId'
  | 'debtorListId'
  | 'currencyId'
  | 'ledgerAccount';

/** De eigen-hersteller-debiteur op het verzekeraar-detail. */
export interface InsurerDebtor {
  repairerName: string | null;
  debtorNumber: number | null;
  defaultPriceListName: string | null;
  defaultPriceListId: string | null;
  debtorListName: string | null;
  debtorListId: string | null;
  debtorGroupId: string | null;
  /** Leesbare naam van de debiteurengroep (alleen-lezen-weergave). Optioneel voor oudere backends. */
  debtorGroupName?: string | null;
  ledgerAccount: string | null;
  currencyName: string | null;
  currencyId: string | null;
  /**
   * Story `be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen` (AC 5) — welke velden deze gebruiker mag
   * schrijven (server-afgeleid). Een vestiging krijgt alleen `debtorNumber`; System/HK de vijf instellingen.
   * Optioneel getypeerd voor oudere mocks/backends: afwezig ⇒ alles alleen-lezen.
   */
  editableFields?: string[];
}

/**
 * Volledig verzekeraar-detail uit `GET /api/insurers/{id}` voor het read-only
 * detailscherm (rail + 6 tabs). Bevat de label-velden (`*Name`) zodat read-only
 * dropdowns hun gekozen waarde tonen zonder aparte lookup-call.
 *
 * `canManageInsurer`/`canEditDebtor` zijn **BE-berekende** permissie-vlaggen
 * (F-01/F-02): de FE verbergt/disablet edit-affordances erop (puur cosmetisch —
 * de BE blijft de autoritatieve poort).
 */
export interface InsurerDetail {
  insurerId: string;
  /**
   * Story `be-fe-meerprijs-dealer-exchange-keten` (AC 6.6): het `RB_Company` van de maatschappij (de sleutel van de
   * meerprijs-sets). `null` als hij ontbreekt; optioneel voor een backend van vóór de story.
   */
  companyId?: string | null;
  // rail / identiteit
  name: string;
  wamCode: string | null;
  porCode: string | null;
  euroMotiveCode: string | null;
  status: 'active' | 'inactive';
  // Verzekeraar-tab (RB_Company NAW)
  parentName: string | null;
  countryName: string | null;
  countryId: string | null;
  website: string | null;
  kvkNumber: string | null;
  vatNumber: string | null;
  vatAccountable: boolean;
  // Instellingen-tab (UA_InsuranceCompany)
  policyServiceName: string | null;
  policyServiceId: string | null;
  isAuthorizedAgent: boolean;
  isLeaseCompany: boolean;
  isFleetOwner: boolean;
  damageNumberRequired: boolean;
  conceptCodeName: string | null;
  onlyVisibleByConceptCode: boolean;
  ignoreCustomerNotification: boolean;
  ownRiskRepair: number | null;
  ownRiskReplace: number | null;
  priceListName: string | null;
  priceListId: string | null;
  exchangeRelationNumber: number | null;
  dispatchRelationNumber: string | null;
  rulesProcedure: string | null;
  /**
   * Story `fe-be-verzekeraar-zoeken-op-groenekaartcode` (E2) — de code waarmee
   * deze maatschappij op een GROENE KAART staat. Komt uit de
   * 1-op-1-uitbreidingstabel `UA_InsuranceCompanyExtension`, niet uit
   * `UA_InsuranceCompany`.
   *
   * ⚠ Het veld is altijd aanwezig; `null` = geen code ingericht. Zou het bij een
   * lege waarde ontbreken, dan toont het beheerscherm ná een refetch weer leeg en
   * denkt de gebruiker dat het niet opslaat.
   */
  greenCardCode: string | null;
  // Polis-tab
  policyFileFolder: string | null;
  policyFormatName: string | null;
  policyFormatId: string | null;
  // Debiteur-tab (eigen-hersteller)
  debtor: InsurerDebtor | null;
  // Opmerkingen-tab
  notes: string | null;
  // permissie-vlaggen (BE = source of truth)
  canManageInsurer: boolean;
  canEditDebtor: boolean;
  // keten-b1: compacte keten-status (Centrale-klant-toggles op de Maatschappij-tab
  // + Keten-/Protocol-tab-zichtbaarheid). `null` als de status niet bepaald is.
  chainStatus?: ChainStatus | null;
}

/**
 * Story `fe-relatiebeheer-verzekeraars-keten-b1` (2026-06-24) — compacte
 * keten-status uit `GET /api/insurers/{id}` (veld `chainStatus`). De FE toont
 * hierop de Centrale-klant-/In-aanmaak-toggles op de Maatschappij-tab en bepaalt
 * de Keten-/Protocol-tab-zichtbaarheid (`canManageChain && isCentralCustomer`).
 */
export interface ChainStatus {
  /** IsSystem OR (eigen-company == ketenOwnerId). HK/glashersteller → false. */
  canManageChain: boolean;
  isCentralCustomer: boolean;
  /** Effectieve weergave (forceer-regel meegerekend). */
  underConstruction: boolean;
  /** Centrale klant zonder EURO-motive code → toggle geforceerd aan + disabled. */
  underConstructionForced: boolean;
  /** Alleen System mag de EURO-motive artikelcode bewerken. */
  canEditEuroMotiveCode: boolean;
}

/**
 * Story `fe-relatiebeheer-verzekeraars-opmerkingen-thread` (2026-06-25) — één
 * verzekeraar-opmerking uit `GET /api/insurers/{id}/comments`. Mirror van
 * `RelationComment` zodat de FE dezelfde rail-kaart + drawer kan voeden. Bron:
 * `GD_Communication` (`SubjectID = UA_InsuranceCompany.CompanyID = RB_Company.ID`).
 */
export interface InsurerComment {
  id: string;
  /** ISO-string. */
  createdDate: string;
  createdByName: string | null;
  /** Body kan HTML zijn; drawer rendert via `sanitizeHtml`. */
  body: string;
  communicationType?: string | null;
}

/** Opmerkingen-historie van een verzekeraar — `count` gelijk aan `items.length`. */
export interface InsurerComments {
  count: number;
  items: InsurerComment[];
}

/** Opmerkingtype (`GD_CommunicationType`) voor de toevoeg-dialog-dropdown. */
export interface InsurerCommentType {
  id: string;
  description: string;
}

/** Doorbelast-modus: percentage of vast bedrag (€). */
export type ChargeMode = 'percentage' | 'fixed';

/**
 * Volledig keten-detail uit `GET /api/insurers/{id}/chain` (story
 * `fe-relatiebeheer-verzekeraars-keten-b1` + `fe-relatiebeheer-verzekeraars-keten-b2`).
 * De eigen `RB_CompanyCompany`-waarden + dropdown-GUID's (`*Id`) + labels (`*Name`)
 * + permissie-/forceer-vlaggen. 403 voor HK/glashersteller (geen `canManageChain`).
 */
export interface ChainDetail {
  insurerId: string;
  // header (read-only)
  insurerName: string | null;
  ownerName: string | null;
  // centrale-klant-surface
  isCentralCustomer: boolean;
  underConstruction: boolean;
  // Algemeen & status
  visible: boolean;
  checkByHQ: boolean;
  hideInCallModule: boolean;
  // Facturatie & prijzen
  defaultPricelistId: string | null;
  defaultPricelistName: string | null;
  brutoPricelistId: string | null;
  brutoPricelistName: string | null;
  nettoPricelistId: string | null;
  nettoPricelistName: string | null;
  calculationOriginId: string | null;
  calculationOriginName: string | null;
  finishedCaseStateId: string | null;
  finishedCaseStateName: string | null;
  policyServiceId: string | null;
  policyServiceName: string | null;
  createBrutoNettoInvoices: boolean;
  // Doorbelasten
  chargeRepairMode: ChargeMode;
  chargeRepairValue: number | null;
  chargeRepairItemCode: string | null;
  chargeReplaceMode: ChargeMode;
  chargeReplaceValue: number | null;
  chargeReplaceItemCode: string | null;
  chargeItemCode: string | null;
  repairReimbursementItemCode: string | null;
  replaceReimbursementItemCode: string | null;
  // Protocol-tab (rich-text HTML)
  protocol: string | null;
  // ── B2: Sub-tab "Korting (lease)" ──
  /** 8 artikelcode-strings voor bruto/volume-korting (repair/replace × incasso/excasso). */
  brutoDiscountRepairIncassoItemCode: string | null;
  brutoDiscountReplaceIncassoItemCode: string | null;
  brutoDiscountRepairExcassoItemCode: string | null;
  brutoDiscountReplaceExcassoItemCode: string | null;
  volumeDiscountRepairIncassoItemCode: string | null;
  volumeDiscountReplaceIncassoItemCode: string | null;
  volumeDiscountRepairExcassoItemCode: string | null;
  volumeDiscountReplaceExcassoItemCode: string | null;
  /** Volumekorting reparatie: % óf € (XOR — legacy-CCSettings-model). */
  volumeDiscountRepairPercentage: number | null;
  volumeDiscountRepairFixed: number | null;
  /** Volumekorting vervanging: % óf € (XOR). */
  volumeDiscountReplacePercentage: number | null;
  volumeDiscountReplaceFixed: number | null;
  // ── B2: Sub-tab "Rapporten & mails" ──
  declarationReportFunctionId: string | null;
  declarationReportFunctionName: string | null;
  brutoDiscountDeclarationReportId: string | null;
  brutoDiscountDeclarationReportName: string | null;
  declarationMailBodyReportId: string | null;
  declarationMailBodyReportName: string | null;
  /** Typo is de exacte DB-kolomnaam (PolicyCheckEmalBodyReportID). */
  policyCheckEmalBodyReportId: string | null;
  policyCheckEmalBodyReportName: string | null;
  declarationMailSubject: string | null;
  attachmentName: string | null;
  declarationEmailAddress: string | null;
  paymentReminderEmail: string | null;
  policyCheckEmailAddress: string | null;
  policyCheckEmailSender: string | null;
  policyCheckEmailSubject: string | null;
  sendAppointmentEmailInsurer: boolean | null;
  sendAppointmentEmailDriver: boolean | null;
  // ── B2: Sub-tab "Intake" ──
  intakeNotificationEmail: string | null;
  intakeNotificationSubject: string | null;
  intakeNotificationBodyReportId: string | null;
  intakeNotificationBodyReportName: string | null;
  /** WYSIWYG HTML. */
  intakeMessage: string | null;
  /** WYSIWYG HTML. */
  calculationMessage: string | null;
  // ── B2: Sub-tab "Voorexpertise" ──
  insurerPreExpertiseRuleId: string | null;
  insurerPreExpertiseRuleName: string | null;
  insurerPreExpertiseReasons: string | null;
  insurerPreExpertiseViaHQ: boolean | null;
  insurerPreExpertiseReportId: string | null;
  insurerPreExpertiseReportName: string | null;
  insurerPreExpertiseEmailAddress: string | null;
  insurerPreExpertiseWarningStoredProcedure: string | null;
  preExpertiseRuleStoredProcedure: string | null;
  preExpertiseStatusId: string | null;
  preExpertiseStatusName: string | null;
  preExpertiseApprovedStatusId: string | null;
  preExpertiseApprovedStatusName: string | null;
  preExpertiseDisApprovedStatusId: string | null;
  preExpertiseDisApprovedStatusName: string | null;
  /** WYSIWYG HTML. */
  preExpertiseInfoMessage: string | null;
  // ── B2: Sub-tab "Artikelen & adviesprijzen" ──
  useSuggestedRetailPrices: boolean | null;
  retailRaisePercentageWindshield: number | null;
  retailRaisePercentageBacklight: number | null;
  retailRaisePercentageBodyGlass: number | null;
  /** Geselecteerde additive-group-ID's (max 2); eigen aparte fetch via GET .../chain/additive-groups. */
  additiveGroupIds: string[];
  // permissie-/forceer-vlaggen
  canManageChain: boolean;
  canEditEuroMotiveCode: boolean;
  underConstructionForced: boolean;
}

/**
 * Additive-group-item uit `GET /api/insurers/{id}/chain/additive-groups` en
 * de lookuplijst `GET /api/lookups/additive-groups`.
 */
export interface AdditiveGroupItem {
  id: string;
  description: string;
}

/** Response van `GET /api/insurers/{id}/chain/additive-groups`. */
export interface ChainAdditiveGroupsResponse {
  additiveGroupIds: string[];
}

/**
 * Write-payload voor `PUT /api/insurers/{id}/chain` (per-veld auto-save; partiële
 * merge — alleen aanwezige velden worden geschreven, een aanwezige `null` wist).
 * 403 bij niet-`canManageChain`; 403 `field_not_editable` op `chargeItemCode` voor
 * niet-System; 400 bij onbekende artikelcode / onvolledige charge-koppeling; 409
 * `euromotive_code_required` bij `underConstruction:false` zonder EM-code.
 * B2-velden: alle RB_CompanyCompany-velden van de 5 resterende sub-tabjes.
 */
export interface ChainWritePayload {
  // B1-velden
  isCentralCustomer?: boolean;
  underConstruction?: boolean;
  visible?: boolean;
  checkByHQ?: boolean;
  hideInCallModule?: boolean;
  defaultPricelistId?: string | null;
  brutoPricelistId?: string | null;
  nettoPricelistId?: string | null;
  calculationOriginId?: string | null;
  finishedCaseStateId?: string | null;
  policyServiceId?: string | null;
  createBrutoNettoInvoices?: boolean;
  chargeRepairMode?: ChargeMode;
  chargeRepairValue?: number | null;
  chargeRepairItemCode?: string | null;
  chargeReplaceMode?: ChargeMode;
  chargeReplaceValue?: number | null;
  chargeReplaceItemCode?: string | null;
  chargeItemCode?: string | null;
  repairReimbursementItemCode?: string | null;
  replaceReimbursementItemCode?: string | null;
  protocol?: string | null;
  // B2: Korting (lease)
  brutoDiscountRepairIncassoItemCode?: string | null;
  brutoDiscountReplaceIncassoItemCode?: string | null;
  brutoDiscountRepairExcassoItemCode?: string | null;
  brutoDiscountReplaceExcassoItemCode?: string | null;
  volumeDiscountRepairIncassoItemCode?: string | null;
  volumeDiscountReplaceIncassoItemCode?: string | null;
  volumeDiscountRepairExcassoItemCode?: string | null;
  volumeDiscountReplaceExcassoItemCode?: string | null;
  volumeDiscountRepairPercentage?: number | null;
  volumeDiscountRepairFixed?: number | null;
  volumeDiscountReplacePercentage?: number | null;
  volumeDiscountReplaceFixed?: number | null;
  // B2: Rapporten & mails
  declarationReportFunctionId?: string | null;
  brutoDiscountDeclarationReportId?: string | null;
  declarationMailBodyReportId?: string | null;
  policyCheckEmalBodyReportId?: string | null;
  declarationMailSubject?: string | null;
  attachmentName?: string | null;
  declarationEmailAddress?: string | null;
  paymentReminderEmail?: string | null;
  policyCheckEmailAddress?: string | null;
  policyCheckEmailSender?: string | null;
  policyCheckEmailSubject?: string | null;
  sendAppointmentEmailInsurer?: boolean | null;
  sendAppointmentEmailDriver?: boolean | null;
  // B2: Intake
  intakeNotificationEmail?: string | null;
  intakeNotificationSubject?: string | null;
  intakeNotificationBodyReportId?: string | null;
  intakeMessage?: string | null;
  calculationMessage?: string | null;
  // B2: Voorexpertise
  insurerPreExpertiseRuleId?: string | null;
  insurerPreExpertiseReasons?: string | null;
  insurerPreExpertiseViaHQ?: boolean | null;
  insurerPreExpertiseReportId?: string | null;
  insurerPreExpertiseEmailAddress?: string | null;
  insurerPreExpertiseWarningStoredProcedure?: string | null;
  preExpertiseRuleStoredProcedure?: string | null;
  preExpertiseStatusId?: string | null;
  preExpertiseApprovedStatusId?: string | null;
  preExpertiseDisApprovedStatusId?: string | null;
  preExpertiseInfoMessage?: string | null;
  // B2: Artikelen & adviesprijzen
  useSuggestedRetailPrices?: boolean | null;
  retailRaisePercentageWindshield?: number | null;
  retailRaisePercentageBacklight?: number | null;
  retailRaisePercentageBodyGlass?: number | null;
}

/**
 * Write-payload voor `PUT /api/insurers/{id}/chain/additive-groups`.
 * Delete-all-then-re-add patroon; max 2 groepen.
 * 400 bij > 2 items of onbekende ID.
 */
export interface ChainAdditiveGroupsWritePayload {
  additiveGroupIds: string[];
}

/** Response van `PUT /api/insurers/{id}/chain` — afgeleide cascade voor de FE-sync. */
export interface ChainWriteResponse {
  insurerId: string;
  isCentralCustomer: boolean;
  underConstruction: boolean;
  underConstructionForced: boolean;
  canManageChain: boolean;
  canEditEuroMotiveCode: boolean;
}

// ─── Keten-fase B3: de twee keten-lijst-popups (RB_CompanyCompanyReport /
//     RB_CompanyCompanyAttachment) — story `fe-relatiebeheer-verzekeraars-keten-b3` ──

/**
 * Eén "extra declaratie-rapport"-regel van een verzekeraar-keten
 * (`RB_CompanyCompanyReport`). `ordinal` is server-toegekend (max+1; niet door de
 * gebruiker invoerbaar). De `*Id`-GUID's voeden de dropdown-vóór-selectie; de
 * `*Name`-labels zijn read-only.
 */
export interface ChainReportRow {
  id: string;
  ordinal: number;
  reportId: string | null;
  reportName: string | null;
  bodyReportId: string | null;
  bodyReportName: string | null;
  sender: string;
  receiver: string;
  cc: string;
  subject: string;
  fileName: string;
  condition: string;
}

/**
 * Write-payload voor `POST/PUT .../chain/reports`. `reportId` verplicht (BE 400
 * `report_required`). De vrije velden zijn optioneel; afwezig → lege string BE-side.
 */
export interface ChainReportWritePayload {
  reportId: string;
  bodyReportId?: string | null;
  sender?: string;
  receiver?: string;
  cc?: string;
  subject?: string;
  fileName?: string;
  condition?: string;
}

/**
 * Eén dossier-bijlage-regel van een verzekeraar-keten
 * (`RB_CompanyCompanyAttachment`). Booleans komen als echte `bool` terug (niet
 * null); `minimumQuantity` is een int (BE-default: 1 als mandatory && <=0).
 */
export interface ChainAttachmentRow {
  id: string;
  attachmentCategoryId: string | null;
  attachmentCategoryName: string | null;
  mandatory: boolean;
  mandatoryWhenAvaillable: boolean;
  forbidden: boolean;
  minimumQuantity: number;
}

/**
 * Write-payload voor `POST/PUT .../chain/attachments`. `attachmentCategoryId`
 * verplicht (BE 400 `attachment_category_required`); dubbele categorie → 400
 * `attachment_category_duplicate`.
 */
export interface ChainAttachmentWritePayload {
  attachmentCategoryId: string;
  mandatory?: boolean;
  mandatoryWhenAvaillable?: boolean;
  forbidden?: boolean;
  minimumQuantity?: number | null;
}

/**
 * Keuzelijst-item voor de rapport- en bijlage-categorie-dropdowns
 * (`GET .../chain/report-options` resp. `.../chain/attachment-category-options`).
 * Wire-shape `{ id, description }` — gelijk aan `LookupOption`.
 */
export interface ChainListOption {
  id: string;
  description: string;
}

/**
 * Write-payload voor `PUT /api/insurers/{id}` (story
 * `fe-relatiebeheer-verzekeraars-fase-a2`, 2026-06-24). Per-veld auto-save:
 * stuur alleen de gewijzigde velden (partiële merge — een **afwezig** veld blijft
 * ongemoeid; een **aanwezig** veld, ook `null`, wordt geschreven). Een PUT op een
 * read-only veld (EuroMotiveCode/Parent) → 403 `field_not_editable`; 403 bij
 * niet-`canManageInsurer`. De dropdowns slaan de GUID op (`*Id`).
 */
export interface InsurerWritePayload {
  // Maatschappij (RB_Company)
  name?: string;
  kvkName?: string;
  website?: string;
  kvkNumber?: string;
  vatNumber?: string;
  vatAccountable?: boolean;
  countryId?: string | null;
  status?: 'active' | 'inactive';
  // Instellingen (UA_InsuranceCompany)
  wamCode?: string;
  porCode?: string;
  policyServiceId?: string | null;
  isAuthorizedAgent?: boolean;
  isLeaseCompany?: boolean;
  isFleetOwner?: boolean;
  damageNumberRequired?: boolean;
  ignoreCustomerNotification?: boolean;
  defaultPricelistId?: string | null;
  exchangeRelationNumber?: number | null;
  dispatchRelationNumber?: string;
  rulesProcedure?: string;
  // Polis
  policyFileFolder?: string;
  policyFormatId?: string | null;
  /**
   * De code van de groene kaart (E3). Wordt server-side genormaliseerd met
   * DEZELFDE functie als het kaartpad (K3); een invoer die niet normaliseert
   * levert **400** met `reason: 'greencard_code_invalid'` + `field`.
   *
   * ⚠ `null`/`''` **wist** de code (E6) — de maatschappij valt dan terug op
   * tonen-only. Een AFWEZIG veld blijft ongemoeid (partiële merge).
   */
  greenCardCode?: string | null;
  // Opmerkingen
  notes?: string;
}

/**
 * Antwoord van `PUT /api/insurers/{id}`.
 *
 * ⚠ `sharedWith` is er sinds de groene-kaart-code-story (E5): delen van één code
 * door meerdere maatschappijen is **toegestaan** (in de meting draagt `118` er
 * vijf), dus de PUT weigert niet. De lijst bestaat om een TYPEFOUT (`118` in
 * plaats van `110`) te kunnen onderscheiden van een echt gedeelde code — en de
 * eerste is precies de fout waarvan de story zegt dat hij vanaf dat moment
 * iedere kaart met die code aan de verkeerde verzekeraar koppelt.
 *
 * ⚠ **Altijd een array, leeg bij een unieke code** — nooit `null`.
 */
export interface InsurerUpdateResult {
  insurerId: string;
  sharedWith: GreenCardInsurerMatch[];
  /**
   * De code van de groene kaart **zoals hij nu is opgeslagen** — dus
   * genormaliseerd, niet zoals hij is ingetikt.
   *
   * ⚠⚠ Het scherm stuurt de RUWE invoer (het normaliseert bewust niet zelf: één
   * definitie, K3). Zonder dit veld blijft `NL/118` in beeld staan terwijl er
   * `118` in de database staat — de data klopt, de weergave niet, tot de
   * volgende GET.
   *
   * ⚠ **Alleen betekenisvol wanneer DEZE PUT het veld droeg**; anders `null`.
   * Lees hem dus uitsluitend wanneer je `greenCardCode` hebt meegestuurd.
   */
  greenCardCode: string | null;
}

/**
 * Create-payload voor `POST /api/insurers`. Minimaal `name` + `wamCode` +
 * `porCode` (natural key; BE 400 als één ontbreekt, 409 bij dubbele Wam/Por).
 */
export interface InsurerCreatePayload {
  name: string;
  wamCode: string;
  porCode: string;
}

/** Response van `POST /api/insurers` — de nieuwe verzekeraar-id voor navigatie. */
export interface InsurerCreated {
  insurerId: string;
}

/**
 * Write-payload voor `PUT /api/insurers/{id}/debtor` (debiteur per hersteller).
 * `repairerCompanyId` is optioneel: een glashersteller laat 'm leeg (de BE
 * gebruikt de eigen company); System/HK sturen de gekozen hersteller mee
 * (verplicht voor hen). De overige velden zijn de bewerkbare debiteur-
 * instellingen (partiële merge). Cross-company → 403/404.
 */
export interface InsurerDebtorWritePayload {
  repairerCompanyId?: string | null;
  /** Alleen de vestiging bij haar eigen debiteur (AC 3/4): 400/409 bij een ongeldig of bezet nummer. */
  debtorNumber?: number;
  debtorGroupId?: string | null;
  debtorListId?: string | null;
  pricelistId?: string | null;
  currencyId?: string | null;
  ledgerAccount?: string;
}

/** Eén zoekresultaat uit `GET /api/relations/search(-by-address)`. */
export interface RelationSearchResult {
  relationId: string;
  name: string;
  address: string;
  zipCode: string;
  city: string;
  /**
   * Story `be-fe-exchange-klant-koppelen` (Story A) — bedrijfstype-label
   * "Persoon" | "Bedrijf" | `null`. Alleen gevuld door `search-by-address`
   * (de klant-koppel-popup toont Particulier/Bedrijf); `search` laat 'm `null`.
   */
  companyType?: string | null;
  /**
   * Story `be-fe-exchange-klant-koppelen` (Story A) — debiteurnummer (goedkoop
   * uit de search-SP). `null` = geen debiteur.
   *
   * Sinds migratie 102 (19-09-2026) vult `search` dit veld ÓÓK: je kunt op het
   * debiteurnummer zoeken, dus toont het zoekvenster het er ook bij.
   */
  debtorNumber?: string | null;
}

/**
 * Scope van `GET /api/relations/search` (backlog 32, besluit eigenaar 15-10-2026).
 * `'own'` (standaard) = alleen de eigen klanten — de set die koppelen aan een dossier
 * accepteert. `'own_and_parent'` = ook het bovenliggende bedrijf; alleen voor
 * Inrichting → Eigen dossiersoorten.
 */
export type RelationSearchScope = 'own' | 'own_and_parent';

/**
 * Eén polis-suggestie-rij uit `GET /api/dossiers/{id}/policy-suggestions?license=`
 * (story `fe-polis-suggestie-kenteken`). Het endpoint doorzoekt `UA_Policy` op
 * (genormaliseerd) kenteken, company-gescoped, en geeft de gevonden polissen
 * terug zodat de Intake-tab er een keuze-popup van toont.
 *
 * Contract-regels (zie story §"Response-DTO-contract"):
 *  - `id` (= `UA_Policy.ID`) is altijd aanwezig en uniek per rij → React-key +
 *    stabiele selectie-identiteit.
 *  - Alle overige velden zijn nullable en komen als `null` terug (niet `""`,
 *    niet weggelaten) — de FE rendert een placeholder ("—") voor lege velden.
 *  - `validFrom` / `validTo` zijn ISO-8601-strings (of `null`). `validFrom`
 *    wordt **alleen in de popup getoond**, niet opgeslagen; `validTo` landt bij
 *    selectie op het dossier-veld `policyValidUntil`.
 *  - Sortering komt al van de BE: `validTo` aflopend, `null`-`validTo` onderaan.
 */
export interface PolicySuggestion {
  id: string;
  insuranceCompanyId: string | null;
  insuranceCompanyName: string | null;
  policyNumber: string | null;
  insuranceTypeId: string | null;
  insuranceTypeName: string | null;
  validFrom: string | null;
  validTo: string | null;
  /**
   * Dekkings-velden (story-uitbreiding 2026-06-27): worden bij selectie
   * mee-overgenomen in het polisdeel, naast de 4 basisvelden — maar alleen
   * wanneer de dekking voor dit dossier bewerkbaar is (anders 403 op
   * Lease/Fleet, zie `handlePolicySelect` in IntakeTab). Wire-keys spiegelen
   * `DossierPolicy`: `excessRepair` / `excessReplacement` (EUR) + `vatIncluded`
   * (bool). `null` = leeg → niet meesturen (geen overschrijven).
   */
  excessRepair: number | null;
  excessReplacement: number | null;
  vatIncluded: boolean | null;
}

/**
 * Volledige relatie-detail uit `GET /api/relations/{id}`. In tegenstelling tot
 * `RelationSearchResult` (één samengestelde `address`-string voor de zoeklijst)
 * zijn de adres-velden hier gesplitst, zodat het bestuurder-formulier volledig
 * gevuld kan worden — net als het klant-koppel-pad dat al doet.
 */
export interface RelationDetail {
  relationId: string;
  name: string;
  address: string;
  houseNumber: string;
  houseNumberExtension: string;
  zipCode: string;
  city: string;
  phone: string;
  mobile: string;
  email: string;
  doesNotProvidePhone: boolean;
  doesNotProvideEmail: boolean;
}

/**
 * Story `fe-relatiebeheer-fase1-B` (2026-06-23) — uitgebreide klant-detail uit
 * `GET /api/relations/{id}/klant-detail` voor het klant-detailscherm. Apart van
 * `RelationDetail` (bestuurder-shape, `MailAddress*`): dit levert het
 * **bezoekadres** (`VisitorAddress*` uit het `_default`-contact, BE-4) + de
 * bedrijfs-/relatie-velden (KvK, BTW, website, parent-naam, bedrijfstype,
 * status, debiteurnr., Bovag-partner). Lege velden komen als `""` (BE trimt +
 * null→`""`); `companyType`/`debtorNumber` kunnen `null` zijn.
 */
export interface RelationKlantDetail {
  relationId: string;
  name: string;
  companyType: 'Persoon' | 'Bedrijf' | null;
  kvkNumber: string;
  vatNumber: string;
  website: string;
  parentName: string;
  status: 'active' | 'inactive';
  debtorNumber: number | null;
  /** Bezoekadres (`_default`-contact · `VisitorAddress*`). */
  zipCode: string;
  houseNumber: string;
  houseNumberExtension: string;
  street: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  mobile: string;
  bovagPartner: boolean;
  doesNotProvideEmail: boolean;
  doesNotProvidePhone: boolean;
}

/**
 * Eén relatie-opmerking uit `GET /api/relations/{id}/comments`. Spiegelt
 * `DossierComment` zodat de FE dezelfde rail-card + drawer kan voeden. Bron:
 * `GD_Communication` (`SubjectID = RB_GenericRelation.ID`) — niet
 * `UC_Registration` (dat is een uren-registratie, geen opmerkingen-store; BE-8).
 */
export interface RelationComment {
  id: string;
  /** ISO-string. */
  createdDate: string;
  createdByName: string | null;
  /** Body kan HTML zijn; drawer rendert via `sanitizeHtml`. */
  body: string;
  communicationType?: string | null;
}

/** Opmerkingen-historie van een relatie — `count` gelijk aan `items.length`. */
export interface RelationComments {
  count: number;
  items: RelationComment[];
}

/**
 * Story `fe-relatiebeheer-fase1-C` (2026-06-23) — write-payload voor
 * `PUT /api/relations/{id}` (per-veld auto-save). Alle velden optioneel zodat
 * de FE per-veld kan PATCH'en; de BE schrijft alleen wat meegestuurd is.
 * `companyType` is de natural-key-string ("Persoon" | "Bedrijf"), niet een GUID.
 * `status`: "active" | "inactive" (soft-deactivate, geen hard delete).
 */
export interface RelationWritePayload {
  name?: string;
  companyType?: 'Persoon' | 'Bedrijf';
  kvkNumber?: string;
  vatNumber?: string;
  website?: string;
  zipCode?: string;
  houseNumber?: string;
  houseNumberExtension?: string;
  street?: string;
  city?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  bovagPartner?: boolean;
  doesNotProvideEmail?: boolean;
  doesNotProvidePhone?: boolean;
  status?: 'active' | 'inactive';
}

/**
 * Create-payload voor `POST /api/relations`. Minimaal `name` + `companyType`
 * (BE 400 als één ontbreekt); de rest is optioneel. Status default Actief.
 */
export interface RelationCreatePayload extends RelationWritePayload {
  name: string;
  companyType: 'Persoon' | 'Bedrijf';
}

/** Response van `POST /api/relations` — de nieuwe relatie-id voor navigatie. */
export interface RelationCreated {
  relationId: string;
}

// ─── Relatiebeheer fase 2 — Contacten-tab (master-detail) ───────────────────

/**
 * Story `fe-relatiebeheer-fase2-contacten-tab` (2026-06-23) — één contactpersoon
 * van een relatie (`GET /api/relations/{id}/contacts`). `isDefault === true` is
 * het hoofdcontact (`Code == "_default"`): contacttype vast op "Hoofdcontact",
 * niet verwijderbaar, gesynct met de Relatie-tab. Lege velden komen als `""`
 * (BE trimt + null→`""`).
 *
 * **Twee echte adressen** (PO-fix 2026-06-23): een contact heeft een
 * **Postadres** (`mail*` ← `MailAddress*`) én een **Bezoekadres** (`visit*` ←
 * `VisitorAddress*`), GESCHEIDEN bewerkbaar. De Relatie-tab toont alléén het
 * bezoekadres van het hoofdcontact. `hasOwnAddress` = `true` zodra één van beide
 * sets gevuld is.
 */
export interface RelationContact {
  contactId: string;
  isDefault: boolean;
  /** "Hoofdcontact" | "Medewerker" | "Crediteurenadministratie" | null. */
  contactType: string | null;
  /** Stabiele natural-key Code ("_default" | "medewerker" | "AdministratieContactPersoon" | null). */
  contactTypeCode: string | null;
  titleId: string | null;
  initials: string;
  firstName: string;
  prefix: string;
  surName: string;
  functionDescription: string;
  languageId: string | null;
  email: string;
  mobile: string;
  phone: string;
  hasOwnAddress: boolean;
  /**
   * `true` wanneer dit (extra) contact het adres van de relatie gebruikt —
   * d.w.z. NIET het hoofdcontact én `ParentID == company's DefaultContactID`
   * (parent-gated, PO-fix 2026-06-23). De FE toont dan geen adresvelden maar de
   * zin "gebruikt het adres van de relatie" + een actie "Eigen adres". Het
   * hoofdcontact zelf is altijd `false` (toont altijd z'n eigen adres).
   */
  usesRelationAddress: boolean;
  /** Postadres (`MailAddress*`). */
  mailStreet: string;
  mailHouseNumber: string;
  mailHouseNumberExtension: string;
  mailZipCode: string;
  mailCity: string;
  /** Bezoekadres (`VisitorAddress*`). */
  visitStreet: string;
  visitHouseNumber: string;
  visitHouseNumberExtension: string;
  visitZipCode: string;
  visitCity: string;
  /** Bankgegevens (`RB_Contact` — rapportage rekent hierop). */
  bankAccountIban: string;
  bankAccountBic: string;
  bankAccountName: string;
  bankAccountCity: string;
}

/** Response van `GET /api/relations/{id}/contacts` — actieve contacten, hoofdcontact bovenaan. */
export interface RelationContacts {
  items: RelationContact[];
}

/**
 * Write-payload voor `PUT /api/relations/{id}/contacts/{contactId}` (per-veld
 * auto-save) en — als basis — `POST /api/relations/{id}/contacts` (nieuw
 * contact). Alle velden optioneel; de BE schrijft alleen wat meegestuurd is.
 * `contactTypeCode` is de natural-key-string ("medewerker" |
 * "AdministratieContactPersoon"), niet een GUID. `status: "inactive"` = soft-
 * delete (RecordStatus → Archive), geen hard delete.
 *
 * Twee adressen GESCHEIDEN (PO-fix 2026-06-23): `mail*` → postadres
 * (`MailAddress*`), `visit*` → bezoekadres (`VisitorAddress*`).
 */
export interface RelationContactWritePayload {
  contactTypeCode?: string;
  titleId?: string | null;
  initials?: string;
  firstName?: string;
  prefix?: string;
  surName?: string;
  functionDescription?: string;
  languageId?: string | null;
  email?: string;
  mobile?: string;
  phone?: string;
  /** Postadres (`MailAddress*`). */
  mailStreet?: string;
  mailHouseNumber?: string;
  mailHouseNumberExtension?: string;
  mailZipCode?: string;
  mailCity?: string;
  /** Bezoekadres (`VisitorAddress*`). */
  visitStreet?: string;
  visitHouseNumber?: string;
  visitHouseNumberExtension?: string;
  visitZipCode?: string;
  visitCity?: string;
  /** Bankgegevens (`RB_Contact`). */
  bankAccountIban?: string;
  bankAccountBic?: string;
  bankAccountName?: string;
  bankAccountCity?: string;
  /**
   * Parent-gating van het adres (PO-fix 2026-06-23). `true` → "Gebruik
   * relatie-adres": re-attach de parent + maak de eigen adresvelden leeg.
   * `false` → "Eigen adres": detach de parent zodat het contact z'n eigen adres
   * gebruikt. Weglaten (`undefined`) → niet aangeraakt. Genegeerd voor het
   * hoofdcontact.
   */
  useRelationAddress?: boolean;
  status?: 'active' | 'inactive';
}

/**
 * Create-payload voor `POST /api/relations/{id}/contacts`. Minimaal
 * `contactTypeCode` + `surName` (BE 400 als één ontbreekt); de rest optioneel.
 */
export interface RelationContactCreatePayload extends RelationContactWritePayload {
  contactTypeCode: string;
  surName: string;
}

/** Response van `POST /api/relations/{id}/contacts` — de nieuwe contact-id. */
export interface RelationContactCreated {
  contactId: string;
}

/** Eén optie van de beperkte contacttype-keuzelijst (`GET /api/relations/contact-types`). */
export interface RelationContactType {
  /** Stabiele natural-key Code ("medewerker" | "AdministratieContactPersoon"). */
  code: string;
  /** Weer te geven label ("Medewerker" | "Crediteurenadministratie"). */
  label: string;
}

// ─── Relatiebeheer fase 3 — Financieel-tab (RB_Debtor-instellingen) ──────────

/**
 * Story `fe-relatiebeheer-fase3-financieel-tab` (2026-06-23) — de
 * debiteur-instellingen (`RB_Debtor`) van een relatie
 * (`GET /api/relations/{id}/financial`). De dropdown-velden dragen zowel de
 * GUID (`*Id`) als het label (`*Name`, read-only display zodat een stale GUID
 * z'n laatste label houdt; de FE overschrijft 'm niet stil naar leeg).
 */
export interface RelationFinancial {
  /** Debiteurnummer — read-only inline; wijzigen via de aparte popup-actie. */
  debtorNumber: number | null;
  debtorGroupId: string | null;
  debtorGroupName: string;
  debtorListId: string | null;
  debtorListName: string;
  pricelistId: string | null;
  pricelistName: string;
  currencyId: string | null;
  currencyName: string;
  ledgerAccount: string;
  invoiceText: string;
  paymentTermDays: number | null;
  vatExemption: boolean;
}

/**
 * Write-payload voor `PUT /api/relations/{id}/financial` (per-veld auto-save).
 * **Null-merge-contract**: een **afwezig** veld blijft op de BE ongemoeid; een
 * **aanwezig** veld (ook `null`) wordt geschreven (`null`/leeg → wissen). De FE
 * stuurt daarom typisch precies één veld per PUT en gebruikt expliciet `null`
 * om een dropdown leeg te maken. Dropdowns slaan de GUID op.
 */
export interface RelationFinancialWritePayload {
  debtorGroupId?: string | null;
  debtorListId?: string | null;
  pricelistId?: string | null;
  currencyId?: string | null;
  ledgerAccount?: string;
  invoiceText?: string;
  paymentTermDays?: number | null;
  vatExemption?: boolean;
}

/**
 * Story `fe-relatiebeheer-fase4-verkopen-tab` (2026-06-24) — één verkochte regel
 * (`UA_DossierRow`) in de Verkopen-tab. Alle velden 1-op-1 uit de bron (geen
 * herberekening BE-side). "Na korting" (stukprijs na korting) toont de FE als
 * pure display-aftrek (`unitPrice - discountAmount`).
 */
export interface RelationSalesRow {
  /** Type-label (`UE_ItemType.Description`), bv. "Eurocode" / "Montage" / "Kit". */
  type: string;
  /** Code (`UA_DossierRow.EuroCode`, fallback `CalculationCode`). */
  code: string;
  description: string;
  quantity: number | null;
  unitPrice: number | null;
  /** Korting-bedrag per stuk (`UA_DossierRow.UnitPriceDiscountAmount`). */
  discountAmount: number | null;
  /** Bedrag / regeltotaal (`UA_DossierRow.Amount`). */
  amount: number | null;
}

/**
 * Eén dossier-groep in de Verkopen-tab: kop (nummer, schadedatum, status), de
 * regels en de OPGESLAGEN totalen (excl. / btw / incl. — overgenomen, niet
 * herberekend).
 */
export interface RelationSalesDossier {
  /** GUID-string — de FE bouwt hiermee de `<Link to="/dossiers/{id}">`. */
  dossierId: string;
  dossierNumber: number | null;
  /** Schadedatum ISO-8601, of null. */
  damageDate: string | null;
  /** Status-label (`WF_CaseState.Description`), of null → FE toont em-dash. */
  status: string | null;
  totalExclVat: number | null;
  totalVat: number | null;
  totalInclVat: number | null;
  rows: RelationSalesRow[];
}

/**
 * Response van `GET /api/relations/{id}/sales` — de per-dossier gegroepeerde
 * verkoophistorie + samenvatting. `dossierCount` + `totalRevenueInclVat` zijn
 * over ALLE dossiers (niet alleen het gecapte deel). `truncated` is `true`
 * wanneer er meer verkoop-dossiers zijn dan getoond (cap = 100 recentste).
 */
export interface RelationSales {
  dossiers: RelationSalesDossier[];
  dossierCount: number;
  totalRevenueInclVat: number;
  truncated: boolean;
}

/** Response van `GET /api/address/lookup` (Pro6PP); `null` wanneer niets gevonden. */
export interface AddressLookupResult {
  streetName: string;
  cityName: string;
}

/** Eén contact-sectie zoals geleverd door `GET /api/dossiers/{id}/contacts`. */
export interface DossierContact {
  name: string | null;
  address: string | null;
  houseNumber: string | null;
  houseNumberExtension: string | null;
  zipCode: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  doesNotProvidePhone?: boolean | null;
  doesNotProvideEmail?: boolean | null;
  companyTypeId?: string | null;
  titleId?: string | null;
}

/**
 * Response van `GET /api/dossiers/{id}/contacts` — aparte sub-resource zodat
 * de hoofd-GET (`DossierDetail`) z'n single-load-invariant houdt (BE-keuze,
 * story `appservices-relaties-en-bestuurder` iter G).
 *
 * `isLeaseDossier`/`isFleetDossier` komen uit de **verzekeraar**
 * (`IsLeaseCompany`/`IsFleetOwner`), niet uit `dossier.insuranceSort` — dus
 * trust deze waarden ipv lokaal te derive'en.
 */
export interface DossierContacts {
  genericRelationId: string | null;
  isInsured: boolean;
  isLeaseDossier: boolean;
  isFleetDossier: boolean;
  customer: DossierContact | null;
  driver: DossierContact | null;
}

/**
 * Response van `POST /api/dossiers/{id}/relation/link` — een delta met de
 * direct-betrokken velden zodat de FE-cache zonder refetch kan bijwerken.
 * Géén `driver` en géén visibility-velden — die blijven gemerget vanuit de
 * eerder geladen `DossierContacts`.
 */
export interface LinkRelationResponse {
  genericRelationId: string;
  customer: DossierContact;
}

/**
 * Body van `POST /api/dossiers/{id}/relation/create-insured` — de gegevens van
 * de verzekerde die de hersteller invult wanneer hij een handmatige
 * dekkingscontrole aanvraagt op een dossier zonder gekoppelde klant.
 *
 * Verplicht (ook BE-side gevalideerd): `name`, `street`, `houseNumber`,
 * `zipCode`, `city`. De overige velden mogen leeg/`null` zijn.
 */
export interface CreateInsuredRelationRequest {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExtension?: string | null;
  zipCode: string;
  city: string;
  email?: string | null;
  phone?: string | null;
}

/**
 * Response van `POST /api/dossiers/{id}/relation/create-insured`.
 *
 * `alreadyLinked` is `true` wanneer het dossier bij aankomst al een relatie
 * had — de BE maakt er dan geen tweede aan en geeft de bestaande koppeling
 * terug (idempotent bij een retry).
 */
export interface CreateInsuredRelationResponse {
  genericRelationId: string;
  alreadyLinked: boolean;
}

/**
 * DTO voor GET /api/dossiers/{id} — V1 read-only.
 *
 * Veld-set is gespiegeld op de werkelijke kolommen van `UA_Dossier`
 * (schema-check 2026-05-19 + 2026-05-20 voor S007.8). Nullable DB-kolommen →
 * `null` in DTO, NOT NULL → DB-waarde. De UI rendert `null` als "—" (em-dash)
 * via `DetailField`.
 *
 * **A13 (S007.8)**: sub-objecten `policy`, `damage`, `calculation`,
 * `attachments`, `comments` zijn ALTIJD aanwezig (nooit null/missing), ook
 * voor dossier-types waar ze niet relevant zijn. Velden binnen sub-object
 * mogen null zijn. FE beslist tab-zichtbaarheid uitsluitend op `dossierType`.
 */
export interface DossierDetail {
  // Identificatie
  id: string;
  number: number | null;
  workOrderNumber: number | null;
  internalReference: string | null;

  // Voertuig
  license: string | null;
  manufacturerText: string | null;
  modelText: string | null;
  modelYear: number | null;
  numberOfKilometers: number | null;
  chassisNumber: string | null;

  // Klant
  customerName: string | null;
  customerAddress: string | null;
  customerHouseNumber: string | null;
  customerHouseNumberExtension: string | null;
  customerZipCode: string | null;
  customerCity: string | null;
  customerCountry: string | null;
  customerPhoneNumber: string | null;
  /** Mobielnummer — `UA_Dossier.MobilePhone`. */
  customerMobile: string | null;
  customerEmailAddress: string | null;
  /**
   * Spiegelt de klant-toggle `UA_Dossier.CustomerDoesNotProvidePhone`. Wanneer
   * `true` telt een ontbrekend telefoonnummer toch als compleet voor de
   * Klant-tab-"klaar"-bepaling ("geen telefoonnummer doorgeven").
   */
  customerDoesNotProvidePhone?: boolean;
  /**
   * Spiegelt de klant-toggle `UA_Dossier.CustomerDoesNotProvideEmail`. Wanneer
   * `true` telt een ontbrekend e-mailadres toch als compleet voor de
   * Klant-tab-"klaar"-bepaling ("geen e-mailadres doorgeven").
   */
  customerDoesNotProvideEmail?: boolean;
  /** Berijder-naam — `UA_Dossier.DriverName` (S007.8 v2). */
  driverName: string | null;

  // ─── Rest van het bestuurder-blok (gebruiker 01-10-2026) ──────────────────
  // Twee gebruikers: bij een LEASE/FLEET-dossier bestaat er alleen een
  // bestuurder en bepaalt dit blok of de Klant-tab klaar is; en de popup van de
  // handmatige dekkingscontrole vult de verzekerde-velden hiermee voor.
  // Optioneel zodat oudere detail-responses blijven werken.
  driverZipCode?: string | null;
  driverHouseNumber?: string | null;
  driverHouseNumberExtension?: string | null;
  /** De straat — `UA_Dossier.DriverAddress`. */
  driverAddress?: string | null;
  driverCity?: string | null;
  driverPhoneNumber?: string | null;
  driverMobile?: string | null;
  driverEmailAddress?: string | null;
  /** "Geen telefoonnummer doorgeven" — dan telt een leeg nummer toch als compleet. */
  driverDoesNotProvidePhone?: boolean;
  /** "Geen e-mailadres doorgeven" — idem. */
  driverDoesNotProvideEmail?: boolean;

  // Opdrachtgever (story `fe-opdrachtgever-debtor-autocomplete`, 2026-06-06)
  /**
   * `UA_Dossier.ReferenceDebtorID` — FK naar de gekoppelde debiteur
   * (`RB_Debtor`). Het Opdrachtgever-veld op de Intake-tab is sinds deze story
   * een gekoppelde debiteur i.p.v. losse vrije tekst. Null als geen debiteur.
   * De FE PATCHt een nieuwe selectie als `ReferenceDebtorID` via `/intake`.
   *
   * Optioneel om backwards-compat te garanderen met detail-responses van vóór
   * deze story (net als {@link version}); de BE vult 'm altijd in nieuwe
   * responses.
   */
  referenceDebtorId?: string | null;
  /**
   * Display-naam van de gekoppelde debiteur (`RB_Company.Name`). Null wanneer
   * geen debiteur gekoppeld. Gebruikt om de huidige Opdrachtgever te tonen bij
   * laden zonder extra lookup-call (samen met {@link referenceDebtorNumber}).
   */
  referenceDebtorName?: string | null;
  /** Debiteurnummer van de gekoppelde debiteur (`RB_Debtor.DebtorNumber`). */
  referenceDebtorNumber?: number | null;

  // ─── Herstellocatie (story S012.10d, 2026-06-09) ──────────────────────
  // Read-only round-trip van de herstellocatie-momentopname die de planbord-
  // afspraak-modal via de intake-passthrough schrijft (`RepairLocation*`-
  // setters → ApplyIntakeUpdate, S012.10c). De Locatie-tab seedt deze velden
  // in view + edit zodat een opgeslagen adres zichtbaar/voorgevuld is. Vlakke
  // camelCase-wire-keys — consistent met de overige vlakke `customer*`-velden.
  // Optioneel voor backwards-compat met detail-responses van vóór deze story.
  /** UA_Dossier.RepairLocationName — naam van de herstellocatie. */
  repairLocationName?: string | null;
  /** UA_Dossier.RepairLocationStreet — straat. */
  repairLocationStreet?: string | null;
  /** UA_Dossier.RepairLocationHouseNumber — huisnummer. */
  repairLocationHouseNumber?: string | null;
  /** UA_Dossier.RepairLocationHouseNumberExtension — huisnummer-toevoeging. */
  repairLocationHouseNumberExtension?: string | null;
  /** UA_Dossier.RepairLocationZipCode — postcode. */
  repairLocationZipCode?: string | null;
  /** UA_Dossier.RepairLocationCity — plaats. */
  repairLocationCity?: string | null;
  /** UA_Dossier.RepairLocationPhone — telefoon. */
  repairLocationPhone?: string | null;
  /** UA_Dossier.RepairLocationEmailAddress — e-mail. */
  repairLocationEmailAddress?: string | null;
  /** UA_Dossier.RepairLocationRemarks — opmerkingen herstellocatie. */
  repairLocationRemarks?: string | null;

  // Schade & herstel
  damageDate: string | null;
  repairDate: string | null;
  reportDate: string | null;

  // Workflow / verzekering
  /** WF_CaseState.Description — workflow-state, niet record-status. */
  status: string | null;
  insuranceCompanyId: string | null;
  insuranceCompanyName: string | null;
  policyNumber: string | null;
  isArchive: boolean;

  // ─── Archiveer-signalen (story fe-be-archiveren-dossier, 2026-06-08) ──
  /**
   * Server-derived blok-signaal voor de exchange-restrictie op de archiveer-
   * actie: `true` wanneer `isExchangeDossier && exchangeStatus !== "Geannuleerd"`.
   * De FE disable't de kebab-actie hierop; de BE her-valideert exact dezelfde
   * regel. Optioneel voor backwards-compat met responses van vóór deze story.
   */
  exchangeBlocksArchive?: boolean | null;
  /**
   * Stabiele huidige `WF_CaseState.StatusValue` (int) van de oorsprong-case.
   * Gebruikt voor de status-blok (∈ {277, 266}) van de archiveer-actie + de
   * "Openstaand"-filter-uitsluiting (status 300). Null bij geen state.
   */
  statusValue?: number | null;
  /**
   * Of er een `UA_Intake` bestaat voor dit dossier. De bevestigingsdialog toont
   * het verplichte reden-veld alleen wanneer `true`.
   */
  hasIntake?: boolean | null;
  /**
   * HK-only-uitbreiding (2026-06-08): vereist archiveren een hoofdkantoor-
   * gebruiker? `true` wanneer de status 241 is OF het hoofdkantoor het dossier
   * ooit heeft goedgekeurd. De FE disable't het "Archiveer"-menu-item wanneer
   * `archiveRequiresHeadquarters && !callerIsHeadquarters`. De BE her-valideert
   * (403 `headquarters_only_archive`).
   */
  archiveRequiresHeadquarters?: boolean | null;
  /**
   * Of de huidige gebruiker een hoofdkantoor-gebruiker is op dit dossier
   * (= `isHeadquartersUser`). Specifiek voor de archiveer-disable-keten.
   */
  callerIsHeadquarters?: boolean | null;

  // Overzicht-extras (S007.8)
  /** Server-side afgeleid type. Zie {@link DossierType}. */
  dossierType: DossierType;
  /** Schade-/meldcode (`UA_Dossier.MeldCode`). */
  damageNumber: string | null;
  /** Provisiebedrag (`UA_Dossier.ProvisionAmount`). */
  commissionAmount: number | null;
  /** Monteur-naam (`UA_Dossier.Fitter`). */
  mechanicName: string | null;
  /** Volmacht-id — null als geen volmacht (ongeacht type). */
  authorizedAgentId: string | null;
  /** Volmacht-naam — uit `RB_Company.Name` via `UA_AuthorizedAgent.CompanyID`. */
  authorizedAgentName: string | null;

  // Sub-objecten (S007.8, A13: altijd aanwezig)
  policy: DossierPolicy;
  damage: DossierDamage;
  /**
   * Voertuig-sub-object — story `fe-voertuig-tab-editable` (2026-05-30).
   * Altijd aanwezig; cross-tab-velden gespiegeld vanuit DTO-root.
   */
  vehicle: DossierVehicle;
  calculation: DossierCalculation;
  attachments: DossierAttachment[];
  comments: DossierComments;

  // Systeemgegevens / audit
  created: string | null;
  createdByName: string | null;
  modified: string | null;
  modifiedByName: string | null;

  /**
   * S007.11 — Optimistic-concurrency-versie. Wordt door BE meegeleverd in
   * elke detail-respons; FE stuurt 'm mee in PATCH `/intake` body als
   * `version`. Optioneel om backwards-compat te garanderen op responses van
   * vóór S007.11 (oudere endpoint-versies).
   */
  version?: number;

  // ─── Intake-velden (S007.11, G-01) ───────────────────────────────────
  // Deze velden zaten ontbraken in S007.8 maar zijn nodig voor de
  // Intake-tab. Zonder deze velden zou `extractIntakeFieldsFromDetail`
  // de form initialiseren met `null` voor data die wél in DB staat —
  // een PATCH die andere velden wijzigt zou daardoor impliciet de
  // bestaande waardes overschrijven (data-overwrite-risk).
  // Alle optional/nullable conform DB-schema (kolommen mogen NULL zijn).

  /** UA_Dossier.Comment — opmerking (intake). */
  comment?: string | null;
  /** UA_Dossier.CommentRepairer — opmerking voor hersteller. */
  commentRepairer?: string | null;
  /**
   * UA_Dossier.IsSalesDossier — true voor B2B / "Niet verzekerd".
   * Samen met `isOffer` bepaalt het de Dossiersoort-dropdown.
   */
  isSalesDossier?: boolean | null;
  /** UA_Dossier.IsOffer — true voor offerte. */
  isOffer?: boolean | null;
  /** UA_Dossier.RepairTimeText — vrij-tekst-tijdstip (Tijdstip-veld). */
  repairTimeText?: string | null;
  /** UA_Dossier.ModelSetText — modelfamilie (UI-label Model in Intake). */
  modelSetText?: string | null;
  /** UA_Dossier.ExtraRepairDate — secundaire herstel-datum. */
  extraRepairDate?: string | null;
  /** UA_Dossier.TypeDescription — vrij-tekst-type voertuig. */
  typeDescription?: string | null;
  /** UA_Dossier.RepairerReference — referentie hersteller. */
  repairerReference?: string | null;
  /** UA_Dossier.FormatLicense — handmatig voertuiggegevens invoeren. */
  formatLicense?: boolean | null;
  /**
   * UA_Dossier.FitterIsReady — "Monteur gereed"-vlag. Verhuisd van het
   * Schade-sub-object (waar 't ten onrechte als `damage.repairReady`
   * stond) naar root in story-A `fe-schade-tab-editable`. Wordt
   * gerenderd als "Klaar"-checkbox op de Intake-tab + als "Monteur
   * gereed"-rij op de Overview-tab. PATCH gaat via /intake.
   */
  fitterIsReady?: boolean | null;

  // ─── Polis-bron-velden (S007.13) ─────────────────────────────────────
  // Bron-flags + service-codes voor het Polisscherm. Backend levert deze
  // velden optioneel (V1 mogelijk leeg in mapper). De pure
  // `derivePolicyScreenState` behandelt missing/undefined als geen-bron.

  /** UA_Dossier.IsExchangeDossier — eXchange-dossier-flag. */
  isExchangeDossier?: boolean | null;
  /** UA_Dossier.IsDispatchDossier — Dispatch-dossier-flag. */
  isDispatchDossier?: boolean | null;
  /** UA_Dossier.IsUBench — uBench-dossier-flag. */
  isUBench?: boolean | null;
  /** Afgeleid uit UA_InsuranceCompany — true bij LocalDatabase-polisservice. */
  isLocalPolicy?: boolean | null;
  /**
   * True wanneer de volmacht (of zonder volmacht de verzekeraar) de RAUWE
   * polisservice-code `ExchangeOfPolisbestand` draagt (volmacht Voogd & Voogd).
   * `policyService.code` is dan al geresolved naar `eXchange` of `LocalDatabase`,
   * dus deze vlag onderscheidt de polisbestand-arm van die volmacht van een échte
   * LocalDatabase-verzekeraar — alleen die arm mag handmatige invoer openzetten.
   */
  isExchangeOrPolicyFileService?: boolean | null;
  /**
   * True zodra de laatste poliscontrole geen polis vond (`PolicyNotFound` in
   * `policyCoverageMessage`). Wordt weer false zodra een controle wél een polis
   * vindt.
   */
  policyCheckedNotFound?: boolean | null;
  /**
   * Heeft de laatste poliscontrole een POLIS gevonden? `null` = nog geen
   * controle gedaan. Voedt de dekkings-mededeling op de Polis-tab.
   *
   * ⚠ Bewust NÁÁST `policyCheckedNotFound`: die vlag leest alleen
   * `PolicyNotFound` uit de audit-blob en mist het normale geval "leeg
   * polis-object terug" — dan staat die vlag op `false` terwijl er niets
   * gevonden is. Deze vlag spiegelt de regel die de uitkomst bepaalt: polisnummer
   * ÉN verzekeringssoort gevuld.
   */
  policyCheckFoundPolicy?: boolean | null;
  /**
   * Het antwoord van de maatschappij op de dekkingsaanvraag per mail, zodra de
   * postvak-verwerking het heeft gekoppeld. Alleen gevuld bij
   * `coverageState === 'pending_async'`. Geen dekkings-signaal: het zegt dát er
   * geantwoord is, niet wát.
   */
  policyMailReply?: {
    /** Echte UTC (met `Z`), dus direct te tonen. */
    receivedAt: string;
    /**
     * De aanvraag waar dit antwoord bij hoort — letterlijk de waarde van
     * `policy.coverageCheckedAt`. Alleen voor die vergelijking; ⚠ niet tonen en
     * niet met `receivedAt` vergelijken (`coverageCheckedAt` is lokale tijd met
     * een `Z`).
     */
    requestedAt: string;
    /** Antwoord niet automatisch te lezen: een mens moet het lezen. */
    needsReview: boolean;
  } | null;
  /**
   * Story `fe-be-handmatige-dekkingscontrole` (2026-08-24) — mag de hersteller
   * voor dit dossier een handmatige dekkingscontrole aanvragen (→ status 222)?
   *
   * BE-afgeleid, samengesteld uit drie dingen zodat de FE er geen tweede
   * werkelijkheid van maakt:
   *   1. de bestaande instelling `UF_CompanyInvoiceSetting.ManualCoverageDisabed`
   *      op `(AuthorizedAgent ?? InsuranceCompany).Company` — let op de INVERSE
   *      semantiek: geen rij = **toegestaan** (herziening H1);
   *   2. de **lokale-polis-arm** (`isLocalPolicy`) — de legacy-feature is
   *      aantoonbaar een lokale-polis-feature en we lopen daarmee gelijk (H16);
   *   3. er is een verzekeraar (zonder partij valt er niets aan te vragen).
   *
   * Dit is uitsluitend de **trigger** (popup, knop, transitie). Het **slot** op
   * de negen polisvelden keyt op iets anders — puur `StatusValue == 222` —
   * omdat `isLocalPolicy` een gedocumenteerde null-modus heeft en een slot dat
   * daarop keyt stil open zou blijven staan (H17).
   */
  manualCoverageRequestAllowed?: boolean | null;
  /** UA_Dossier.IsNH1816 — NH1816-specifieke-flag. */
  isNH1816?: boolean | null;
  /** UA_Dossier.LastDispatchResultCode — 'D' = definitief geleverd. */
  lastDispatchResultCode?: string | null;
  /** UA_Dossier.ExternalDossierMessage — 'D' = Carculate/uBench definitief. */
  externalDossierMessage?: string | null;
  /** UA_Dossier.ExternalDossierNumber — extern dossiernummer (Carculate/Dispatch). */
  externalDossierNumber?: string | null;
  /**
   * Story `fe-be-ehbo-tab` (2026-07-04) — `UA_Dossier.DispatchBrand` /
   * `DispatchModel` (eigen dispatch-koppelvelden). Optioneel: alleen bewerkbaar
   * via de EHBO-tab; niet elke detail-respons vult ze (afwezig → EHBO toont
   * leeg). Los van `DispatchCarInformation`/`DispatchStatusDescription`
   * (read-only sync, buiten scope).
   */
  dispatchBrand?: string | null;
  dispatchModel?: string | null;
  /**
   * UA_Dossier.ExchangeDossierNumber — eXchange-portaal-dossiernummer (int,
   * sentinel `0` betekent "geen"). Wire-key `exchangeDossierNumber`; root-veld
   * op DossierDetail (NIET onder `policy.*`).
   */
  exchangeDossierNumber?: string | null;
  /**
   * Door eXchange geleverde status (bv. "Open", "Gereed"), uit
   * UA_Dossier.ExchangeStatusCode (fallback ExchangeStatusDescription). Getoond in de
   * linker context-rail achter het eXchange-dossiernummer. `null` = geen eXchange-status.
   */
  exchangeStatus?: string | null;
  /** UA_Dossier.CheckViaMail — NH1816 mail-pad. */
  checkViaMail?: boolean | null;
  /** UA_Dossier.MailPolicyCheckRequested — mail-aanvraag verstuurd. */
  mailPolicyCheckRequested?: boolean | null;
  // VERWIJDERD 2026-09-14 — `canCheckPolicyForNH1816`. Story
  // `fe-be-iclaim-poliscontrole-en-aanbieden` fase 1 (K3): de poliscontrole-
  // poort voor NH1816-dossiers is vervallen (controle is nu altijd mogelijk).
  // Het BE stuurt het veld niet meer; het kostte per dossier-opening een
  // Domain-evaluatie (SP `spRepairerHasConcept`).
  /** UA_Dossier.CommentInsuranceCompany — Dispatch-link / -instructie. */
  commentInsuranceCompany?: string | null;
  /** UA_Dossier.ExchangeAppointmentUrl — KlantVertellen-link. */
  exchangeAppointmentUrl?: string | null;
  /**
   * S007.14d Delta 1 — dossier-specifieke eligibility-flag voor
   * "Controleer polis via mail". Berekend via Domain-aanroep
   * `Dossier.RepairerCanCheckPolicyByMail(agent ?? insurer)`. Was eerder
   * `currentUser.repairerCanCheckPolicyByMail`, maar dat veld is niet
   * dossier-onafhankelijk te bepalen (repairer × verzekeraar).
   */
  repairerCanCheckPolicyByMail?: boolean | null;
  /**
   * S007.14d Gate 2 — blokkerende meldingen voor mail-poliscontrole,
   * uit legacy `Dossier.CanCheckPolicyByMail()`. FE filtert zelf op
   * `category === '1'` (blokkerend). Leeg/ontbrekend = geen blokkades.
   */
  mailPolicyCheckBlockingReasons?: MailPolicyCheckRule[] | null;
  /**
   * Type-verzekerd-keuze van de hersteller. Opgeslagen in `GD_FieldValue`
   * sidecar (GD_FieldGroup.Name='UE_DossierExtra',
   * GD_FieldGroupField.Name='insurancesort') sinds S007.14c — NIET als kolom
   * op `UA_Dossier`. Mogelijke waardes: 'Verzekerd' / 'Lease' / 'Fleetowner'.
   * Null voor legacy dossiers zonder sidecar-rij.
   */
  insuranceSort?: string | null;
  /**
   * S007.15 — JSON-audit-blob van de laatste dekkingscontrole
   * (geserializeerde `PolicyHistory`). Geschreven door
   * `DomainPolicyCheckExecutor` bij elke `POST /policy/check`.
   * Null als nog nooit een controle is uitgevoerd.
   */
  policyCoverageMessage?: string | null;
  /** UA_Dossier.DamageCauseID — geselecteerde oorzaak. */
  damageCauseId?: string | null;
  /** UA_Dossier.InsuranceCompanySpecificationID — geselecteerd filiaal van verzekeraar. */
  insuranceCompanySpecificationId?: string | null;
  /**
   * Polis-service-object. `code` is een legacy CamelCase-string-code:
   * 'eXchange' / 'Mail' / 'LocalDatabase' / 'Dispatch' / 'CarculateNew' /
   * 'CarculateService' / 'Ubench' / 'Handmatigdoorverzekeraar' / 'None'.
   * Null wanneer de verzekeraar geen polisservice-koppeling heeft.
   */
  policyService?: {
    code: string | null;
    /**
     * Story `be-fe-dekking-d1-popup-en-slot` (gat 18) — wat deze service kan, uit
     * de ENE lijst van de server: `automatic` · `mail` · `none` · `unknown`.
     * Ontbreekt bij een oudere backend; de FE valt dan terug op de code.
     */
    checkKind?: 'automatic' | 'mail' | 'none' | 'unknown' | (string & {}) | null;
  } | null;

  /**
   * Story `fe-be-hoofdkantoor-controle-...` (2026-06-02) — per-request
   * BE-berekend: mag de huidige user de hoofdkantoor-controle-acties
   * (goedkeuren/afkeuren/parkeren) zien/doen op dit dossier? `true` bij de
   * Hoofdkantoor-rol of een System/IsAutoglazHK-override. De FE gebruikt dit
   * (samen met `status===241`) voor de zichtbaarheid van het Hoofdkantoor-tabblad.
   */
  isHeadquartersUser?: boolean;

  /**
   * Story `fe-be-ehbo-tab` (2026-07-04) — per-request BE-berekend: het ZUIVERE
   * system-user-signaal (`GU_User.IsSystemUser`), losgekoppeld van
   * `isHeadquartersUser` (dat óók de HK-rol + AutoglazHK-override omvat). De FE
   * gebruikt UITSLUITEND dit veld voor de zichtbaarheid van de EHBO-
   * noodreparatie-tab (`isEhboTabVisible === isSystemUser === true`), ongeacht
   * dossier-status of archiefvlag. Optioneel voor backwards-compat — afwezig
   * telt als `!== true` → geen EHBO-tab (fail-closed).
   */
  isSystemUser?: boolean;

  /**
   * Story `fe-be-hoofdkantoor-controle-...` (2026-06-02) — per-request
   * BE-berekend: is bewerken voor de huidige user geblokkeerd? `true` wanneer
   * het dossier in status 241/242/243 staat én de user géén HK/override is.
   * De FE zet de edit-velden read-only + toont een lock-banner. BE is source of
   * truth — NIET lokaal afleiden uit `status` + rol (defense-in-depth: de
   * BE-403-guard is de echte afdwinging).
   */
  editLocked?: boolean;

  /**
   * Story `fe-be-edit-lock-archief-facturen` (2026-06-09) — de REDEN van de
   * edit-lock (`"none" | "headquarters" | "archived" | "invoiced"`). Prioriteit
   * BE-side: `archived > invoiced > headquarters > none`. Invariant:
   * `editLocked === (editLockReason !== "none")`. De FE-banner kiest z'n tekst
   * op deze reden (archived/invoiced winnen van de 243-stilte; binnen
   * `headquarters` kiest `status` de 241/242/243-variant). Optioneel zodat een
   * oudere detail-respons zonder dit veld backwards-compat blijft (de banner
   * valt dan terug op de status-gebaseerde HK-keuze).
   */
  editLockReason?:
    | 'none'
    | 'headquarters'
    | 'archived'
    | 'invoiced'
    /**
     * Story `fe-be-dossier-aanbieden-maatschappij` (2026-08-24) — het dossier is
     * aangeboden aan de maatschappij (status 630) en staat dossierbreed op slot.
     * Niemand mag het nog bewerken behalve `system`: de hersteller niet (er is
     * geen terugweg) en de verzekeraar evenmin. Alleen drie dingen mogen nog,
     * gebonden door één criterium — voegt het informatie toe zónder te
     * veranderen wat er is aangeboden? Een opmerking toevoegen, een indicator
     * toevoegen en een bijlage toevoegen.
     */
    | 'offered_to_insurer'
    /**
     * Story `be-fe-slot-verzonden-naar-exchange-256` (29-09-2026) — het dossier is
     * verzonden naar eXchange (status 256) en staat dossierbreed op slot; alleen
     * `system` mag nog bewerken. Toevoegen van een opmerking, indicator of bijlage
     * mag nog. ⚠ Een EIGEN reden, niet die van 630: op 256 blijven factureren en
     * "Verzend naar maatschappij" gewoon beschikbaar.
     */
    | 'sent_to_exchange';

  /**
   * Story `fe-be-dossier-aanbieden-maatschappij` (2026-08-24) — mag dit dossier
   * aan de maatschappij aangeboden worden? Dat is de TWEEDE poort: de instelling
   * `OfferDossierToInsurer` op de maatschappij van dít dossier (volmacht wint van
   * verzekeraar).
   *
   * De EERSTE poort (de omgeving) is instantie-breed en wordt al in
   * `GET /api/dossiers/status-transitions` gespiegeld — daar valt de hele entry
   * weg. Deze tweede poort hangt aan één dossier en kan dus niet in dat
   * dossier-onafhankelijke, cachebare endpoint zitten; vandaar dit veld.
   *
   * ⚠ Dit is een AFFORDANCE, geen handhaving: de echte poort staat server-side in
   * de transitie-kern en weigert ook wanneer de FE hem omzeilt. Fail-closed —
   * ontbrekend of `false` betekent: geen knop.
   */
  offerToInsurerAvailable?: boolean;

  /**
   * Story `fe-be-afgekeurd-dossier-heropenen` (2026-06-06) — per-request
   * BE-berekend: is de huidige user de glashersteller op dit dossier? `true`
   * bij de GlasHersteller-rol op dit dossier (STRIKT de `WF_CaseUser`-rol —
   * géén System/IsAutoglazHK-override, in tegenstelling tot
   * `isHeadquartersUser`). De FE toont de "Dossier weer in bewerking nemen"-knop
   * (op status 243) wanneer dit `true` is. BE is source of truth — NIET lokaal
   * afleiden uit rol/status.
   */
  isRepairerUser?: boolean;

  /**
   * Story `fe-be-uitvoerende-hersteller-acties` (2026-06-09) — per-request
   * BE-berekend afgeleid actie-recht: mag de huidige user de reopen-actie
   * (243→210, "Dossier weer in bewerking nemen") zien/doen? Formule
   * `isUitvoerendeHersteller || isSystem`. De keten-company (met de
   * reconcile-GlasHersteller-rol), HK/directie en AutoglazHK krijgen dit NIET
   * meer; system wél (versoepeling). De FE gate't de reopen-knop hierop i.p.v.
   * op `isRepairerUser` (waar de keten nog onder valt). Optioneel voor
   * backwards-compat — afwezig telt als `!== true` → knop verborgen
   * (fail-closed, veiligste default). BE is source of truth.
   */
  canReopen?: boolean;

  /**
   * Story `fe-be-uitvoerende-hersteller-acties` (2026-06-09) — per-request
   * BE-berekend afgeleid actie-recht: mag de huidige user facturen aanmaken?
   * Formule `isUitvoerendeHersteller` — ALLEEN de uitvoerende hersteller;
   * system, keten en HK/AutoglazHK NIET. De FE verbergt de "Facturen
   * aanmaken"-knop wanneer dit niet `true` is; de BE weigert met 403
   * `forbidden_not_repairer_invoice` (defense-in-depth). Optioneel voor
   * backwards-compat — afwezig telt als `!== true` → knop verborgen
   * (fail-closed). BE is source of truth.
   */
  canCreateInvoice?: boolean;

  /**
   * Story `be-voorexpertise-aanvraag-backend` (2026-07-03, EPIC-017) — per-request
   * BE-berekend afgeleid actie-recht: mag de huidige user voor dit dossier
   * voorexpertise aanvragen? `true` iff status 210 ÉN
   * `UA_Dossier.IsVoorexpertiseVmyNew` (eXchange-dossier óf keten-instellingen)
   * ÉN de caller de hersteller op dit dossier is (server-side bron van waarheid,
   * spiegelt `canCreateInvoice`). De FE toont de "Voorexpertise aanvragen"-knop
   * (dialog-opener) UITSLUITEND wanneer dit `true` is; de gate wordt niet
   * FE-side geëvalueerd. Optioneel voor backwards-compat — afwezig telt als
   * `!== true` → knop verborgen (fail-closed).
   */
  canRequestPreExpertise?: boolean;

  /**
   * Story `fe-be-voorexpertise-afhandelen-hk` (2026-07-04, EPIC-017) — per-request
   * BE-berekend afgeleid actie-recht: mag de huidige user voor dit dossier de
   * voorexpertise-uitslag afhandelen (Goedgekeurd / Afgekeurd)? `true` iff status
   * **252 "Voorexpertise aangevraagd"** ÉN de caller de **HK-rol** op dit dossier
   * heeft ÉN het **geen eXchange-dossier** is (bij een eXchange-dossier komt het
   * resultaat automatisch binnen). Server-side bron van waarheid (spiegelt exact
   * `canRequestPreExpertise`): read-only, niet-nullable in de BE (default `false`,
   * fail-closed), geen version-bump. De FE toont de "Voorexpertise afhandelen"-knop
   * (dialog-opener) UITSLUITEND wanneer dit `true` is; de gate wordt niet FE-side
   * geëvalueerd. Optioneel voor backwards-compat — afwezig telt als `!== true` →
   * knop verborgen (fail-closed).
   */
  canHandlePreExpertise?: boolean;

  /**
   * Story `fe-be-voorexpertise-waarschuwing-banner` (2026-07-04, S017.6, EPIC-017) —
   * per-request BE-berekende, read-only lijst van proactieve voorexpertise-
   * waarschuwing-regels (mirror `canRequestPreExpertise`). Gevoed door de per-keten
   * waarschuwings-SP (`UA_Dossier.PreExpertiseWarnings`, `@DossierID`+`@UserID`-
   * gescoped): elke regel is 1-op-1 de SP-tekst (geen herformulering/vertaling/
   * sortering). De FE toont daaruit een rustige, niet-blokkerende amber banner in
   * de context-rail — self-gate op `length >= 1` (leeg/afwezig → geen banner).
   * Niet-vertrouwde per-keten data → React-escaped renderen (nooit innerHTML).
   * Optioneel voor backwards-compat — afwezig telt als leeg → geen banner.
   */
  preExpertiseWarnings?: string[];

  /**
   * Story `fe-be-facturen-centraal-hk-goedkeuring` (item 15, 2026-06-09) — apart
   * reden-signaal voor de FE-hint-chip "Wacht op goedkeuring hoofdkantoor". Gezet
   * op `"headquarters_approval_pending"` wanneer de "Facturen aanmaken"-knop
   * verdwijnt WEGENS de HK-goedkeuring-eis (centraal dossier dat het hoofdkantoor
   * nog nooit heeft goedgekeurd) terwijl de caller wél de uitvoerende hersteller
   * is — te onderscheiden van "knop weg wegens rol" (waar dit veld `null`/afwezig
   * blijft). De FE toont de hint-chip iff dit signaal gezet is ÉN de knop anders
   * zou bestaan (relatie + calculatie, render ≠ done) — afgeleid uit
   * reeds-aanwezige DTO-velden, niet uit gelekte gate-info (F4). Optioneel voor
   * backwards-compat; afwezig/`null` → geen hint-chip.
   *
   * EPIC-019 S-G (AC 27/28) voegt een derde waarde toe: `"restricted_repairer"` —
   * deze VESTIGING maakt geen facturen aan. Die waarde WINT van de andere twee en
   * staat als eerste tak in `ResolveInvoiceBlockedReason`; zonder die precedentie
   * zou op een eXchange-dossier dat niet "Gereed" is de chip *"Wacht op eXchange
   * (Gereed)"* blijven staan en zou AC 28's *"geen vervangende chip"* onwaar zijn.
   *
   * ⚠ Dit veld is `string`, GEEN union (review L27): de derde waarde is een
   * machine-CODE, maar `"Wacht op eXchange (Gereed)"` — de bestaande tweede waarde —
   * is NL-proza. Een union zou dat verschil verhullen. TypeScript vangt hier dus
   * niets af; de borg is een runtime-testcase dat een onbekende waarde de FE nooit
   * laat crashen.
   */
  invoiceBlockedReason?: string | null;

  /**
   * Story `fe-be-aanbieden-alleen-centraal-dossier` (2026-06-06) — server-derived
   * via `UA_Dossier.IsCentralCustomer` (eXchange-dossier OF
   * `RB_CompanyCompany.IsCentralCustomerSelfOrParent`). `true` wanneer dit een
   * CENTRAAL dossier is. De FE gebruikt deze vlag om de "Aanbieden aan
   * hoofdkantoor"-knop (status 210, actie `submit-to-headquarters`) te disablen
   * wanneer het dossier niet centraal is — alleen een centraal dossier mag
   * worden aangeboden. De BE dwingt dezelfde regel af (422 `not_central_dossier`).
   * Optioneel zodat een oudere detail-respons zonder dit veld niet breekt
   * (backwards-compat); afwezig/`undefined` wordt behandeld als niet-centraal
   * voor de gate.
   */
  isCentralCustomer?: boolean;

  /**
   * Story `be-fe-centraal-alleen-binnen-keten` (29-09-2026) — server-afgeleid: mag
   * de knop "Verstuur naar eXchange" er staan? `true` ⇔ eXchange-dossier ∧ de
   * hersteller hoort niet bij de keten van deze instantie ∧ de status is 210 of de
   * geresolveerde "Voorexpertise goedgekeurd". Afwezig of `false` ⇒ geen knop
   * (fail-closed). Affordance: de BE toetst hetzelfde in de transitie-kern.
   */
  canSendToExchange?: boolean;

  /**
   * Story `fe-be-aanmaken-facturen` (rework 2026-06-07) — `UA_Dossier.GenericRelationID`
   * als string-GUID. Null wanneer er geen klant-relatie aan het dossier
   * gekoppeld is. De FE leidt de zichtbaarheid van de "Facturen aanmaken"-knop
   * puur FE-side af uit dit veld + `calculation.rows.length` (gekoppelde relatie
   * + calculatie → knop tonen; de debiteur-check zit in de popup). Optioneel
   * voor backwards-compat met responses van vóór deze rework.
   */
  genericRelationId?: string | null;

  /**
   * Story `fe-be-aanmaken-facturen` (rework 2026-06-07) — `UA_Dossier.HasInvoices`.
   * `true` wanneer er al facturen voor dit dossier zijn aangemaakt; de FE toont
   * dan de read-only chip "✓ Facturen aangemaakt" i.p.v. de knop (geen eager
   * preview-call meer). Optioneel voor backwards-compat.
   */
  hasInvoices?: boolean;

  /**
   * Story `be-fe-afhandeling-verzenden-mail` (story B, AA-6) — staat er een
   * verzendronde open (stap 3 "Nu aan de beurt" of een mislukte ontvanger)? Voedt
   * de rail-link "Verzenden →" (`canShowSendLink`). `null` = niet vast te stellen
   * (geen link). Optioneel voor backwards-compat.
   */
  sendPending?: boolean | null;

  /**
   * Story `be-fe-afhandeling-verzenden-exchange-dispatch` (story C, §8) — zit het dossier
   * in de nieuwe verzendflow (er is een verzendronde)? Uit dezelfde read als
   * `sendPending`. `false` = de oude flow (de oude eXchange-afsluitknop mag); `null` of
   * ontbrekend (oude backend) = niet vast te stellen ⇒ geen oude knop op de rail.
   */
  inSendFlow?: boolean | null;

  /**
   * Besluit eigenaar 19-10-2026 (vervolg) — het kenmerk "Te verzenden" (géén status) in de
   * rail. Dezelfde regel als het lijstkenmerk, het filter `handling=to_send` en de
   * dashboardtegel (één BE-predicaat), dus óók `true` voor een dossier dat gearchiveerd werd
   * vóór verzenden (alleen mailen). Ontbreekt bij een leesfout of een oude backend — dan
   * valt de rail terug op `canShowSendLink`. Stuurt NIET de verzendstap (dat blijft
   * `sendPending`).
   */
  toSend?: boolean;

  /**
   * Story `fe-be-dossier-protocol` (2026-06-10) — read-only verzekeraar-/
   * herstelpartner-protocol-HTML voor dit dossier. Bevat de protocol-body
   * ZONDER de legacy `<font color='red'>`-prefix (de rode waarschuwing rendert
   * FE-side als native banner op `protocolUnderConstruction`). `null` wanneer
   * er geen protocol is — de FE toont de rail-affordance uitsluitend wanneer dit
   * veld niet-null en niet-leeg is. De HTML wordt via `sanitizeHtml` (DOMPurify)
   * gerenderd. Optioneel voor backwards-compat met responses van vóór deze story.
   */
  protocol?: string | null;

  /**
   * Story `fe-be-dossier-protocol` (2026-06-10) — `true` wanneer de
   * verzekeraar-koppeling "onder constructie" is. De FE rendert dan een native
   * rode waarschuwingsbanner ("Let op: deze verzekeraar is nog niet actief")
   * bovenaan de protocol-drawer. Heeft GEEN invloed op de affordance-
   * zichtbaarheid (die hangt uitsluitend aan `protocol`). Optioneel voor
   * backwards-compat.
   */
  protocolUnderConstruction?: boolean;

  /**
   * Story `fe-be-fieldsarevalid-autosync` (2026-07-01) — `UA_Dossier.FieldsAreValid`
   * (`bool?`). Legacy-handmatig "chkValidate"-vinkje dat nu FE-gedreven wordt
   * gesynchroniseerd met de Voertuig- + Schade-tab-completeness. Tri-state:
   * `true` (beide tabs compleet), `false` (een tab onvolledig na een eerdere
   * `true`), `null` (nog nooit gezet — verse/legacy dossiers). **De BE-mapping
   * geeft DB-`NULL` door als `null`** (NIET coalescen naar `false`): de
   * FE-flip-sync onderscheidt "nog nooit gezet" van "expliciet false" om een
   * false-write-storm op verse verzekerings-dossiers te vermijden
   * (`useFieldsValidAutoSync`). Optioneel voor backwards-compat met responses van
   * vóór deze story. NB: dit veld voedt de sync/accept-flow; er is GEEN nieuw
   * zichtbaar UI-element.
   */
  fieldsAreValid?: boolean | null;

  // Werkzaamheden + Levering: NIET op DossierDetail. Komen uit UA_Activity
  // (per-hersteller) via UA_DossierActivity-join. Aparte feature: FU-S007.11-11.
}

// ─── S007.11 — Intake-tab (UA_Dossier) ───────────────────────────────────

/**
 * Response van `GET /api/dossiers/{id}/intake/schema`.
 *
 * Bevat de huidige `WF_CaseState.Code` voor logging/banner-rendering, de
 * optimistic-concurrency-versie en de per-veld policies voor de Intake-set
 * (~20 velden, resolved met huidige user-context + state).
 */
export interface IntakeSchemaResponse {
  entity: 'UA_Dossier';
  /** `WF_CaseState.Code` van het dossier. `null` als geen actieve state. */
  state: string | null;
  /** Optimistic-concurrency-versie van het dossier op moment van fetch. */
  version: number;
  /** Per-veld visibility + editability. Lege array betekent geen policies geseeded. */
  fieldPolicies: FieldPolicy[];
}

/**
 * Body van `PATCH /api/dossiers/{id}/intake`.
 *
 * `fields` is een sparse object: alleen velden die wijzigen. Server skipt
 * onbekende keys met log; lege string voor nullable nvarchar → NULL.
 * Decimal: client stuurt canonical dot-notation (`"12.50"`); server
 * accepteert ook komma. Datetime: ISO 8601 met timezone-offset.
 */
export interface IntakePatchRequest {
  version: number;
  fields: Record<string, unknown>;
}

/**
 * S007.11 — Error-body bij 409 (`currentVersion` is de huidige server-versie
 * zodat de UI direct kan invalidateren / banner kan tonen).
 */
export interface IntakeVersionMismatchError {
  error: string;
  correlationId?: string;
  currentVersion: number;
}

/**
 * S007.11 — Error-body bij 403 (`fields` lijst met geblokkeerde veld-codes,
 * zodat de UI ze in een toast kan opsommen en uit de pending diff kan halen).
 */
export interface IntakeFieldsBlockedError {
  error: string;
  correlationId?: string;
  fields: string[];
}

/**
 * Story-A — generieke lookup-rij voor `UA_CarCondition` /
 * `UA_RepairType`-dropdowns. Beide lookup-tabellen zijn company-scoped
 * (UA_-prefix) en MOETEN per-request via `Interaction.UA_CarCondition_SelectList`
 * / `Interaction.UA_RepairType_SelectList` opgehaald worden — nooit via een
 * statische `<X>.<seed>`-accessor (CLAUDE.md "Statische Domain-accessors").
 */
export interface DamageLookupOption {
  id: string;
  description: string;
  /**
   * Story `fe-schade-soort-reparatie-startype` (2026-06-14) — alleen gezet op
   * `GET /api/lookups/repair-types`: of deze optie de config-backed
   * "Reparatie"-rij is (server-side via `ResolveReparatieId()`, geen statische
   * seed-accessor). De FE leest dit op de lokaal-gekozen "Soort herstel"-optie
   * om het conditionele veld "Soort reparatie" (StarType) wel/niet te tonen —
   * zonder PATCH-roundtrip (live toggle). Optioneel/`undefined` voor lookups
   * die de vlag niet leveren (bv. car-conditions) → behandel als `false`.
   */
  isRepair?: boolean;
}

// ─── Story-A `fe-schade-tab-editable` — Schade-tab schema + PATCH ─────────

/**
 * Response van `GET /api/dossiers/{id}/damage/schema`. Spiegelt de
 * Intake-variant: state-code voor logging/banner, optimistic-concurrency
 * `version` en per-veld policies voor de Schade-velden.
 */
export interface DamageSchemaResponse {
  entity: 'UA_Dossier';
  /** `WF_CaseState.Description` van het dossier. `null` als geen actieve state. */
  state: string | null;
  /** Optimistic-concurrency-versie van het dossier op moment van fetch. */
  version: number;
  /** Per-veld visibility + editability. Lege array betekent geen policies geseeded. */
  fieldPolicies: FieldPolicy[];
}

/**
 * Body voor `PATCH /api/dossiers/{id}/damage`. Sparse: FE stuurt alleen
 * gemuteerde velden. Lege strings → null (consistent met Intake AC #48).
 */
export interface DamagePatchRequest {
  version: number;
  fields: Record<string, unknown>;
}

// ─── Story `fe-voertuig-tab-editable` — Voertuig-tab schema + PATCH ──────

/**
 * Response van `GET /api/dossiers/{id}/vehicle/schema`. Spiegelt de
 * Intake/Damage-variant: state-code, optimistic-concurrency `version` en 12
 * field-policies (RDW-datums NIET in de set).
 */
export interface VehicleSchemaResponse {
  entity: 'UA_Dossier';
  /** `WF_CaseState.Description` van het dossier. `null` als geen actieve state. */
  state: string | null;
  /** Optimistic-concurrency-versie op moment van fetch. */
  version: number;
  /** Per-veld visibility + editability. 12 entries (RDW-datums ontbreken). */
  fieldPolicies: FieldPolicy[];
}

/**
 * Body voor `PATCH /api/dossiers/{id}/vehicle`. Sparse: FE stuurt alleen
 * gemuteerde velden. Lege strings → null (consistent met Intake AC #48 /
 * Schade AC #18). PATCH op een read-only RDW-datum-wire-key (`firstRegistration`/
 * `nameRegistration`/`dutchRegistration`) levert 403 met
 * `{ reason: "field_not_editable", fields }`.
 */
export interface VehiclePatchRequest {
  version: number;
  fields: Record<string, unknown>;
}

/**
 * Generieke lookup-rij voor de Voertuig-tab dropdowns (Carrosserievorm).
 * Output-shape consistent met `getCarConditions` / `getRepairTypes`:
 * `{ id, description }`.
 */
export interface VehicleLookupOption {
  id: string;
  description: string;
}

// ─── S007.14 — Polis-invoer ──────────────────────────────────────────────

/**
 * Lookup-resultaat voor verzekeraar/volmacht autocomplete (`GET /api/insurers/lookup`).
 * Eén lichte rij — id + naam — geen volledige insurance-company-DTO.
 */
export interface InsurerLookupResult {
  id: string;
  name: string;
}

/**
 * Eén zoekresultaat uit `GET /api/lookups/debtors?query=` — debiteur-
 * autocomplete voor het Opdrachtgever-veld op de Intake-tab (story
 * `fe-opdrachtgever-debtor-autocomplete`, 2026-06-06).
 *
 * `id` = `RB_Debtor.ID` (de `ReferenceDebtorID` die de FE PATCHt). `name` =
 * bedrijfsnaam (`RB_Company.Name`). `debtorNumber` = `RB_Debtor.DebtorNumber`
 * (nullable). De FE stelt de getoonde displaynaam zelf samen als
 * `"{name} ({debtorNumber})"`.
 */
export interface DebtorSearchResult {
  id: string;
  name: string;
  debtorNumber: number | null;
}

/**
 * Eén oorzaak (`GET /api/policy/damage-causes?agentId|insurerId={id}`).
 * `sourceName` is de naam van de partij wiens lijst dit is — zichtbaar in
 * het bron-aanduiding-label naast de geselecteerde waarde (AC #17).
 */
export interface DamageCause {
  id: string;
  name: string;
  sourceName?: string | null;
  /**
   * BL-29 (PB-12) — `true` wanneer dit de terugvallijst is (dossier zonder
   * verzekeraar én volmacht, of `scope=fallback`). Optioneel zodat een oudere
   * backend zonder het veld als "geen terugvallijst" leest.
   */
  isFallback?: boolean;
  /**
   * BL-29 / GAP-04 — alleen aanwezig in de dossier-modus wanneer de opgeslagen oorzaak
   * NIET in de lijst staat: kan de poliscontrole met de service die hij echt gebruikt
   * draaien? `false` → toon geen "kies opnieuw" (de controle zou daarna alsnog falen).
   */
  policyCheckSupported?: boolean | null;
}

/**
 * Filiaal-specificatie van een verzekeraar (`GET /api/insurers/{id}/specifications`).
 * Zichtbaar in de Filiaal-dropdown wanneer een verzekeraar specifications heeft.
 */
export interface InsurerSpecification {
  id: string;
  description: string;
}

/**
 * Eén verzekeringssoort (`GET /api/policy/insurance-types`). Bron-tabel
 * `UA_InsuranceType` (3 vaste rijen: WA / WA_Casco / WA_Plus). Statische
 * lijst, niet partij-afhankelijk. `code` = `UA_InsuranceType.EuroMotiveCode`;
 * `description` = label voor de UI.
 */
export interface InsuranceType {
  id: string;
  code: string;
  description: string;
  /**
   * Story `be-fe-dekking-d1-popup-en-slot` — `UA_InsuranceType.HasCoverage`. De popup
   * "Dekking zelf opgeven" laat bij "Dekking afgegeven" alleen een soort mét dekking
   * toe. Optioneel voor een oudere backend (dan: onbekend).
   */
  hasCoverage?: boolean | null;
}

/**
 * Story `fe-be-ehbo-tab` (2026-07-04) — één EHBO-actie: precies één veld +
 * verplichte reden + version (1 actie = 1 WF_Trace-regel). `value` is
 * veld-afhankelijk (string/number/boolean/null); `null` = het veld legen.
 * `field` is een wire-key uit de BE-whitelist (bv. `license`, `vatIncluded`,
 * `excessRepair`, `damageDate`, `insuranceTypeId`, `isArchive`,
 * `dispatchBrand`, `status`).
 */
export interface EhboPatchRequest {
  field: string;
  value: string | number | boolean | null;
  reason: string;
  version?: number | null;
}

/** 200-respons van `PATCH /ehbo` — het gemuteerde veld + de post-write version. */
export interface EhboPatchResponse {
  field: string;
  version: number;
}

/** EHBO-status-dropdown-item (alle WF_CaseState-waarden). */
export interface EhboStatusOption {
  statusValue: number;
  description: string | null;
}

/** EHBO-verzekeringstype-dropdown-item. */
export interface EhboInsuranceType {
  id: string;
  description: string | null;
}

/** EHBO-logboek-regel (read-only, categorie "EHBO"). */
export interface EhboLogEntry {
  timestamp: string | null;
  userName: string | null;
  description: string | null;
}

/**
 * S007.14 — Velden die via `PATCH /api/dossiers/{id}/policy` bewerkt mogen
 * worden. Sparse: FE stuurt alleen het delta-veld. Server retourneert na
 * succes de complete `DossierDetail`-DTO (zelfde patroon als intake).
 *
 * Lease-contact-velden bestaan **niet** als aparte kolommen: legacy hergebruikt
 * `insuranceContactPerson` + `contactMomentInsurer` voor beide situaties.
 * De UI toont ze afhankelijk van `insuranceSort` in een andere positie.
 */
export type PolicyPatchFields = Partial<{
  insuranceCompanyId: string | null;
  authorizedAgentId: string | null;
  insuranceSort: 'Verzekerd' | 'Lease' | 'Fleetowner';
  insuranceCompanySpecificationId: string | null;
  damageCauseId: string | null;
  policyNumber: string | null;
  /** UA_Dossier.MeldCode (ruit-meldcode). Niet het Schadenummer — zie internalReference. */
  damageNumber: string | null;
  /** Schadenummer — UA_Dossier.InternalReference. */
  internalReference: string | null;
  damageDate: string | null;
  insuranceContactPerson: string | null;
  contactMomentInsurer: string | null;
  coverageType: string | null;
  /** Korting ER — altijd open (story `be-fe-dekking-d1-popup-en-slot`, A-2). */
  excessDiscount: number | null;
  // ⚠ Story `be-fe-dekking-d1-popup-en-slot` (21-10-2026, D1-2): de dekkingsvelden
  //   (insuranceTypeId, policyValidUntil, excessReplacement, excessRepair, vatIncluded,
  //   adjustedExcess, isCovered) staan hier NIET MEER in. Ze zijn altijd dicht — de server
  //   weigert ze met 403 `field_not_editable` — en worden alleen geschreven door de
  //   poliscontrole, de verzekeraar (/coverage-check), de popup (`postOwnCoverage`) en EHBO.
}>;

/**
 * Herkomst van een polis-PATCH (bugfix 12-10-2026). `policy_suggestion` = de
 * hersteller heeft een op kenteken gevonden polis overgenomen. De BE slaat de
 * velden dan gewoon op, maar legt GEEN eigen-invoer-dekkingsrij vast: het
 * overnemen van een suggestie is geen dekkingsbeslissing. Weglaten = de
 * hersteller vult zelf in (standaard). Elke andere waarde ⇒ 400
 * `invalid_patch_origin`.
 */
export type PolicyPatchOrigin = 'policy_suggestion';

export interface PolicyPatchRequest {
  version: number;
  fields: PolicyPatchFields;
  /** Optioneel; zie {@link PolicyPatchOrigin}. */
  origin?: PolicyPatchOrigin;
}

/**
 * Story `fe-be-verzekeraar-dekkingscontrole` (2026-06-29) — de **strikt
 * kleinere** veld-allowlist voor de verzekeraar-dekkingscontrole. Bewust géén
 * alias van {@link PolicyPatchFields}: het coverage-check-endpoint accepteert
 * UITSLUITEND deze 9 dekkingsvelden en weigert de rest met 400
 * `field_not_allowed_for_coverage_check` (kleinere autorisatie-oppervlakte dan
 * `PATCH /policy`). De verzekeraar mag dus NIET `insuranceCompanyId`,
 * `authorizedAgentId`, `damageCauseId`, `damageDate`, `internalReference`, …
 * muteren.
 *
 * - `policyValidFrom`/`policyValidUntil` — ISO-date-strings (`YYYY-MM-DD`) of
 *   null. Geen future-block (een polis mag in de toekomst geldig zijn); wel de
 *   range-regel `policyValidFrom <= policyValidUntil` (FE + BE).
 * - `excessRepair`/`excessReplacement` — EUR-bedragen (`number`), ondergrens 0.
 * - `isCovered`/`vatIncluded` — booleans (`null` = onbekend / nog niet gezet).
 * - `insuranceTypeId` — GUID-string (FK `UA_InsuranceType`) of null.
 * - `commentInsuranceCompany` — vrij-tekst-opmerking van de verzekeraar
 *   (hergebruikt `UA_Dossier.CommentInsuranceCompany`).
 */
export type CoverageCheckFields = Partial<{
  isCovered: boolean | null;
  policyNumber: string | null;
  insuranceTypeId: string | null;
  policyValidFrom: string | null;
  policyValidUntil: string | null;
  excessRepair: number | null;
  excessReplacement: number | null;
  vatIncluded: boolean | null;
  commentInsuranceCompany: string | null;
}>;

/**
 * Body voor `POST /api/dossiers/{id}/coverage-check`. Spiegelt de
 * `{ version, fields }`-shape van `PATCH /policy` zodat de delta-merge-
 * machinerie (`mergeDeltaIntoDossierDetail`) ongewijzigd kan worden hergebruikt.
 */
export interface CoverageCheckRequest {
  version: number;
  fields: CoverageCheckFields;
}

// ─── Story `fe-be-verzekeraar-expertise` (2026-06-29, story 3) ──────────────

/**
 * De drie verzekeraar-expertise-uitkomsten vanuit status 240:
 *  - `approve-insurer` → 280 "Goedgekeurd" (geen opmerking, geen melding);
 *  - `return-for-adjustment-insurer` → 245 "Teruggegeven voor aanpassing"
 *    (verplichte opmerking + hersteller-melding);
 *  - `reject-final-insurer` → 290 "Afgekeurd" eindstatus (verplichte opmerking
 *    + hersteller-melding).
 * Exact + case-sensitive op de wire (de BE matcht `Ordinal`).
 */
export type InsurerExpertiseAction =
  | 'approve-insurer'
  | 'return-for-adjustment-insurer'
  | 'reject-final-insurer';

/**
 * Body voor `POST /api/dossiers/{id}/insurer-expertise`. `comment` is verplicht
 * bij `return-for-adjustment-insurer` / `reject-final-insurer` (leeg → 400
 * `comment_required`) en wordt genegeerd bij `approve-insurer`. De respons is
 * een {@link DossierPatchResponse}-delta die de status kantelt (240→280/245/290).
 */
export interface InsurerExpertiseRequest {
  version: number;
  action: InsurerExpertiseAction;
  comment?: string | null;
}

// ─── Story `fe-voorexpertise-aanvraag-popup` (2026-07-03, EPIC-017, S017.3) ──

/**
 * Bron-route van de voorexpertise-aanvraag (BE kiest op
 * `UA_Dossier.IsExchangeDossier`). `"exchange"` ⟺ `exchange != null &&
 * settings == null`; `"settings"` ⟺ omgekeerd (preflight-invariant TV-2).
 */
export type VoorexpertiseSource = 'exchange' | 'settings';

/**
 * eXchange-precheck-booleans op het preflight-antwoord (alleen bij
 * `source === "exchange"`). Voedt de proactieve Voorwaarden-checklist (frame 2A).
 * Elke boolean is optioneel: ontbreekt hij, dan toont de FE die regel grijs/
 * onbekend en blokkeert NIET proactief (TV-3) — de POST blijft de harde gate.
 */
export interface VoorexpertisePreflightExchange {
  /** Calculatie aanwezig (via de bestaande calc-read op het dossier). */
  calculationPresent?: boolean | null;
  /** eXchange-status == "Open" (`Dossier.ExchangeDossier.status`). */
  exchangeStatusOpen?: boolean | null;
  /** Aantal bijlagen met `IsImage == true` (VF-6, mirror pass-1). */
  photoCount?: number | null;
  /** `GS_SystemParameter.MinimumAttachmentCountVoorExpertise`. */
  photoRequired?: number | null;
}

/**
 * Keten-instellingen-waarden op het preflight-antwoord (alleen bij
 * `source === "settings"`). Voedt het "Wordt verstuurd naar"-blok (frame 2B,
 * BS-3): concrete verzekeraar-naam / e-mailadres / rapportnaam.
 */
export interface VoorexpertisePreflightSettings {
  /** `InsuranceToUse.Name` — verzekeraar-naam. */
  insurerName?: string | null;
  /** `UA_Dossier.InsurerPreExpertiseEmailAddress()` — voorexpertise-adres keten. */
  email?: string | null;
  /** `PreExpertiseMailREport.Name/Description` — rapportnaam. */
  reportName?: string | null;
}

/**
 * Antwoord van `GET /api/dossiers/{id}/voorexpertise/preflight` (BS-4). Read-only,
 * geen mutatie/version-bump. Vult de reden-dropdown (`reasons`) + het route-
 * afhankelijke bovenblok. Precies één van `exchange`/`settings` is gevuld (TV-2).
 */
export interface VoorexpertisePreflightResult {
  source: VoorexpertiseSource;
  /** Reden-opties (zonder placeholder — de FE zet die zelf bovenaan). */
  reasons: string[];
  /**
   * Route-onafhankelijke blokkerende "Regel SP"-uitzonderingsregels
   * (`UA_Dossier.InsurerPreExpertiseExceptionRules()`). Niet-leeg → de popup toont
   * ze als rood blok én disable't "Aanvragen"; de POST weigert met 422
   * `pre_expertise_exception_rule`. Leeg/afwezig = geen blokkade.
   */
  exceptionRules?: string[] | null;
  /**
   * Route-onafhankelijk keten-informatie-bericht (opgemaakte admin-WYSIWYG-HTML,
   * `UA_Dossier.PreExpertiseInformationMessage`). Puur informatief (blokkeert NIET);
   * leeg/null = niets tonen.
   */
  infoMessage?: string | null;
  /** Alleen bij `source === "exchange"`. */
  exchange?: VoorexpertisePreflightExchange | null;
  /** Alleen bij `source === "settings"`. */
  settings?: VoorexpertisePreflightSettings | null;
}

/**
 * Body voor `POST /api/dossiers/{id}/voorexpertise` (pass-1 contract). `reason`
 * = de gekozen dropdown-waarde; `toelichting` = de vrije toelichting (op de
 * eXchange-route max. 200 tekens). Beide verplicht (niet-leeg). De respons is
 * een {@link DossierPatchResponse}-delta die de status naar "Voorexpertise
 * aangevraagd" kantelt (het dossier verlaat 210).
 */
export interface VoorexpertiseRequest {
  reason: string;
  toelichting: string;
}

/**
 * Succes-respons van `POST /api/dossiers/{id}/voorexpertise` (pass-1 contract):
 * `{ ok, status, statusLabel }`. `status` = de nieuwe `StatusValue` (het dossier
 * verlaat 210 → "Voorexpertise aangevraagd"); `statusLabel` = het bijbehorende
 * label. Geen delta-`fields` — de FE invalideert het detail op de status-grens
 * zodat `canRequestPreExpertise` naar `false` kantelt (knop verdwijnt).
 */
export interface VoorexpertiseResult {
  ok: boolean;
  status: number;
  statusLabel?: string | null;
}

// ─── Story `fe-be-voorexpertise-afhandelen-hk` (2026-07-04, EPIC-017) ────────

/**
 * De twee voorexpertise-uitslag-acties die het HK vanuit status 252
 * "Voorexpertise aangevraagd" kan vastleggen:
 *  - `approve-pre-expertise` → 246 "Voorexpertise goedgekeurd";
 *  - `reject-pre-expertise` → 247 "Voorexpertise afgekeurd".
 * Exact + case-sensitive op de wire (de BE matcht `Ordinal`). Bron én doel zijn
 * BE-config-gedreven (`GS_SystemParameter`); de FE stuurt alleen de action-key.
 */
export type PreExpertiseResultAction = 'approve-pre-expertise' | 'reject-pre-expertise';

/**
 * Body voor `POST /api/dossiers/{id}/pre-expertise-result`. Anders dan de
 * verzekeraar-expertise-mirror is `comment` **verplicht bij beide** uitkomsten
 * (leeg/whitespace → 400 `comment_required`; > 4000 tekens → 400
 * `comment_too_long`). `version` is optioneel (afwezig → version-gate
 * overgeslagen). De respons is een {@link DossierPatchResponse}-delta die de
 * status kantelt (252 → 246/247).
 */
export interface PreExpertiseResultRequest {
  action: PreExpertiseResultAction;
  comment: string;
  version?: number;
}

// ─── S007.15 — Controleer-polis dispatcher-uitkomst ─────────────────────

/**
 * Server-side classificatie van de `Interaction.GetPolicy`-uitkomst per
 * service (eXchange / Mail / Dispatch / Carculate / uBench / LocalDatabase).
 *
 * - `covered` — polis heeft dekking voor deze schade
 * - `not_covered` — polis bestaat maar dekt deze schade niet (verlopen / WA-only / etc.)
 * - `no_policy` — verzekeraar kent geen polis voor dit voertuig
 * - `pending_async` — Mail-pad: aanvraag verzonden, antwoord komt handmatig
 * - `pending_external` — dispatcher heeft request gestuurd, wacht op extern systeem
 * - `no_response` — service gaf geen interpreteerbare data terug (V1-fallback)
 */
export type PolicyCheckOutcome =
  | 'covered'
  | 'not_covered'
  | 'no_policy'
  | 'pending_async'
  | 'pending_external'
  | 'no_response';

export interface PolicyCheckResult {
  outcome: PolicyCheckOutcome;
  /** Notificatie-tekst Mail-pad. Bevat e-mailadres → FE rendert als plain text. */
  mailNotification: string | null;
  /** Extern dossier-nummer (eXchange / Carculate). */
  externalDossierNumber: string | null;
  /** Polisnummer uit dispatcher-respons (kan afwijken van het ingevoerde). */
  policyNumber: string | null;
}

/**
 * Story `appservices-dispatch-resultaat-persist` (2026-06-21) — getypeerde
 * shape van de delta-`fields` die de poliscontrole terugstuurt. Blijft een
 * open `Record` (de BE emit afhankelijk van service/outcome wisselende keys),
 * maar pint de Dispatch-cascade-keys zodat ze type-safe doorkomen in
 * `mergeDeltaIntoDossierDetail`:
 *
 * - `lastDispatchResultCode` — `'D'` = definitieve dekking (stuurt Scenario 10:
 *   lock + "Verwijder koppeling met Dispatch").
 * - `commentInsuranceCompany` — Dispatch-portal/schadelink (stuurt Scenario 9:
 *   "Schadenummer ophalen").
 * - `authorizedAgentId` — door Dispatch bepaalde volmacht (mag `null`).
 * - `insuranceCompanyId` — door Dispatch bepaalde maatschappij.
 *
 * De FE-merge is generiek (`mergeDeltaIntoDossierDetail` itereert over álle
 * keys) — deze interface is alleen typing-borg, geen merge-logica.
 */
export interface PolicyCheckResponseFields {
  lastDispatchResultCode?: string | null;
  commentInsuranceCompany?: string | null;
  authorizedAgentId?: string | null;
  insuranceCompanyId?: string | null;
  /**
   * Vervolg-iteratie (2026-06-21) — FK-derived cascade-keys. Na een
   * Dispatch-poliscontrole met dekking bepaalt Dispatch (mogelijk) een andere
   * verzekeraar/volmacht; de BE stuurt naast de FK-GUID's nu ook de afgeleide
   * NAAM + derived velden mee zodat de FE de juiste maatschappij/volmacht toont
   * zonder hard-refresh. Dezelfde keys als het PATCH-`/policy`-pad emit (één
   * gedeelde `ExpandPolicyDerivedFields`-helper → geen drift). `policyService`
   * komt genest binnen; `mergeDeltaIntoDossierDetail` doet er deep-merge op.
   */
  insuranceCompanyName?: string | null;
  insuranceSort?: string | null;
  dossierType?: string | null;
  authorizedAgentName?: string | null;
  policyService?: {
    code: string | null;
    /**
     * Story `be-fe-dekking-d1-popup-en-slot` (gat 18) — wat deze service kan, uit
     * de ENE lijst van de server: `automatic` · `mail` · `none` · `unknown`.
     * Ontbreekt bij een oudere backend; de FE valt dan terug op de code.
     */
    checkKind?: 'automatic' | 'mail' | 'none' | 'unknown' | (string & {}) | null;
  } | null;
  isExchangeDossier?: boolean | null;
  exchangeDossierNumber?: string | null;
  isUBench?: boolean | null;
  isLocalPolicy?: boolean | null;
  isNH1816?: boolean | null;
  [key: string]: unknown;
}

export interface PolicyCheckResponse {
  /**
   * Story `appservices-lichte-response-en-resolver-fix` (2026-05-24, PO #3):
   * de check retourneert een delta-shape — alleen de velden die de check
   * heeft (her-)gevuld op het dossier, plus de nieuwe `version`. FE merget
   * dit in de TanStack-cache van `['dossiers', id]`.
   *
   * Het oude `dossier: DossierDetail`-veld is bewust verwijderd om
   * `LoadFullDetail`-overhead (~2.5s) te elimineren.
   *
   * Story `appservices-dispatch-resultaat-persist` (2026-06-21): typing
   * aangescherpt naar `PolicyCheckResponseFields` zodat de Dispatch-cascade-
   * keys type-safe binnenkomen. Blijft index-open voor de overige delta-keys.
   */
  version: number;
  fields: PolicyCheckResponseFields;
  /** Dispatcher-uitkomst (onafhankelijk van de delta-merge). */
  policyCheck: PolicyCheckResult;
}

/**
 * Story `appservices-lichte-response-en-resolver-fix` (2026-05-24) —
 * lichte response-shape voor PATCH /policy + PATCH /intake.
 *
 * `version` = row-version na de write (of huidige version bij noop).
 * `fields` = sparse veld-set die de FE in haar TanStack-cache moet mergen.
 * Bij noop is `fields` `{}` met dezelfde `version` (idempotent merge).
 *
 * **Type-guard (review F-H1)**: bij FE-deploy-lag retourneert de server
 * mogelijk nog de oude full-DTO-shape. `isDossierPatchResponse` faalt dan
 * met heldere parse-error i.p.v. silently-broken state.
 */
export interface DossierPatchResponse {
  version: number;
  fields: Record<string, unknown>;
  /**
   * S012.8 Fase 2b — aantal planbord-afspraken dat deze PATCH heeft opgeruimd
   * (soft-deleted) doordat de bijbehorende hersteldatum is leeggemaakt. Additief
   * + optioneel: `0`/afwezig in alle bestaande paden. De FE toont een toast
   * wanneer `> 0` ("de geplande afspraak is van het planbord verwijderd omdat de
   * hersteldatum is leeggemaakt").
   */
  planbordAppointmentsRemoved?: number;
  /**
   * Story `fe-schade-soort-reparatie-startype` — uitbreiding 2026-06-14
   * (auto-calc, AC #31/#32). `true` wanneer de StarType-PATCH als best-effort
   * side-effect automatisch een ster-calculatie heeft aangemaakt (dossier
   * zonder calc + Reparatie + geldig ster-artikel). Additief + optioneel:
   * `false`/afwezig in alle bestaande paden. De FE toont dan een toast
   * ("Calculatie aangemaakt") + invalidate't de Calculatie-tab-query van dit
   * dossier zodat de nieuwe rij(en) zonder hard-refresh verschijnen.
   */
  calculationCreated?: boolean;
  /**
   * Story `fe-schade-soort-reparatie-startype` — uitbreiding 2026-06-14.
   * Aantal door de auto-calc toegevoegde calculatie-rijen (parent + child(s),
   * bv. Plakset). `0`/afwezig wanneer er niets is aangemaakt.
   */
  calculationRowsAdded?: number;
  /**
   * Story `fe-be-schadeoorzaak-zonder-maatschappij` (BL-29, PB-9). Alleen aanwezig
   * wanneer deze schrijfactie de schadeoorzaak heeft LEEGGEMAAKT omdat de (nieuwe)
   * maatschappij geen eenduidige tegenhanger kent. `fields.damageCauseId` is dan
   * `null`. Een geslaagde omzetting meldt niets.
   */
  damageCauseCleared?: DamageCauseCleared;
}

/**
 * BL-29 — de melding "de oorzaak is leeggemaakt" (PB-9). `previousCauseName` is
 * `null` wanneer de oude oorzaak-rij niet meer bestond (review F-11).
 */
export interface DamageCauseCleared {
  previousCauseName: string | null;
}

/**
 * Post-write delta van `POST /api/dossiers/{id}/archive` (story
 * `fe-be-archiveren-dossier`, 2026-06-08). De FE merget deze in de
 * `['dossiers', id]`-cache zodat het dossier zonder refetch `isArchive=true` +
 * status 300 toont en uit het "Openstaand"-filter valt.
 */
export interface ArchiveDossierResponse {
  /** Altijd `true` na succes/idempotente no-op. */
  isArchive: boolean;
  /** Status-string-vorm "300. Archief". */
  status: string;
  /** Stabiele `WF_CaseState.StatusValue` = 300. */
  statusValue: number;
  /** Post-write `UA_Dossier.Version` (DB-authoritatief). */
  version: number;
  /** Of de best-effort WF_Trace-audit is geschreven. */
  auditTraceWritten: boolean;
  /** True wanneer het verzoek een idempotente no-op was (al gearchiveerd). */
  noOp: boolean;
}

// ─── Story R3 `be-fe-dossier-crediteren-geheel` — dossier in zijn geheel crediteren ───

/** Een factuur in de crediteer-preview. */
export interface DossierCreditInvoice {
  id: string;
  number: number | null;
  typeCode: string | null;
  typeDescription: string | null;
  totalSales: number;
  totalVat: number;
  /** Zelf een creditnota of al gecrediteerd (detector, fail-closed). */
  creditedOrCredit: boolean;
}

/** De toestand van een korting-transactie in de preview. */
export type DossierCreditLineState =
  | 'planned'
  | 'already_reversed'
  | 'left_behind'
  | 'blocked'
  | 'reversal';

/** Een transactie in de preview (korting of overig). */
export interface DossierCreditTransaction {
  id: string;
  /** `incasso` / `excasso` bij een korting-transactie. */
  direction: string | null;
  word: string | null;
  discountInvoiceNumber: number | null;
  companyName: string | null;
  companyCode: string | null;
  itemCode: string | null;
  description: string | null;
  amount: number;
  invoiced: boolean;
  runNumber: number | null;
  /** `yyyy-MM-ddTHH:mm:ss`, lokale tijd. */
  created: string | null;
  /** `null` bij overige transacties. */
  state: DossierCreditLineState | null;
  reversalAmount: number | null;
}

/** AC 9a — een kortingsnota met maar aan één kant een transactie. */
export interface DossierCreditOneSided {
  invoiceNumber: number;
  word: string;
  presentDirection: string;
  missingDirection: string;
}

/** Een groep die niet eenduidig tegen te boeken is. */
export interface DossierCreditAmbiguousGroup {
  companyId: string;
  companyName: string | null;
  itemCode: string;
  openAmount: number;
  originalsAmount: number;
}

/** De reden waarom crediteren niet kan (preview `blockedReason`, of de reden van een 409/422). */
export type DossierCreditBlockedReason =
  | 'snelstart_not_supported'
  | 'invoice_owner_without_user'
  | 'invoices_already_credited'
  | 'dossier_partly_credited'
  | 'nothing_to_credit'
  | 'legacy_route_unsupported'
  | 'original_created_unknown'
  | 'reversal_target_missing'
  | 'discount_transaction_invalid'
  | 'discount_reversal_ambiguous'
  | 'double_item_check_blocks_reversal';

/** `GET /api/dossiers/{id}/credit-preview`. */
export interface DossierCreditPreview {
  dossierId: string;
  dossierNumber: number | null;
  version: number;
  invoices: DossierCreditInvoice[];
  discountTransactions: DossierCreditTransaction[];
  otherTransactions: DossierCreditTransaction[];
  leftBehind: DossierCreditTransaction[];
  oneSided: DossierCreditOneSided[];
  ambiguousGroups: DossierCreditAmbiguousGroup[];
  plannedReversals: number;
  plannedReversalAmount: number;
  snelstart: 'not_applicable' | 'blocked';
  canCredit: boolean;
  blockedReason: DossierCreditBlockedReason | null;
  previewFingerprint: string;
}

/** Body van `POST /api/dossiers/{id}/credit`. */
export interface DossierCreditRequest {
  expectedVersion: number;
  previewFingerprint: string;
  /** Letters, cijfers en koppeltekens (1-64). Zelfde id bij opnieuw proberen ⇒ replay. */
  clientRequestId: string;
}

/** Antwoord 200 van `POST /api/dossiers/{id}/credit`. */
export interface DossierCreditResult {
  replayed: boolean;
  creditedInvoices: number;
  creditNumbers: number[];
  reversals: number;
  reversalAmount: number;
  leftBehind: DossierCreditTransaction[];
  leftBehindCount: number;
  /** Post-write `UA_Dossier.Version`. */
  version: number;
}

/**
 * Maximum aantal dossier-id's dat `POST /api/dossiers/central-flags` per
 * request verwerkt. Spiegelt **exact** de BE-cap
 * `DossiersController.CentralFlagsMaxIds` (`DossiersCentralFlagsController.cs`).
 *
 * De BE **clampt stil**: id's voorbij de cap worden genegeerd en ontbreken dus
 * in de respons. Omdat de FE-gate fail-closed is (vlag onbekend → geen knop),
 * zou een lijst met meer dan 200 zichtbare rijen — bereikbaar via infinite
 * scroll bij `PAGE_SIZE = 50` vanaf de vijfde batch — de archiveer-knop op alle
 * volgende rijen stil laten verdwijnen. `fetchDossierCentralFlags` chunkt de
 * id's daarom zelf op deze grens.
 */
export const DOSSIER_CENTRAL_FLAGS_MAX_IDS = 200;

/** Request-body van `POST /api/dossiers/central-flags`. */
export interface DossierCentralFlagsRequest {
  /**
   * Dossier-id's (Guid-strings) waarvoor de effectief-centraal-vlag gevraagd
   * wordt. BE negeert lege/ongeldige waarden en dedupliceert.
   */
  ids: string[];
}

/**
 * 200-body van `POST /api/dossiers/central-flags` (story
 * `fe-be-archiveren-vanuit-dossieroverzicht`, Spoor 1). Per dossier-id de
 * **effectief-centraal**-vlag.
 *
 * **Let op — afwezig ≠ false.** Id's waar de caller geen toegang toe heeft,
 * onbekende id's en id's voorbij de BE-cap ontbreken in `flags`. Een consument
 * moet "afwezig" als **onbekend** behandelen (fail-closed), nooit als
 * "niet-centraal".
 */
export interface DossierCentralFlagsResponse {
  flags: Record<string, boolean>;
}

/**
 * 200-body van `POST /api/dossiers/{id}/exchange-close` ("Verzend naar
 * maatschappij", story `be-fe-exchange-factuur-gate-en-verzend-maatschappij`,
 * 2026-07-03). De seam-close is geslaagd (`closed: true`); `archived` geeft aan
 * of het lokale archiveren óók slaagde. Bij semi-succes (F-06: afgemeld maar
 * lokaal archiveren mislukt) is `archived: false` + `archive: null`.
 */
export interface ExchangeCloseResult {
  /** Altijd `true` in een 200-body (de close is geslaagd). */
  closed: boolean;
  /** `true` wanneer het lokale archiveren óók slaagde; `false` bij semi-succes. */
  archived: boolean;
  /** Post-archive-delta (status 300 + IsArchive); `null` bij semi-succes. */
  archive: ExchangeCloseArchive | null;
}

/** Post-archive-delta binnen `ExchangeCloseResult` (status 300 + IsArchive). */
export interface ExchangeCloseArchive {
  isArchive: boolean;
  /** Status-string-vorm "300. Archief". */
  status: string;
  /** Stabiele `WF_CaseState.StatusValue` = 300. */
  statusValue: number;
}

// ─── Tab "Afhandeling" (story `fe-be-afhandeling-tab`, 05-10-2026) ──────────

/** Sleutel van een afhandel-stap; altijd in deze volgorde. */
export type HandlingStepKey = 'control' | 'invoices' | 'send';

/**
 * Toestand van een afhandel-stap. Een onbekende waarde uit een nieuwere backend
 * moet de FE níet laten crashen — vandaar de `string`-uitwijk.
 */
export type HandlingStepState =
  | 'done'
  | 'current'
  | 'on_hold'
  | 'waiting'
  | 'not_applicable'
  | 'error'
  | (string & {});

/** Eén stap van de stappenbalk. `reason` is een Engelse machine-code of `null`. */
export interface HandlingStep {
  key: HandlingStepKey;
  state: HandlingStepState;
  reason: string | null;
  /** ISO-tijdstip, of `null` wanneer geen bestaande read het levert. */
  at: string | null;
  /** Alleen stap 1: wie het deed. */
  by: string | null;
  /** Alleen stap 2: het aantal facturen. */
  count: number | null;
  /** Alleen stap 1: de afkeurredenen; nooit null. */
  reasons: string[];
}

/** Eén regel uit de verzendlog van de documentverwerking — tekst letterlijk. */
export interface HandlingSendLogEntry {
  at: string | null;
  text: string;
}

/** Antwoord van `GET /api/dossiers/{id}/handling`. */
export interface DossierHandling {
  channel: 'mail' | 'exchange' | 'dispatch' | (string & {});
  currentStep: HandlingStepKey | 'done';
  /** Altijd precies drie: control, invoices, send. */
  steps: HandlingStep[];
  /**
   * Story B (AA-5) — per dossier: gefactureerd ⇒ het dossier zit in de nieuwe
   * verzendflow (een duurzame markering); nog niet gefactureerd ⇒ de voorspelling
   * (schakelaar actief ∧ kanaal mail) voor stap 2.
   */
  sendStepActive: boolean;
  /**
   * Fix 09-10-2026 — alleen NIET gefactureerd met een voorspelde nieuwe flow: het kanaal dat
   * factureren zal kiezen (dezelfde BE-beslissing als `sendStepActive`). Anders `null`/afwezig
   * (oud pad, onbekend, of al gefactureerd — dan geldt `sendChannel`).
   */
  predictedSendChannel?: 'mail' | 'exchange' | 'dispatch' | 'ubench' | (string & {}) | null;
  /** Nooit null; leeg = `[]`. */
  legacySendLog: HandlingSendLogEntry[];
  /** Story B (D-12) — mag deze gebruiker verzenden/afsluiten (de uitvoerende hersteller)? */
  canSend?: boolean;
  /** Story B (§7.1) — de ontvangers; alleen in de nieuwe flow, anders `null`. */
  parties?: HandlingParty[] | null;
  /** Story B — de laatste verzending bij een afgeronde ronde (niet gearchiveerd), of `null`. */
  alreadySentAt?: string | null;
  /** Story B — archiveert verzenden het dossier? `null` = niet vast te stellen. */
  closesAfterSend?: boolean | null;
  /**
   * Story C (§7) — het EFFECTIEVE kanaal van de verzendronde (G-9: geen eXchange-dossier
   * meer ⇒ `mail`); `null` zonder ronde. In de nieuwe flow leest de FE dit, nooit `channel`.
   */
  sendChannel?: 'mail' | 'exchange' | 'dispatch' | 'ubench' | (string & {}) | null;
  /** Story C — bij `sendChannel = exchange`: het afmelden in deze ronde (ISO, lokaal), of `null`. */
  exchangeClosedAt?: string | null;
  /** Story C — bij een eXchange-dossier: het aanbod bij eXchange (ISO, lokaal), of `null`. */
  exchangeOfferedAt?: string | null;
  /** Story C — de Dispatch-aanname (`sendChannel = dispatch` of status 440); anders `null`. */
  dispatchState?: 'accepted' | 'not_accepted' | 'rejected' | (string & {}) | null;
  /** Story C — bij `sendChannel = dispatch`: "Dossier aangeboden bij Dispatch." (ISO), of `null`. */
  dispatchOfferedAt?: string | null;
  /** Story C (G-7) — gearchiveerd met een open ronde: alleen nog mailen. `null` zonder ronde. */
  mailOnly?: boolean | null;
  /** Story C — centraal `true` / `false` / `null` (niet vast te stellen). */
  isCentral?: boolean | null;
  // ─── Story U (§6) — alleen bij `sendChannel = ubench`, anders `null` ─────
  /** De declaratie bij uBench in deze ronde (ISO, lokaal), of `null`. */
  ubenchSentAt?: string | null;
  /** Afgeleverd met waarschuwingen (uBench-waarde 5). */
  ubenchWarning?: boolean | null;
  /** `ubench_rejected` | `ubench_error` bij een mislukte declaratie in deze ronde. */
  ubenchFailure?: 'ubench_rejected' | 'ubench_error' | (string & {}) | null;
  /** De uBench-omschrijving (al geparseerd) bij een afwijzing of waarschuwing. */
  ubenchMessage?: string | null;
  /** Waarom "Verzenden naar uBench" nu niet kan (U1-U6b), of `null`. */
  ubenchBlockedReason?: string | null;
  /**
   * Story KV (§5) — de uitnodiging voor Klanten Vertellen in stap 3; alleen met een verzendronde, anders `null`.
   * De zichtbaarheid komt van de server (`display`); de FE leidt haar nooit af uit de reden.
   */
  klantVertellen?: HandlingKlantVertellen | null;
  // ─── Story G3 (be-fe-geen-documenten-achteraf) ──────────────────────────────
  /**
   * De partijen die bij factureren (in deze verzendronde) geen documenten kregen en nog niet zijn afgehandeld.
   * Alleen in de nieuwe flow; weggelaten wanneer leeg. Stap 3 toont er het voorstel met
   * "Overnemen en documenten maken" en "Alleen voor dit dossier".
   */
  documentsMissing?: HandlingDocumentsMissing[] | null;
  /**
   * Mag het maken (en een eventuele tweede ronde) nu? `false` bij een gesloten eXchange- of uBench-ronde of een
   * niet-aangenomen Dispatch-ronde. Alleen samen met `documentsMissing`.
   */
  makeDocumentsAllowed?: boolean | null;
  /** Oude pad (geen verzendronde): er is gefactureerd zonder documenten — alleen een melding met de tip. */
  noDocumentsAtInvoicing?: boolean | null;
}

/** Story G3 — één partij die bij factureren geen documenten kreeg. */
export interface HandlingDocumentsMissing {
  party: number;
  label: string;
}

/** Story G3 — body van `POST /api/dossiers/{id}/handling/make-documents`. */
export interface HandlingMakeDocumentsRequest {
  party: number;
  /** V2: `true` = de documenten maken zonder een verzendregel vast te leggen. */
  onlyThisDossier: boolean;
  conditions?: DeliveryProposalConditions | null;
  documents: Array<{ code: string; mailen: boolean }>;
  mailTemplateId?: string | null;
}

/** Story G3 — antwoord (200) van `POST /handling/make-documents`. */
export interface HandlingMakeDocumentsResult {
  roundStarted: boolean;
  sendable: boolean;
  renderFailed: boolean;
  rulesWritten: number;
  mailOnly: boolean;
}

/** Story KV — de aanhef op de wire (de server vertaalt naar de legacy-tekst). */
export type KlantVertellenSalutation = 'none' | 'mr' | 'mrs' | 'mr_mrs';

/** Story KV — de velden van de uitnodiging (body van verzenden en de voorvulling van GET). */
export interface KlantVertellenInvite {
  email: string;
  salutation: KlantVertellenSalutation | (string & {});
  firstName: string;
  lastName: string;
}

/** Story KV (§5) — `GET /handling` → `klantVertellen`. */
export interface HandlingKlantVertellen {
  display: 'hidden' | 'disabled' | 'available' | (string & {});
  /** Engelse reden-code (`klantvertellen_*`), of `null` bij `available`. */
  reason: string | null;
  lastOutcome: 'sent' | 'unconfirmed' | 'failed' | (string & {}) | null;
  /** ISO zonder zone (lokale tijd). */
  lastAttemptAt: string | null;
  lastRecipient: string | null;
  /** Alleen bij `available`. */
  prefill: KlantVertellenInvite | null;
}

/** Story KV (§3) — antwoordveld van verzenden; `null` = niet gevraagd. */
export interface HandlingSendKlantVertellenResult {
  status: 'sent' | 'unconfirmed' | 'failed' | 'skipped' | (string & {});
  reason: string | null;
}

/** Story B — één document van een ontvanger in stap 3. */
export interface HandlingPartyDocument {
  code: string;
  label: string;
  /** De opgeslagen bijlage, of `null` (wordt bij verzenden gerenderd). */
  attachmentId: string | null;
}

/** Story B — één ontvanger in stap 3 (§7.1). */
export interface HandlingParty {
  /** 1 = verzekeraar/volmacht, 2 = lease/fleet, 3 = klant, 4 = opdrachtgever. */
  party: number;
  /** "Maatschappij" | "Klant" | "Opdrachtgever". */
  label: string;
  relationName: string | null;
  /** Heeft deze ontvanger documenten in de ronde? */
  sendable: boolean;
  address: string | null;
  /** Story `be-fe-afhandeling-administratie-adres`: `administration` = het e-mailadres administratie van klant/opdrachtgever. */
  addressSource: 'declaration_address' | 'insurer_fallback' | 'relation_email' | 'administration' | 'entered' | 'none' | (string & {}) | null;
  documents: HandlingPartyDocument[];
  /** `sent` | `failed` | `not_confirmed` | `self_sent` | `null`. */
  lastStatus: string | null;
  lastReason: string | null;
  lastAt: string | null;
  recipient: string | null;
  paymentLinkAmount: number | null;
  /** Story K7-a — de vastgelegde bron van de ronde; weggelaten wanneer onbekend. */
  documentSource?: DeliveryDocumentSource | null;
  /** Story K7-a (PB-20) — de partij is geblokkeerd (reden-code); weggelaten wanneer niet. */
  blockedReason?: string | null;
  /**
   * Story K7-a (PB-12) — "Kopie naar …" bij een keten-partij. Story `be-fe-afhandeling-administratie-adres`: bij klant
   * en opdrachtgever het CC-adres administratie (getoond als "CC: …").
   */
  cc?: string | null;
  /** Story K7-a (PB-7/D-16) — `credit_render_failed`: de declaratie gaat zonder creditnota. */
  creditFailure?: string | null;
  /** Story K7-a (PB-21) — waarom de maatschappij leeg is (`route_no_address`, `route_exchange`, …). */
  notice?: string | null;
  /**
   * Story K7-b1 (PB-31) — mag "Of: zelf versturen" getoond worden? Dezelfde BE-regel als poort 15a: `false`
   * voor de maatschappij bij een centraal of onbekend-centraal dossier. De FE leidt "centraal" nooit zelf af.
   */
  selfSendAllowed?: boolean;
  /**
   * Story K7-b2 (PB-31) — de extra declaratie-mails (`RB_CompanyCompanyReport`), alleen bij de keten-partij met een
   * keten-declaratie (route postbus); weggelaten elders. Vóór het vastleggen de preview (`status: null`), daarna per
   * rij status, reden en tijd.
   */
  extraReports?: HandlingExtraReport[];
}

/** Story K7-b2 (PB-31) — één extra declaratie-mail in stap 3. */
export interface HandlingExtraReport {
  /** De rij-ID; `null` bij "de set kon niet worden bepaald" (`extra_reports_unknown`). */
  id: string | null;
  /** De rapportnaam; `null` als het rapport ontbreekt (`extra_report_missing`). */
  reportName?: string | null;
  receiver?: string | null;
  /** `sent` | `failed` | `not_confirmed` | `self_sent`; `null` = nog niet verzonden (preview) of vervallen. */
  status: string | null;
  /** Engelse reden-code (`extra_report_missing`, `extra_receiver_invalid`, `extra_reports_unknown`, `extra_report_row_removed`, `mail_failed`, …). */
  reason: string | null;
  at?: string | null;
  /**
   * Coverage R-10 — staat deze extra nog open (neemt de volgende verzending hem mee)? Berekend door de BE uit dezelfde
   * afleiding als het verzenden; de FE leidt "open" nooit zelf af.
   */
  open?: boolean;
}

/** Story B — body van `POST /api/dossiers/{id}/handling/send`. */
export interface HandlingSendRequest {
  clientRequestId: string;
  version: number;
  parties?: number[];
  resend?: boolean;
  insurerEmail?: string;
  rememberInsurerEmail?: boolean;
  selfSentParties?: number[];
  /** Story K7-b2 (PB-27) — alleen deze extra declaratie-mails (rij-ID's uit de vastgelegde set) versturen. */
  extraReports?: string[];
  /** Story K7-b2 (PB-30) — een `not_confirmed` extra als verzonden markeren (bevestigen; zelf versturen kan niet). */
  selfSentExtraReports?: string[];
  /** Story KV (§3) — de uitnodiging voor Klanten Vertellen; weggelaten = de schakelaar staat uit. */
  klantVertellen?: KlantVertellenInvite;
}

/** Story B — de uitkomst voor één ontvanger. */
export interface HandlingSendPartyResult {
  party: number;
  label: string;
  status: string;
  reason: string | null;
  recipient: string | null;
  at: string | null;
}

/** Story B — antwoord (200) van verzenden. */
export interface HandlingSendResult {
  replayed: boolean;
  version: number;
  archived: boolean;
  closeDecision:
    | 'archived'
    | 'awaiting_export'
    | 'export_status_unknown'
    | 'archive_failed'
    | 'archived_before_send'
    | null
    | (string & {});
  parties: HandlingSendPartyResult[];
  /** Story C — is de ronde afgemeld in eXchange? `null` bij een ander kanaal. */
  exchangeClosed?: boolean | null;
  /** Story C (G-7) — ging deze verzending via de alleen-mailen-tak? */
  mailOnly?: boolean;
  /** Story U — is de declaratie in deze ronde bij uBench afgeleverd? `null` bij een ander kanaal. */
  ubenchSent?: boolean | null;
  /** Story U — afgeleverd met waarschuwingen? `null` bij een ander kanaal. */
  ubenchWarning?: boolean | null;
  /** Story K7-b2 — de extra declaratie-mails van deze request (ook in een replay). */
  extraReports?: Array<{ id: string | null; status: string | null; reason: string | null; recipient: string | null; at: string | null }>;
  /** Story KV (§3) — de uitkomst van de uitnodiging voor Klanten Vertellen; `null` = niet gevraagd. */
  klantVertellen?: HandlingSendKlantVertellenResult | null;
}

/** Story B — antwoord (200) van afsluiten. */
export interface HandlingCloseResult {
  archived: boolean;
  version: number;
}

/**
 * Request-body van `POST /api/dossiers/{id}/copy` (story `kopieer-dossier`,
 * 2026-06-09). UITSLUITEND groep-keuze-booleans — de BE bouwt de veld-
 * uitsluitingen (`exclusiveProps`) server-side (AC-7). Geen `cancelExchange`
 * (buiten V1). Defaults (AC-5): alle inhoud aan, `copyWorkOrder` +
 * `archiveSource` uit.
 */
export interface CopyDossierBody {
  copyPolicy: boolean;
  copyCustomer: boolean;
  copyVehicle: boolean;
  copyDamage: boolean;
  copyLicense: boolean;
  copyKilometers: boolean;
  copyWarranty: boolean;
  copyReportDate: boolean;
  copyWorkOrder: boolean;
  copyCalculation: boolean;
  copyAttachments: boolean;
  copyRemarks: boolean;
  archiveSource: boolean;
}

/**
 * Best-effort-archiveer-deelresultaat in de copy-response (AC-A3a). De kopie is
 * altijd de primaire (geslaagde) actie; `requested=false` wanneer Archiveer-bron
 * niet was aangevinkt.
 */
export interface CopyArchiveResult {
  requested: boolean;
  succeeded: boolean;
  /** `"archive_failed"` bij een mislukte best-effort-archivering; afwezig anders. */
  reason?: string;
}

/**
 * 200-response van `POST /api/dossiers/{id}/copy` (AC-A3a / R1). Draagt de
 * identiteit van het nieuwe dossier (voor de navigatie + succes-toast) plus het
 * `archive`-blok dat de bron-archivering beschrijft.
 */
export interface CopyDossierResponse {
  /** GUID van het nieuwe (gekopieerde) dossier. */
  id: string;
  /** Dossiernummer van de kopie (voor de toast "Dossier gekopieerd naar {nummer}"). */
  number: number | null;
  /** Best-effort-archiveer-resultaat van het bron-dossier. */
  archive: CopyArchiveResult;
  /** BL-29 — de kopie verloor haar schadeoorzaak (hoort niet bij haar maatschappij). */
  damageCauseCleared?: DamageCauseCleared;
}

// ─── SMS versturen naar klant (story `sms-versturen-klant`, 2026-06-10) ──

/** Eén SMS-template-entry in {@link SmsOptions.templates}: `{ id, name }`. */
export interface SmsTemplate {
  id: string;
  name: string;
}

/**
 * Response van `GET /api/dossiers/{id}/sms/options`. `serviceAvailable=false`
 * → de dialog toont de "geen SMS-dienst"-fallback (geen invoervelden).
 * `customerNumber` is het voorgevulde (06-genormaliseerde) klant-nummer, leeg
 * wanneer er geen mobiel nummer op het dossier staat (State 3).
 */
export interface SmsOptions {
  serviceAvailable: boolean;
  customerNumber: string;
  templates: SmsTemplate[];
}

/** Response van `GET /api/dossiers/{id}/sms/render?templateId=…`: `{ text }`. */
export interface SmsRenderResult {
  text: string;
}

/** Request-body van `POST /api/dossiers/{id}/sms`: `{ number, message }`. */
export interface SmsSendBody {
  number: string;
  message: string;
}

/**
 * 200-response van `POST /api/dossiers/{id}/sms` (status-matrix). `sent:true` =
 * succes (WF_Trace + GD_Communication geschreven); `sent:false` = gateway-/
 * business-fout — de letterlijke foutstring staat in `error` en er is GEEN
 * trace/comm geschreven. `number` is het genormaliseerde ontvanger-nummer.
 */
export interface SmsSendResult {
  sent: boolean;
  number: string;
  error?: string;
}

/**
 * Type-guard die bevestigt dat een server-response de delta-shape heeft
 * (F-H1 / TC-CONTRACT-FE-01..03). Beschermt tegen partial-deploys waar
 * server nog full-DTO stuurt: dan ontbreekt `fields` als plain object en
 * de guard faalt expliciet.
 */
export function isDossierPatchResponse(value: unknown): value is DossierPatchResponse {
  if (value == null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  if (typeof r.version !== 'number') return false;
  if (typeof r.fields !== 'object' || r.fields == null || Array.isArray(r.fields)) {
    return false;
  }
  return true;
}

// ─── Gegevens overnemen uit een ander dossier ────────────────────────────
//
// Story `fe-be-gegevens-overnemen-dossier` (2026-06-27) —
// POST /api/dossiers/{targetId}/copy-sections. CROSS-TENANT WRITE: de caller
// moet toegang hebben tot bron én target (anders 404, geen data-lek). Neemt
// per sectie de bron-waarden over naar het huidige (target) dossier;
// calculatie wordt VERVANGEN. Kenteken wordt NOOIT overgenomen.

/** De tien sectie-toggles (alle booleans). Spiegelt het FE-dialoog. */
export interface CopyDossierSectionsSelection {
  policy: boolean;
  customer: boolean;
  vehicle: boolean;
  damage: boolean;
  kilometers: boolean;
  reportDate: boolean;
  calculation: boolean;
  attachments: boolean;
  remarks: boolean;
  warranty: boolean;
}

/** Request-body voor de overname. */
export interface CopyDossierSectionsRequest {
  /** GUID van het BRON-dossier waarvan de secties worden overgenomen. */
  sourceDossierId: string;
  sections: CopyDossierSectionsSelection;
}

/** 200-body: post-write version + overgenomen secties + tellingen. */
export interface CopyDossierSectionsResponse {
  targetId: string;
  version: number;
  copiedSections: string[];
  counts: {
    calculationRows: number;
    attachments: number;
    remarks: number;
  };
  /** BL-29 — de overname maakte de schadeoorzaak van het doeldossier leeg. */
  damageCauseCleared?: DamageCauseCleared;
}

// ─── Inrichting / UE_ItemType ────────────────────────────────────────────

export interface ItemTypeListItem {
  id: string;
  description: string;
  ownerCompanyId: string | null;
  ownerCompanyName: string | null;
  accessTypeId: string | null;
  accessTypeCode: string | null;
  isLinkedToCurrentCompany: boolean;
  canEdit: boolean;
  canDelete: boolean;
  isActive: boolean;
  /**
   * De bewerkbare velden van het formulier. Optioneel voor een oudere backend;
   * ontbreken ze, dan blijven ze bij opslaan ongewijzigd (zie `toItemTypePayload`).
   */
  salesLedgerNumber?: string | null;
  purchaseLedgerNumber?: string | null;
  isWebshop?: boolean | null;
  isGlassInsured?: boolean | null;
  /** Audit-velden — alleen gepopuleerd door de single-record-GET. */
  created?: string | null;
  createdByName?: string | null;
  modified?: string | null;
  modifiedByName?: string | null;
  version?: number | null;
  /** "Active" / "Archive" / "Deleted" — uit GS_RecordStatus. */
  recordStatusCode?: 'Active' | 'Archive' | 'Deleted' | null;
}

export interface AccessTypeOption {
  id: string;
  code: string;
  description: string;
}

export interface ItemTypeWriteRequest {
  description: string;
  salesLedgerNumber?: string | null;
  purchaseLedgerNumber?: string | null;
  isWebshop?: boolean | null;
  isGlassInsured?: boolean | null;
  displayOrder?: number | null;
  exchangeItemCode?: string | null;
  dispatchItemCode?: string | null;
  vatId?: string | null;
  rowTypeId?: string | null;
  accessTypeId?: string | null;
  defaultPricelistId?: string | null;
}

// ─── Inrichting / UA_HeadquartersControlReason (controleredenen) ──────────
//
// Story `fe-be-controleredenen-beheer-crud-scaffold` (story 3). Systeembrede
// referentielijst — géén company-/owner-velden. Spiegelt de itemtypes-shape.

export interface ControlReason {
  id: string;
  code: string;
  description: string;
  appliesToApprove: boolean;
  appliesToReject: boolean;
  appliesToPark: boolean;
  requiresComment: boolean;
  sortOrder: number;
  isActive: boolean;
  /**
   * Pre-flight-hint: `false` betekent NIET "verboden" maar "DELETE wordt een
   * soft-delete" (de reden is in gebruik). De BE-DELETE-respons is leidend.
   * Alleen door de single-record-GET gepopuleerd (lijst = default `true`).
   */
  canDelete: boolean;
  /** Audit — de tabel heeft alleen Created + Version. */
  created?: string | null;
  version?: number | null;
}

export interface ControlReasonWriteRequest {
  code: string;
  description: string;
  appliesToApprove: boolean;
  appliesToReject: boolean;
  appliesToPark: boolean;
  requiresComment: boolean;
  sortOrder: number;
  isActive?: boolean | null;
}

/**
 * Respons-body bij een SOFT-delete (in gebruik). Hard-delete = 204 (geen body,
 * `deleteControlReason` resolved met `null`). Soft-delete = 200 + deze body.
 */
export interface ControlReasonDeleteResult {
  softDeleted: boolean;
  message: string;
  id: string;
}

// ─── Inkoop / Leveranciers (suppliers) ───────────────────────────────────
//
// Story `fe-be-inkoop-leveranciers-beheer`. Proxy over de externe stock-API.
// LEAK-GUARD: het wachtwoord komt NOOIT terug naar de FE — alleen de afgeleide
// `hasPassword`-bool. Write-only: leeg password = behoud bestaande credential.

export interface Supplier {
  /** Stabiele record-id = de Supplier-GUID. */
  id: string;
  code: string;
  providerId: string | null;
  providerName: string | null;
  pricelistId: string | null;
  pricelistName: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Pre-flight-hint voor de generieke CRUD-delete-pagina: een leverancier wordt
   * NOOIT hard verwijderd (altijd soft-delete = deactiveren), dus altijd `false`.
   * De scaffold toont daarop de "Deactiveren"-variant. */
  canDelete: boolean;
  /** Afgeleid: er bestaat een login-credential met een wachtwoord. Nooit het
   * wachtwoord zelf. */
  hasPassword: boolean;
  username: string | null;
  salesOrganisation: string | null;
  shipTo: string | null;
  plantCode: string | null;
  /**
   * S022.9: de leverancier heeft provider `Intern` (interne leverancier). Wordt automatisch ingericht en is in
   * Leveranciersbeheer alleen-lezen. Optioneel: ontbreekt bij een backend van vóór S022.9.
   */
  isInternal?: boolean;
  /** S022.9: de pool achter een interne leverancier, alleen als de gebruiker die pool mag zien. */
  internalPoolId?: string | null;
  /**
   * Alleen in het antwoord op opslaan: `item_type_not_set` als de leverancier wel is opgeslagen maar het
   * artikeltype Eurocode niet gezet kon worden (besluit eigenaar 30-10-2026). Ontbreekt anders.
   */
  warning?: string | null;
}

/**
 * Leverancier-type (story `fe-be-inkoop-leveranciers-conditionele-velden`): de
 * EXPLICIETE bron-discriminator. De BE normaliseert mutual-exclusion op dit
 * signaal (NOOIT op veld-aanwezigheid):
 *   - `provider`  → providerId verplicht; pricelistId server-side op null;
 *   - `pricelist` → pricelistId verplicht; providerId server-side op null + geen credential;
 *   - `none`      → beide null + geen credential.
 */
export type SupplierType = 'provider' | 'pricelist' | 'none';

export interface SupplierWriteRequest {
  /** Bron-discriminator (zie {@link SupplierType}). */
  type: SupplierType;
  code: string;
  /** Alleen relevant bij `type: 'provider'`. Weglaten/leeg in andere modi. */
  providerId?: string | null;
  /** Alleen relevant bij `type: 'pricelist'`. Weglaten/leeg in andere modi. */
  pricelistId?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
  username?: string | null;
  salesOrganisation?: string | null;
  shipTo?: string | null;
  plantCode?: string | null;
  /** Write-only. Leeg/weggelaten = behoud; niet-leeg = overschrijf. */
  password?: string | null;
}

/** Eén provider-optie met de drie mandatory-flags die de credential-veld-
 * zichtbaarheid in het form sturen (data-gedreven, per provider). */
export interface SupplierProviderOption {
  id: string;
  name: string;
  /** SalesOrganisation-veld tonen voor deze provider. */
  salesOrganisationMandatory: boolean;
  /** ShipTo-veld tonen voor deze provider. */
  shipToMandatory: boolean;
  /** PlantCode-veld tonen voor deze provider. */
  plantCodeMandatory: boolean;
}

/** Provider-/prijslijst-keuze-opties voor het leverancier-form. Providers dragen
 * hun mandatory-flags mee (story conditionele velden). */
export interface SupplierSchema {
  providers: SupplierProviderOption[];
  pricelists: { id: string; name: string }[];
  /**
   * S022.9 (UX-correctie 29-10-2026): de keuze **Intern** in de providerlijst van *Nieuw leverancier*. Alleen aanwezig
   * als de gebruiker een interne leverancier mag aanmaken (System, of een hersteller voor zijn eigen bedrijf). Staat
   * bewust los van `providers`: Intern gaat nooit via `createSupplier` maar via `createInternalSupplier`.
   */
  internalSupplier?: SupplierInternalOption;
}

/** De optie "Intern" in het leveranciersformulier (S022.9). */
export interface SupplierInternalOption {
  /** Schermnaam van de optie ("Intern"). */
  label: string;
  /** Eigen beheer: het bedrijf dat eigenaar wordt (alleen getoond). `null` voor System, die de eigenaar kiest. */
  ownCompany: { companyId: string; name: string; code: string | null } | null;
}

/** Respons-body bij een (soft-)delete/deactiveren. */
export interface SupplierDeleteResult {
  softDeleted: boolean;
  message: string;
  id: string;
}

// ─── Inrichting / Vervangend vervoer (rental cars) ───────────────────────────
//
// Story `fe-be-vervangend-vervoer-voertuigen`. Beheer van de huurauto's die een
// hersteller als vervangend vervoer kan meegeven (legacy `UA_RentalCar`).
// Company- én hersteller-gescoped op de BE. V1 toont kenteken + omschrijving +
// actief; de tarieven (`Rate`/`RatePerKilometer`) en de dossier-koppeling
// (`UA_RentalCarPerDossier`) volgen in een latere story.

export interface RentalCar {
  /** Stabiele record-id = de UA_RentalCar-GUID. */
  id: string;
  /** Kenteken — natural key (uniek per hersteller). */
  license: string;
  /** Omschrijving (merk/model/type). */
  description: string;
  /** Volledig huurtarief (€), bijv. 50. `null` = niet gezet. Mag samen met
   * {@link ratePerKilometer}. */
  rate: number | null;
  /** Tarief per gereden kilometer (€). `null` = niet gezet. */
  ratePerKilometer: number | null;
  /** Afgeleid: RecordStatus = Active. Uit = gedeactiveerd. */
  isActive: boolean;
  /** Pre-flight-hint voor de generieke CRUD-delete-pagina: een huurauto wordt
   * NOOIT hard verwijderd (altijd soft-delete = deactiveren), dus altijd `false`.
   * De scaffold toont daarop de "Deactiveren"-variant. */
  canDelete: boolean;
}

export interface RentalCarWriteRequest {
  license: string;
  description: string;
  rate?: number | null;
  ratePerKilometer?: number | null;
  isActive?: boolean | null;
}

/** Respons-body bij een (soft-)delete/deactiveren. */
export interface RentalCarDeleteResult {
  softDeleted: boolean;
  message: string;
  id: string;
}

// ─── Eigen dossiersoorten (UA_CustomDossierType) ─────────────────────────────
//
// Story `be-fe-eigen-dossiersoorten`. De soorten van de EIGEN vestiging
// (`session.RepairerId`); zonder vestiging 403 `no_repairer_context`. Writes
// lopen BE-zijdig via Interaction.UA_CustomDossierTypeAdd/Update/Delete.

/** De keuze "Soort" (AC5): verzekerd, niet verzekerd of balieverkoop. */
export type CustomDossierTypeKind = 'insured' | 'not_insured' | 'counter_sale';

export interface CustomDossierType {
  id: string;
  /** `{euromotivecode}_{n}`, door het systeem bepaald en nooit gewijzigd. */
  code: string;
  description: string;
  kind: CustomDossierTypeKind;
  isOffer: boolean;
  isDelivery: boolean;
  isLease: boolean;
  visibleInIntake: boolean;
  isDefault: boolean;
  /** RB_GenericRelation-id van de standaardklant. */
  defaultRelationId: string | null;
  defaultRelationName: string | null;
  /** RB_Debtor-id van de opdrachtgever (zoals legacy). */
  referenceDebtorId: string | null;
  referenceDebtorName: string | null;
  defaultItemId: string | null;
  defaultItemCode: string | null;
  defaultItemDescription: string | null;
  pricelistId: string | null;
  pricelistName: string | null;
  provisionAmount: number | null;
  calculationTextLines: string | null;
  status: 'active' | 'archived';
  version: number;
}

/**
 * Body van POST/PUT (volledige vervanging). Opdrachtgever: `referenceDebtorRelationId`
 * = een nieuw gekozen relatie (de BE zoekt de debiteur op); `referenceDebtorId` = de
 * huidige debiteur ongewijzigd laten.
 */
export interface CustomDossierTypeWriteRequest {
  description: string;
  kind: CustomDossierTypeKind;
  isOffer: boolean;
  isDelivery: boolean;
  isLease: boolean;
  visibleInIntake: boolean;
  isDefault: boolean;
  defaultRelationId: string | null;
  referenceDebtorRelationId: string | null;
  referenceDebtorId: string | null;
  defaultItemId: string | null;
  pricelistId: string | null;
  provisionAmount: number | null;
  calculationTextLines: string | null;
  status: 'active' | 'archived';
  /** Verplicht bij wijzigen (409 `version_conflict` bij een verouderde versie). */
  version?: number;
}

/** Antwoord van POST/PUT: de opgeslagen soort + waarschuwingen van vervolgstappen. */
export interface CustomDossierTypeSaveResponse {
  item: CustomDossierType;
  /** `default_reset_failed` / `packing_slip_setting_failed`. */
  warnings: string[];
}

export interface CustomDossierTypeOptions {
  repairerName: string | null;
  euroMotiveCode: string | null;
  pricelists: { id: string; name: string }[];
}

/** Antwoord van DELETE (AC10). */
export interface CustomDossierTypeDeleteResult {
  outcome: 'deleted' | 'archived';
}

// ─── Monteurs (UA_Fitter) ────────────────────────────────────────────────────
//
// Story `fe-monteurs-beheer-crud` (story 1). Monteur-CRUD + optioneel een
// bestaande gebruiker koppelen. Status = 3-waarden-enum plat naar 2 op de wire
// (`active` = GS_RecordStatus.Active, `inactive` = Archive — B-CRIT-2, GEEN kale
// bool). `linkedUser` komt autoritatief uit PB_FitterUser (B-MAJ-1).

/** Wire-status van een monteur: `active` (Active) of `inactive` (Archive). */
export type FitterStatusValue = 'active' | 'inactive';

/** De gekoppelde inlog-gebruiker van een monteur (uit PB_FitterUser + GU_User). */
export interface FitterLinkedUser {
  userId: string;
  /** Inlognaam zoals in de gebruiker-chip (bv. `0421jan`). */
  loginName: string;
  /**
   * Story `fe-be-monteur-s1-basis` (S021.1): "Alleen monteur-app" van deze
   * gebruiker (`PB_FitterUserSetting.IsFitterOnly`; geen rij → `false`). Optioneel
   * getypeerd zodat oudere/gemockte payloads blijven compileren.
   */
  isFitterOnly?: boolean;
  /** System-gebruiker? Dan mag het schuifje niet aan (B-6). */
  isSystem?: boolean;
}

/** Eén monteur in het beheer-scherm. */
export interface Fitter {
  /** Stabiele record-id = de UA_Fitter-GUID. */
  id: string;
  name: string;
  /** Code — uniek per hersteller (per OwnerID). */
  code: string;
  status: FitterStatusValue;
  /** De gekoppelde gebruiker, of `null` (geen login). */
  linkedUser: FitterLinkedUser | null;
}

/** Een kiesbare company-gebruiker voor de koppel-dropdown (variant B). */
export interface FitterCompanyUser {
  id: string;
  /** Inlognaam (in de dropdown getoond als `loginName · name`). */
  loginName: string;
  /** Weergave-naam (Connonical). */
  name: string;
  /**
   * Story `fe-be-monteur-s1-basis` (TA-review F-3): de BESTAANDE instelling "Alleen
   * monteur-app" van deze gebruiker — de default van het schuifje bij variant B.
   */
  isFitterOnly?: boolean;
  /** TA-review F-21: system-gebruiker? Dan staat het schuifje uitgeschakeld met reden. */
  isSystem?: boolean;
}

/** Body van `PUT /api/inrichting/fitters/{id}/fitter-only` (S021.1). */
export interface FitterOnlyRequest {
  isFitterOnly: boolean;
}

/** Respons van `PUT /api/inrichting/fitters/{id}/fitter-only` (S021.1). */
export interface FitterOnlyResult {
  fitterId: string;
  userId: string | null;
  isFitterOnly: boolean;
}

/** Response van `GET /api/inrichting/fitters` — monteurs + kiesbare users. */
export interface FittersListResponse {
  fitters: Fitter[];
  users: FitterCompanyUser[];
}

/** Write-request (create/update). `OwnerID` NOOIT uit de wire. */
export interface FitterWriteRequest {
  name: string;
  code: string;
}

/**
 * Write-request voor "nieuwe inlog-gebruiker bij een monteur aanmaken" (story
 * `fe-monteurs-nieuwe-gebruiker`, variant C). De gebruikersnaam wordt door het
 * systeem bepaald (legacy-SP) — de wire kent 'm NIET. Verplicht: `firstName` +
 * `surname` + geldige `email`; `titleId`/`prefix`/`cellPhone` optioneel.
 */
export interface FitterCreateUserRequest {
  /** Aanhef (RB_Title-GUID), optioneel. */
  titleId?: string;
  firstName: string;
  /** Tussenvoegsel, optioneel. */
  prefix?: string;
  surname: string;
  /** E-mail voor het gemailde tijdelijke wachtwoord (verplicht + geldig format). */
  email: string;
  cellPhone?: string;
  /**
   * Story `fe-be-monteur-s1-basis` (S021.1, B-5): "Alleen monteur-app". Ontbreekt
   * hij, dan zet de BE hem aan (standaard aan bij een nieuwe monteur-gebruiker).
   */
  isFitterOnly?: boolean;
}

/**
 * Response van `POST /api/inrichting/fitters/{id}/create-user`. Het tijdelijke
 * wachtwoord staat UITSLUITEND in deze respons (nergens gepersisteerd/gecached/
 * gelogd) en wordt door de FE ÉÉN keer getoond. `emailSent` = of de mail lukte
 * (config- óf transport-fout → `false`, niet-fataal → UI-fallback).
 */
export interface FitterCreateUserResult {
  loginName: string;
  temporaryPassword: string;
  emailSent: boolean;
}

/**
 * Response van `DELETE /api/inrichting/fitters/{id}`. Onderscheidt hard-delete
 * (`deleted:true`) van de soft-fallback (`deactivated:true` + `message`), zodat de
 * FE de juiste melding kan tonen zonder pre-flight-aanname.
 */
export interface FitterDeleteResult {
  deleted: boolean;
  deactivated: boolean;
  message?: string;
  id: string;
}

// ─── Vervangend vervoer op een dossier (UA_RentalCarPerDossier) ──────────────
//
// Deel 2 van vervangend vervoer: één huurauto koppelen aan een dossier (max 1).
// V1 = alleen registratie (geen facturatie). `finalRate` wordt op de FE bepaald
// (per-km × gereden km of het vaste bedrag) en is door de gebruiker aanpasbaar.

/** Betaler van de huur. */
export type DossierRentalCarPayer = 'customer' | 'insurance';

/** Eén keuze-optie voor de huurauto-dropdown (actieve auto's van de hersteller). */
export interface DossierRentalCarOption {
  id: string;
  license: string;
  description: string;
  rate: number | null;
  ratePerKilometer: number | null;
}

/** De huidige vervangend-vervoer-koppeling op een dossier. */
export interface DossierRentalCar {
  rentalCarId: string;
  license: string | null;
  description: string | null;
  /** ISO datum+tijd `yyyy-MM-ddTHH:mm` of `null`. */
  rentalStartDate: string | null;
  rentalEndDate: string | null;
  kilometersStart: number | null;
  kilometersEnd: number | null;
  driverName: string | null;
  driverLicense: string | null;
  remarks: string | null;
  payer: DossierRentalCarPayer | null;
  /** Het bedrag voor de huur (per-km × gereden km of het vaste bedrag; door de
   * gebruiker aanpasbaar). */
  finalRate: number | null;
  isInvoiced: boolean;
}

/** Respons van `GET /api/dossiers/{id}/rental-car`. */
export interface DossierRentalCarResponse {
  /** De huidige koppeling, of `null` als er geen vervangend vervoer is. */
  rentalCar: DossierRentalCar | null;
  /** Actieve huurauto's van de hersteller van dit dossier (dropdown). */
  availableCars: DossierRentalCarOption[];
  /** Bewerkbaar = niet edit-locked. */
  editable: boolean;
  /**
   * EPIC-019 S-G (AC 32) — waarom de knop "Factuur aanmaken" niet getoond mag
   * worden, of `null`. Vandaag exact één waarde: `"restricted_repairer"` (deze
   * vestiging maakt geen facturen aan). Machine-CODE: de FE beslist erop en toont
   * hem nooit.
   *
   * ⚠ Dit raakt ALLEEN het factureren. Een beperkte hersteller mag een huurauto
   * gewoon vastleggen, wijzigen en verwijderen — GET/PUT/DELETE op deze route zijn
   * bewust niet gepoort (AC 13). Additief; `rentalCar`, `availableCars` en
   * `editable` zijn ongewijzigd.
   */
  invoiceBlockedReason?: string | null;
}

/** Write-request voor de upsert (`PUT`). */
export interface DossierRentalCarWriteRequest {
  rentalCarId: string;
  rentalStartDate?: string | null;
  rentalEndDate?: string | null;
  kilometersStart?: number | null;
  kilometersEnd?: number | null;
  driverName?: string | null;
  driverLicense?: string | null;
  remarks?: string | null;
  payer?: DossierRentalCarPayer | null;
  /** Het (eventueel door de gebruiker aangepaste) bedrag voor de huur. */
  finalRate?: number | null;
}

// ─── Aanvullende factuur (provisienota / losse verkoopfactuur) ──────────────
// Story `fe-be-aanvullende-factuur` (2026-07-15). Één handmatige losse factuur
// per dossier per soort, aangemaakt vanuit het kebab-menu op het dossier-detail.

/** Eén beschikbaar debiteur-type voor de losse verkoopfactuur — alleen types met
 * een geresolveerde niet-null `RB_Debtor` (AC-16a/20). */
export interface AanvullendeDebtorType {
  /** Wire-waarde: `klant` | `verzekeraar` | `opdrachtgever`. */
  type: 'klant' | 'verzekeraar' | 'opdrachtgever' | string;
  /** UI-label (bv. "Klant"). */
  label: string;
  /** Naam van de afgeleide debiteur (informatief). */
  debtorName: string | null;
  /** "Wordt gemaild"-preview voor een verkoopfactuur op deze debiteur (follow-up A).
   * Spiegelt 1-op-1 de echte factuur-scoped mail-pas. */
  mailInfo?: AanvullendeMailInfo;
}

/** Mail-preview voor de aanvullende-factuur-dialog (follow-up A). `willMail` is `true`
 * alleen als de echte pas zou mailen (verzendregel + Mailen aan + ≥1 document +
 * partij-mailtemplate + geldige ontvanger); `recipient` = exact het adres dat de pas
 * zou gebruiken, of `null` bij `willMail=false` (of nog onbekend, zoals de vrij-gekozen
 * provisie-opdrachtgever). */
export interface AanvullendeMailInfo {
  willMail: boolean;
  recipient: string | null;
}

/** Eén artikel (`UE_Item` type Facturatie) voor de losse verkoopfactuur. */
export interface AanvullendeItem {
  id: string;
  description: string | null;
}

/** Eén btw-tarief (`UE_Vat`). `isDefault` markeert `NL_Hoog21`. */
export interface AanvullendeVat {
  id: string;
  label: string;
  code: string | null;
  isDefault: boolean;
  /** Btw-percentage (bv. 21) — voedt de hint "bedrag incl. btw" (F-7). */
  percentage: number;
}

// ─── Handmatige Dispatch-acties ────────────────────────────────────────────
// Wens eigenaar 30-09-2026 — port van legacy `Glas\DispatchActions.aspx`.

/** De drie acties. Wire-waarden; de Nederlandse labels staan in de FE. */
export type DispatchActionName = 'offer' | 'offer-manual-review' | 'credit';

/** Antwoord van `GET /api/dossiers/{id}/dispatch-actions`. */
export interface DispatchActionAvailability {
    /** Mag deze gebruiker nú acties doen op dit dossier? */
    available: boolean;
    /**
     * Waarom niet, wanneer `available` false is. Machine-code; de FE vertaalt
     * 'm. Bekende waarden: `dispatch_actions_forbidden` (geen HK/support/system),
     * `not_a_dispatch_dossier`, `headquarters_approval_required`,
     * `dispatch_state_unknown`, `dispatch_action_failed`.
     */
    reason: string | null;
    /** Max lengte van de opmerking bij handmatige beoordeling (legacy: 300). */
    commentMaxLength?: number;
}

/** Body van `POST /api/dossiers/{id}/dispatch-actions`. */
export interface DispatchActionRequest {
    action: DispatchActionName;
    /** Verplicht bij `offer-manual-review`; bij de andere twee genegeerd. */
    comment?: string | null;
}

/** Antwoord van `POST /api/dossiers/{id}/dispatch-actions`. */
export interface DispatchActionResult {
    action: string | null;
    /** Heeft Dispatch het aangenomen? Afgeleid uit `resultCode`, niet uit tekst. */
    accepted: boolean;
    /** De ruwe uitkomstcode van Dispatch; leeg = geen antwoord geparseerd. */
    resultCode: string | null;
    /** Het dossiernummer dat Dispatch toekende, indien bekend. */
    dispatchDossierNumber: string | null;
}

/** Options-DTO die de kebab-gating (AC-1/2/3) én de dialog-lookups voedt. */
export interface AanvullendeFactuurOptions {
  /** Mag de gebruiker een aanvullende factuur aanmaken? (min. één soort + factureerbaar) */
  canCreate: boolean;
  /** BE-geleverde reden waarom `canCreate` false is (source of truth), of `null`. */
  disabledReason: string | null;
  /** Is een provisienota nog toegestaan? (`!HasProvisionInvoice`) */
  allowProvision: boolean;
  /** Is een losse verkoopfactuur nog toegestaan? (`!HasExtraInvoice`) */
  allowExtraSales: boolean;
  /** Voorgevuld provisiebedrag (`UA_Dossier.ProvisionAmount` als > 0), of `null`. */
  provisionAmount: number | null;
  /** Heeft de uitvoerende hersteller Mollie ingesteld? Voedt de zichtbaarheid van
   * het betaallink-vinkje (verborgen bij `false`) — story
   * `fe-be-aanvullende-factuur-mailen-mollie` (F-5). */
  mollieConfigured: boolean;
  /** Beschikbare debiteur-types voor de verkoopfactuur (alleen resolvbare). */
  debtorTypes: AanvullendeDebtorType[];
  /** Artikelen (type Facturatie). */
  items: AanvullendeItem[];
  /** Btw-tarieven; er is er hooguit één met `isDefault: true`. */
  vats: AanvullendeVat[];
  /** "Wordt gemaild"-preview voor de provisienota (partij Opdrachtgever) — follow-up A.
   * `recipient` is `null` (de opdrachtgever wordt vrij gekozen; adres pas ná selectie
   * bekend); `willMail` weerspiegelt de verzendregel-configuratie. */
  provisionMailInfo?: AanvullendeMailInfo;
}

/** Request-body voor `POST /invoices/provision`. */
export interface ProvisionInvoiceRequest {
  referenceDebtorId: string;
  amount: number;
  ignoreWarning?: boolean;
}

/** Request-body voor `POST /invoices/extra-sales`. */
export interface ExtraSalesInvoiceRequest {
  debtorType: 'klant' | 'verzekeraar' | 'opdrachtgever' | string;
  itemId: string;
  description: string;
  amount: number;
  vatId: string;
  ignoreWarning?: boolean;
  /** Story `fe-be-aanvullende-factuur-mailen-mollie` (deel B): maak een EXTRA
   * Mollie-betaallink bij deze verkoopfactuur. Alleen zinvol bij debiteur Klant +
   * Mollie ingesteld + bedrag > 0; de server her-evalueert de beslistabel (F-4). */
  createPaymentLink?: boolean;
}

/** 201-respons van beide create-endpoints. */
export interface AanvullendeInvoiceCreated {
  invoiceId: string | null;
  invoiceNumber: string | null;
}

// ─── Polis-reply-mailbox (MailAccount) ──────────────────────────────────────

/** Mailbox-doel (Purpose) — bepaalt de poll-dispatch. V1 verwerkt alleen
 * `Poliscontrole`; de andere twee zijn voorbereide doelen (selecteerbaar, geen
 * verwerking). */
export type MailPurpose = 'Poliscontrole' | 'Herstellerbijlagen' | 'Expertiseresultaat';

/** Mailbox-protocol — V1 = `M365`; IMAP/POP zijn voorbereid. */
export type MailProtocol = 'M365' | 'IMAP' | 'POP';

/** Eén te pollen mailbox-account (read-DTO). Bevat NOOIT het secret — alleen de
 * afgeleide {@link MailAccount.hasSecret}-vlag (write-only-pariteit). */
export interface MailAccount {
  id: string;
  protocol: MailProtocol | string;
  mailboxAddress: string;
  purpose: MailPurpose | string;
  inboxFolder: string;
  processedFolder: string;
  forwardUnmatchedTo: string | null;
  pollIntervalSeconds: number;
  enabled: boolean;
  scope: string;
  tenantId: string | null;
  clientId: string | null;
  host: string | null;
  port: number | null;
  useSsl: boolean | null;
  username: string | null;
  /** SMTP-forward (IMAP/POP) — story `fe-be-mailbox-imap-pop-smtp`. NOOIT het
   * secret; alleen {@link MailAccount.hasSmtpSecret}. */
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUseSsl: boolean | null;
  smtpUsername: string | null;
  serviceUserId: string | null;
  /** FK naar `UA_Repairer` (story `fe-be-herstellerbijlagen-mailbox`). Alleen gevuld
   * bij doel `Herstellerbijlagen`; `null` voor andere doelen. Geen secret. */
  repairerId: string | null;
  /** FK naar `DA_Chain` (story `be-fe-nh1816-mail-intake-declaraties`, K1).
   * Alleen gevuld bij doel `DeclaratieAanlevering`; `null` voor andere doelen.
   * ⚠ Dit is NIET "onze" keten (`KetenHoofdkantoor` e.a.) maar de externe partij
   * die bij ons declaraties aanlevert. Geen secret. */
  chainId: string | null;
  /** Afgeleid: er staat een (versleuteld) secret in de DB. Nooit de waarde zelf. */
  hasSecret: boolean;
  /** Afgeleid: er staat een (versleuteld) SMTP-secret in de DB (write-only-pariteit). */
  hasSmtpSecret: boolean;
  /** De CRUD-scaffold leest dit voor de delete-variant; mailbox = altijd hard-delete. */
  canDelete?: boolean;
}

// ─── NH1816-partijstructuur (story `be-fe-nh1816-partijstructuur-en-aanlevering`) ──

/** Eén aanleverende keten van de verzekeraar (`DA_Chain`).
 * ⚠ Dit is NIET "onze" keten (`KetenHoofdkantoor` / `KetenFacturatie.OwnerId` /
 * `RB_Company.IsAutoglazHK`) — die gaan over herstellers die klant zijn. */
export interface DeclarationChain {
  id: string;
  name: string;
  /** Besluit P2 (story `be-fe-nh1816-mail-intake-declaraties`) — de verzekeraar
   * voor wie deze keten aanlevert. ⚠ Leeg mag: dan komt de aanlevering zonder
   * verzekeraar binnen en blijft haar state `ontvangen`. */
  insuranceCompanyId: string | null;
  isActive: boolean;
  modified: string | null;
  modifiedBy: string | null;
}

export interface DeclarationChainWriteRequest {
  name: string;
  insuranceCompanyId?: string | null;
  isActive: boolean;
}

/** Eén keten-herkenningsmethode (`DA_ChainRecognition`, K2/AC-8): waar kijken we
 * bij DEZE keten naar om de vestiging op een factuur terug te vinden.
 * ⚠ Leeg is toegestaan — dan is de outcome later "niet te herleiden" in plaats
 * van een gok. Een ONBEKENDE kind wordt geweigerd (`kind_unknown`). */
export interface DeclarationChainRecognition {
  id: string;
  chainId: string;
  kind: string;
  /** Story `be-fe-nh1816-vestiging-herleiden` (R3) — de INGEVOERDE value op
   * keten-niveau: het jokerteken `*@keten.nl`. Leeg mag: een keten-uitspraak mag
   * waardeloos zijn ("bij ABS kijken we naar de IBAN"). */
  value: string | null;
  /** De vergelijkingsvorm, zodat de beheerder ziet waarop vergeleken wordt. */
  normalizedValue: string | null;
  /** De voorrang — laag getal eerst. */
  sortOrder: number;
}

export interface DeclarationChainRecognitionWriteRequest {
  kind: string;
  /** R3 — het jokerteken (`*@keten.nl`). Leeg mag. */
  value?: string | null;
  sortOrder: number;
}

/** Eén vestiging van zo'n keten (`DA_Branch`). */
export interface DeclarationBranch {
  id: string;
  chainId: string;
  chainName: string | null;
  name: string;
  /** AC-12 — optionele koppeling met een CARculate-hersteller.
   * ⚠⚠ Verandert NIETS aan de code-resolutie: de `bodyshop_code` komt
   * uitsluitend uit `DA_BranchCode` en erft van niets. */
  repairerId: string | null;
  isActive: boolean;
  modified: string | null;
  modifiedBy: string | null;
}

export interface DeclarationBranchWriteRequest {
  chainId: string;
  name: string;
  repairerId: string | null;
  isActive: boolean;
}

/** Eén herkenningskenmerk (`DA_BranchTrait`). `value` is de INGEVOERDE
 * (getrimde) vorm; `normalizedValue` is waarop vergeleken wordt. */
export interface DeclarationTrait {
  id: string;
  branchId: string;
  kind: string;
  value: string;
  normalizedValue: string;
  sortOrder: number;
}

export interface DeclarationTraitWriteRequest {
  kind: string;
  value: string;
  sortOrder: number;
}

/** De externe code op het paar (vestiging × verzekeraar) — `DA_BranchCode`.
 * ⚠ `code` is TEKST en blijft teken voor teken gelijk: `0005` is niet `5`. */
export interface DeclarationBranchCode {
  id: string;
  branchId: string;
  insuranceCompanyId: string;
  insuranceCompanyName: string | null;
  code: string;
}

export interface DeclarationCodeWriteRequest {
  insuranceCompanyId: string;
  code: string;
}

/** Keuzelijst-item (verzekeraar of hersteller). */
export interface DeclarationPartyOption {
  id: string;
  name: string;
}

/** Eén hersteller voor de mailbox-picker (story `fe-be-herstellerbijlagen-mailbox`).
 * Read-only lookup: bedrijfsnaam (primair label) + RepairerCode (ter onderscheid). */
export interface MailRepairer {
  id: string;
  companyName: string | null;
  repairerCode: string | null;
}

// ─── Declaratie-mail-intake (story `be-fe-nh1816-mail-intake-declaraties`) ────
//
// ⚠ Deze lijst is READ-ONLY en system-only. Er zijn geen acties: geen bewerken,
//   geen verwijderen, geen opnieuw-proberen (dat is de werkvoorraad, buiten scope).

/** Eén ontvangen declaratie-aanlevering (`DA_Submission`).
 * ⚠ `sequenceNumber` is per AANLEVERING en niet per bericht: een mail met drie
 * facturen levert drie opeenvolgende nummers. */
export interface DeclarationSubmissionRow {
  id: string;
  sequenceNumber: number;
  receivedAt: string;
  chainId: string | null;
  chainName: string | null;
  pdfFileName: string | null;
  state: string;
  hasPdf: boolean;
  sourceMessageKey: string | null;

  // ── Story `be-fe-nh1816-vestiging-herleiden` ───────────────────────────────

  /** De RUWE `From`-regel van het bericht (besluit S1). ⚠ De verwerker heeft hem
   * nodig om te kunnen BESLISSEN bij welke vestiging deze aanlevering hoort —
   * dat is de hele handeling van de koppelactie. Zonder hem is de reden
   * "onbekende fromAddress" onbruikbaar. */
  fromAddress: string | null;

  /** Waarom deze rij (nog) niet herleid is: `onbekende_afzender` |
   * `vestiging_onbekend` | `dubbelzinnig` | `partij_inactief` | `keten_conflict`.
   * ⚠ `null` betekent HERLEID; de reden staat APART van `state`. */
  resolutionReason: string | null;

  /** De herleide vestiging, of `null`. */
  branchId: string | null;
  branchName: string | null;
}

/** Het antwoord van de koppelactie (story `be-fe-nh1816-vestiging-herleiden`).
 *
 * ⚠ `herbeoordeeld` is het aantal ÓVERIGE wachtende submissions dat in
 * dezelfde handeling opnieuw beoordeeld is en daardoor herkomst kreeg. Het staat
 * er omdat "0 herbeoordeeld" anders niet te onderscheiden is van "de lus draaide
 * niet". */
export interface DeclarationLinkResult {
  submissionId: string;
  branchId: string | null;
  chainId: string | null;
  insuranceCompanyId: string | null;
  /** De genormaliseerde fromAddress die als kenmerk is vastgelegd. ⚠ Afkomstig van
   *  de RIJ, nooit uit het verzoek. */
  kenmerk: string | null;
  /** `false` bij de idempotente tak: het kenmerk stond er al bij dezelfde
   *  vestiging. Dat is een GESLAAGDE outcome. */
  traitCreated: boolean;
  herbeoordeeld: number;
}

/** Eén bericht-spoor uit `ProcessedMailMessage`.
 * ⚠⚠ Dit is de helft zonder welke "de lijst is leeg" niet te onderscheiden is
 * van "het mechanisme staat stil": álle vier de faalvormen van deze story
 * leveren géén aanlevering op, drie ervan wél een spoor hier. */
export interface DeclarationMessageRow {
  sourceMessageKey: string | null;
  mailboxAddress: string | null;
  /** `processing` | `coupled` | `unmatched` | `needs_review` | `error`. */
  outcome: string | null;
  timestamp: string;
  submissionCount: number;
}

/** Eén postvak met dit doel — zodat "er kwam niets binnen" te onderscheiden is
 * van "dit postvak staat stil". */
export interface DeclarationMailboxRow {
  id: string;
  mailboxAddress: string | null;
  enabled: boolean;
  chainId: string | null;
  chainName: string | null;
  /** De reden waarom dit postvak niets doet (`keten_ontbreekt` /
   * `doorstuuradres_ontbreekt`), of `null`. */
  impediment: string | null;
}

export interface DeclarationIntakeOverview {
  submissions: DeclarationSubmissionRow[];
  messages: DeclarationMessageRow[];
  mailboxes: DeclarationMailboxRow[];
}

/** Write-request voor create/update. `clientSecret`/`password` zijn write-only —
 * leeg/weggelaten = behoud bestaand secret; niet-leeg = overschrijf. */
export interface MailAccountWriteRequest {
  protocol?: string | null;
  mailboxAddress: string;
  purpose?: string | null;
  inboxFolder?: string | null;
  processedFolder?: string | null;
  forwardUnmatchedTo?: string | null;
  pollIntervalSeconds?: number | null;
  enabled?: boolean | null;
  scope?: string | null;
  tenantId?: string | null;
  clientId?: string | null;
  /** Write-only. */
  clientSecret?: string | null;
  host?: string | null;
  port?: number | null;
  useSsl?: boolean | null;
  username?: string | null;
  /** Write-only (IMAP/POP). */
  password?: string | null;
  /** SMTP-forward (IMAP/POP) — story `fe-be-mailbox-imap-pop-smtp`. */
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUseSsl?: boolean | null;
  smtpUsername?: string | null;
  /** Write-only (SMTP-forward). Leeg/weggelaten = behoud bestaand SMTP-secret. */
  smtpPassword?: string | null;
  serviceUserId?: string | null;
  /** FK naar `UA_Repairer` (story `fe-be-herstellerbijlagen-mailbox`). Verplicht bij
   * doel `Herstellerbijlagen`; bij een ander doel genegeerd (BE forceert NULL). */
  repairerId?: string | null;
  /** FK naar `DA_Chain` (story `be-fe-nh1816-mail-intake-declaraties`, K1).
   * **Verplicht** bij doel `DeclaratieAanlevering` (400 `keten_required`); bij een
   * ander doel genegeerd (BE forceert NULL). ⚠ Bij dat doel is óók het
   * doorstuuradres verplicht (400 `forward_address_required`): dat is daar de
   * enige uitgang voor een bericht zonder bruikbare PDF. */
  chainId?: string | null;
}

/**
 * Request-body voor de "Test verbinding"-knop (`POST .../test-connection`). Bevat
 * de HUIDIGE form-velden (test vóór opslaan — raakt de DB niet). `clientSecret`
 * is write-only: leeg laten + `id` meesturen → de BE hergebruikt het opgeslagen
 * secret. Het secret reist NOOIT terug in de respons.
 */
export interface MailConnectionTestRequest {
  /** Optioneel — bij bewerken meesturen zodat een leeg secret het opgeslagen
   * secret hergebruikt. Weglaten bij een nieuw account. */
  id?: string | null;
  /** Protocol-discriminator (M365/IMAP/POP) — stuurt de protocol-bewuste test
   * (story `fe-be-mailbox-imap-pop-smtp`). Leeg = M365. */
  protocol?: string | null;
  tenantId?: string | null;
  clientId?: string | null;
  /** Write-only. Leeg + `id` → opgeslagen secret hergebruiken. */
  clientSecret?: string | null;
  mailboxAddress?: string | null;
  inboxFolder?: string | null;
  processedFolder?: string | null;
  /** IMAP/POP-ontvangst (protocol-bewuste test). Password write-only. */
  host?: string | null;
  port?: number | null;
  useSsl?: boolean | null;
  username?: string | null;
  password?: string | null;
  /** SMTP-forward — alleen testen wanneer gezet. SmtpPassword write-only. */
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUseSsl?: boolean | null;
  smtpUsername?: string | null;
  smtpPassword?: string | null;
}

/** Gestructureerd resultaat van de connectiviteitstest. Bevat NOOIT secret/token —
 * alleen een schone message + de deel-checks voor de inline-status. */
export interface MailConnectionTestResult {
  /** True wanneer een token gehaald is én de inbox bereikbaar was. */
  ok: boolean;
  /** Schone, mensvriendelijke uitleg (NL). */
  message: string;
  /** De inbox-map kon gelezen worden. */
  inboxReachable: boolean;
  /** De geconfigureerde Verwerkt-map bestaat. N.v.t. voor POP (blijft false). */
  processedFolderExists: boolean;
  /** De SMTP-verzendconfig kon ingelogd worden (alleen bij IMAP/POP mét SMTP-config;
   * story `fe-be-mailbox-imap-pop-smtp`). */
  smtpReachable?: boolean;
  /** Onderwerp van het eerste inbox-bericht (optioneel, bevestigend). */
  sampleSubject?: string | null;
}

// ─── Systeemberichten (GS_SystemMessage) ─────────────────────────────────────

/**
 * Eén systeembericht (read-DTO). Port van legacy `GS_SystemMessage` (story
 * `fe-systeemberichten`). Het bericht is plain text (geen HTML). `dateFrom`/
 * `dateTill` zijn ISO-datum+TIJD-strings (`YYYY-MM-DDTHH:mm`, zonder tijdzone):
 * de UI gebruikt `datetime-local`-velden en de BE slaat exact op wat de
 * gebruiker invoert — GEEN start/end-of-day-normalisatie. Het actief-venster
 * wordt op de volledige timestamp vergeleken (`dateFrom <= nu <= dateTill`),
 * beide grenzen inclusief.
 *
 * `userRule`/`ownerId` zitten NIET in de DTO: ze worden server-side bepaald
 * (`userRule = "*"`, `ownerId = system-context-company`) en mogen niet via de
 * wire vertrouwd worden.
 */
export interface SystemMessage {
  id: string;
  /** Plain text. */
  message: string;
  /** Zichtbaar vanaf (ISO-datum+tijd `YYYY-MM-DDTHH:mm`, inclusief). */
  dateFrom: string | null;
  /** Zichtbaar t/m (ISO-datum+tijd `YYYY-MM-DDTHH:mm`, inclusief). */
  dateTill: string | null;
  /** Wegklikbaar door de gebruiker (banner-kruisje). `null`/`false` ⇒ geen kruisje. */
  canDeleteMessage: boolean | null;
  /** Afgeleide read-only status uit het datumvenster (beheerlijst). */
  status?: 'active' | 'upcoming' | 'expired';
  /** Audit-veld — alleen door de single-record-GET gepopuleerd. */
  created?: string | null;
  /** Optimistic-concurrency-versie (round-trip op update). */
  version?: number | null;
  /** De CRUD-scaffold leest dit voor de delete-variant; systeembericht = altijd hard-delete. */
  canDelete?: boolean;
}

/**
 * Het ge-actieve-feed-item van de banner (`GET /api/system/messages/active`).
 * Bewust minimaal: alleen wat de banner toont — globaal, zonder filtering.
 *
 * `version` is nodig voor de per-apparaat wegklik-onthouding: de banner onthoudt
 * een wegklik als `"<id>:<version>"`. Wordt het bericht daarna bewerkt (Version
 * loopt op), dan matcht de opgeslagen dismiss niet meer en verschijnt het
 * bericht OPNIEUW (de gebruiker moet een gewijzigde melding weer zien).
 */
export interface ActiveSystemMessage {
  id: string;
  message: string;
  canDeleteMessage: boolean;
  /** Optimistic-concurrency-versie — onderdeel van de dismiss-sleutel `id:version`. */
  version: number;
}

/**
 * Write-request voor create/update. `userRule`/`ownerId` worden NIET meegestuurd
 * — die forceert de BE server-side (de UI kent ze niet). `dateFrom`/`dateTill`
 * zijn ISO-datum+TIJD-strings (`YYYY-MM-DDTHH:mm`); de BE slaat ze exact op
 * (geen normalisatie).
 */
export interface SystemMessageWriteRequest {
  message: string;
  dateFrom: string | null;
  dateTill: string | null;
  canDeleteMessage?: boolean | null;
}

// ─── Nieuwsberichten (GS_NewsMessage) ────────────────────────────────────────

/**
 * Doelgroep-discriminator (story `fe-be-nieuwsberichten`, F-01) — de enige
 * scoping-sleutel en BINAIR: `System` (iedereen) of `Keten` (de keten van deze
 * instantie; welke keten = de `KetenHoofdkantoor`-config BE-side).
 */
export type NewsAudience = 'System' | 'Keten';

/** Eén bijlage van een nieuwsbericht. */
export interface NewsAttachment {
  id: string;
  name: string;
  sizeBytes: number;
  mimeType: string | null;
  /** Echte download-URL (BE her-gate't op het parent-bericht). */
  downloadUrl: string;
}

/**
 * Eén nieuwsbericht (read-DTO) — gedeeld door de feed (dashboard/menu/popup), de
 * detail-weergave én het beheer-formulier. `bodyHtml` is BE-side gesanitiseerd
 * (allowlist; stored-XSS-preventie) → veilig voor `dangerouslySetInnerHTML`.
 * `dateFrom`/`dateTill` zijn ISO datum+TIJD-strings (`YYYY-MM-DDTHH:mm`); de BE
 * slaat exact op (geen normalisatie). `version` voedt de FE-dismiss-/read-sleutel
 * `id:version`.
 */
export interface NewsMessage {
  id: string;
  title: string;
  bodyHtml: string | null;
  audience: NewsAudience;
  /** Leesbare doelgroep-badge ("Iedereen" / de keten-naam van deze instantie). */
  audienceLabel: string;
  dateFrom: string | null;
  dateTill: string | null;
  showAsPopup: boolean;
  /** Afgeleide read-only status uit het datumvenster (beheerlijst). */
  status?: 'active' | 'upcoming' | 'expired';
  /** Aanmaakdatum (ISO) — tiebreak van de gedeelde feed-sortering. */
  created?: string | null;
  version: number;
  attachmentCount: number;
  /** Volledige bijlage-lijst (alleen in de detail-read; leeg/`null` in de feed). */
  attachments?: NewsAttachment[] | null;
  /** Mag de huidige caller dit bericht beheren (system ‖ eigen keten)? */
  canManage?: boolean;
}

/**
 * Write-request (create/update). `audience` wordt voor een keten-user server-side
 * geforceerd (client-waarde genegeerd — hard-rule). `version` is verplicht bij
 * update (optimistic concurrency → 409 bij mismatch).
 */
export interface NewsMessageWriteRequest {
  title: string;
  bodyHtml: string | null;
  audience: NewsAudience;
  dateFrom: string | null;
  dateTill: string | null;
  showAsPopup: boolean;
  version?: number | null;
}

// ─── Inrichting / UE_PriceList ───────────────────────────────────────────

export interface PriceListListItem {
  id: string;
  description: string;
  remarks: string | null;
  ownerCompanyId: string | null;
  ownerCompanyName: string | null;
  accessTypeId: string | null;
  accessTypeCode: string | null;
  vatId: string | null;
  currencyId: string | null;
  supplierId: string | null;
  startDate: string | null;
  expiryDate: string | null;
  isSales: boolean;
  onlyDefinedItems: boolean;
  isLinkedToCurrentCompany: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /**
   * De referentieprijslijst ("bovenliggende" lijst): heeft deze lijst voor een
   * artikel geen eigen prijs, dan wordt die van de referentielijst gebruikt.
   */
  referencePricelistId: string | null;
  /** Omschrijving van de referentieprijslijst (server-derived, geen extra lookup nodig). */
  referencePricelistName: string | null;
  /** Verschil met de referentieprijslijst niet als korting op de calculatieregel tonen. */
  hideDiscountFromReferencePricelist: boolean;
  /** Audit-velden — alleen gepopuleerd door de single-record-GET, niet in de lijst. */
  created?: string | null;
  createdByName?: string | null;
  modified?: string | null;
  modifiedByName?: string | null;
  version?: number | null;
  /** "Active" / "Archive" / "Deleted" — uit GS_RecordStatus. */
  recordStatusCode?: 'Active' | 'Archive' | 'Deleted' | null;
}

export interface PriceListWriteRequest {
  description: string;
  remarks?: string | null;
  accessTypeId?: string | null;
  vatId?: string | null;
  currencyId?: string | null;
  supplierId?: string | null;
  startDate?: string | null;
  expiryDate?: string | null;
  isSales?: boolean | null;
  onlyDefinedItems?: boolean | null;
  /**
   * De gekozen referentieprijslijst, of `null` om de verwijzing te wissen.
   * De server weigert een keuze die een kring in de keten zou maken (400 met
   * `reason: 'reference_pricelist_cycle'` / `..._self`).
   */
  referencePricelistId?: string | null;
  hideDiscountFromReferencePricelist?: boolean | null;
}

// ─── Inrichting / UE_PriceListTier (staffels) ────────────────────────────

/**
 * De vijf glassoorten waarvoor een afwijkende staffel kan bestaan. De wire-
 * waarden zijn Engels en worden server-side EXACT vergeleken
 * (`PriceListTierVocabulary.IsKnownGlassKind`) — `"windscreen"` of
 * `"Voorruit"` levert een 400. De Nederlandse labels horen in de FE.
 */
export type PriceListTierGlassKind =
  | 'Windscreen'
  | 'Backlight'
  | 'SideWindow'
  | 'RoofGlass'
  | 'Accessory';

/**
 * Eén prijsband. Alleen de BOVENgrens wordt opgeslagen: de ondergrens is de
 * bovengrens van de vorige band en de eerste band begint bij 0,00.
 *
 * ⚠ Alle drie de bedrag-velden zijn **nullable en `0` betekent iets anders dan
 * `null`**:
 *  - `toAmount: null` → "en hoger", de verplichte laatste band;
 *  - `percentage: null` → 100 % (prijs blijft ongewijzigd); `0` zou de prijs op
 *    0,00 zetten en wordt server-side geweigerd;
 *  - `surcharge: null` → geen bedrag erbij; `0` is juist een geldige waarde.
 */
export interface PriceListTierDto {
  toAmount: number | null;
  percentage: number | null;
  surcharge: number | null;
  sortOrder: number;
}

/** Alle banden van één glassoort. `glassKind: null` = alle artikelen. */
export interface PriceListTierGroupDto {
  glassKind: PriceListTierGlassKind | null;
  tiers: PriceListTierDto[];
}

/**
 * Wat er vandaag in `UE_PriceList.UnitPriceRule` staat.
 *
 * ⚠ `'supplier'` is er in september 2026 bijgekomen (feature "prijslijst op
 * leveranciersprijzen"): een regel die vóór de uitbreiding van
 * `PriceListRuleClassifier` nog als `'foreign'` binnenkwam. Elke consument die
 * op `'foreign'` vertakt moet daarom óók op `'supplier'` vertakken — anders valt
 * een leveranciersregel door naar een editor die hem niet mag aanraken. Zie
 * `TierTab` (S2 AC7a).
 */
export type PriceListUnitPriceRuleKind = 'none' | 'tier' | 'supplier' | 'foreign';

/** Antwoord van `GET /api/inrichting/pricelists/{id}/tiers`. */
export interface PriceListTiersResponse {
  canEdit: boolean;
  /** `pricelist_not_owned` of `pricelist_price_is_calculated`; `null` als bewerken mag. */
  canEditReason: string | null;
  unitPriceRuleKind: PriceListUnitPriceRuleKind;
  /** Alleen een waarde > 0 komt door; nul/leeg/negatief zijn `null`. */
  hourlyRate: number | null;
  ownerCompanyName: string | null;
  version: number | null;
  /** Nooit `null`; een lege array betekent "nog geen staffel". */
  groups: PriceListTierGroupDto[];
}

/** Body van `PUT /api/inrichting/pricelists/{id}/tiers`. */
export interface PriceListTiersWriteRequest {
  /** De versie zoals de client die kent; afwijkend → 409 `pricelist_version_conflict`. */
  version: number | null;
  /** "Staffel gebruiken op deze prijslijst". Uit bewaart de banden en maakt de regel leeg. */
  isEnabled: boolean;
  groups: PriceListTierGroupDto[];
}

// ─── Inrichting / prijslijst op leveranciersprijzen ──────────────────────

/**
 * De wijze waarop de prijzen van de gekozen leveranciers tot één prijs komen.
 * Wire-waarden zijn STRIKT kleine letters: de backend vergelijkt exact
 * (`SupplierPriceVocabulary.IsKnownMode`), dus `'Lowest'` levert een 400 met
 * `supplier_mode_invalid`. De Nederlandse labels horen in de FE.
 */
export type PriceListSupplierPriceMode = 'lowest' | 'average' | 'highest';

/**
 * De afronding ná het kiezen van de prijs. `'cents'` is twee decimalen (de
 * default-tak van de motor), `'none'` laat de waarde onafgerond.
 */
export type PriceListSupplierPriceRounding = 'none' | 'up' | 'down' | 'cents';

/** Eén leverancier zoals het inrichtingsscherm hem toont. */
export interface PriceListSupplierDto {
  id: string;
  code: string | null;
  /** Provider-/prijslijstnaam met de code als terugval; `null` bij `isKnown: false`. */
  name: string | null;
  isActive: boolean;
  /**
   * Hangt er een provider aan deze leverancier? Zo niet, dan hangt hij alleen aan
   * een prijslijst en valt `name` terug op die prijslijstnaam — vandaar regels als
   * "Lege prijslijst". Het scherm kan hierop filteren.
   *
   * ⚠ Bij `isKnown: false` staat hier `false` omdat het onbekend is, niet omdat
   * het zeker geen provider is. Een onbekende leverancier hoort daarom altijd
   * zichtbaar te blijven, ook met het filter aan.
   */
  isProvider: boolean;
  /**
   * `false` = deze leverancier staat wél in de prijsregel maar niet (meer) in de
   * company-set van de stock-API. Hij blijft in de lijst staan: stil wegvallen
   * maakt "de laagste van drie" ongemerkt "de laagste van twee".
   */
  isKnown: boolean;
}

/** Antwoord van `GET /api/inrichting/pricelists/{id}/supplier-prices`. */
export interface PriceListSupplierPricesResponse {
  canEdit: boolean;
  /** `pricelist_not_owned`, `price_rule_not_generated_by_us` of `pricelist_price_is_calculated`. */
  canEditReason: string | null;
  unitPriceRuleKind: PriceListUnitPriceRuleKind;
  /**
   * Is de legacy-tag `<_SupplierPrice>` uitgerold? `false` vergrendelt
   * uitsluitend het AANZETTEN — nooit het tonen en nooit het uitzetten.
   */
  tagAvailable: boolean;
  /** Staat er vandaag een leveranciersregel op deze lijst? */
  isEnabled: boolean;
  mode: PriceListSupplierPriceMode;
  rounding: PriceListSupplierPriceRounding;
  /** De gekozen leveranciers, in de volgorde van de regel. */
  supplierIds: string[];
  suppliers: PriceListSupplierDto[];
  /** De stock-API antwoordde niet. Geen fout: de GET blijft 200. */
  suppliersUnavailable: boolean;
  /** Het aantal eigen `UE_Price`-rijen op deze lijst; die vervallen bij aanzetten. */
  ownPriceCount: number;
  ownerCompanyName: string | null;
  version: number | null;
}

/** Body van `PUT /api/inrichting/pricelists/{id}/supplier-prices`. */
export interface PriceListSupplierPricesWriteRequest {
  version: number | null;
  /** Uit = een LEGE prijsregel; dat wist tegelijk de selectie (de regel is de enige bron). */
  isEnabled: boolean;
  mode: PriceListSupplierPriceMode;
  rounding: PriceListSupplierPriceRounding;
  supplierIds: string[];
}

/** Body van `POST …/supplier-prices/preview`. */
export interface PriceListSupplierPricePreviewRequest {
  eurocode: string;
  mode: PriceListSupplierPriceMode;
  rounding: PriceListSupplierPriceRounding;
  supplierIds: string[];
}

/** Wat één leverancier in de proefbevraging teruggaf. */
export interface PriceListSupplierPricePreviewRow {
  supplierId: string;
  supplierName: string | null;
  /**
   * ⚠ Heet bewust `hasPrice` en niet `answered`. Het dekt drie werkelijkheden
   * die met de huidige legacy niet te onderscheiden zijn: de leverancier
   * antwoordde niet, hij gaf nul of minder, of er stond een gecachete nul uit
   * een eerdere storing. "Geen antwoord" zou in dat derde geval onjuist zijn.
   */
  hasPrice: boolean;
  price: number | null;
}

/** Antwoord van de proefbevraging. */
export interface PriceListSupplierPricePreviewResponse {
  eurocode: string;
  itemDescription: string | null;
  /** Evenveel rijen als er ids gestuurd zijn, in dezelfde volgorde. */
  results: PriceListSupplierPricePreviewRow[];
  /** Het aantal leveranciers dat meetelde (prijs groter dan nul). */
  usedCount: number;
  /** De uitkomst vóór afronden; `null` wanneer niemand meetelde. */
  rawResult: number | null;
  /** De uitkomst ná afronden; `0` wanneer niemand meetelde. */
  result: number;
}

/**
 * S007.10 — Per-veld policy (visibility + editability). Bron: server-side
 * resolver (`CsFieldDisplayPolicyService`) op basis van de `CS_FieldDisplay`-
 * row voor de huidige `UserContextType` + huidige workflow-state.
 *
 * - `visible` = veld zit in `readableFields` (mag de waarde tonen).
 * - `editable` = veld zit in `editableFields` AND ContextType-match AND
 *   state-match AND (optionele) gate-override. Defensieve default = `false`.
 *
 * Een typo in een `fieldCode` levert op de FE een `{visible:false, editable:
 * false}`-lookup op — er is bewust geen branded-type-bescherming in V1
 * (zie refined story F-17).
 */
export interface FieldPolicy {
  fieldCode: string;
  visible: boolean;
  editable: boolean;
}

export interface EntitySchema {
  entity: string;
  userContextType: UserContextType;
  readableFields: string[];
  editableFields: string[];
  /**
   * Default kolommen + filter-velden uit `CS_FieldDisplay` voor de huidige
   * user-context. Optioneel: bestaat alleen als de DB-display-data ingevuld is
   * voor deze entiteit. EPIC-005.
   */
  systemListLayout?: SystemListLayout | null;
  /**
   * S007.10 — Per-veld policy (visibility + editability) zoals door de
   * server-side `CsFieldDisplayPolicyService` afgeleid.
   *
   * Nullable / optioneel voor backward-compat: oudere controllers (en V1
   * UA_Dossier) leveren dit veld (nog) niet. In V1 vullen alleen
   * `/api/inrichting/itemtypes/schema` en `/api/inrichting/pricelists/schema`
   * deze property — gemirrord uit `editableFields[]`:
   *
   *     fieldPolicies = readableFields.map(fc => ({
   *       fieldCode: fc,
   *       visible: true,
   *       editable: editableFields.includes(fc),
   *     }))
   *
   * Schema-driven enforcement via `PolicyService` start vanaf S007.11.
   */
  fieldPolicies?: FieldPolicy[];
}

export interface SystemListLayout {
  entityLabelSingular: string;
  entityLabelPlural: string;
  iconCode: string | null;
  appGroup: string | null;
  listRoute: string | null;
  columns: ListColumn[];
  filterFields: FilterField[];
}

export interface ListColumn {
  fieldCode: string;
  label: string;
  order: number;
  width: number | null;
  align: 'L' | 'C' | 'R' | null;
}

export interface FilterField {
  fieldCode: string;
  label: string;
}

// ──────────────────────────────────────────────────────────────────────────
// Story: extra-artikelen-modal
//
// GET /api/dossiers/{dossierId}/additive-groups — levert de Standaard / Eigen /
// Keten-groepen met categorieën + items. Per item alleen `id` + `name`: de
// drie ItemCodes (Carculate/Exchange/Dispatch) blijven BE-side; de PolicyService-
// mapping naar de juiste ItemCode gebeurt server-side bij bulk-add.
//
// POST /api/dossiers/{dossierId}/calculation-rows/bulk-add — atomic multi-item
// add (M-eam.02). Cross-tenant additiveItemIds worden BE-side afgewezen met
// 422 InvalidAdditiveItemId (AC 4.6 / IDOR-guard).
// ──────────────────────────────────────────────────────────────────────────

/** Eén selecteerbaar artikel — alleen id + display-naam. */
export interface AdditiveItemDto {
  id: string;
  name: string;
}

/** Eén categorie binnen een groep (bv. "Kit", "Kalibratie"). */
export interface AdditiveCategoryDto {
  name: string;
  /** 1..15 — positie voor sortering (legacy UA_AdditiveLocation). */
  location: number;
  items: AdditiveItemDto[];
}

/** Stabiele key per groep — FE gebruikt 'm o.a. voor het tab-state-bookkeeping. */
export type AdditiveGroupKey = 'default' | 'own' | 'chain';

/** Eén groep (Standaard / Eigen / Keten). */
export interface AdditiveGroupDto {
  key: AdditiveGroupKey;
  /** Display-naam (`"Standaard"` / `"Eigen"` / `"Keten"`). */
  name: string;
  categories: AdditiveCategoryDto[];
}

/** Response-shape voor het additive-groups GET-endpoint. */
export interface AdditiveGroupsResponseDto {
  groups: AdditiveGroupDto[];
}

/** Body voor `POST /api/dossiers/{id}/calculation-rows/bulk-add`. */
export interface BulkAddCalculationRowsRequestDto {
  /** Strict-lowercase `"filiaal"` in V1. */
  calculationCode: string;
  /** GUIDs van geselecteerde `UA_AdditiveItem`s — minimaal 1, max 50. */
  additiveItemIds: string[];
  /**
   * Verplichte prijslijst-GUID — komt uit `useDossierCalculationPricelist`
   * (`data.selectedPricelistId`). Zelfde patroon als single-add S009.5; voorkomt
   * dat BE en FE de pricelist verschillend resolven (BE keek alleen naar
   * `Dossier.DossierPricelistID.Item1`, wat onder een gevulde calc op een
   * andere/verborgen pricelist `null` opleverde → 422 `NoPricelistAssigned`).
   *
   * Bij `Guid.Empty` of leeg → BE 400 `PricelistRequired`; bij niet-bestaand/
   * niet-zichtbaar → BE 404 `PricelistNotFound`.
   */
  pricelistId: string;
}

/** Response-shape voor de bulk-add POST. */
export interface BulkAddCalculationRowsResponseDto {
  /** Aantal IDs na server-side dedupe. */
  requestedCount: number;
  /** Aantal daadwerkelijk toegevoegde rijen. */
  addedCount: number;
  /** Item-IDs die geen ItemCode voor de huidige PolicyService hadden — debug. */
  skippedItemIds: string[];
  /** RowNumbers van toegevoegde rijen — zelfde semantiek als single-add. */
  addedRowNumbers: number[];
  /** Totaal aantal rijen na de save. */
  totalRowsAfter: number;
}

/**
 * Request voor `POST /api/dossiers/{id}/calculation-rows/bulk-delete`
 * (story `fix-calculatie-batch-delete`, 2026-06-11). Alle te verwijderen
 * rijen in één save — nodig omdat `UA_DossierSaveCalculation` delete-and-replace
 * doet en row-GUID's hernummert, waardoor N losse DELETEs op dode id's landen.
 */
export interface BulkDeleteCalculationRowsRequestDto {
  /** `UA_DossierRow.ID`-GUID's die verwijderd moeten worden — minimaal 1. */
  rowIds: string[];
  /** Strict-lowercase `"filiaal"` in V1. */
  calculationCode: string;
}

/**
 * Response van de batch-delete. `deletedRowCount` telt parent + child-cascades;
 * `notFoundCount` de aangeleverde id's die al weg waren (idempotent).
 */
export interface BulkDeleteCalculationRowsResponseDto {
  requestedCount: number;
  deletedRowCount: number;
  notFoundCount: number;
  totalRowsAfter: number;
}

// ─── Eigen artikelen (story fe-eigen-artikelen-picker, 2026-06-01) ────────

/**
 * Eén item-type / leverancier-optie in de lookups-DTO van de Eigen-artikelen-
 * picker. Output-shape `{ id, name }` zoals het BE-contract het levert
 * (verschilt van de generieke `{ id, description }`-lookups).
 */
export interface OwnItemsLookupOptionDto {
  id: string;
  name: string;
}

/**
 * Response van `GET /api/dossiers/{id}/own-items/lookups`.
 *
 * - `itemTypes` / `suppliers` voeden de Groep- en Leverancier-dropdowns.
 * - `defaultItemTypeId` = invoice-setting `DefaultItemTypeOwnItems`. Wanneer
 *   non-null én ∈ `itemTypes` → de Groep-dropdown staat vóór-geselecteerd en
 *   de modal vuurt bij openen één initiële zoekactie (legacy-pariteit met
 *   `SearchOwnItems.ascx`). `null` → lege "—"-optie + placeholder-state.
 * - `isCentralCustomer` = derived `UA_Dossier.IsCentralCustomer`. De lookups-
 *   endpoint weigert NIET bij een centraal dossier (geen 403) — de FE leest
 *   deze vlag om de knop te disablen / een toast te tonen.
 */
export interface OwnItemsLookupsDto {
  itemTypes: OwnItemsLookupOptionDto[];
  suppliers: OwnItemsLookupOptionDto[];
  defaultItemTypeId: string | null;
  isCentralCustomer: boolean;
}

/**
 * Eén eigen artikel uit `GET /api/dossiers/{id}/own-items`. `price` is
 * `null` wanneer het artikel geen prijs heeft (kolom toont em-dash; 0 is een
 * geldige prijs en wordt als `€ 0,00` getoond).
 */
export interface OwnItemDto {
  /** `UE_Item.ID` als lowercase-dashed Guid-string. */
  id: string;
  /** `UE_Item.Code`. */
  code: string;
  /** `UE_Item.Description`. */
  description: string;
}

/**
 * Query-params voor `GET /api/dossiers/{id}/own-items`. Alle optioneel; lege
 * velden worden niet meegestuurd (BE delegeert 1-op-1 aan
 * `UE_Item_SearchOwnItems("", "", null, null)` — geen verplicht-veld-check).
 */
export interface OwnItemsSearchParams {
  /**
   * Gecombineerde 1-veld-zoekterm: matcht zowel code als omschrijving (de BE
   * draait 'm in twee company-scoped searches en dedupliceert). Heeft voorrang
   * op de losse `code`/`description` wanneer gezet.
   */
  query?: string;
  code?: string;
  description?: string;
  itemTypeId?: string;
  supplierId?: string;
}

/**
 * Body voor `POST /api/dossiers/{id}/calculation-rows/bulk-add-own-items`.
 *
 * **Let op** — de body-key is `itemIds` (directe `UE_Item.ID`'s), NIET
 * `additiveItemIds`. Het additieven-`bulk-add`-endpoint valideert tegen de
 * additive-groups-set + mapt via een policy-service-`ItemCode`; eigen
 * artikelen hebben die snapshot-structuur niet en gaan via een dedicated
 * endpoint dat tegen de company-eigen `UE_Item`-set valideert.
 */
export interface BulkAddOwnItemsRequestDto {
  /** Strict-lowercase `"filiaal"` in V1. */
  calculationCode: string;
  /** GUIDs van geselecteerde `UE_Item`s — minimaal 1. */
  itemIds: string[];
  /** Verplichte prijslijst-GUID — uit `useDossierCalculationPricelist`. */
  pricelistId: string;
}

// ─── Inkoopprijzen — eurocode-calc-regel (story fe-inkoopprijzen-calculatieregel) ──

/**
 * Eén leverancier-rij in de inkoopprijs-tooltip
 * (kolommen Leverancier · Status · Inkoopprijs).
 */
export interface PurchasePriceSupplier {
  /** Leverancier-naam (kolom "Leverancier"). */
  providerName: string;
  /**
   * Pass-through display-string voor de Status-kolom (Contract C-2). De
   * stock-API levert al een leesbare status; de FE rendert 'm verbatim
   * (geen enum-mapping/vertaling FE-side).
   */
  available: string;
  /**
   * Numerieke inkoopprijs (Contract C-1). De FE doet de NL-format
   * (`formatEur`). `null` wanneer de stock-API geen prijs gaf.
   */
  unitPrice: number | null;
  /**
   * S022.9 (A.5): de leverancier gaf een foutstatus — de voorraad was niet op te vragen. Prijs en aantal zijn dan 0
   * en betekenen niets; de FE toont "voorraad niet op te vragen". Alleen aanwezig als `true`.
   */
  stockUnavailable?: boolean;
}

/**
 * Eén inkoopprijs-resultaat voor een unieke eurocode (Contract C-3: meerdere
 * calc-regels met dezelfde eurocode delen dit result).
 */
export interface PurchasePrice {
  /** De eurocode (koppel-sleutel naar de calc-regel(s)). */
  euroCode: string;
  /**
   * Of de wagen-indicator getoond mag worden. `false` → géén icoon (D-4,
   * geen grijze placeholder).
   */
  showCart: boolean;
  /** Leveranciers met hun inkoopprijs. Kan leeg zijn. */
  suppliers: PurchasePriceSupplier[];
  /**
   * **Additief** (story `fe-be-plaats-op-bestellijst`, AC 1.3): het totaal
   * aantal van deze eurocode dat al op de bestellijst staat voor dit dossier
   * (som van `quantityAlreadyPreOrdered` uit de stock-info). De FE toont
   * "al op bestellijst" bij `alreadyPreOrdered > 0` en biedt dan geen tweede
   * plaatsing aan (dedup, D-E). Optioneel/backwards-compatible — afwezig of
   * `0` wanneer er niets voorbesteld is (breekt de read-story niet).
   */
  alreadyPreOrdered?: number;

  // ─── Voorraadtabel-blok (story fe-be-voorraadtabel-tooltip, ADDITIEF) ─────
  // Surfacet de stockLocationInfo[] + totalen die de stock-API al teruggeeft.
  // Allemaal optioneel/backwards-compatible — afwezig wanneer de stock-API
  // geen voorraad-info levert.

  /** `true` wanneer dit een voorraad-artikel is — toont Min/Vrij/Beschikbaar op de Totaal-rij. */
  isStockItem?: boolean;
  /** `true` wanneer dit artikel al besteld is → groene winkelwagen + "besteld bij X"-regel. */
  alreadyOrdered?: boolean;
  /** Leverancier waar besteld is (voor de "besteld bij {orderedSupplierName}"-regel). */
  orderedSupplierName?: string | null;
  /** Bestelprijs (voor de "voor {orderedPrice}"-regel). `null` wanneer onbekend. */
  orderedPrice?: number | null;
  /** Totaal aantal op voorraad over alle locaties — "Vrd" op de Totaal-rij. */
  stockInStock?: number | null;
  /** Totaal beschikbaar over alle locaties — "Beschikbaar" op de Totaal-rij. */
  stockAvailable?: number | null;
  /** Per-locatie voorraadtabel-rijen (Locatie · Code · Vrd · Geres · Bestel · Order · Min · Vrij · Beschikbaar). */
  stockLocations?: PurchasePriceStockLocation[];

  // ─── EPIC-022 S022.4: eigen voorraad (alleen bij `stockSource: 'carculate'`) ─────
  /** Het artikel bij deze eurocode (één treffer). */
  itemId?: string;
  /** Per actief eigen magazijn één rij (ook met 0); ontbreekt bij een meerduidige eurocode of een leesfout. */
  ownStock?: OwnStockRow[];
  /** De eurocode hoort bij meer dan één artikel: geen rijen. */
  ownStockAmbiguous?: boolean;
  /** S022.5 (AC 33): besteld zonder standaardmagazijn voor ontvangst (alleen > 0). */
  orderedWithoutWarehouse?: number;
}

/**
 * Eén voorraadtabel-rij in de inkoopprijs-tooltip (story
 * `fe-be-voorraadtabel-tooltip`). Kolommen Locatie · Code · Vrd · Geres ·
 * Bestel · Order · Min · Vrij · Beschikbaar. Min/Vrij/Beschikbaar worden FE-side
 * alleen getoond bij `isStockItem` (anders leeg, zoals legacy).
 */
export interface PurchasePriceStockLocation {
  /** Locatie-/contact-naam (kolom "Locatie"). */
  contactName?: string | null;
  /** De eurocode van deze rij (kolom "Code"). */
  code?: string | null;
  /** Aantal op voorraad (kolom "Vrd"). */
  inStock?: number | null;
  /** Aantal gereserveerd (kolom "Geres"). */
  reserved?: number | null;
  /** Aantal voorbesteld (kolom "Bestel"). */
  preOrdered?: number | null;
  /** Aantal op order (kolom "Order"). */
  ordered?: number | null;
  /** Minimum-voorraad (kolom "Min", alleen bij isStockItem). */
  minimum?: number | null;
  /** Vrije voorraad (kolom "Vrij", alleen bij isStockItem). */
  free?: number | null;
  /** Beschikbaar (kolom "Beschikbaar", alleen bij isStockItem). */
  available?: number | null;
  /** `true` → toon Min/Vrij/Beschikbaar voor deze rij. */
  isStockItem?: boolean;
}

/**
 * Response van `POST /api/dossiers/{id}/purchase-prices`. Uniforme shape
 * (Contract C-4): álle "geen prijzen"-paden geven `{ available:false,
 * items:[] }`; succes geeft `{ available:true, items:[…] }`.
 *
 * **Veiligheid** (AC 4.2): bevat géén bearer-token of stock-API-host — die
 * blijven server-side.
 */
export interface PurchasePricesResult {
  available: boolean;
  items: PurchasePrice[];
  /**
   * Diagnostiek-code die aangeeft wáárom `available:false` is (of `"ok"` bij
   * succes). Veilige enum-string — bevat nooit een token-/host-waarde. Bedoeld
   * voor de Network-tab / support; de FE gebruikt 'm niet functioneel.
   * Waarden: `ok` · `gate_off` · `no_rows` · `no_token` · `config_missing` ·
   * `read_error` · `stock_error`.
   */
  reason?: string;

  // ─── EPIC-022 S022.4: alleen bij een hersteller die zijn voorraad in CARculate bijhoudt ─────
  /** `'carculate'`; ontbreekt bij een stock-API-hersteller. De FE kiest de weergave UITSLUITEND hierop (nooit op whoami). */
  stockSource?: 'carculate';
  /** `false` = de eigen voorraad kon niet geladen worden (de prijzen wel). */
  ownStockAvailable?: boolean;
  /** De actieve reserveringen voor dit dossier. */
  reservations?: OwnReservation[];
  /** S022.5 (AC 34): `false` = Besteld kon niet geladen worden (de kolom toont "—", nooit 0). */
  orderedAvailable?: boolean;
}

// ─── Plaats op bestellijst (story fe-be-plaats-op-bestellijst) ──────────────

/**
 * Request-body voor `POST /api/dossiers/{id}/order-list` — de schrijf-actie
 * "Plaats in bestellijst" op een eurocode-calc-regel. Minimaal: de proxy
 * resolvt server-side het magazijn (D-A) + (optioneel) het aantal uit de
 * calc-regel. De FE stuurt géén token/host en géén provider-velden (V1
 * generieke plaatsing, `action="order"` impliciet).
 */
export interface PlaceOnOrderListRequest {
  /** De eurocode van de te-plaatsen regel. */
  euroCode: string;
  /** Het aantal; weglaten → server-side default uit de calc-regel. */
  quantity?: number;
}

/**
 * Response van `POST /api/dossiers/{id}/order-list`. De mutatie koppelt succes
 * én fout expliciet terug (D-D): `placed:true` → toast "Op bestellijst
 * geplaatst"; `placed:false` → fout-toast met de `reason` als diagnostiek.
 *
 * **Veiligheid** (AC 4.2): bevat géén bearer-token of stock-API-host.
 */
export interface OrderListResult {
  /** `true` bij een geslaagde plaatsing (stock-API HTTP 2xx). */
  placed: boolean;
  /**
   * Status-/diagnostiek-code. Waarden: `ok` · `already_placed` ·
   * `no_warehouse` · `no_token` · `config_missing` · `row_not_found` ·
   * `invalid_quantity` · `read_error` · `stock_error`. S022.4: `already_reserved` (met `warehouseName`) ·
   * `inventory_state_unknown`. 29-10-2026: `no_warehouse` = het bedrijf heeft geen magazijn-contact
   * (RB_Contact van het type Magazijn) in de voorraad-inrichting; de server maakt er geen aan.
   */
  reason?: string;
  /** S022.4: bij `already_reserved` het magazijn van de reservering. */
  warehouseName?: string;
}

// ─── Bestellijst-overzicht (story fe-be-bestellijst-overzicht, S-INK.1) ─────

/**
 * Eén reservering (klus) achter een voorbestelregel op de bestellijst.
 */
export interface OrderReservation {
  /** Kenteken. */
  license: string | null;
  /**
   * Reparatiedatum als ISO date-only (`yyyy-MM-dd`) of `null`. De FE doet
   * hierop een lokale date-only-vergelijking voor de lead-time-filter.
   */
  repairDateIso: string | null;
  /** Dossier-GUID — voedt de `<Link to="/dossiers/:id">`. */
  dossierId: string | null;
  /** Dossiernummer (display). */
  dossierNumber: string | null;
}

/**
 * Eén voorbestelregel op de bestellijst (read-only basisdata; geen prijzen).
 */
export interface OrderListLineDto {
  /** De eurocode. */
  euroCode: string | null;
  /** Vrije omschrijving (optioneel). */
  description: string | null;
  /** Stabiele id van de voorbestelregel (React-key / latere mutaties). */
  preOrderLineId: string | null;
  /** Benodigd aantal. */
  quantityNeeded: number;
  /** Eigen vrije voorraad (kolom "Vrd"). Ontbreekt bij een CARculate-hersteller (S022.4). */
  freeStock?: number;
  /**
   * Minimum-voorraad (kolom "Min") — `SUM(StockItem.MinimumQuantity)` per
   * (ItemID, ContactID). Regel zonder stockitem → 0. Optioneel zodat oudere
   * responses (van vóór de 4-wensen-story) zonder breuk passen.
   */
  minimumQuantity?: number;
  /**
   * Gereserveerd aantal (kolom "Geres.") — `COUNT(*)` reserveringen per ItemID
   * binnen de company (zonder contact-filter). Regel zonder reservering → 0.
   * Optioneel zodat oudere responses zonder breuk passen.
   */
  reservedCount?: number;
  /** De gekoppelde reservering(en). Kan leeg zijn. */
  reservations: OrderReservation[];
  /**
   * Kenteken-preview-popup-velden (Bestellijst) — mee-geladen uit de
   * dossier-summary zodat de popup instant uit de rij-data rendert (geen
   * per-open `getDossier`-fetch). Allen optioneel zodat oudere responses
   * (van vóór deze story) zonder breuk passen; een regel zonder dossier →
   * null. `damageDate` is een ISO date-only string (`yyyy-MM-dd`), net als
   * `repairDateIso`.
   */
  customerName?: string | null;
  /** Merk (`UA_Dossier.ManufacturerText`). */
  manufacturer?: string | null;
  /** Model (`UA_Dossier.ModelText`). */
  model?: string | null;
  /** Type (`UA_Dossier.TypeDescription`). */
  type?: string | null;
  /** Schadedatum als ISO date-only (`yyyy-MM-dd`) of null. */
  damageDate?: string | null;
  /**
   * Story S-INK.7 (AC 34): de lopende of onzekere bestelronde van deze regel. Ontbreekt wanneer er geen
   * ronde loopt. Zo'n regel is niet toewijsbaar en telt niet mee in *Goedkoopste*.
   */
  orderRound?: OrderRoundInfo;
  /** S022.4: per eigen magazijn waar het artikel ligt de vrije stand (alleen bij `stockSource: 'carculate'`). */
  ownStock?: OrderListOwnStock[];
  /** S022.4: de actieve reservering op deze regel (opruimen staat nog open). Zo'n regel is niet toewijsbaar. */
  reservation?: OrderListLineReservation;
  /** S022.5 (AC 33): besteld zonder standaardmagazijn voor ontvangst (alleen > 0). */
  orderedWithoutWarehouse?: number;
}

/** Een lopende (`sending`) of onzekere (`uncertain`) bestelronde op een regel (S-INK.7, AC 34). */
export interface OrderRoundInfo {
  roundId: string;
  state: 'sending' | 'uncertain';
  supplierCode: string | null;
  /** Begin van de ronde, lokale tijd (`yyyy-MM-ddTHH:mm:ss`). */
  since: string;
  message: string;
}

/**
 * Eén leverancier-kolomdefinitie. **Geen prijsvelden** — de live prijzen
 * komen in de vervolgstory S-INK.2; in S-INK.1 toont de FE placeholder-cellen.
 */
export interface OrderSupplierDto {
  /** Stabiele leverancier-id (kolom-key). */
  id: string | null;
  /** Korte code/label voor de kolomkop. */
  code: string | null;
  /** `true` → live leverancier (📶); `false` → handmatig (🔌). */
  isExternalProvider: boolean;
  /** Provider-id (voor latere prijs-calls in S-INK.2). */
  providerId: string | null;
  /**
   * Story S-INK.7 (AC 37): staat de provider een bezorgmethode toe? Ontbreekt of `false` ⇒ de FE vraagt
   * geen bezorging en stuurt geen `shippingCondition`.
   */
  showShippingConditions?: boolean;
}

/**
 * Response van `GET /api/order-list` — de bestellijst-basislijst. Company-breed
 * (server-side gescoped). Een geslaagde call met een lege lijst is
 * `{ lines: [], suppliers: [] }`; een laad-FOUT is een HTTP-4xx/5xx (ApiError),
 * **niet** een 200-met-lege-lijst (AC 4.3 — fout ≠ leeg).
 *
 * **Veiligheid** (AC 5.2): bevat géén bearer-token of stock-API-host.
 */
export interface OrderListDto {
  /** De voorbestelregels. */
  lines: OrderListLineDto[];
  /** De leverancier-kolomdefinitie (geen prijzen). */
  suppliers: OrderSupplierDto[];
  /** S022.4: `'carculate'` bij een hersteller die zijn voorraad in CARculate bijhoudt; anders ontbreekt hij. */
  stockSource?: 'carculate';
  /** S022.4: `false` = de eigen voorraad kon niet geladen worden (reserveren kan dan niet). */
  ownStockAvailable?: boolean;
  /** S022.4: de actieve eigen magazijnen (op code). */
  ownWarehouses?: OwnWarehouse[];
  /** S022.5 (AC 34): `false` = Besteld kon niet geladen worden ("—", nooit 0). */
  orderedAvailable?: boolean;
}

// ─── Orders zoeken & inzien (story S-INK.3, V1 read-only) ──────────────────

/**
 * Status-segment voor `GET /api/orders` — één segmented control (concept
 * STATE 1). De BE vertaalt dit segment naar de 4 legacy-booleans
 * (`open`/`delivered`/`retour`/`retournotcompleted`) op de stock-API.
 */
export type OrderStatusSegment =
  | 'open'
  | 'delivered'
  | 'retour'
  | 'retournotcompleted'
  | 'all';

/** Eén orderlijst-regel (STATE 1 — lijst). */
export interface OrderSummaryDto {
  /** Stabiele order-id — voedt de `<Link to="/inkoop/orders/:orderId">`. */
  orderId: string | null;
  /** Ordernummer (display + link-label). */
  orderNumber: string | null;
  /** Orderdatum NL `dd-MM-yyyy` (server-genormaliseerd) of null. */
  orderDate: string | null;
  /** Leverancier-naam. */
  supplierName: string | null;
  /** Extern ordernummer (leeg → FE toont "—"). */
  orderNumberExt: string | null;
  /** Status-tekst (label, sinds iteratie 2 = `GS_CodeTableValue.Description`). */
  orderState: string | null;
  /**
   * Stabiele status-SystemCode (sinds iteratie 2, bv. `UA_OrderStatus_Complete`)
   * — de badge-mapping keyt hierop; afwezig/`null` → fallback op `orderState`.
   */
  statusCode?: string | null;
  /** Opmerking (leeg → FE toont "—"). */
  remark: string | null;
  /** Aantal regels (kolom "Regels"). */
  lineCount: number;
}

/**
 * Response van `GET /api/orders` — de orderlijst. Een geslaagde lege lijst is
 * `{ orders: [] }`; een laad-FOUT is een HTTP-4xx/5xx (`ApiError`), **niet** een
 * 200-met-lege-lijst (AC 4.1 — fout ≠ leeg). Bevat géén token/host (AC 5).
 */
export interface OrderListSearchDto {
  orders: OrderSummaryDto[];
}

/**
 * Server-afgeleide retour-credit-state (S-INK.6) — `none`/`not_received`/
 * `received`, berekend uit de status-GUID (`OrderStateID`/`OrderLine.StatusID`),
 * NIET uit de SystemCode. De twee credit-statussen én een gewone "Besteld"-order
 * delen ÁLLE DRIE `UA_OrderStatus_Ordered` → alleen deze derived state
 * discrimineert. De FE-gate/badge keyt UITSLUITEND hierop.
 */
export type RetourCreditState = 'none' | 'not_received' | 'received';

/** Eén orderregel (read-only) op het detail-scherm (STATE 2). */
export interface OrderLineDto {
  /**
   * Stabiele orderregel-GUID (bron `Warehouse.OrderLine.ID`) — read-only. Voedt
   * de ontvangst-boeken-write `PUT /api/orders/{orderId}/lines/{orderLineId}/receipt`
   * (S-INK.4). `null` → de FE rendert geen "Nu ontvangen"-invoer voor de regel
   * (AC-A2 — een regel zonder stabiele id kan niet geboekt worden).
   */
  orderLineId?: string | null;
  /**
   * Server-afgeleide per-regel retour-credit-state (S-INK.6) — alleen een regel
   * op `not_received` toont de "markeer credit ontvangen"-actie (concept STATE 3).
   * Afgeleid uit `OrderLine.StatusID`, NIET uit de SystemCode (collision).
   */
  retourCreditState?: RetourCreditState | null;
  /** Artikel-eurocode. */
  euroCode: string | null;
  /** Magazijn-naam. */
  warehouseName: string | null;
  /**
   * Dossier-GUID — voedt de `<Link>` naar het dossier. `null`/leeg/all-zero-GUID
   * = voorraad-regel (geen dossier → FE toont "— (voorraad)" zonder link, AC 2.4).
   */
  dossierId: string | null;
  /** Dossiernummer (display). */
  dossierNumber: string | null;
  /** Kenteken. */
  license: string | null;
  /** Besteld aantal. */
  quantity: number;
  /** Reeds ontvangen. */
  quantityReceived: number;
  /**
   * Openstaand (= besteld − ontvangen, bron-of-truth). Server clampt ≥ 0
   * (AC 2.2a — geen FE-herberekening, nooit negatief).
   */
  backorderQuantity: number;
  /** Regelprijs (voor ordertotaal-som, AC 2.3). */
  price: number;
  /** S022.5 (AC 6): alleen bij een CARculate-hersteller en een regel met dossier: gefactureerd of gearchiveerd. */
  dossierClosed?: boolean;
  /** S022.5 (AC 11): een onafgemaakte ontvangst (voorraad geboekt, order niet bijgewerkt). */
  pendingReceipt?: PendingOrderReceipt;
}

/** Eén volledig order met regels (STATE 2 — detail). */
export interface OrderDto {
  orderId: string | null;
  orderNumber: string | null;
  /** Orderdatum NL `dd-MM-yyyy` of null. */
  orderDate: string | null;
  supplierName: string | null;
  orderNumberExt: string | null;
  orderState: string | null;
  /** Stabiele status-SystemCode (iteratie 2) — zie {@link OrderSummaryDto.statusCode}. */
  statusCode?: string | null;
  /**
   * Server-afgeleide retour-credit-state op order-niveau (S-INK.6, aggregaat):
   * `not_received` zodra minstens één regel nog op `not_received` staat. De FE
   * gate/badge keyt UITSLUITEND hierop — NOOIT op de SystemCode (collision).
   */
  retourCreditState?: RetourCreditState | null;
  remark: string | null;
  lines: OrderLineDto[];
}

/**
 * Response van `GET /api/orders/{orderId}` — het order-detail. `order` is
 * `null` wanneer de stock-API een geslaagde 200 maar geen order teruggeeft
 * (AC 2.7 — "niet gevonden" ≠ laad-fout; een laad-fout is een HTTP-4xx/5xx).
 */
export interface OrderDetailDto {
  order: OrderDto | null;
  /** S022.5 (AC 5): `'carculate'` bij een hersteller die zijn voorraad in CARculate bijhoudt; anders afwezig. */
  stockSource?: 'carculate';
  /** S022.5 (AC 5): de actieve eigen magazijnen (bestemming van een ontvangst). */
  warehouses?: OrderReceiptWarehouse[];
}

/**
 * Request-body voor `PUT /api/orders/{orderId}/lines/{orderLineId}/receipt`
 * (S-INK.4 — ontvangst boeken). De FE stuurt UITSLUITEND het nu-ontvangen aantal
 * (DELTA, niet het nieuwe totaal) + optionele opmerking; `supplierId`/`contactId`
 * + de aantallen worden server-side uit de orderregel geresolved.
 */
export interface OrderReceiptRequest {
  /** Het nu-ontvangen aantal (DELTA). Moet 1..openstaand zijn (BE her-valideert). */
  numberToProcess: number;
  /** Optionele ontvangst-opmerking → `RemarksReceived`. */
  remark?: string;
  /**
   * S022.5 (AC 12): één GUID per invoer; gelijk bij een herhaalde klik met dezelfde inhoud. Altijd meegestuurd (een
   * stock-API-hersteller negeert hem).
   */
  clientRequestId?: string;
  /** S022.5 (AC 6/7): het bestemmingsmagazijn — alleen bij `stockSource === 'carculate'`. */
  warehouseId?: string;
  /** S022.5 (AC 8): alleen op de order boeken, niet in de voorraad (regel zonder artikel, na bevestiging). */
  withoutStock?: boolean;
}

/** Bevestigend resultaat van een geslaagde ontvangst-boeking (S-INK.4). */
export interface OrderReceiptResult {
  booked: boolean;
  orderLineId: string;
  /** Het nieuwe totaal-ontvangen (server-side = oude + DELTA). */
  quantityReceived: number;
  /** Het nieuwe openstaand (besteld − ontvangen, ≥ 0). */
  backorderQuantity: number;
  /** S022.5 (AC 9): alleen bij een CARculate-hersteller. */
  inventory?: OrderReceiptInventoryResult;
  /** S022.5 (AC 12): een herhaling van dezelfde invoer. */
  replayed?: boolean;
}

/**
 * Request-body voor `PUT /api/orders/{orderId}/lines/{orderLineId}/retour`
 * (S-INK.5 — retour aanmaken, concept STATE 1 Retour-kolom + STATE 2 dialog). De
 * FE stuurt UITSLUITEND het retour-aantal (DELTA, 1..quantityReceived) + optionele
 * reden + de verplichte Ja/Nee-keuze uit de bevestigingsdialog;
 * `supplierId`/`contactId` + de aantallen worden server-side uit de orderregel
 * geresolved.
 */
export interface OrderRetourRequest {
  /** Het retour-aantal (DELTA). Moet 1..quantityReceived zijn (BE her-valideert). */
  numberToProcess: number;
  /** Optionele retour-reden. */
  remark?: string;
  /**
   * De Ja/Nee-keuze uit de dialog: `true` = "Ja, nieuwe bestelregel", `false` =
   * "Nee, alleen retour". Altijd aanwezig in de body (de stock-API verwacht 'm
   * altijd — ook bij `false`).
   */
  createNewPreOrderLine: boolean;
}

/** Bevestigend resultaat van een geslaagde retour-aanmaak (S-INK.5). */
export interface OrderRetourResult {
  retourCreated: boolean;
  orderLineId: string;
  /** Het geretourneerde aantal (DELTA). */
  numberToProcess: number;
  /** De doorgegeven Ja/Nee-keuze (NIET door de BE overschreven). */
  newPreOrderLineRequested: boolean;
}

/**
 * Bevestigend resultaat van een geslaagde retour-credit-markering (S-INK.6,
 * concept STATE 3). Geen gefabriceerde status-waarheid — de FE leest de echte
 * (mogelijk gekantelde) status uit het detail-refetch. `marked` is `true` ook
 * bij de idempotente no-op (de regel stond al elders; geen fout).
 */
export interface OrderMarkReceivedResult {
  marked: boolean;
  orderLineId: string;
}

// ─── Bestellijst — leverancier-prijzen (story S-INK.2b) ────────────────────

/**
 * Request-body voor `POST /api/order-list/prices/supplier` (story S-INK.2b —
 * progressief, lock-vrij, gecached). **Eén** provider-leverancier per call: de FE
 * vuurt N van deze calls **parallel** (één per kolom) en merge't elke kolom
 * onafhankelijk zodra 'ie binnen is — geen aggregate-cap die trage-maar-leverbare
 * leveranciers als "—" verbergt. Token/host komen NOOIT op de wire.
 */
export interface SupplierPriceRequest {
  /** De leverancier-id (kolom-key). */
  supplierId: string;
  /** Provider-id van de leverancier (live/externe provider). */
  providerId: string;
  /** De eurocodes van de bestellijst-regels (één call dekt ze allemaal voor deze leverancier). */
  eurocodes: string[];
  /**
   * `true` → cache-bypass: forceer een verse stock-API-call ("Prijzen
   * verversen"/retry, AC 3.3). Default `false` → cache-hit binnen TTL.
   */
  bypassCache?: boolean;
}

/**
 * Respons van `POST /api/order-list/prices/supplier` (story S-INK.2b): de
 * prijs-cellen van ÉÉN leverancier + een eerlijke laad-status. Faal-veilig: een
 * fout (transport/non-2xx/parse/timeout) → `{ ok:false, reason:"stock_error" }`
 * (HTTP 200, geen 500) zodat de FE de kolom als **fout/retry** rendert — nooit als
 * "geen voorraad". `reason` is een veilige diagnostiek-code
 * (`ok`/`no_token`/`config_missing`/`stock_error`/`no_rows`/`not_external`).
 */
export interface SupplierPriceResult {
  /** De leverancier-id (kolom-key) waarvoor dit resultaat geldt. */
  supplierId: string | null;
  /** `true` = de stock-API-call slaagde (ook bij 0 cellen) → FE rendert `loaded`. */
  ok: boolean;
  /** De prijs-cellen voor deze leverancier. Kan leeg zijn (geslaagde lege batch). */
  cells: SupplierPriceCell[];
  /** Veilige diagnostiek-code. */
  reason: string | null;
}

/**
 * Eén prijs-cel in de respons van `POST /api/order-list/prices/supplier`. De FE
 * landt 'm op (kolom = `supplierId`, rij = `euroCode`).
 *
 * `available` is een **pass-through string** (D-1, géén enum) — de FE leidt de
 * stip af via `availabilityToDot(available, unitPrice)`. `unitPrice` is het
 * numerieke veld waarop "goedkoopste leverbare" wordt bepaald (niet op de
 * geformatte string).
 */
export interface SupplierPriceCell {
  /** De eurocode (rij-sleutel). */
  euroCode: string | null;
  /** De leverancier-id (kolom-sleutel). */
  supplierId: string | null;
  /** De inkoopprijs (`null` bij geen prijs). */
  unitPrice: number | null;
  /** De geformatte prijs-weergave zoals de stock-API 'm levert; kan `null` zijn. */
  unitPriceFormatted: string | null;
  /** De part-code (artikelcode bij de leverancier). */
  partCode: string | null;
  /** Beschikbaarheid als pass-through string. */
  available: string | null;
  /**
   * `true` = de leverancier kan dit artikel daadwerkelijk leveren — het primaire
   * leverbaarheids-signaal voor de stip (D-1 herzien, uit de GR-1-golden).
   */
  isOrderable: boolean;
  /** Opmerkingen bij de prijs. */
  remarks: string | null;
  /**
   * S022.9 (A.5): de leverancier gaf voor deze eurocode een foutstatus — de voorraad was niet op te vragen. Prijs is
   * dan 0 en betekent niets; de FE toont "voorraad niet op te vragen". Alleen aanwezig als `true`.
   */
  stockUnavailable?: boolean;
}

// ─── Bestellijst — bestellen bij een leverancier (story S-INK.7) ──────────

/** Bezorgmethode (wire). Standaard · Express · Ophalen. */
export type PurchaseShippingCondition = 'standard' | 'express' | 'pickup';

/** Eén regel in `POST /api/order-list/orders`. Identiteitsvelden leest de server zelf. */
export interface PurchaseOrderRequestLine {
  preOrderLineId: string;
  /** Geheel getal 1-99. */
  quantity: number;
  /** De verwachte prijs zoals getoond; weglaten als er geen prijs is (handmatige leverancier). */
  unitPrice?: number;
  /** De partcode uit de prijscel; weglaten als leeg. */
  partCode?: string;
}

/** Body van `POST /api/order-list/orders` (AC 14). */
export interface PurchaseOrderRequest {
  /** UUID per geopend bevestigingsvenster (AC 11). */
  clientRequestId: string;
  supplierId: string;
  remark?: string;
  /** Alleen bij een live leverancier met `showShippingConditions`. */
  shippingCondition?: PurchaseShippingCondition;
  /** `yyyy-MM-dd`, alleen bij een handmatige leverancier. */
  orderDate?: string;
  lines: PurchaseOrderRequestLine[];
}

/** De uitkomst van een groep of regel (AC 24-27). */
export type PurchaseOrderOutcome = 'ordered' | 'rejected' | 'not_ordered' | 'not_sent' | 'uncertain';

export interface PurchaseOrderLineResult {
  preOrderLineId: string;
  outcome: PurchaseOrderOutcome | 'released';
  orderId?: string;
  orderNumber?: string;
  confirmedQuantity?: number;
  unitPrice?: number;
  warning?: string;
  /** Uitleg bij een niet-geslaagde regel (bv. AC 25b). */
  message?: string;
}

export interface PurchaseOrderGroupResult {
  outcome: PurchaseOrderOutcome;
  message?: string;
  lines: PurchaseOrderLineResult[];
}

/** Antwoord van `POST /api/order-list/orders` (AC 27): ná het claimen altijd HTTP 200. */
export interface PurchaseOrderRoundResult {
  roundId: string;
  supplierCode?: string;
  isLive: boolean;
  /** `true` bij een herhaling met hetzelfde `clientRequestId` (AC 30). */
  replayed: boolean;
  allOrdered: boolean;
  anyUncertain: boolean;
  groups: PurchaseOrderGroupResult[];
}

/** Antwoord van `…/order-rounds/{roundId}/verify` en `…/release` (AC 35). */
export interface PurchaseOrderRoundActionResult {
  roundId: string;
  lines: PurchaseOrderLineResult[];
}

// ─── Dossier — Aanmaken (S007.9) ─────────────────────────────────────────

/**
 * Eén keuze in de "+ Nieuw dossier"-dropdown.
 *
 * - `kind: 'standard'` → één van de vier vaste categorieën (verzekerd /
 *   leasefleet / b2b / offerte). `customDossierTypeId` is dan `null` en
 *   `newEntityTemplate` is `null`.
 * - `kind: 'custom'` → per-hersteller geconfigureerd custom-type.
 *   `customDossierTypeId` bevat de GUID van het custom-type; backend zet
 *   `code = customDossierTypeId.toString()`.
 *
 * `workflowToStart` is de string-GUID van de te starten workflow (server
 * heeft de flag-prioriteit al toegepast voor custom-rows). FE stuurt deze
 * waarde 1-op-1 terug in `POST /api/dossiers`.
 */
export interface DossierCreateOption {
  kind: 'standard' | 'custom';
  code: string;
  label: string;
  sortOrder: number;
  workflowToStart: string;
  newEntityTemplate: string | null;
  customDossierTypeId: string | null;
}

/**
 * Body voor `POST /api/dossiers`. Veldwaarden komen rechtstreeks uit de
 * gekozen `DossierCreateOption`. Server valideert opnieuw (TOCTOU — refined
 * story #35/#36): RepairerID-match, RecordStatus=Active, lookup-active.
 */
export interface DossierCreateRequest {
  workflowToStart: string;
  newEntityTemplate: string | null;
  customDossierTypeId: string | null;
}

/** Antwoord op succesvolle 201 — alleen de id van het nieuwe dossier. */
export interface DossierCreateResponse {
  id: string;
}

/**
 * Uitkomst-status van de OVI-voertuiglookup endpoint.
 *
 * - `found`     : OVI gaf minimaal één record terug → velden gevuld.
 * - `not_found` : OVI gaf een lege array terug → alle velden null.
 * - `error`     : timeout, netwerkfout of upstream non-2xx → alle velden null.
 *                 FE houdt bestaande dossier-velden ongemoeid en toont toast.
 */
export type VehicleLookupStatus = 'found' | 'not_found' | 'error';

/**
 * Response van `GET /api/vehicles/by-license/{license}`.
 *
 * Velden zijn allemaal nullable: bij `not_found`/`error` is alles `null`,
 * bij `found` kan een specifiek OVI-veld nog null zijn (bv. `modelYear`
 * bij voertuig zonder bekende eerste-toelating-datum).
 */
export interface VehicleLookupResult {
  status: VehicleLookupStatus;
  /** Genormaliseerd NL-kenteken met streepjes (bv. `AB-12-CD`). */
  license: string | null;
  /** OVI `merk`. */
  manufacturerText: string | null;
  /** OVI `handelsbenaming`. */
  modelSetText: string | null;
  /** OVI `type`; fallback op `uitvoering` als `type` leeg is. */
  modelText: string | null;
  /** OVI `datum_eerste_toelating_dt.Year`, alleen wanneer > 1900. */
  modelYear: number | null;
}

/**
 * Status van de server-side OVI-handler (`POST /api/dossiers/{id}/apply-rdw-
 * lookup`). Identieke tri-state als `VehicleLookupStatus` zodat de FE-handler
 * dezelfde routing kan gebruiken.
 */
export type ApplyRdwLookupStatus = 'found' | 'not_found' | 'error';

/**
 * Response van `POST /api/dossiers/{id}/apply-rdw-lookup`.
 *
 * Story `fe-voertuig-ovi-uitbreiding` (2026-05-30): server-side OVI-handler
 * doet RDW-fetch + Interaction-write in één call. Bij `status='found'`
 * bevatten `version` + `fields` de delta in dezelfde shape als PATCH /vehicle
 * (top-level + `vehicle.*` sub-object) zodat de FE-cache via
 * `mergeDeltaIntoDossierDetail` ververst kan worden zonder refetch.
 *
 * Bij `not_found` / `error` is `fields` een leeg object en `version` de
 * pre-write versie (geen mutatie heeft plaatsgevonden).
 */
export interface ApplyRdwLookupResponse {
  status: ApplyRdwLookupStatus;
  version: number;
  fields: Record<string, unknown>;
}

/**
 * Story `fe-voertuig-chassis-lookup` (2026-05-30) — Argic-SOAP-call voor
 * chassisnummer (VIN). Aparte (betaalde) call los van de Socrata-OVI; alleen
 * op user-knop "Opvragen" achter het Chassisnummer-veld.
 *
 * Status-discriminator: `"found"` (VIN ontvangen + ChassisNumber/MeldCode
 * gepersist), `"not_found"` (Argic respondeerde maar VIN leeg — legacy
 * "Geen vinnumber bekend"), `"error"` (SOAP-call faalde — `error` bevat
 * de exception-message voor de toast).
 */
export type ChassisLookupStatus = 'found' | 'not_found' | 'error';

export interface ChassisLookupRequest {
  /** NL-kenteken zoals user heeft getypt (met of zonder streepjes). */
  license: string;
}

export interface ChassisLookupResponse {
  status: ChassisLookupStatus;
  version: number;
  fields: Record<string, unknown>;
  /** Aanwezig bij `status='error'` — SOAP-exception-message voor toast. */
  error?: string;
}

/**
 * Standaard error-body shape voor 400 / 403 / 500 op de dossier-create
 * endpoints (refined story F-12). `error` is een gebruikersbericht (geen
 * stack-trace, geen DB-tabelnamen). `correlationId` wordt opgenomen bij
 * server-zijdige fouten zodat de gebruiker een referentie kan delen.
 */
export interface ApiErrorBody {
  error: string;
  correlationId?: string;
}

// ─── PhotoProtocol — story fotoprotocol-beheer-v1 (2026-05-29) ───────────
//
// Aggregate-shapes voor de PP_*-tabellen onder AppServices/PhotoProtocols.
// Statussen / scope-types als const-objecten ipv enums om JSON-vriendelijk te
// blijven (BE stuurt tinyint via JSON-nummer).

export const PhotoProtocolStatus = {
  Concept: 0,
  Active: 1,
  Expired: 2,
  Archived: 3,
} as const;
export type PhotoProtocolStatusValue =
  (typeof PhotoProtocolStatus)[keyof typeof PhotoProtocolStatus];

export const PhotoProtocolScopeType = {
  Global: 0,
  /** Legacy — niet meer aangeboden in de UI sinds 2026-05-30. */
  Repairer: 1,
  InsuranceCompany: 2,
  CustomDossierType: 3,
  IsExchange: 4,
  IsDispatch: 5,
  IsUbench: 6,
  IsCentralCustomer: 7,
  IsInsured: 8,
  IsLeaseOrFleet: 9,
  IsB2BSales: 10,
} as const;
export type PhotoProtocolScopeTypeValue =
  (typeof PhotoProtocolScopeType)[keyof typeof PhotoProtocolScopeType];

export type PhotoProtocolAccessCode = 'Level' | 'TopDown' | 'BottomUp';

export interface PhotoProtocolDto {
  id: string;
  code: string;
  name: string;
  protocolVersion: string;
  validFrom: string;            // ISO datetime
  validUntil: string | null;
  status: PhotoProtocolStatusValue;
  ownerID: string | null;
  accessTypeCode: PhotoProtocolAccessCode | null;
  description: string | null;
  created: string;
  modified: string;
  version: number;
  scopes?: PhotoProtocolScopeDto[];
  scenarios?: PhotoProtocolScenarioDto[];
}

export interface PhotoProtocolScopeDto {
  id: string;
  scopeType: PhotoProtocolScopeTypeValue;
  scopeRefID: string | null;
  priority: number;
}

export interface PhotoProtocolScenarioDto {
  id: string;
  code: string;
  label: string;
  evaluationOrder: number;
  isDefault: boolean;
  conditions: PhotoProtocolConditionDto[];
  items: PhotoProtocolItemDto[];
}

export interface PhotoProtocolConditionDto {
  id: string;
  conditionType: string;
  conditionValue: string | null;
  negate: boolean;
}

export interface PhotoProtocolItemDto {
  id: string;
  sortOrder: number;
  label: string;
  attachmentCategoryID: string;
  attachmentCategoryCode: string | null;
  required: boolean;
  minCount: number;
  helpText: string | null;
}

export interface PhotoProtocolListFilter {
  status?: PhotoProtocolStatusValue;
  ownerId?: string;
  scopeType?: PhotoProtocolScopeTypeValue;
}

export interface PhotoProtocolDuplicateRequest {
  newCode: string;
  newVersion: string;
  newValidFrom?: string;  // ISO date
}

export interface PhotoProtocolPreviewResponse {
  candidates: Array<{
    protocolID: string;
    protocolCode: string;
    scopeType: PhotoProtocolScopeTypeValue;
    scopeRefID: string | null;
    priority: number;
    validFrom: string;
    isWinner: boolean;
    hasAccess: boolean;
  }>;
  chosenProtocolID: string | null;
  chosenReason: string;
  output: PhotoProtocolResolverOutput | null;
}

/**
 * Lookup-DTO voor `GET /api/photoprotocols/lookups/attachment-categories` —
 * dropdown-data voor de items-CRUD-rij in het beheer-scherm.
 */
export interface AttachmentCategoryLookup {
  id: string;
  code: string | null;
  description: string | null;
}

// ─── Planbord (EPIC-012, story S012.1) ─────────────────────────────────────

/**
 * Eén resource-kolom op het planbord = een UA_Fitter (monteur), verrijkt met
 * PB_ResourceProfile. Geen profiel-rij → veilige defaults (`isVisible=true`,
 * `color`/`sortOrder` = null). Resources zijn altijd een monteur (persoon).
 */
export interface PlanbordResource {
  fitterId: string;
  name: string;
  /** Bord-kleur; null bij geen profiel. */
  color: string | null;
  /** Kolomvolgorde; null bij geen profiel. */
  sortOrder: number | null;
  isVisible: boolean;
  /**
   * S012.7 — "mijn monteur": `true` wanneer deze fitter in `PB_FitterUser`
   * gekoppeld is aan de INGELOGDE gebruiker (server-side bepaald uit
   * `CurrentSession.UserId`). Voedt het "mijn agenda"-filter op de werkbalk.
   * Optioneel zodat een oudere server-respons zonder dit veld niet breekt
   * (afwezig/`false` → niet "mijn").
   */
  mine?: boolean;
}

/**
 * Eén afspraak in de range. `fitterIds` bevat de gekoppelde resources
 * (>= 1; defensief `[]` bij data-vuil). `dossierId` is null bij type 3/4.
 */
export interface PlanbordAppointment {
  id: string;
  /** 1=Declaratie 2=Werkbon 3=Los 4=Afwezig. */
  appointmentType: number;
  /**
   * Werksoort (S012.5): 1=Reparatie 2=Vervanging; `null` bij type Los/Afwezig of
   * legacy-rijen zonder werksoort. Voedt de bord-kleur-op-werksoort (sluit Q-A
   * uit S012.2). Optioneel zodat een oudere server-respons zonder dit veld niet
   * breekt (failsafe op `appointmentType` in `colorClassForAppointment`).
   */
  workType?: number | null;
  /** ISO-datetime ("yyyy-MM-ddTHH:mm:ss"). */
  startDateTime: string;
  /** ISO-datetime ("yyyy-MM-ddTHH:mm:ss"). */
  endDateTime: string;
  dossierId: string | null;
  /** Kenteken + klant-cache of vrije tekst (1-op-1 uit kolom). */
  title: string | null;
  /**
   * Weergave-verrijking (read-only) voor dossier-gekoppelde afspraken (type 1/2),
   * gebruikt door de detail-kaart. Alle drie optioneel/nullable; `null` bij type
   * Los/Afwezig of een lege bronwaarde.
   */
  /** Merk + model display (`UA_Dossier.ManufacturerText` + `ModelText`). */
  vehicle?: string | null;
  /** Verzekeraar-naam (`RB_Company.Name` via `InsuranceCompanyID`). */
  insurerName?: string | null;
  /** Klantnaam (`UA_Dossier.CustomerName`) — los van `title`. */
  customerName?: string | null;
  /**
   * Kenteken (`UA_Dossier.License`) — authoritatief uit het gekoppelde dossier,
   * los van `title`. De kaart/plaat rendert het kenteken uit dit veld (met
   * fallback naar het kenteken-deel van `title` voor afspraken zonder
   * dossier-license, bv. vrije tekst). `null`/ontbrekend bij type Los/Afwezig
   * (geen dossier) of leeg. Optioneel zodat een oudere server-respons zonder
   * dit veld niet breekt.
   */
  license?: string | null;
  /**
   * Afspraak-eigen Locatie-tab-velden (`PB_Appointment.LocationText/LocationKind/Note`).
   * Voeden de Locatie-tab van de afspraak-modal bij heropenen (view/edit) zodat
   * een opgeslagen herstellocatie/notitie niet leeg lijkt. Optioneel/nullable —
   * een oudere server-respons zonder deze velden breekt de modal niet.
   */
  /** Herstellocatie-naam / vrije tekst van de afspraak. */
  locationText?: string | null;
  /** Soort herstellocatie (gedrag-optie); `null` bij vrije tekst / geen keuze. */
  locationKind?: number | null;
  /** Vrije notitie van de afspraak. */
  note?: string | null;
  /**
   * Reden van een afwezigheid (`PB_Appointment.AbsenceReason`, type 4). Vult de
   * keuzelijst "Reden" bij het heropenen. Optioneel: een oudere server stuurt 'm niet.
   */
  absenceReason?: string | null;
  /**
   * S012.11 — gekoppeld eigen-werkbontype (`PB_Appointment.WorkOrderTypeID`),
   * read-only. `null`/ontbrekend bij geen koppeling of niet-Werkbon. Optioneel
   * zodat een oudere server-respons zonder dit veld niet breekt.
   */
  workOrderTypeId?: string | null;
  /**
   * S012.11 — bord-kleur (hex `#RRGGBB`) van het gekoppelde werkbontype
   * (`PB_WorkOrderType.Color`), read-only. Een gedeactiveerd type levert nog
   * steeds z'n kleur (AC 3/14). `null`/ontbrekend wanneer er geen werkbontype is
   * → het bord valt terug op de werksoort/type-kleur. De FE rendert deze
   * dynamische hex inline (geen vaste tailwind-class). Optioneel voor
   * backwards-compat.
   */
  workOrderTypeColor?: string | null;
  /** 0=gepland 1=bezig 2=klaar. */
  status: number;
  version: number;
  fitterIds: string[];
}

/** Open-dag van de hersteller. `weekday` 1=ma .. 7=zo (ISO). */
export interface PlanbordOpenDay {
  weekday: number;
  isOpen: boolean;
  /**
   * Openingstijd ("HH:mm") van een OPEN dag; `null`/afwezig bij gesloten of niet
   * ingesteld. De dag-view volgt deze window voor z'n uren-as (S012.4b).
   */
  openTime?: string | null;
  /** Sluitingstijd ("HH:mm") van een OPEN dag; `null`/afwezig bij gesloten / niet ingesteld. */
  closeTime?: string | null;
}

/** Werktijd-tijdvak per monteur per weekdag. Tijden als "HH:mm". */
export interface PlanbordWorkingHours {
  fitterId: string;
  weekday: number;
  startTime: string;
  endTime: string;
}

/** Beschikbaarheids-data: open-dagen (company-breed) + werktijden (per zichtbare resource). */
export interface PlanbordAvailability {
  openDays: PlanbordOpenDay[];
  workingHours: PlanbordWorkingHours[];
}

/**
 * Respons van `GET /api/planbord?from&to`: alles wat het bord nodig heeft
 * voor een datumrange in één call. Alle arrays zijn niet-nullable (lege array
 * i.p.v. null).
 */
export interface PlanbordResponse {
  /** Inclusieve startdatum (ISO-date "yyyy-MM-dd"). */
  from: string;
  /** Inclusieve einddatum (ISO-date "yyyy-MM-dd"). */
  to: string;
  resources: PlanbordResource[];
  appointments: PlanbordAppointment[];
  availability: PlanbordAvailability;
}

/** Eén backlog-dossier (`GET /api/planbord/unscheduled`). */
export interface UnscheduledDossier {
  dossierId: string;
  dossierNumber: string | null;
  license: string | null;
  customerName: string | null;
  /** ISO-date ("yyyy-MM-dd"). */
  repairDate: string | null;
  /** Multi-dag legacy-veld; ISO-date of null. */
  extraRepairDate: string | null;
  /** Huidige workflow-status (informatief), de stabiele StatusValue als string. */
  statusCode: string | null;
  /**
   * 1=Declaratie 2=Werkbon — afgeleid uit het dossier; voedt de direct-create
   * bij rail→bord-drop. De FE stuurt dit 1-op-1 mee als `appointmentType` in de
   * create-call (en `workType: null`, geverifieerd toegestaan voor type 1/2).
   */
  appointmentType: number;
}

/** Respons van `GET /api/planbord/unscheduled`: de niet-ingepland-backlog. */
export interface UnscheduledResponse {
  dossiers: UnscheduledDossier[];
}

// ─── Planbord write-laag (EPIC-012, story S012.5) ──────────────────────────

/**
 * Intake-payload (subset) die bij een Declaratie/Werkbon meteen op het
 * (nieuwe/gekoppelde) dossier wordt gezet via het bestaande intake-pad.
 * Alle velden optioneel.
 */
export interface AppointmentIntakeInput {
  license?: string | null;
  manufacturerText?: string | null;
  modelSetText?: string | null;
  modelText?: string | null;
  modelYear?: number | null;
  chassisNumber?: string | null;
  numberOfKilometers?: number | null;
  /** Opdrachtgever (debiteur-FK) — cascade → customerName. */
  referenceDebtorId?: string | null;
  customerName?: string | null;
  /** O-6: expliciete hersteldatum (ISO-date); weglaten → afgeleid uit startDateTime. */
  repairDate?: string | null;
  repairerReference?: string | null;
  /**
   * S012.10a (Fase A) — Klant/verzekerde-relatie (`RB_GenericRelation.ID` →
   * `UA_Dossier.GenericRelationID`), **los** van `referenceDebtorId`
   * (opdrachtgever). De BE koppelt 'm via het bestaande
   * `ApplyLinkRelation`-pad (incl. `FillFromGenericRelation`-cascade), niet via
   * een kale FK-setter. `null`/weglaten = niet aanraken.
   */
  customerRelationId?: string | null;
  /**
   * S012.10b (Fase B) — inline-aan-te-maken klant/verzekerde-relatie. Wanneer
   * gezet (en `customerRelationId` NIET) schrijft de BE eerst de `customer*`-
   * velden op het dossier en roept dan de Iter-F-flow
   * (`create-from-dossier`) aan, die een nieuwe `RB_GenericRelation` +
   * default-contact maakt én aan het dossier koppelt. Person/Bedrijf via
   * `companyTypeId` (FK uit `GET /api/lookups/company-types`); `null` → BE
   * valt terug op Persoon/Particulier. `customerRelationId` wint als beide
   * gezet zijn (bestaande relatie boven nieuwe).
   */
  newRelation?: AppointmentNewRelationInput | null;
  /**
   * S012.10c (Locatie-tab herstellocatie-lookup) — adres-snapshot van het
   * gekozen `RepairLocation`-contact (`GET /api/repair-locations`). Wanneer
   * gezet schrijft de BE deze velden via de `RepairLocation*`-setters op het
   * dossier (momentopname; `RepairLocationID` blijft leeg). De naam zelf loopt
   * via `CreateAppointmentRequest.locationText` → `RepairLocationName`.
   * `null`/weglaten = geen herstellocatie-snapshot (gedrag-optie of vrije tekst).
   */
  repairLocation?: AppointmentRepairLocationInput | null;
}

/**
 * S012.10c — adres-/contact-snapshot van een gekozen RepairLocation-contact.
 * Landt 1-op-1 op de `UA_Dossier.RepairLocation*`-kolommen (momentopname). De
 * naam loopt los hiervan via `locationText` → `RepairLocationName`.
 */
export interface AppointmentRepairLocationInput {
  street?: string | null;
  houseNumber?: string | null;
  houseNumberExtension?: string | null;
  zipCode?: string | null;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
}

/**
 * S012.10c — één RepairLocation-contact uit `GET /api/repair-locations`
 * (company-scoped). `id`/`name` voeden de dropdown; de adres-/contact-velden
 * worden bij keuze als snapshot in de write-payload meegestuurd
 * (`AppointmentIntakeInput.repairLocation` + `locationText`).
 */
export interface RepairLocationOption {
  id: string;
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExtension: string;
  zipCode: string;
  city: string;
  phone: string;
  email: string;
}

/**
 * S012.10e — adres-shape voor de Locatie-tab-autofill van "Eigen werkplaats"
 * (`GET /api/company/workshop-address`) en "Bij opdrachtgever"
 * (`GET /api/debtors/{id}/repair-address`). Identiek aan de adres-/contact-
 * velden van {@link RepairLocationOption} plus `name` (i.p.v. `id`); spiegelt
 * 1-op-1 de BE-`RepairLocationAddressDto`. Alle velden zijn altijd aanwezig
 * (lege string bij geen waarde — stabiele shape; een leeg/niet-gevonden contact
 * geeft een 200 met lege velden, geen fout).
 */
export interface RepairLocationAddress {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExtension: string;
  zipCode: string;
  city: string;
  phone: string;
  email: string;
}

/**
 * S012.10b (Fase B) — payload voor een inline-aan-te-maken relatie vanuit de
 * planbord-afspraak-modal (Klant-tab). De velden landen op de
 * `UA_Dossier.Customer*`-velden; daarna materialiseert + koppelt de BE de
 * relatie via de bestaande create-from-dossier-flow. `name` is verplicht
 * (leeg → BE maakt geen relatie aan).
 */
export interface AppointmentNewRelationInput {
  /** `RB_CompanyType`-FK (Persoon/Bedrijf); `null` → Particulier-fallback BE-side. */
  companyTypeId?: string | null;
  name: string;
  address?: string | null;
  houseNumber?: string | null;
  houseNumberExtension?: string | null;
  zipCode?: string | null;
  city?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
}

/** Verzekering-payload (subset) — alleen type 1 (Declaratie). */
export interface AppointmentPolicyInput {
  insuranceCompanyId?: string | null;
  authorizedAgentId?: string | null;
  policyNumber?: string | null;
  /** ISO-date; her-gevalideerd BE-side (schade-datum-keten R1-R6). */
  damageDate?: string | null;
  damageCauseId?: string | null;
  /** BTW-verzekerd. */
  vatIncluded?: boolean | null;
  /**
   * S012.10a (Fase A) — Polis-type (FK naar `UA_InsuranceType` →
   * `UA_Dossier.InsuranceTypeID`). Bron-lijst: `GET /api/policy/insurance-types`.
   * Landt via de bestaande `insuranceTypeId`-key in `PolicyFieldSetters`.
   */
  insuranceTypeId?: string | null;
  /**
   * S012.10a (Fase A) — ER vervanging (eigen risico vervanging, EUR →
   * `UA_Dossier.OwnriskReplace`). Wire-key `excessReplacement` in
   * `PolicyFieldSetters` (geverifieerd via legacy PolisV2.aspx.cs:1065
   * `txtOwnRiskReplace → OwnriskReplace`). NL-decimaal FE-side geparsed.
   */
  excessReplacement?: number | null;
  /**
   * S012.10a (Fase A) — ER reparatie (eigen risico reparatie, EUR →
   * `UA_Dossier.OwnriskRepair`). Wire-key `excessRepair` in `PolicyFieldSetters`
   * (geverifieerd via legacy PolisV2.aspx.cs:1064 `txtOwnRiskRepair →
   * OwnriskRepair`). NL-decimaal FE-side geparsed.
   */
  excessRepair?: number | null;
}

/**
 * Body voor `POST /api/appointments` (S012.5). `appointmentType`:
 * 1=Declaratie 2=Werkbon 3=Los 4=Afwezig. `workType` (1=Reparatie
 * 2=Vervanging) is verplicht bij type 1/2, weggelaten/null bij 3/4.
 * `intake`/`policy`/`dossierId` alleen bij type 1/2 (policy alleen type 1).
 */
export interface CreateAppointmentRequest {
  appointmentType: number;
  workType?: number | null;
  /** ISO-datetime "yyyy-MM-ddTHH:mm[:ss]" — verplicht. */
  startDateTime: string;
  /** ISO-datetime — verplicht; moet > start. */
  endDateTime: string;
  /** ≥ 1 resource-id (UA_Fitter), binnen de company-scope. */
  fitterIds: string[];
  title?: string | null;
  note?: string | null;
  /** Alleen type 4 (Afwezig) — verplicht. */
  absenceReason?: string | null;
  locationKind?: number | null;
  locationText?: string | null;
  /**
   * S012.11 — optionele eigen-werkbontype-koppeling. Alleen relevant bij
   * afspraaktype Werkbon (2); bij een ander type negeert de BE 'm. Server-side
   * gevalideerd dat het type van de eigen company is (AC 9 → anders 400
   * `work_order_type_not_accessible`). `null`/weglaten = geen koppeling.
   */
  workOrderTypeId?: string | null;
  /**
   * F-2 idempotentie: client-genereerde key (bv. `crypto.randomUUID()`). Een
   * dubbel-submit met dezelfde key maakt geen tweede dossier/afspraak.
   */
  idempotencyKey?: string | null;
  /** O-1: gezet → koppel bestaand dossier; weglaten → BE maakt nieuw dossier. */
  dossierId?: string | null;
  /** Alleen type 1/2. */
  intake?: AppointmentIntakeInput;
  /** Alleen type 1 (Declaratie). */
  policy?: AppointmentPolicyInput;
}

/**
 * Body voor `PATCH /api/appointments/{id}` (S012.5). Optimistic concurrency via
 * `expectedVersion`; sparse `fields`. `appointmentType` is immutable (O-8) → 400.
 * Toegestane keys: startDateTime, endDateTime, fitterIds, workType, title, note,
 * absenceReason, locationKind, locationText, status.
 */
export interface UpdateAppointmentRequest {
  expectedVersion: number;
  fields: Record<string, unknown>;
  /**
   * S012.10a (Fase A) — optionele intake-/klant-doorzet bij het bewerken van
   * een dossier-gekoppelde afspraak (type 1/2). Genegeerd BE-side wanneer de
   * afspraak geen dossier heeft. Landt via hetzelfde passthrough-pad als de
   * create-flow (`ApplyIntakeUpdate`/`ApplyLinkRelation`).
   */
  intake?: AppointmentIntakeInput;
  /**
   * S012.10a (Fase A) — optionele polis-doorzet bij het bewerken van een
   * Declaratie-afspraak (type 1). Landt via `ApplyPolicyUpdate`.
   */
  policy?: AppointmentPolicyInput;
}

/**
 * Response van POST (201) / PATCH (200). Spiegelt `PlanbordAppointment`
 * aangevuld met `dossierId`/`workType`/`note`/`absenceReason`/`location*` zodat
 * de FE-cache zonder refetch kan bijwerken.
 */
export interface AppointmentResponse {
  id: string;
  appointmentType: number;
  workType: number | null;
  startDateTime: string;
  endDateTime: string;
  dossierId: string | null;
  title: string | null;
  note: string | null;
  absenceReason: string | null;
  locationKind: number | null;
  locationText: string | null;
  status: number;
  version: number;
  fitterIds: string[];
  /**
   * S012.11 — gekoppeld eigen-werkbontype (`null` bij geen koppeling/niet-Werkbon)
   * zodat de FE-cache zonder refetch de bord-kleur kan bijwerken. Optioneel voor
   * backwards-compat met oudere server-responses.
   */
  workOrderTypeId?: string | null;
  /** BL-29 — de polis-passthrough maakte de schadeoorzaak van het dossier leeg. */
  damageCauseCleared?: DamageCauseCleared;
}

// ─── Planbord-beschikbaarheid (EPIC-012, story S012.4) ──────────────────────

/**
 * Open-dag van de hersteller (company-breed). `weekday` is ISO (1=ma .. 7=zo).
 * `GET /api/repairer/open-days` levert altijd alle 7 dagen — ontbrekende
 * DB-rijen worden server-side aangevuld met de default (ma-vr open, za-zo dicht).
 */
export interface RepairerOpenDay {
  weekday: number;
  isOpen: boolean;
  /**
   * Openingstijd ("HH:mm") van een OPEN dag; `null`/afwezig bij gesloten of niet
   * ingesteld (BE vult een open dag zonder tijden aan met de 08:00-default).
   */
  openTime?: string | null;
  /** Sluitingstijd ("HH:mm") van een OPEN dag; `null`/afwezig bij gesloten / niet ingesteld. */
  closeTime?: string | null;
}

/** Response van `GET`/`PUT /api/repairer/open-days`: altijd alle 7 weekdagen. */
export interface RepairerOpenDaysResponse {
  openDays: RepairerOpenDay[];
}

/** Body voor `PUT /api/repairer/open-days` — vervangt de hele set. */
export interface PutRepairerOpenDaysRequest {
  openDays: RepairerOpenDay[];
}

/**
 * Werktijd-tijdvak per monteur per weekdag. Tijden als "HH:mm". Meerdere rijen
 * per (weekdag) = gebroken dienst (bv. 08:00–12:00 + 13:00–17:00).
 */
export interface FitterWorkingHours {
  weekday: number;
  startTime: string;
  endTime: string;
}

/** Response van `GET`/`PUT /api/resources/{fitterId}/working-hours`. */
export interface FitterWorkingHoursResponse {
  workingHours: FitterWorkingHours[];
}

/** Body voor `PUT /api/resources/{fitterId}/working-hours` — vervangt de hele set. */
export interface PutFitterWorkingHoursRequest {
  workingHours: FitterWorkingHours[];
}

// ─── Planbord-resource-inrichting (EPIC-012, story S012.3) ──────────────────

/**
 * Eén resource-rij in de response van `GET /api/resources/profiles`: álle
 * company-fitters (ook verborgen/ongeconfigureerde) met hun PB_ResourceProfile
 * (defaults toegepast: `isVisible=true`, `color`/`sortOrder` = null). NIET op
 * zichtbaarheid gefilterd — de inrichting moet ook onzichtbare resources kunnen
 * tonen/aanzetten. Resources zijn altijd een monteur (persoon).
 */
export interface ResourceProfile {
  fitterId: string;
  name: string;
  /** Stabiele natural-key van de fitter (UA_Fitter.Code); kan `null` zijn. */
  code: string | null;
  /** Bord-kleur (hex); `null` bij geen profiel / niet ingesteld. */
  color: string | null;
  /** Kolomvolgorde; `null` bij geen profiel / niet ingesteld. */
  sortOrder: number | null;
  isVisible: boolean;
  /**
   * S012.7 — de gekoppelde inlog-gebruiker (`PB_FitterUser.UserID`), of `null`
   * bij geen koppeling. `UNIQUE(FitterID)` → hoogstens één user per fitter. De
   * inrichting toont 'm in de gebruiker-dropdown. Optioneel voor backwards-compat.
   */
  userId?: string | null;
}

/**
 * S012.7 — een kiesbare company-gebruiker (`GU_User`) voor de monteur↔gebruiker-
 * dropdown. `name` = weergave-naam (`GU_User.Connonical`, fallback `LoginName`).
 */
export interface CompanyUser {
  id: string;
  name: string;
}

/**
 * Response van `GET /api/resources/profiles`. S012.7 vouwt de monteur↔gebruiker-
 * koppeling hierin: `userId` per resource + `users` als de kiesbare-gebruikers-
 * lijst (geen aparte fetch nodig in het inrichtingsscherm).
 */
export interface ResourceProfilesResponse {
  resources: ResourceProfile[];
  /**
   * S012.7 — kiesbare company-gebruikers voor de koppel-dropdown. Optioneel zodat
   * een oudere server-respons zonder dit veld niet breekt (afwezig → geen keuze).
   */
  users?: CompanyUser[];
}

/**
 * Body voor `PUT /api/resources/{fitterId}/profile`: upsert van het
 * PB_ResourceProfile. `color` leeg/`null` → wordt server-side NULL; `sortOrder`
 * int/`null`; `isVisible` bool.
 */
export interface PutResourceProfileRequest {
  color: string | null;
  sortOrder: number | null;
  isVisible: boolean;
}

/**
 * S012.7 — body voor `PUT /api/resources/{fitterId}/user`: zet of wis de
 * gekoppelde gebruiker. `userId` `null` → koppeling wissen (DELETE); een GUID →
 * upsert (vervangt een bestaande koppeling, `UNIQUE(FitterID)`).
 */
export interface PutFitterUserRequest {
  userId: string | null;
}

/** Response van `PUT /api/resources/{fitterId}/user`. */
export interface FitterUserResponse {
  fitterId: string;
  userId: string | null;
}

// ─── Eigen werkbontypes per hersteller (EPIC-012, story S012.11) ────────────

/**
 * Eén eigen werkbontype van de ingelogde company (`GET /api/work-order-types`).
 * Alleen ACTIEVE types worden door de GET teruggegeven; een gedeactiveerd type
 * verdwijnt uit de keuze maar behoudt z'n kleur op bestaande afspraken via de
 * planbord-read (AC 3/14). `color` = hex `#RRGGBB`; `defaultDurationMinutes` =
 * gehele minuten (> 0, ≤ 1440). Company-scoped server-side — een hersteller ziet
 * NOOIT andermans types (AC 7).
 */
export interface WorkOrderType {
  id: string;
  name: string;
  /** Bord-kleur (hex `#RRGGBB`). */
  color: string | null;
  /** Standaard-duur in minuten (> 0, ≤ 1440); voedt de eindtijd-prefill (AC 12/13). */
  defaultDurationMinutes: number | null;
  /** Sorteervolgorde of `null`. */
  sortOrder: number | null;
}

/** Response van `GET /api/work-order-types`: de eigen actieve werkbontypes. */
export interface WorkOrderTypesResponse {
  workOrderTypes: WorkOrderType[];
}

/**
 * Body voor `POST /api/work-order-types` (create) en
 * `PUT /api/work-order-types/{id}` (update). Naam + kleur (`#RRGGBB`) +
 * standaard-duur (minuten) verplicht; server-side gevalideerd (400 met `reason`:
 * `name_required` / `name_too_long` / `name_duplicate` / `invalid_color` /
 * `invalid_duration`).
 */
export interface SaveWorkOrderTypeRequest {
  name: string;
  color: string;
  defaultDurationMinutes: number;
  sortOrder?: number | null;
}

// ─── Bestaande afspraken importeren (EPIC-012, story S012.8 Fase 3) ─────────

/**
 * Resultaat van `POST /api/planbord/migrate-appointments` — de self-service
 * één-keer-import die bestaande dossiers (met een hersteldatum vanaf vandaag)
 * als planbord-afspraken neerzet. Company-scoped server-side.
 *
 * De call kent twee standen via `dryRun`:
 *  - dry-run (`dryRun=true`): er wordt niets aangemaakt; `appointmentsCreated`
 *    is het aantal afspraken dat een echte run ZOU aanmaken (preview).
 *  - echte run (`dryRun=false`): `appointmentsCreated` is het aantal
 *    DAADWERKELIJK aangemaakte afspraken.
 *
 * De `skipped*`-tellers verklaren waarom kandidaat-dossiers niet leiden tot een
 * afspraak; `failedDossiers` telt onverwachte fouten per dossier (toon als
 * waarschuwing wanneer > 0).
 */
export interface PlanbordMigrationResult {
  /** `true` voor een preview-run (niets aangemaakt), `false` voor een echte import. */
  dryRun: boolean;
  /** Aantal dossiers dat als kandidaat voor import is bekeken. */
  candidateDossiers: number;
  /** Aangemaakte afspraken (echte run) of "zou aanmaken" (dry-run). */
  appointmentsCreated: number;
  /** Overgeslagen: hersteldatum in het verleden. */
  skippedPast: number;
  /** Overgeslagen: dossier had al een afspraak. */
  skippedAlreadyHasAppointment: number;
  /** Overgeslagen: geen monteur gekoppeld. */
  skippedNoFitters: number;
  /** Aantal dossiers dat onverwacht faalde (waarschuwing bij > 0). */
  failedDossiers: number;
}

/**
 * Response van `GET /api/photoprotocols/lookups/attachment-categories/glas`
 * — de beschikbare categorieën + de default-foto-categorie-ID voor de
 * bijlagen-flow van een glasdossier.
 *
 * `categories` = de categorieën die het geldende fotoprotocol voor dit
 * dossier eist ∪ de geconfigureerde extra categorieën
 * (`GS_SystemParameter.ExtraAttachmentCategoryCodes`). De protocol-tak is
 * onvoorwaardelijk: een categorie die het protocol verplicht stelt is altijd
 * kiesbaar, ook als de configuratie 'm niet noemt.
 */
export interface GlasAttachmentCategoryLookupResponse {
  categories: AttachmentCategoryLookup[];
  defaultPhotoCategoryId: string | null;
}

/**
 * Generieke {id, name}-lookup voor scope-picker-dropdowns
 * (insurance-companies, custom-dossier-types).
 */
export interface IdNameLookup {
  id: string;
  name: string;
}

/**
 * Story `bijlagen-tab-v1` (2026-05-30) — wire-shape voor de Bijlagen-tab.
 * Spiegelt `CARculate.AppServices.Attachments.AttachmentDto`.
 *
 * `isPhoto` wordt server-side bepaald (magic-bytes-detect) en bepaalt of
 * de tegel in de foto-grid (`true`) of in de documenten-lijst (`false`)
 * gerendered wordt. Sectie-keuze is MIME-gebaseerd — NIET categorie-
 * gebaseerd (AC-3.6).
 */
export interface AttachmentDto {
  id: string;
  name: string | null;
  description: string | null;
  categoryId: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  typeCode: string | null;
  mimeType: string | null;
  sizeBytes: number;
  created: string | null;
  createdBy: string | null;
  createdByName: string | null;
  modified: string | null;
  version: number;
  /** EXIF DateTimeOriginal (alleen JPG, null bij andere of geen EXIF). */
  exifDateTimeOriginal: string | null;
  /** v2-flag: true als er een rij in `GD_AttachmentAnnotation` bestaat. */
  hasAnnotations: boolean;
  /** Server-bepaald via MIME-prefix `image/*`. */
  isPhoto: boolean;
}

/**
 * Body voor `PUT /api/attachments/{id}` — alleen de wijzigbare velden
 * uit AC-3. `clearCategoryId` is een expliciete sentinel om "leegmaken
 * naar NULL" (AC-3.6) te kunnen onderscheiden van "niet wijzigen".
 *
 * `version` is verplicht voor optimistic-concurrency (TA-H-4). FE stuurt
 * de waarde uit het laatst-geladen DTO; mismatch → 409 met `currentVersion`.
 */
export interface AttachmentPutRequest {
  name?: string;
  description?: string;
  categoryId?: string | null;
  clearCategoryId?: boolean;
  version: number;
}

/** Response na succesvolle PUT. */
export interface AttachmentPutResponse {
  id: string;
  version: number;
}

/** Response na succesvolle multipart POST. */
export interface AttachmentUploadResponse {
  ids: string[];
  count: number;
}

/**
 * Input voor één bestand in de upload-batch. `categoryIdOverride` is null
 * wanneer de rij de master volgt; gezet wanneer de user 'm in de dialog
 * heeft overruled (AC-2.3).
 */
export interface AttachmentUploadFile {
  file: File;
  categoryIdOverride: string | null;
  description: string | null;
}

// ─── Monteur: foto's (story fe-be-monteur-s4-fotos, S021.4) ──────────────

/**
 * Eén foto uit de wachtrij van de monteur, voor
 * `POST /api/dossiers/{id}/attachments` met de twee optionele velden van S021.4:
 * - `attachmentId` → `attachmentIdsByIndex[0]`: het id dat de telefoon koos. Elke
 *   herhaling stuurt hetzelfde id; de server slaat de foto nooit twee keer op (§5.5).
 * - `clientWatermark` → `true`: de server zet GEEN eigen datumstempel. De
 *   monteur-weergave stuurt dit bij ELKE upload (review A-3).
 */
export interface DossierPhotoUpload {
  /** De (voorbereide) foto. */
  blob: Blob;
  fileName: string;
  categoryId: string | null;
  description: string | null;
  attachmentId: string;
}

/** Een foto onder een protocol-item (`GET …/photo-checklist`). */
export interface PhotoChecklistPhoto {
  id: string;
  name: string | null;
  created: string | null;
  /** Server-afgeleid: eigen foto én geen slot (B-2). Stuurt alleen de knop. */
  canDelete: boolean;
}

/** Eén protocol-item met de foto's die de server eraan heeft toegewezen (§3). */
export interface PhotoChecklistItem {
  order: number;
  label: string;
  helpText: string | null;
  categoryId: string;
  required: boolean;
  minCount: number;
  satisfied: boolean;
  attachments: PhotoChecklistPhoto[];
}

/** Een bijlage die bij geen item hoort ("Overig"). */
export interface PhotoChecklistOther {
  id: string;
  name: string | null;
  created: string | null;
  categoryId: string | null;
  categoryName: string | null;
  isPhoto: boolean;
  canDelete: boolean;
}

/** Een kiesbare categorie voor "Extra foto". */
export interface PhotoChecklistCategory {
  id: string;
  code: string | null;
  description: string | null;
}

/**
 * `GET /api/dossiers/{id}/photo-checklist` (S021.4 §9.1). De toewijzing van bestaande
 * foto's aan de kopjes gebeurt op de server; de FE rekent niets na.
 */
export interface PhotoChecklist {
  protocolCode: string | null;
  scenarioLabel: string | null;
  items: PhotoChecklistItem[];
  others: PhotoChecklistOther[];
  requiredDone: number;
  requiredTotal: number;
  categories: PhotoChecklistCategory[];
  defaultPhotoCategoryId: string | null;
  /** De categorie `Kilometerstand`, of `null` wanneer die niet is ingericht. */
  odometerCategoryId: string | null;
}

// ─── Klachtenafhandeling (story fe-be-klachtenafhandeling, 2026-06-08) ──

/**
 * Wire-shape van de klacht op een glas-dossier (`UA_ComplaintForm`,
 * 1-per-dossier, vaste Ordinal 0). Spiegelt `CARculate.AppServices.Dossiers.ComplaintDto`.
 *
 * `id` is `null` voor een nog niet opgeslagen template (`exists=false`); de FE
 * gebruikt 'm als subject voor de bijlagen (pas beschikbaar na de eerste save).
 * `ordinal`/`subjectId` staan bewust NIET op de shape: ze worden server-geforceerd.
 *
 * Datums komen als ISO-8601-string binnen; de FE neemt het date-deel
 * (`yyyy-MM-dd`) voor de date-pickers.
 */
export interface Complaint {
  id: string | null;
  exists: boolean;
  version: number | null;
  /** Herkomst (Klant/Verzekeraar/Opdrachtgever). FE-only verplicht. */
  originTypeName: string | null;
  /** Aangenomen door. Verplicht (FE + domain-rule). */
  acceptedBy: string | null;
  /** Datum van de klacht (ISO). Verplicht (FE + domain-rule). */
  complaintDate: string | null;
  /** Naam van de klager. Verplicht (FE-besluit + domain-rule). */
  complainant: string | null;
  complaintDescription: string | null;
  actions: string | null;
  settledBy: string | null;
  /** Datum afhandeling (ISO). Optioneel; verleden of toekomst toegestaan. */
  dateSettled: string | null;
  result: string | null;
}

/**
 * Body voor `PUT /api/dossiers/{id}/complaint`. Alleen de negen user-velden
 * gaan over de wire; `ordinal`/`subjectId` worden server-geforceerd. `version`
 * wordt meegestuurd voor optimistic-concurrency (stale → 409).
 */
export interface ComplaintWriteRequest {
  version: number | null;
  originTypeName: string | null;
  acceptedBy: string | null;
  complaintDate: string | null;
  complainant: string | null;
  complaintDescription: string | null;
  actions: string | null;
  settledBy: string | null;
  dateSettled: string | null;
  result: string | null;
}

export interface PhotoProtocolResolverOutput {
  protocolCode: string;
  protocolVersion: string;
  scenarioCode: string;
  scenarioLabel: string;
  items: Array<{
    order: number;
    label: string;
    categoryCode: string | null;
    categoryId: string;
    required: boolean;
    minCount: number;
    helpText: string | null;
  }>;
}

// ─── Rapportage per rol (story rapportage-per-rol-fase1) ─────────────────────

/**
 * Eén knoop in de rapportenboom (rapport of submenu), recursief. Bron:
 * `GF_MenuLine` → `GF_Function`. `isMenu` onderscheidt een submenu van een
 * rapport; `children` is leeg voor een rapport en bij een submenu op de
 * diepte-cap / cyclus-grens.
 */
export interface ReportMenuNode {
  /** `GF_Function.ID` (string-GUID) — stabiele id + route-param in fase 2. */
  id: string;
  /** `GF_Function.Code`. */
  code: string | null;
  /** Weergavenaam (`GF_Function.Description`). */
  name: string | null;
  /** Is dit een submenu (recursief) i.p.v. een rapport? */
  isMenu: boolean;
  /**
   * Best-effort response-type voor de type-chip (bv. `pdf`/`xlsx`/`link`).
   * `null` voor submenu's of wanneer niet goedkoop resolvebaar (geen harde AC).
   */
  responseType: string | null;
  /** Aantal directe kinderen (alleen zinvol voor submenu's). */
  childCount: number;
  /** De onderliggende rapporten/submenu's. */
  children: ReportMenuNode[];
}

/** Response van `GET /api/rapportage/menu` (Scherm B) + `.../preview` (Scherm A). */
export interface ReportMenuResponse {
  /** De via precedence gekozen rol, of `null` (geen rol → lege staat). */
  roleCode: string | null;
  /** Is er een niet-lege rapportenboom te tonen? */
  hasMenu: boolean;
  /** De gekoppelde menu-functie-id, of `null`. */
  menuFunctionId: string | null;
  /** Code van het gekoppelde menu (header-chip), of `null`. */
  menuCode: string | null;
  /** Naam van het gekoppelde menu, of `null`. */
  menuName: string | null;
  /** De rapportenboom (leeg bij lege staat). */
  nodes: ReportMenuNode[];
}

/** Eén rol + z'n huidige menu-toewijzing in het beheerscherm (AC1). */
export interface ReportRoleAssignment {
  roleCode: string;
  /** De gekoppelde menu-functie-id, of `null` (— nog geen menu —). */
  menuFunctionId: string | null;
  menuCode: string | null;
  menuName: string | null;
}

/** Eén kiesbare menu-functie voor de dropdown (AC3). */
export interface ReportMenuOption {
  id: string;
  code: string | null;
  description: string | null;
}

/**
 * Body van `PUT /api/rapportage/admin/roles/{roleCode}`. `menuFunctionId = null`
 * betekent "— geen menu —" (AC6: bestaande koppeling wissen).
 */
export interface AssignRoleMenuRequest {
  menuFunctionId: string | null;
}

// ─── Rapportage per rol — Fase 2 (parameterscherm + rapport draaien) ─────────

/** Eén keuze-optie voor een lookup-parameter (`{ id, text }`). */
export interface ReportLookupOption {
  /** De waarde (getypeerd per `dataType`: guid/int/string), als string. */
  id: string | null;
  /** De weergavetekst. */
  text: string | null;
}

/**
 * Eén parameterveld van een rapport-param-scherm. `widgetType`/`dataType` zijn de
 * string-naam van de legacy-enum (de FE-widget-switch is naam-gebaseerd).
 */
export interface ReportParamField {
  fieldName: string;
  label: string | null;
  helpText: string | null;
  /** `WidgetType`-enum als string-naam (bv. `Date`, `Integer`, `Boolean`, `List`). */
  widgetType: string;
  /** `LookupValueFieldDataType`-naam (`Guid`/`Integer`/`String`), of `null`. */
  dataType: string | null;
  isMandatory: boolean;
  /** Van/tot-veld → twee inputs, verstuurd als één waarde `"<van>|<tot>"`. */
  isFromTo: boolean;
  isReadOnly: boolean;
  /** Password-widget → FE maskeert (type=password). */
  isPassword: boolean;
  length: number | null;
  regEx: string | null;
  /** Huidige/default-waarde als string, of `null`. */
  value: string | null;
  /** Keuze-opties voor lookup-widgets; leeg voor niet-lookups. */
  lookups: ReportLookupOption[];
}

/** Response van `GET /api/rapportage/rapport/{functionId}/params`. */
export interface ReportParamScreen {
  functionId: string;
  reportName: string | null;
  /** Best-effort response-type voor de type-chip (bv. `pdf`/`xlsx`/`link`), of `null`. */
  responseType: string | null;
  /** Toon de "Opslaan als default"-knop? (systeemparameter `GR_Report_ShowdefaultButton`). */
  showDefaultButton: boolean;
  fields: ReportParamField[];
}

/** `fieldName → value`-paren voor het draaien/opslaan van een rapport. */
export type ReportRunValues = Record<string, string | number | boolean | null>;

/** JSON-respons van een link-rapport (`window.open` in een nieuw tabblad). */
export interface ReportLinkResult {
  url: string | null;
}

// ─── Annotations (story annotaties-mvp / S-BL.2, 2026-05-30) ────────────

/**
 * Eén pijl-shape op een geannoteerde foto. Coords zijn 0..1-ratios
 * relatief aan de image-bounds (resolutie-onafhankelijk). Server rondt
 * af op 4 decimalen met half-up (zie BE `ClampAndRound`).
 */
export interface AnnotationArrowShape {
  type: 'arrow';
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;     // `#RRGGBB`-hex
  thickness: number; // {2, 4, 6}
}

export interface AnnotationCircleShape {
  type: 'circle';
  cx: number;
  cy: number;
  r: number;
  color: string;
  thickness: number;
}

export interface AnnotationTextShape {
  type: 'text';
  x: number;
  y: number;
  text: string;
  color: string;
  fontSize: number;
}

/**
 * Forward-compat: V2 voegt `stamp_datetime` toe; V1-editor rendert 'm
 * niet maar bewaart 'm wel bij heropenen. BE valideert onbekende types
 * niet (AC-15.3).
 */
export interface AnnotationUnknownShape {
  type: string;
  [key: string]: unknown;
}

export type AnnotationShape =
  | AnnotationArrowShape
  | AnnotationCircleShape
  | AnnotationTextShape
  | AnnotationUnknownShape;

/** JSON-payload zoals opgeslagen op `GD_AttachmentAnnotation.AnnotationDataJson`. */
export interface AnnotationData {
  version: number;        // payload-format-version (V1 = 1), niet de DB Version
  shapes: AnnotationShape[];
}

/** Response van `GET /api/attachments/{id}/annotation` (200). */
export interface AnnotationGetResponse {
  annotationDataJson: string;
  version: number;
}

/** Body van `PUT /api/attachments/{id}/annotation`. */
export interface AnnotationPutRequest {
  annotationDataJson: string;
  /** Ontbreekt = create-intent (AC-3.1). Aanwezig = must-match-or-409. */
  expectedVersion?: number;
}

/** Response van `PUT /api/attachments/{id}/annotation` (200). */
export interface AnnotationPutResponse {
  annotationDataJson: string;
  version: number;
}

// ─── Monteurs per dossier (story fe-monteur-multiselect-intake, 2026-06-06) ──

/**
 * Eén monteur-optie in de multi-select op de Intake-tab. Komt uit de unie van
 * actieve hersteller-monteurs ∪ reeds-toegewezen monteurs (incl. gearchiveerde
 * die toegewezen zijn). `selected` = de monteur is toegewezen aan dit dossier.
 */
export interface DossierFitterOption {
  id: string;
  name: string | null;
  selected: boolean;
}

/**
 * Response van `GET /api/dossiers/{id}/fitters`. `editable=false` → de FE
 * disablet de checkbox-lijst (edit-lock). Lege `fitters` → de FE verbergt de
 * Monteur-sectie (geen/onbekende hersteller).
 */
export interface DossierFittersResponse {
  editable: boolean;
  fitters: DossierFitterOption[];
}

// ─── Calc-voortgang (calc-progress, 2026-06-11) ─────────────────────────────

/**
 * Eén voortgang-event zoals de calc-keten het meldt via de BE-hooks.
 * `step` is een stabiele, INTERNE key (bv. `item_lookup`, `gross_price`);
 * `detail` is de gebruiker-zichtbare tekst.
 *
 * ⚠ De FE rendert `detail` **letterlijk** — geen whitelist, geen vertaaltabel,
 * geen icoon-mapping op `step`. Elke string die de backend hierin zet staat dus
 * één-op-één op het scherm van de hersteller. Daar hoort nooit de naam van een
 * leverancier in te staan (CLAUDE.md; er staat een scan op).
 */
export interface CalcProgressEventDto {
  seq: number;
  step: string;
  detail: string;
}

/**
 * Response van `GET /api/calc-progress/{operationId}`.
 * `found:false` = nog niets geschreven of entry verlopen; `done:true` = de
 * calc-POST is afgerond (poll mag stoppen).
 */
export interface CalcProgressResponseDto {
  found: boolean;
  done: boolean;
  events: CalcProgressEventDto[];
}

// ─── Melding hersteller accept/weiger (story fe-be-melding-hersteller-...) ──

/** Verwerkings-state van een melding (`UA_Intake`). */
export type IntakeState = 'new' | 'accepted' | 'rejected';

/** Eén dagdeel-keuze van een afspraakmoment. */
export type IntakeAppointmentPeriod = 'morning' | 'afternoon' | 'none';

/**
 * Eén geparst afspraakmoment uit `UA_Intake.DesiredAppointments` (AC #8). De
 * `optionId` is een stabiele keuze-id die de FE in de accept-body terugstuurt.
 */
export interface IntakeAppointmentOption {
  optionId: string;
  /** ISO date-only (`yyyy-MM-dd`). */
  date: string;
  period: IntakeAppointmentPeriod;
  /** Leesbaar label zoals in de bron-tekst (bv. "16-06-2026 ochtend"). */
  label: string;
}

/** Bijlage van een melding (read-only). */
export interface IntakeAttachment {
  id: string;
  name: string | null;
  category: string | null;
  type: string | null;
}

/**
 * Detail-DTO van `GET /api/intakes/{nummer}`. `carPosition`/`windshieldPosition`
 * zijn RUWE CSV-strings — de FE mapt ze via dezelfde `DAMAGE_ZONE_LABELS`/
 * `formatDamageZones` als de Schade-tab (geen BE-vertaling).
 */
export interface IntakeDetail {
  intakeNumber: number;
  createdAt: string | null;
  state: IntakeState;
  isUrgent: boolean;

  // Klant
  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;
  customerAddress: string | null;
  customerHouseNumber: string | null;
  customerZipcode: string | null;
  customerCity: string | null;

  // Voertuig
  license: string | null;
  makeModel: string | null;

  // Schade
  carPosition: string | null;
  windshieldPosition: string | null;
  damageTypeLabel: string | null;
  causeName: string | null;
  damageDate: string | null;
  repairTypeName: string | null;

  // Verzekering
  insurerName: string | null;
  agentName: string | null;
  isInsured: boolean | null;
  isLease: boolean | null;
  ownRiskReplace: number | null;
  vatInsured: boolean | null;

  // Afspraak & wensen
  desiredAppointments: string | null;
  appointmentOptions: IntakeAppointmentOption[];
  rentalCarRequired: boolean | null;
  takeAndReturnServiceRequired: boolean | null;
  useWaitingRoom: boolean | null;
  isMobileRepair: boolean | null;
  mobileRepairLocation: string | null;

  // Opmerkingen
  remarks: string | null;

  /**
   * Story `fe-be-melding-detail-contextrail` (2026-06-14): de opmerkingen-
   * historie van de melding (`GD_Communication` met `SubjectID = UA_Intake.ID`),
   * zelfde shape als het dossier (`DossierComments`) zodat de FE de rail-card +
   * drawer hergebruikt. Nieuwste boven; `count === items.length`.
   */
  comments: DossierComments;

  // Bijlagen
  attachments: IntakeAttachment[];

  // Reeds-verwerkt
  dossierId: string | null;
  dossierNumber: number | null;
  rejectReason: string | null;
  /**
   * Regel gebruiker 12-10-2026: mag de huidige sessie deze melding accepteren of
   * weigeren? Alleen de hersteller van de melding (`true`); systeem en
   * hoofdkantoor zien de melding wel, maar krijgen `false`. De FE toont de
   * accept/weiger-knoppen uitsluitend op `canDecide === true`.
   */
  canDecide: boolean;
}

/**
 * Body van `POST /api/intakes/{nummer}/comments` (opmerking op een melding
 * toevoegen). Het communicatietype wordt server-side per-request op "Opmerking"
 * geresolved (geen client-keuze, anders dan het dossier), dus de FE stuurt
 * alleen de tekst. Whitespace-only wordt server-side naar null genormaliseerd.
 */
export interface IntakeCommentCreateRequest {
  message?: string | null;
}

/** Body van `POST /api/intakes/{nummer}/accept`. */
export interface IntakeAcceptRequest {
  sendCustomerMail: boolean;
  /** `"later"`, een geldige `IntakeAppointmentOption.optionId`, of `"custom"`. */
  appointmentChoice: string;
  /**
   * Alleen bij `appointmentChoice === 'custom'`: door de hersteller ingevoerde
   * datum/tijd (`datetime-local`, bv. `"2026-06-16T10:30"`). Wordt het
   * `RepairDate` van het nieuw aangemaakte dossier.
   */
  appointmentDateTime?: string;
}

/** Response van een geslaagde accept. */
export interface IntakeAcceptResponse {
  dossierId: string;
  dossierNumber: number;
  customerMailSent: boolean;
  intakeNumber: number;
}

/** Body van `POST /api/intakes/{nummer}/reject`. */
export interface IntakeRejectRequest {
  /** Lijst-code of `"other"`. */
  reasonCode: string;
  /** Verplichte toelichting bij `"other"`. */
  reasonText: string;
}

/** Response van een geslaagde reject. */
export interface IntakeRejectResponse {
  intakeNumber: number;
  state: IntakeState;
}

/**
 * Bron van een melding (story `fe-be-meldingen-overzicht`, 2026-06-14).
 * `null` = niet betrouwbaar af te leiden → FE toont "—".
 */
export type IntakeSource = 'callcenter' | 'website' | null;

/**
 * Eén rij in het hersteller-meldingen-overzicht (`GET /api/intakes`). De
 * Nummer-kolom is een echte `<Link to="/intakes/{intakeNumber}">`; bij
 * `state === 'accepted'` linkt de Dossier-kolom naar het gekoppelde dossier.
 */
export interface IntakeListItem {
  intakeNumber: number;
  license: string | null;
  customerName: string | null;
  insurerName: string | null;
  source: IntakeSource;
  createdAt: string | null;
  isUrgent: boolean;
  state: IntakeState;
  /** Het gekoppelde dossiernummer — alleen bij `state === 'accepted'`. */
  dossierNumber: number | null;
  /** Het gekoppelde dossier-GUID — alleen bij `state === 'accepted'`. */
  dossierId: string | null;
}

/** Response van `GET /api/intakes` (offset-paging). */
export interface IntakeListResponse {
  items: IntakeListItem[];
  hasMore: boolean;
}

/** Query-params voor `GET /api/intakes`. */
export interface GetIntakesParams {
  /** 0-based pagina-index; 0/undefined → weggelaten. */
  page?: number;
  /** Aantal rijen per pagina (FE: 50; BE capt op 100). */
  pageSize?: number;
  /** Vrije zoekterm (kenteken/klant/nummer/maatschappij). */
  q?: string;
  /** `'open'` (default; weggelaten) of `'all'` (incl. verwerkt). */
  status?: 'open' | 'all';
  /** `true` → alleen spoed-meldingen. */
  urgent?: boolean;
}

// ─── Globaal zoeken — ⌘K command-palette (story fe-be-globaal-zoeken) ────────

/**
 * Story `fe-be-globaal-zoeken-commandpalette` (2026-06-24) — welk veld de match
 * veroorzaakte op een dossier-resultaatrij. Voedt de chip-/highlight-keuze in
 * de overlay (precedentie license > number > invoice > name; AC14 / verfijning 4).
 *  - `'license'`  — kenteken-prefix-match (of via de `license`-hint uit AC7).
 *  - `'number'`   — dossiernummer-prefix-match.
 *  - `'invoice'`  — factuurnummer-prefix-match (via factuur-join).
 *  - `'name'`     — klantnaam-contains-match.
 */
export type GlobalSearchMatchField = 'license' | 'number' | 'invoice' | 'name';

/**
 * Eén dossier-resultaatrij van `GET /api/search`. Alleen weergavevelden + id
 * (geen zware dossier-DTO). `matchField` benoemt waaróm de rij matcht zodat de
 * FE de juiste chip/highlight kan tonen. `invoiceNumber` is alleen relevant
 * (en gezet) bij een `matchField === 'invoice'`-rij.
 */
export interface GlobalSearchDossier {
  /** `UA_Dossier.ID` — GUID; gebruikt voor de `<Link to="/dossiers/{id}">`. */
  id: string;
  /** Dossiernummer (`UA_Dossier.Number`); `null` bij een net-aangemaakt dossier. */
  number: number | null;
  /** Kenteken (`UA_Dossier.License`); genormaliseerd NL-formaat of `null`. */
  license: string | null;
  /** Klantnaam (`UA_Dossier.CustomerName`) of `null`. */
  customerName: string | null;
  /** Status-label voor de status-chip (bv. "In behandeling") of `null`. */
  statusLabel: string | null;
  /** Welk veld de match veroorzaakte (bepaalt chip/highlight). */
  matchField: GlobalSearchMatchField;
  /**
   * Het kale matchende factuurnummer (`UF_Invoice.InvoiceNumber`, int) wanneer
   * `matchField === 'invoice'`; anders `null`. De FE toont 'm als "F-<nummer>".
   */
  invoiceNumber: number | null;
}

/** Eén klant-resultaatrij van `GET /api/search` (`RB_GenericRelation`). */
export interface GlobalSearchKlant {
  /** `RB_GenericRelation.ID` — GUID; `<Link to="/klanten/{relationId}">`. */
  relationId: string;
  /** Bedrijfs-/klantnaam (`RB_Company.Name`). */
  name: string | null;
  /** Plaats (best-effort) of `null`. */
  city: string | null;
  /** Debiteurnummer of `null`. */
  debtorNumber: number | null;
}

/** Eén melding-resultaatrij van `GET /api/search` (`UA_Intake`). */
export interface GlobalSearchMelding {
  /** Meldingnummer (`UA_Intake.DossierNumber`); `<Link to="/intakes/{intakeNumber}">`. */
  intakeNumber: number;
  /** Kenteken (`UA_Intake.Licenseplate`) of `null`. */
  license: string | null;
  /** Klantnaam of `null`. */
  customerName: string | null;
  /** Verzekeraar-naam of `null`. */
  insurerName: string | null;
  /** Status-label voor de status-chip of `null`. */
  statusLabel: string | null;
}

/**
 * Response-body van `GET /api/search?q=<term>[&license=<12-AB-CD>]`. Drie
 * gescopede groepen, elk gecapt op 5 (BE). `q` < 2 (getrimde) tekens → alle drie
 * de arrays leeg, geen DB-hit.
 */
export interface GlobalSearchResponse {
  dossiers: GlobalSearchDossier[];
  klanten: GlobalSearchKlant[];
  meldingen: GlobalSearchMelding[];
}

// ─── Facturatie-export (story fe-be-facturatie-export-achtergrond, FE-deel) ──

/**
 * Response van `GET /api/facturatie/export/status` (AC 10). BE = source-of-truth
 * voor de start-toestand (State 1 vs 1b); FE bepaalt alleen de presentatie-tekst.
 *
 * Shape exact gespiegeld aan de BE-DTO `ExportStatusDto`
 * (`AppServices/Facturatie/Dtos/FacturatieExportDtos.cs`).
 */
export interface FacturatieExportStatus {
  /** Mag de gebruiker nu een export starten? `false` → State 1b. */
  canStart: boolean;
  /**
   * Machine-leesbare reden bij `canStart=false`. In V1 enige waarde
   * `"export_already_running"`; `null` wanneer `canStart=true`.
   */
  blockReason: 'export_already_running' | null;
  /**
   * Sinds wanneer loopt de actieve export (UTC, ISO-8601) — of `null`. De FE
   * toont dit lokaal ("gestart om HH:mm") in de State-1b-reden.
   */
  runningSince: string | null;
  /**
   * Voorgevuld e-mailadres (bedrijfsinstelling `InvoiceExportEmail`, fallback
   * `DefaultContact.EmailAddress`). Kan leeg/`null` zijn.
   */
  exportEmail: string | null;
}

/**
 * 202-respons van een succesvolle start (`POST /api/facturatie/export`, AC 11).
 * Spiegelt de BE-DTO `ExportStartedDto`.
 */
export interface FacturatieExportStarted {
  /** Altijd `"started"` bij een 202. */
  status: string;
  /** Job-/correlatie-id van de gestarte run (start-bekend, niet journalEntryId). */
  runId: string;
}

/**
 * Eén regel in "Recente exports" (`GET /api/facturatie/exports`, AC 20).
 * Spiegelt de BE-DTO `ExportFileDto`.
 */
export interface FacturatieExportFile {
  /** `GD_Attachment.ID` — gebruikt in de download-URL. */
  id: string;
  /** Bestandsnaam (bv. `20260624_1612_verkoopfacturen.xml`). */
  name: string;
  /** Bestandsgrootte in bytes. */
  sizeBytes: number;
  /** Aanmaakmoment (ISO-8601) of `null`. */
  created: string | null;
}

/** Lijst-respons van "Recente exports" (`ExportListDto`). */
export interface FacturatieExportList {
  items: FacturatieExportFile[];
}

// ─── Documentatie-module (story fe-be-documentatie-module) ──────────────────

/**
 * Doelgroep-niveau van een documentatie-node. Effectieve zichtbaarheid van een
 * document = de striktste van (categorie, per-document-override) — de BE lost
 * dit op en levert het al opgelost via `effectiveVisibility`.
 */
export type DocVisibility = 'All' | 'Keten' | 'System';

/**
 * Eén document-blad in de boom-respons. `effectiveVisibility` is server-side
 * al opgelost (override ?? categorie, narrows-only). `hasBody` bepaalt of de
 * lezer een tekst-blok toont; `attachmentCount` voedt de bijlagen-badge.
 */
export interface DocTreeDocument {
  id: string;
  categoryId: string;
  title: string;
  effectiveVisibility: DocVisibility;
  /** Per-document-override; `null` = erft de categorie-doelgroep. */
  visibilityOverride: DocVisibility | null;
  sortOrder: number;
  /** Optimistic-locking-token — de bewerk-dialog stuurt 'm mee bij update. */
  version: number;
  hasBody: boolean;
  attachmentCount: number;
}

/** Eén categorie-knoop met haar (voor de caller zichtbare) documenten. */
export interface DocTreeCategory {
  id: string;
  groupId: string;
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
  /** Optimistic-locking-token — de bewerk-dialog stuurt 'm mee bij update. */
  version: number;
  documents: DocTreeDocument[];
}

/** Eén groep-knoop met haar (voor de caller zichtbare) categorieën. */
export interface DocTreeGroup {
  id: string;
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
  /** Optimistic-locking-token — de bewerk-dialog stuurt 'm mee bij update. */
  version: number;
  categories: DocTreeCategory[];
}

/**
 * Respons van `GET /api/documentation/tree`. `canManage` is BE-autoritatief
 * (`System || keten`); de FE leidt 'm daarnaast lokaal af uit whoami voor de
 * render-gate, maar vertrouwt de write-gate nooit puur client-side.
 */
export interface DocTreeResponse {
  canManage: boolean;
  groups: DocTreeGroup[];
}

/** Eén bijlage van een document (blob/bestand via de GD_Attachment-laag). */
export interface DocAttachment {
  id: string;
  name: string;
  sizeBytes: number;
  mimeType: string | null;
  /**
   * Exact pad voor de download (`/api/documentation/attachments/{id}/data`) —
   * direct bruikbaar als `href` in een echte `<a download>`; de BE her-evalueert
   * de effectieve zichtbaarheid van het parent-document (geen boom-vertrouwen).
   */
  downloadUrl: string;
}

/** Soort inhoud van een document in de module Documentatie (migratie 130). */
export type DocBodyFormat = 'rich' | 'html';

/**
 * Respons van `GET /api/documentation/documents/{id}` — de volledige inhoud van
 * één document voor lezer + bewerk-paneel. `bodyHtml` is server-side
 * gesanitiseerd (allowlist) → veilig te renderen. `visibility` = de per-document
 * override (`null` = erft de categorie); `effectiveVisibility` is de opgeloste
 * doelgroep die de lezer-badge toont. `version` = optimistic-locking token.
 */
export interface DocDocumentDetail {
  id: string;
  categoryId: string;
  groupId: string;
  groupName: string;
  categoryName: string;
  title: string;
  bodyHtml: string | null;
  /**
   * `rich` = de tekst-editor, `html` = een HTML-document dat in een afgeschermd kader wordt getoond.
   * Ontbreekt bij een oudere backend ⇒ `rich`.
   */
  bodyFormat?: DocBodyFormat;
  visibility: DocVisibility | null;
  effectiveVisibility: DocVisibility;
  sortOrder: number;
  version: number;
  modifiedByName: string | null;
  modifiedAt: string | null;
  canManage: boolean;
  attachments: DocAttachment[];
}

/** Body voor `POST /api/documentation/groups`. */
export interface DocGroupCreateRequest {
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
}

/** Body voor `PUT /api/documentation/groups/{id}` — bevat het version-token. */
export interface DocGroupUpdateRequest {
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
  version: number;
}

/** Body voor `POST /api/documentation/categories`. */
export interface DocCategoryCreateRequest {
  groupId: string;
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
}

/** Body voor `PUT /api/documentation/categories/{id}`. */
export interface DocCategoryUpdateRequest {
  name: string;
  visibility: DocVisibility;
  sortOrder: number;
  version: number;
}

/**
 * Body voor `POST /api/documentation/documents`. `visibility: null` = erf van
 * categorie (default voor nieuw document). `bodyHtml`/`sortOrder` optioneel.
 */
export interface DocDocumentCreateRequest {
  categoryId: string;
  title: string;
  bodyHtml?: string | null;
  /** Leeg = `rich`. `html` zonder migratie 130 ⇒ 409 `html_format_unavailable`; boven 5 MB ⇒ 400 `body_too_large`. */
  bodyFormat?: DocBodyFormat;
  visibility?: DocVisibility | null;
  sortOrder?: number;
}

/**
 * Body voor `PUT /api/documentation/documents/{id}`. `visibility: null` = erf
 * van categorie. `version` verplicht → 409 `{ reason:'version_conflict',
 * currentVersion }` bij stale token.
 */
export interface DocDocumentUpdateRequest {
  title: string;
  bodyHtml: string | null;
  bodyFormat?: DocBodyFormat;
  visibility: DocVisibility | null;
  sortOrder: number;
  version: number;
}

/** Respons van create/update op groep/categorie/document (id + evt. version). */
export interface DocNodeMutationResponse {
  id: string;
  version?: number;
}

/** Respons van `POST /api/documentation/documents/{id}/attachments`. */
export interface DocAttachmentUploadResponse {
  ids: string[];
  count: number;
}

// ─── Claimfilter-info bij verzekeraar-expertise ────────────────────────────
// Story `fe-be-verzekeraar-claimfilter-info` (2026-08-17, story 5).

/**
 * Eén claimfilter-regel uit `GET /api/dossiers/{id}/claimfilter-rules`
 * (read-only). Het BE is een doorgeefluik: geen `severity`-enum, geen sortering,
 * geen dedupe en geen counts — de FE classificeert en telt zelf (AC 10/14).
 *
 * `isException` is het **autoritatieve uitval-signaal** (legacy
 * `ClaimFilterCommand` zet het dossier op expertise via
 * `rules.Any(i => i.Exception == true)`) → de FE toont die regels als
 * **blokkerend**; al het overige is een **waarschuwing** (AC 11/12).
 *
 * `code`/`reason` zijn **niet-gevalideerde tekst van de externe
 * claimfilter-leverancier**: uitsluitend als tekst renderen (AC 41).
 */
export interface ClaimFilterRule {
  /** Regel-code van de leverancier (bv. `CAL-02`); kan leeg/ontbrekend zijn. */
  code?: string | null;
  /** Leesbare reden; kan leeg/ontbrekend zijn (AC 13 vult dan terug op `code`). */
  reason?: string | null;
  /** `true` ⇒ blokkerend (rood). Ontbrekend/`null` ⇒ waarschuwing (AC 12). */
  isException?: boolean | null;
  /** Signaal zonder uitval (amber). Wordt niet met `isException` samengevoegd. */
  isWarning?: boolean | null;
}

/**
 * Respons van `GET /api/dossiers/{id}/claimfilter-rules`. Nul regels →
 * `{ rules: [] }` (state C: het blok rendert dan niet) — dat is een geldige,
 * betekenisvolle uitkomst en moet van een fout te onderscheiden zijn (AC 7/19).
 */
export interface ClaimFilterRulesResult {
  rules: ClaimFilterRule[];
  /**
   * De **code van de meting** waarop het claimfilter is uitgevallen — één van de zes
   * uit `CLAIMFILTER_MEASUREMENT_CODES`, of `null`/ontbrekend.
   *
   * ⚠⚠ Dit is met opzet de METING en niet de oordeel-reden. De reden draagt de
   * prefixen `MUNTWORP:` / `REGEL:` plus de ingestelde grenzen — interne inrichting
   * die system/support niet verlaat. De meting is een gesloten codeverzameling
   * zonder vrije tekst en zonder interne getallen, en mag wél naar de verzekeraar.
   *
   * ⚠ **Geen meting is niet één geval maar vijf**, en ze betekenen niet hetzelfde:
   * goedgekeurd, een steekproef, een oningerichte maatschappij, een oordeel van
   * vóór de oordeelsregels (de muntworp), en een dossier dat helemaal niet via de
   * nieuwe keten op expertise kwam. De FE rendert daarom géén lege meting-regel:
   * dat zou van elk van die vijf hetzelfde raadsel maken.
   */
  measurement?: string | null;

  /**
   * **De volledige onderbouwing uit het nieuwe claimfilter** — dezelfde gegevens
   * waarop het claimfilter-detailscherm zijn Referentie-blok bouwt, of `null`
   * wanneer dit dossier geen claimfilter-kop heeft (PO 2026-09-03: *"graag alles
   * tonen waarbij ook nog zichtbaar wordt waarom het uitgevallen is"*).
   *
   * ⚠⚠ **`reason` zit er met opzet NIET in.** Het "waarom" wordt zichtbaar uit de
   * vergelijking plus `measurement` — de feitelijke onderbouwing — en niet uit de
   * oordeel-reden, die de prefixen `MUNTWORP:` / `REGEL:` en de ingestelde grenzen
   * draagt en tot system/support beperkt blijft. Dat is geen weglating maar de kern
   * van deze projectie; er staat een bronscan op.
   */
  record?: ClaimFilterInsurerRecord | null;
}

/**
 * De verzekeraar-projectie van één claimfilter-kop: precies genoeg om de
 * vergelijking te tonen, en geen veld meer.
 *
 * Bewust dezelfde `ClaimFilterRecordRow`- en `ClaimFilterReference`-types als het
 * admin-scherm, zodat `ClaimFilterReferenceBlock` letterlijk hergebruikt kan worden.
 * Twee eigen types zouden twee weergaven van dezelfde vergelijking opleveren, en die
 * lopen uiteen zodra er één meting bijkomt.
 */
export interface ClaimFilterInsurerRecord {
  /** Bij een reparatie hoort er géén eurocode-regel te zijn — dat is de normale
   *  toestand en het blok zegt dat ook zo. */
  isRepair: boolean;
  /** De regels zoals de HERSTELLER ze declareerde: de linkerhelft van elke
   *  vergelijking. Een lege lijst is een uitspraak, geen laadtoestand. */
  rows: ClaimFilterRecordRow[];
  /** Het Referentie-blok: wat er per glaspositie is opgevraagd en teruggekomen. */
  reference: ClaimFilterReference;
  /** Wanneer het oordeel geveld is. */
  verdictAt: string | null;
  /** Een oordeel van vóór de oordeelsregels — dan is er geen meting en staat de
   *  vergelijking los van de uitkomst. Het blok moet dat kunnen zeggen. */
  isCoinFlip: boolean;

  /**
   * **De oordeel-reden, letterlijk** — de tekst die ook op het claimfilter-detail
   * staat, inclusief de prefix `REGEL:` of `MUNTWORP:`.
   *
   * ⚠⚠ **Dit veld bestaat op uitdrukkelijk besluit van de PO (03-09-2026) en het
   * doorbreekt een eerder vastgelegde afscherming.** AC-56 hield deze tekst tot
   * system/support beperkt en er stond een scan op dat zij `features/claimfilter/`
   * niet verlaat. De PO heeft die keuze omgedraaid — hij wil dat de maatschappij de
   * volledige onderbouwing ziet — en heeft daarbij het muntworp-bezwaar expliciet
   * gewogen: op acceptatie dragen 19 van de 21 oordelen `MUNTWORP:`, en zijn
   * afweging is dat dit een testomgeving is en dat de muntworp op een andere
   * omgeving nooit meer voorkomt.
   *
   * ⚠ Wie dit veld ooit wil weghalen: dat is een PO-besluit, niet een opruimactie.
   * En wie het wil UITBREIDEN naar een ander publiek: de reden noemt de voor die
   * maatschappij ingestelde bandbreedte. Dat mag zij over zichzelf weten; een
   * hersteller of een derde niet.
   */
  reason: string | null;
}

// --- Hersteller-blok in de rail (verzekeraar) --------------------------------
// PO-verzoek 2026-08-18.

/**
 * Respons van `GET /api/dossiers/{id}/repairer` — naam + plaats van de hersteller
 * voor het rail-blok dat de verzekeraar ziet. Beide velden mogen `null` zijn: geen
 * hersteller op het dossier, of de plaats kon niet worden opgelost (die komt
 * best-effort uit de default-contact van de hersteller). De FE verbergt dan de
 * betreffende regel, en het hele blok als beide leeg zijn.
 */
export interface DossierRepairerInfo {
  name: string | null;
  city: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Claimfilter-beheerscherm (story `fe-be-claimfilter-beheerscherm`, 2026-08-31)
//
// Zes READ-ONLY responses. Er zit BEWUST geen vrij tekstveld in waarin
// `CF_ClaimFilter.VerdictReason` alsnog kan landen: het MUNTWORP-predicaat blijft
// in SQL en alleen de afgeleide markering (`isCoinFlip`) reist mee (AC-22/AC-41).
// ─────────────────────────────────────────────────────────────────────────────

/** Groen / oranje / rood / grijs / neutraal. ⚠ De TEKST draagt de toestand, niet
 *  de kleur (AC-44, WCAG 1.4.1) — dit veld is voor de opmaak. */
export type ClaimFilterLampSeverity = 'Green' | 'Amber' | 'Red' | 'Grey' | 'Neutral';

/**
 * Het meetmoment van één blok — de DRIE toestanden van AC-11.
 *
 * ⚠ `measured: false` is "niet gekeken"; `measurementFailed: true` is "gekeken en
 * het ging mis". Alleen `measured: true` zonder fout rechtvaardigt een getal.
 * **Een blok dat niet gemeten is toont nooit een `0`.**
 */
export interface ClaimFilterBlock {
    block: string;
    measured: boolean;
    measuredAt: string | null;
    measurementFailed: boolean;
    failureMessage: string | null;
}

export interface ClaimFilterLamp {
    state: string;
    severity: ClaimFilterLampSeverity;
    title: string;
    detail: string;
    /** ⚠ `null` — en nooit `0` — wanneer er geen ronde is. */
    lastRoundAgeSeconds: number | null;
}

export interface ClaimFilterFinding {
    invariant: 'PhaseOneOffered' | 'PhaseTwoCandidates' | 'ReferenceFailures';
    message: string;
    expected: number;
    actual: number;
}

export interface ClaimFilterRoundSummary {
    id: string;
    startedAt: string;
    finishedAt: string;
    durationMs: number;
    /** ⚠ De eerste kolom van de chip is de ronde-UITKOMST, niet de reden-code. */
    succeeded: boolean;
    failureKind: string | null;
    failureText: string | null;
    offeredCount: number | null;
    created: number;
    alreadyLinked: number;
    stillReserved: number;
    skipped: number;
    failed: number;
    candidates: number;
    transitioned: number;
    toApproved: number;
    toExpertise: number;
    referenceEnabled: boolean;
    dominantReason: string | null;
    /** Twee app-pool-processen: overlap is een BEVINDING, geen weergavefout (AC-42). */
    overlapsPrevious: boolean;
    /** ⚠ Leeg = geen bevinding, en óók geen keurmerk (review M-09). */
    findings: ClaimFilterFinding[];
}

export interface ClaimFilterJobLink {
    /** ⚠ `false` → het scherm toont "jobkoppeling ontbreekt", nooit `Done` (AC-3). */
    linked: boolean;
    status: string | null;
    /** Al naar LOKALE tijd omgezet — `UA_Job` is UTC (AC-23). */
    startedAtLocal: string | null;
    finishedAtLocal: string | null;
}

export interface ClaimFilterDossierRef {
    dossierId: string;
    dossierNumber: string | null;
}

export interface ClaimFilterFailureGroup {
    /** De code, LETTERLIJK en onvertaald — het is de grep-sleutel. */
    reason: string;
    explanation: string | null;
    count: number;
    firstAt: string | null;
    lastAt: string | null;
    /** ⚠ `stale_reservation` is de VEEG en telt NIET als mislukking (AC-27.3). */
    countsAsFailure: boolean;
    /** Zichtbare voetnoot, o.a. bij de altijd-nul van `link_lost_race` (AC-27.4). */
    footnote: string | null;
    dossiers: ClaimFilterDossierRef[];
}

export interface ClaimFilterCheckRow {
    dossierId: string | null;
    dossierNumber: string | null;
    claimFilterNumber: string | null;
    verdict: number | null;
    count: number | null;
    createdAt: string | null;
    /** De drie gebreken van controle C als BOOLEANS — nooit de tekst (AC-24.2). */
    verdictOutOfDomain: boolean;
    verdictAtMissing: boolean;
    verdictReasonMissingOrUnexpected: boolean;
}

export interface ClaimFilterCheck {
    code: string;
    title: string;
    count: number;
    /** ⚠ Controle G is een CONSTRUCTIEFOUT zodra hij boven nul komt (AC-25). */
    isConstructionError: boolean;
    measurementFailed: boolean;
    /**
     * Waaróm de meting mislukte; `null` zolang er niets misging.
     *
     * ⚠ "Meting mislukt" zonder reden is alleen een ander soort stilte: dan weet de
     * lezer dát er iets is en nog steeds niet wát.
     */
    failureMessage: string | null;
    measuredAt: string | null;
    rows: ClaimFilterCheckRow[];
}

export interface ClaimFilterOfferedCount {
    /** ⚠ `null` bij een mislukte meting — nooit `0`. */
    count: number | null;
    measuredAt: string | null;
    measurementFailed: boolean;
    failureMessage: string | null;
}

export interface ClaimFilterToday {
    rounds: number;
    created: number;
    toApproved: number;
    toExpertise: number;
    failed: number;
}

export interface ClaimFilterTraceLog {
    /** ⚠ `false` → het scherm MELDT dat, i.p.v. naar een niet-geschreven bestand
     *  te wijzen (AC-28). */
    configured: boolean;
    directory: string | null;
    filePattern: string | null;
    searchTerm: string | null;
}

export interface ClaimFilterOverview {
    scanLamp: ClaimFilterLamp;
    referenceLamp: ClaimFilterLamp;
    lastRound: ClaimFilterRoundSummary | null;
    lastRoundJob: ClaimFilterJobLink | null;
    /** ⚠ Bij het laden ALTIJD niet-gemeten — het getal komt achter een knop (AC-8/19). */
    offeredWaiting: ClaimFilterOfferedCount;
    warnings: ClaimFilterFailureGroup[];
    checks: ClaimFilterCheck[];
    today: ClaimFilterToday | null;
    /** ⚠ Uit het ANTWOORD, nooit uit een getal in de FE (AC-45). */
    scanIntervalSeconds: number | null;
    scanEnabled: boolean | null;
    historyStartsAt: string | null;
    traceLog: ClaimFilterTraceLog;
    blocks: ClaimFilterBlock[];
}

export interface ClaimFilterRoundList {
    rounds: ClaimFilterRoundSummary[];
    page: number;
    pageSize: number;
    total: number;
    historyStartsAt: string | null;
    blocks: ClaimFilterBlock[];
}

export interface ClaimFilterRoundEvent {
    dossierId: string;
    /** ⚠ Kan `null` zijn; het scherm valt dan op het id terug (review L-03). */
    dossierNumber: string | null;
    outcome: string;
    reason: string;
    reasonExplanation: string | null;
    createdAt: string;
}

export interface ClaimFilterReasonCount {
    reasonCode: string;
    explanation: string | null;
    count: number;
}

export interface ClaimFilterRoundDetail {
    round: ClaimFilterRoundSummary;
    headsWithoutRows: number;
    completedWithoutTransition: number;
    noVerdict: number;
    transitionFailed: number;
    completeFailed: number;
    notVisited: number;
    /** ⚠ BUITEN de som — achter een eigen inleiding tonen (AC-21). */
    missingAuditTrace: number;
    lostCompleteRace: number;
    refCandidates: number;
    refRequestedPositions: number;
    refRequestedClaimFilters: number;
    refHarvested: number;
    refPolled: number;
    refTransientRetried: number;
    refCollisions: number;
    refOverBudget: number;
    refHeadUnreadable: number;
    refSuppressedByFlag: number;
    refExpired: number;
    refFailures: number;
    /**
     * ⚠ Kandidaten waarvan de behandeling OMVIEL — een storing in onze eigen keten, en
     * nadrukkelijk NIET hetzelfde als `refFailures` (aanvragen die de deur uit gingen en
     * mislukten, mogelijk betaald).
     */
    refHandlingFailed: number;
    /** ⚠ Een MAXIMUM, geen teller: `null` = geen wachtende kandidaat (M-04). */
    refOldestWaitingMinutes: number | null;
    referenceReasons: ClaimFilterReasonCount[];
    events: ClaimFilterRoundEvent[];
    job: ClaimFilterJobLink | null;
    traceLog: ClaimFilterTraceLog;
    blocks: ClaimFilterBlock[];
}

export interface ClaimFilterDossier {
    dossierId: string;
    dossierNumber: string | null;
    claimFilterCount: number;
    linkStatus: number | null;
    linkStatusLabel: string | null;
    verdict: number | null;
    verdictLabel: string | null;
    /** De AFGELEIDE markering; de ruwe tekst verlaat de database niet (AC-22). */
    isCoinFlip: boolean;
    currentStatusCode: number | null;
    /** ⚠ Uit de GEBEURTENIS-tabel — telt ronden, geen reserveringspogingen (AC-27.1). */
    roundsSeen: number;
    failures: ClaimFilterFailureGroup[];
}

export interface ClaimFilterDossierList {
    dossiers: ClaimFilterDossier[];
    filter: string;
    blocks: ClaimFilterBlock[];
}

/**
 * De staat van één UITGAANDE koppeling (B26).
 *
 * ⚠ Twee vragen met verschillende antwoorden: is hij **geconfigureerd** en is hij
 * **bereikbaar**. Op 02-09-2026 was alles keurig geconfigureerd en weigerde de host
 * de verbinding — vier groene controles, en toch werkte er niets. Ze samenvouwen tot
 * één "werkt/werkt niet" maakt onzichtbaar wíe het moet oplossen.
 */
export interface ClaimFilterConnection {
    name: string;
    /** Host en poort. ⚠ Nooit een sleutel of wachtwoord. */
    target: string | null;
    /** `compleet` · `half` · `leeg` */
    configState: string;
    /** Namen van de ontbrekende sleutels, nooit waarden. */
    missingKeys: string[];
    /** `bereikbaar` · `onbereikbaar` · `niet geprobeerd` */
    reachability: string;
    /** Wat er misging, in mensentaal; `null` wanneer er niets misging. */
    diagnosis: string | null;
    probeMilliseconds: number;
}

export interface ClaimFilterHealth {
    checks: ClaimFilterCheck[];
    measuredAt: string | null;
    blocks: ClaimFilterBlock[];
    /** ⚠ De vier controles kijken naar ONZE database; dit kijkt naar buiten. */
    connections: ClaimFilterConnection[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Het VIJFDE subscherm — Claimfilters (§5.7, AC-46 t/m AC-57)
//
// Deel 2 t/m 4 tonen wat de keten DOET; dit deel toont waaróver geoordeeld is.
// Zonder dat laatste zie je wél dát er goedgekeurd is, maar niet op welke regels
// — en dan is een muntworp niet van een echte vergelijking te onderscheiden.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * De DRIE oordeel-toestanden, en ze zijn onderling verschillend (AC-49).
 *
 * ⚠ `None` is een **wachtstand** en geen uitkomst: migratie 075 legt dat
 * letterlijk vast. "Nog geen oordeel" en "geen oordeel gegeven" mogen nooit
 * dezelfde tekst krijgen, en `None` mag nooit als lege cel renderen.
 * `OutOfDomain` is staande controle C — het getal wordt dan letterlijk getoond.
 */
export type ClaimFilterVerdictState = 'None' | 'Approved' | 'Expertise' | 'OutOfDomain';

/**
 * De vergelijking van de twee chassisnummers — **vier** uitkomsten (AC-50).
 *
 * ⚠ `OneMissing` is nadrukkelijk géén `Different`: één leeg veld is een
 * ontbrekende waarde en geen meningsverschil tussen hersteller en RDC.
 */
export type ClaimFilterChassisComparison = 'Equal' | 'Different' | 'OneMissing' | 'BothMissing';

/** Eén claimfilter op de lijst (§5.7.2). */
export interface ClaimFilterRecord {
    /** ⚠ Waar een MENS naar wijst — de lijst, de URL en het zoekveld keyen hierop,
     *  niet op de GUID. Het is een `IDENTITY`: `MAX − MIN` is nooit een aantal. */
    number: number;
    createdAt: string;
    license: string | null;
    /** ⚠ BEIDE maatschappijcodes: wélke van de twee gevuld is zegt iets. */
    insurerPorCode: string | null;
    insurerWamCode: string | null;
    repairerCode: string | null;
    dossierId: string | null;
    dossierNumber: string | null;
    linkStatus: number | null;
    linkStatusLabel: string | null;
    /** ⚠ `false` = een WEES. Hij blijft zichtbaar mét die vaststelling (AC-55). */
    hasActiveLink: boolean;
    isRepair: boolean;
    /** ⚠ Uit DEZELFDE query als de lijst — nooit een query per rij (AC-57.3). */
    rowCount: number;
    verdict: number | null;
    verdictState: ClaimFilterVerdictState;
    verdictLabel: string;
    verdictAt: string | null;
    /** ⚠ Op de LIJST alleen de afgeleide markering; de reden staat uitsluitend op
     *  het detail (AC-56). */
    isCoinFlip: boolean;
}

export interface ClaimFilterRecordList {
    records: ClaimFilterRecord[];
    filter: string;
    query: string | null;
    dossierId: string | null;
    from: string | null;
    to: string | null;
    /**
     * ⚠⚠ **Deze lijst is een RECONSTRUCTIE en het scherm zegt dat ook** (AC-54).
     * Er bestaat géén vastgelegde verwijzing van een ronde naar een claimfilter;
     * het tijdvak is de enige beschikbare sleutel en hij is **niet scherp** bij
     * overlappende ronden (AC-42).
     */
    timeWindowFilter: boolean;
    page: number;
    pageSize: number;
    /** Het aantal MÉT filter — de basis van de paginering. */
    total: number;
    /** ⚠ Het aantal ZÓNDER filter. Zonder dit getal lezen "geen treffers voor dit
     *  filter" en "er zijn nog geen claimfilters" hetzelfde (AC-55). */
    totalWithoutFilter: number;
    /** ⚠ Hoever de historie teruggaat (AC-46.2) — anders leest het begin van de
     *  tabel als een storing. */
    historyStartsAt: string | null;
    blocks: ClaimFilterBlock[];
}

/** Eén regel van een claimfilter (AC-51, AC-52). */
export interface ClaimFilterRecordRow {
    ordinal: number;
    eurocode: string | null;
    quantity: number | null;
    /**
     * ⚠⚠ **Beide prijzen staan er, naast elkaar, LETTERLIJK** (AC-51). Het scherm
     * leidt de ene nooit uit de andere af en poetst een bruto van `0,00` naast een
     * gevulde netto niet weg — dat is open punt **O-11**, geen eis van dit scherm.
     * `null` en `0` lezen verschillend: *"nul is een prijs, leeg is geen prijs"*.
     */
    grossUnitPrice: number | null;
    netUnitPrice: number | null;
    /** ⚠ 075: "BEWUST LEEG in bouwstap 2" — het scherm toont "niet gevuld",
     *  herkenbaar anders dan een gemeten leegte (AC-52). */
    labourUnits: number | null;

    /**
     * De artikelcode van de kalibratieregel uit hetzelfde blok als deze ruit.
     *
     * ⚠⚠ `null` betekent **niet herkend** en NOOIT "er is niet gekalibreerd". De
     * herkenning gaat via de beheertabel `UA_CalibrationItem`, en die is aantoonbaar
     * incompleet: gemeten op 03-09-2026 staat 4.500 van de 7.098 kalibratie-achtige
     * calculatieregels er niet in. Het scherm moet dat verschil benoemen.
     */
    calibrationCode: string | null;
    /** ⚠ Alle drie onwaar bij een code die de beheertabel niet (meer) kent — dat is iets
     *  anders dan een lege code. */
    calibrationIsStatic: boolean;
    calibrationIsDynamic: boolean;
    calibrationIsStereo: boolean;

    /** ⚠ Blijft leeg (O-8): de calculatieregel draagt geen ruitpositie, en dat is niet
     *  met een beheertabel op te lossen. */
    glassPositionCode: string | null;
    createdAt: string | null;

    // ── De BLOKTOTALEN VAN DE HERSTELLER (migratie 083) ──────────────────────
    //
    // ⚠⚠ Een blok loopt van de eurocode-regel tot (niet inclusief) de volgende: de
    //    ruit plus montage, kit, kleinmateriaal en kalibratie. Die grens spiegelt de
    //    statistiektabel waar de gevalideerde tegenhanger uit komt.
    //
    // ⚠ Leeg op elke regel die vóór 03-09-2026 is aangeboden, en dat blijft leeg:
    //   met terugwerkende kracht vullen zou het bedrag van NU onder de vlag van
    //   TOEN zetten.

    /** Het bloktotaal inclusief de ruit. ⚠ `null` = niet gemeten, nooit 0. */
    totalAmount: number | null;
    /** Hetzelfde blok zonder de ruit-regel. */
    totalExclEurocode: number | null;
}

// ── Het Referentie-blok B25 (§5.7.5, AC-58 t/m AC-64) ────────────────────────
//
// ⚠⚠ DE REFERENTIE IS EEN WAARNEMING EN NOOIT EEN OORDEEL. De motor leest
//    `ReferenceEurocode` NERGENS: de poort wacht wél op een lopende aanvraag,
//    maar de uitkomst wordt daarna niet gebruikt — het oordeel is een muntworp
//    (O-13). Daarom staat dit blok VÓÓR het oordeel: een lezer die eerst
//    "Goedgekeurd" ziet, leest alles daarna als onderbouwing.

/**
 * De drie statussen, en de letterlijke vierde (AC-59).
 *
 * ⚠ Deze nummers betekenen iets ANDERS dan in `UA_RuitIdRequest` (die kent een
 * `4 = Fout` die hier bewust niet bestaat). Het scherm neemt dus geen
 * labelfunctie van een andere tabel over.
 */
export type ClaimFilterReferenceStatusState =
    | 'Requested'
    | 'Available'
    | 'Unavailable'
    | 'OutOfDomain';

/**
 * De partitie van een reden-code (AC-60.2).
 *
 * ⚠⚠ **Een eis en geen versiering.** `NotRequestedFree` (drie codes) betekent
 * *geen uitgaande call, dus geen rekening*; `RequestedPossiblyPaid` (zes codes)
 * betekent *er is een aanvraag de deur uit gegaan, of hij is mogelijk
 * aangekomen*. Wie die twee op één hoop telt kan de vraag *"wat kost claimfilter
 * ons deze maand?"* niet beantwoorden — en dat staat letterlijk zo in de code.
 *
 * `Unknown` is een **bevinding**: een code die het woordenboek niet kent. Hij
 * mag nooit stil in de gratis-hoek belanden. `null` is iets anders: die rij
 * heeft helemaal geen reden.
 */
export type ClaimFilterReferencePartition =
    | 'NotRequestedFree'
    | 'RequestedPossiblyPaid'
    | 'Unknown';

/** ⚠ Eén van de twee leeg is GÉÉN tegenspraak (AC-62.6). */
export type ClaimFilterCalibrationState = 'Both' | 'OnlyText' | 'OnlyMinutes' | 'None';

/**
 * De volgorde referentie ↔ oordeel (AC-63).
 *
 * ⚠⚠ **`VerdictBeforeRequest` is de NORMALE toestand en geen storing**: de
 * referentie kwam ná het oordeel binnen en heeft er per definitie niet aan
 * bijgedragen. Alleen `VerdictAfterRequestStillOpen` is een bevinding. Alles op
 * één hoop labelen maakt elk claimfilter van vóór het aanzetten rood — en dán is
 * de echte bevinding onvindbaar.
 */
export type ClaimFilterReferenceOrder =
    | 'None'
    | 'VerdictBeforeRequest'
    | 'VerdictAfterRequestStillOpen'
    | 'VerdictAfterResolve'
    | 'Unknown';

/** Eén referentierij — één glaspositie (AC-58). */
export interface ClaimFilterReferenceRow {
    /**
     * Het bedrag. ⚠ `null` wanneer er geen prijs is, **nooit `0`** — een nul leest
     * als "gratis, dus binnen de marge".
     */
    referenceGrossPrice: number | null;

    /** `0` nog niet aan de beurt of bezig · `2` er is een bedrag · `3` uitgepraat. */
    grossPriceStatus: number;

    /** Waarom er geen prijs is; `null` zolang er niets misging. */
    grossPriceUnavailableReason: string | null;

    /**
     * Wélke prijs er is opgevraagd — `"4"` is bruto, `"1"` is de inkoopprijs van de
     * hersteller.
     *
     * ⚠⚠ Reist MET het bedrag mee. Beide waarden leveren een geldig bedrag op; alleen
     * de betekenis verschilt. Zonder dit ernaast is een verkeerd ingestelde koppeling
     * van buiten niet te zien — je leest een plausibel getal dat het verkeerde is.
     */
    grossPriceExtraField: string | null;

    /** Het materiaalnummer kwam uit een verlopen cacherij; het bedrag hoort dan
     *  mogelijk niet meer bij deze ruit. */
    grossPriceFromStaleMaterial: boolean;

    grossPriceAt: string | null;

    /**
     * ⚠⚠ **`0` is GÉÉN ruit** maar de markering *"er is geen positie"*. Hij mag
     * nooit als de eerste positie uit de labellijst renderen — die begint bij 1
     * en kent 0 niet (AC-62.3). ⚠ En bij een **reparatie** is positie 1
     * *afgeleid* en niet uit `CarPosition` gelezen; hij kan dus afwijken van wat
     * de kop bij Schadelocatie toont (AC-62.4).
     */
    glassPositionId: number;
    /** De ruwe waarde — óók buiten `{1,2,3}`; die toont dan letterlijk. */
    referenceStatus: number;
    statusState: ClaimFilterReferenceStatusState;
    statusLabel: string;
    /** ⚠ Gevuld ZONDER eurocode is echt (`no_eurocode_returned`); andersom hoort
     *  niet te kunnen. Beide velden staan er altijd en de ene wordt NOOIT uit de
     *  andere afgeleid (AC-62.5). */
    ruitId: string | null;
    referenceEurocode: string | null;
    /**
     * De RUWE waarde uit het antwoord.
     *
     * ⚠⚠ AutoQraft heeft dit contract op 03-09-2026 OMGEDRAAID. Tot claimfilter 18
     * kwamen er minuten en bleef dit veld leeg; sinds 19 draagt dit veld een CODE
     * (`0` geen · `1` statisch · `2` dynamisch · `3` stereo) en staan de minuten op 0.
     * De code blijft ONVERTAALD meereizen naast `referenceCalibrationType`, precies
     * zoals bij de reden-codes: hij is de grep-sleutel én het bewijs wanneer ons
     * woordenboek een waarde niet kent.
     */
    referenceCalibration: string | null;
    /** ⚠ Niet interessant sinds het contract omging — de vraag is de SOORT. Blijft
     *  staan voor de rijen van vóór claimfilter 19, die alléén minuten dragen. */
    referenceCalibrationMinutes: number | null;
    /**
     * De soort: `geen` · `statisch` · `dynamisch` · `stereo`.
     *
     * ⚠⚠ `null` betekent **"geen code geleverd, of een code die wij niet kennen"** en
     * NOOIT `geen`. `"geen"` is een uitspraak van de leverancier; `null` is een gat aan
     * onze kant. Toon die twee verschillend.
     */
    referenceCalibrationType: string | null;
    calibrationState: ClaimFilterCalibrationState;
    /** ⚠ `test` is simulatie en gratis, `autovalid` kost geld — en dit is de
     *  dienst van het moment van AANVRAGEN, bij het oogsten nooit overschreven. */
    sourceService: string | null;
    /** ⚠ ONVERTAALD (O-9: grep-sleutel in het traclog). */
    unavailableReason: string | null;
    /** De Nederlandse uitleg ernaast, ZICHTBAAR en niet in een tooltip. `null`
     *  voor een code die het woordenboek niet kent — die hoort zichtbaar te
     *  ontbreken (AC-60). */
    reasonExplanation: string | null;
    reasonPartition: ClaimFilterReferencePartition | null;
    reasonPartitionLabel: string | null;
    /** ⚠ AC-60.3 — `request_outcome_unknown` en `handle_missing` zijn
     *  nadrukkelijk NIET `request_failed`. */
    reasonNotRefused: boolean;
    /** Het aantal uitgegane POSTs — niet het aantal rondes. */
    requestAttempts: number;
    attemptsAboveCeiling: boolean;
    requestedAt: string | null;
    resolvedAt: string | null;
    createdAt: string | null;
    /** ⚠ Status 1 met een lege `requestedAt`: de klok staat stil en het
     *  verstrijken raakt hem nooit — alleen een volgende ronde lost hem op. */
    clockStopped: boolean;
    // ── De GEVALIDEERDE MONTAGETIJD ──────────────────────────────────────────
    //
    // ⚠⚠ EEN OPZOEKING, GEEN AANVRAAG. De eurocode en de brutoprijs worden bij een
    //    derde partij opgevraagd — daar hoort een status bij, een pogingenteller en
    //    mogelijk een rekening. Deze tijd komt uit een statistiektabel in onze eigen
    //    database (gemiddelden per eurocode-DEEL, de eerste vijf posities). Geef deze
    //    velden dus nooit een status: dan lezen ze als een mislukte aanvraag.

    /** Het eurocode-deel (vijf posities) waaronder de tijd gevonden is. ⚠ Toon hem
     *  altijd NAAST het getal — anders is niet te zien waarop er gematcht is. */
    montageEurocodePart: string | null;
    /** De gemiddelde montagetijd in UREN. `null` = niet gevonden. */
    montageAvgHours: number | null;
    /** ⚠ De spreiding hoort bij het gemiddelde: 2,50 uit een reeks 1,9–3,8 is iets
     *  anders dan 2,50 uit 2,4–2,6, en dit scherm velt geen oordeel. */
    montageMinHours: number | null;
    montageMaxHours: number | null;
    montageDossiers: number | null;
    /** ⚠ Het enige getal op dit scherm dat MEEBEWEEGT — de rest is een momentopname. */
    montageComputedAt: string | null;

    // ── De GEVALIDEERDE BLOKTOTALEN ──────────────────────────────────────────
    //
    // ⚠⚠ Uit DEZELFDE rij als de montagetijd, en dat is waarom ze vergelijkbaar zijn
    //    met `totalAmount` / `totalExclEurocode` van de hersteller: beide tellen het
    //    blok op dezelfde grens af.

    /** Het gemiddelde bloktotaal inclusief de ruit. */
    validatedTotalAmount: number | null;
    /** ⚠ De spreiding weegt hier zwaar: op 8579A loopt het totaal van −504,81 tot
     *  1472,49 rond een gemiddelde van 542,29. */
    validatedTotalAmountMin: number | null;
    validatedTotalAmountMax: number | null;
    /** Het gemiddelde bloktotaal zonder de ruit-regel. */
    validatedTotalExclEurocode: number | null;
    validatedTotalExclEurocodeMin: number | null;
    validatedTotalExclEurocodeMax: number | null;

    /** ⚠ Een rij op Beschikbaar met een lege eurocode is een BEVINDING, geen lege
     *  cel (AC-59.2). */
    availableWithoutEurocode: boolean;
    verdictOrder: ClaimFilterReferenceOrder;
}

/** Het Referentie-blok (B25) van het detail (§5.7.5). */
export interface ClaimFilterReference {
    rows: ClaimFilterReferenceRow[];
    /** ⚠ "Er is niets aangevraagd" is een UITSPRAAK — nooit een streepje en nooit
     *  een lege tabel, en herkenbaar anders dan "aangevraagd en niets
     *  teruggekregen" (AC-62.1). */
    hasRows: boolean;
    /** ⚠ Zolang er ÉÉN rij op status 1 staat is het claimfilter niet
     *  beoordeelbaar. Die uitspraak staat bij het BLOK, niet per regel. */
    hasOpenRequest: boolean;
    /** Uit de INSTELLING, niet uit een getal in de FE (spiegel van AC-45). */
    deadlineMinutes: number;
    maxRequestAttempts: number;
    eurocodeComparison: 'NotCompared' | 'Equal' | 'Different';
    declaredEurocodes: string[];
    referenceEurocodes: string[];
    verdictOrder: ClaimFilterReferenceOrder;
}

/** Het oordeel-blok van het detail (B24). */
export interface ClaimFilterVerdict {
    verdict: number | null;
    state: ClaimFilterVerdictState;
    label: string;
    verdictAt: string | null;
    isCoinFlip: boolean;
    /**
     * ⚠⚠ **DE ENE UITZONDERING OP AC-22, en zij is bij naam geregeld** (AC-56).
     *
     * Dit is het enige veld in de hele API dat de ruwe
     * `CF_ClaimFilter.VerdictReason`-tekst draagt. Het publiek van dit ene scherm
     * is system/support. **Nooit doorgeven** aan een tweede scherm, een generiek
     * `notes`-veld of een component buiten `features/claimfilter/` — daar staat een
     * scan-test op die in twee richtingen faalt.
     */
    reason: string | null;

    /**
     * De CODE van de meting die de doorslag gaf: `eurocode` · `grossprice` ·
     * `calibration` · `labourunits` · `totalincl` · `totalexcl`.
     *
     * ⚠⚠ Hoort BIJ `reason` en vervangt hem niet. De reden draagt hele constanten
     * zonder waarden — de maatschappij leest die tekst — en verwijst naar dít veld
     * voor de vraag *welke* meting. Toon je hem niet, dan wijst de reden naar iets
     * wat nergens staat; precies dat is op 03-09-2026 gemeld.
     *
     * ⚠ `null` bij goedgekeurd, bij een steekproef, bij een oningerichte maatschappij
     * en bij elk oordeel van vóór de oordeelsregels. Alle vier betekenen "geen meting
     * gaf de doorslag" — de reden zegt welke van de vier.
     */
    measurement: string | null;
}

/** Eén claimfilter, uitgeklapt (§5.7.2, AC-47). */
export interface ClaimFilterRecordDetail {
    number: number;
    createdAt: string;

    // ── Voertuig ─────────────────────────────────────────────────────────────
    license: string | null;
    /** ⚠ BEIDE nummers, altijd. Het scherm kiest er nooit één en toont nooit een
     *  samengevoegd veld (AC-50). */
    chassisNumberRepairer: string | null;
    chassisNumberRdc: string | null;
    chassisComparison: ClaimFilterChassisComparison;
    /** ⚠ De zone-codes ONVERTAALD; de FE labelt met de gedeelde vocabulaire
     *  (`formatDamageZones`) — niet hernummeren, niet vertalen. */
    carPosition: string | null;

    // ── Partijen ─────────────────────────────────────────────────────────────
    insurerPorCode: string | null;
    insurerWamCode: string | null;
    repairerCode: string | null;
    dossierId: string | null;
    dossierNumber: string | null;
    linkStatus: number | null;
    linkStatusLabel: string | null;
    hasActiveLink: boolean;

    // ── Werk ─────────────────────────────────────────────────────────────────
    isRepair: boolean;
    /** ⚠ Bewust nog niet gevuld in deze bouwstap (AC-52). */
    repairPrice: number | null;

    /**
     * ⚠⚠ Een **lege lijst is een uitspraak** en geen laadtoestand (AC-48). Kop en
     * regels worden in dezelfde intake-handeling geschreven, dus nul regels is
     * definitief — en bij een reparatie is het de verwachte toestand.
     */
    rows: ClaimFilterRecordRow[];
    /**
     * Het Referentie-blok (B25, §5.7.5) — **vóór** het oordeel, en dat is geen
     * opmaakkeuze: een lezer die eerst *"Goedgekeurd"* ziet, leest alles daarna
     * als onderbouwing, en dat is het vandaag juist niet (AC-58.1, O-13).
     */
    reference: ClaimFilterReference;
    verdict: ClaimFilterVerdict;
    blocks: ClaimFilterBlock[];
}

// ─── De oordeelsregels (story `fe-be-claimfilter-oordeelsregels-beheer`) ──────
//
// ⚠⚠ Dit is de INRICHTING, niet de uitkomst. Geen enkel type hieronder draagt de
//    oordeel-reden of iets wat eruit is afgeleid — die hoort bij een claimfilter en
//    blijft aan de andere kant van de afscherming. Er staat een BE-borg op dat geen
//    van deze DTO's ooit een reden-veld krijgt.

/**
 * Eén scope: de basisset van een maatschappij, of één hersteller-uitzondering.
 *
 * ⚠⚠ Een uitzondering VERVANGT de basisset, hij vult hem niet aan. De motor kiest
 * "bestaat er een hersteller-set, dan is die volledig leidend" — dus een meting die
 * in een uitzondering uit staat wordt voor die hersteller NIET getoetst, ook al staat
 * hij in de basisset aan.
 */
export interface ClaimFilterRuleScope {
    insurerId: string;
    /** ⚠ Mag `null` zijn — dan is de naam niet op te zoeken en toont het scherm de
     * id. Nooit een verzonnen "Onbekend": dat leest als een echte naam. */
    insurerName: string | null;
    /** `null` = de basisset (alle herstellers). */
    repairerId: string | null;
    repairerName: string | null;
    /** Hoeveel van de metingen ingericht zijn. ⚠ Nul is de gevaarlijkste toestand:
     * dan gaat élk aangeboden dossier naar expertise. */
    configuredMeasurements: number;
    activeMeasurements: number;
    /** Hoeveel metingen er bestaan — meegestuurd, niet hardgecodeerd, zodat het
     * getal niet op twee plekken staat. */
    totalMeasurements: number;
}

export interface ClaimFilterRuleScopes {
    scopes: ClaimFilterRuleScope[];
}

/**
 * Eén hersteller in de keuzelijst achter "uitzondering toevoegen" (09-09-2026).
 *
 * ⚠ De code staat bewust náást de naam. Twee vestigingen van dezelfde keten dragen vaak
 * bijna dezelfde bedrijfsnaam; zonder de code kiest de gebruiker de verkeerde, en dat
 * merkt niemand tot de regels op de andere vestiging blijken te gelden.
 */
export interface ClaimFilterRuleRepairer {
    id: string;
    /** ⚠ Mag `null` zijn — dan toont het scherm de code. Nooit een verzonnen "Onbekend". */
    name: string | null;
    repairerCode: string | null;
}

export interface ClaimFilterRuleRepairers {
    repairers: ClaimFilterRuleRepairer[];
}

export interface ClaimFilterRuleEntry {
    /** Een van de zes codes uit `CLAIMFILTER_MEASUREMENT_CODES`. */
    measurement: string;
    isActive: boolean;
    /** ⚠ `null` = geen grens. Nooit `0` daarvoor gebruiken: nul is een geldige grens
     * ("geen enkele afwijking getolereerd") en dat is iets anders. */
    lowerBound: number | null;
    upperBound: number | null;
    unknownIsExpertise: boolean;
}

export interface ClaimFilterRuleSet {
    insurerId: string;
    repairerId: string | null;
    entries: ClaimFilterRuleEntry[];
    /** ⚠ `null` ("nooit ingesteld") is iets anders dan `0` ("bewust geen
     * steekproef"). Beide trekken niets, maar alleen de tweede is een keuze. */
    samplePercentage: number | null;
}

export interface ClaimFilterRuleSaveRequest {
    insurerId: string;
    repairerId: string | null;
    entries: ClaimFilterRuleEntry[];
    samplePercentage: number | null;
}

export interface ClaimFilterRuleHistoryEntry {
    sequence: number;
    /** Created / Updated / Deleted. ⚠ Zonder de actie is een verwijderde
     * uitzondering niet te onderscheiden van een die er nooit was. */
    action: string;
    measurement: string;
    isActive: boolean;
    lowerBound: number | null;
    upperBound: number | null;
    unknownIsExpertise: boolean;
    samplePercentage: number | null;
    changedAt: string;
}

export interface ClaimFilterRuleHistory {
    entries: ClaimFilterRuleHistoryEntry[];
}

/** De weigergronden van het opslaan — een gesloten verzameling, geen vrije tekst. */
export const CLAIMFILTER_RULE_SAVE_ERRORS = [
    'bound_order',
    'bound_on_exact',
    'unknown_measurement',
    'incomplete_set',
    'sample_out_of_range',
] as const;

export type ClaimFilterRuleSaveError = (typeof CLAIMFILTER_RULE_SAVE_ERRORS)[number];

// ─── eXchange notities (story fe-be-exchange-notities, 2026-09-29) ─────────

/**
 * Eén partij waaraan een notitie gericht kan worden — verzekeraar, volmacht of
 * expertisebureau, uitsluitend met `relationNumber > 0`.
 */
export interface ExchangeNoteParty {
  relationNumber: number;
  /** Schermnaam: "Verzekeraar" / "Volmacht" / "Expertise bureau". */
  name: string;
}

/** Eén regel in het draadje: een bericht of een antwoord. */
export interface ExchangeNote {
  id: number;
  author: string;
  /** Tijdstip zoals eXchange het levert. */
  created: string;
  message: string;
  recipientRelationNumbers: number[];
  recipientNames: string[];
  /** Antwoorden, oudste eerst. Op een antwoord altijd leeg — eXchange kent
   *  geen antwoord-op-antwoord. */
  replies: ExchangeNote[];
}

/**
 * Waarom de `Aan`-lijst leeg is. ⚠ Dit zijn vijf verschillende dingen die
 * NIET dezelfde schermtekst mogen krijgen: "geen partijen bekend" stuurt de
 * gebruiker bij een uitgezette omgeving of een half gekoppeld dossier op zoek
 * naar een inrichtingsfout bij de verzekeraar terwijl het probleem aan onze
 * kant zit.
 */
export type ExchangeNotesPartiesState =
  | 'available'
  | 'no_parties'
  | 'exchange_offline'
  | 'not_linked'
  | 'role_not_supported';

/** Antwoord van `GET /api/dossiers/{id}/exchange-notes`. */
export interface ExchangeNotesThread {
  /** Berichten, nieuwste eerst. */
  notes: ExchangeNote[];
  /** Berichten **plus** antwoorden — het getal achter het menu-label. */
  totalCount: number;
  parties: ExchangeNoteParty[];
  partiesState: ExchangeNotesPartiesState;
}

/** Body van het plaatsen van een bericht of een antwoord. */
export interface ExchangeNoteWriteRequest {
  recipients: number[];
  message: string;
}

/* ─── Callcenter: melding aannemen ──────────────────────────────────────── */

/** Welke ruit de klant noemt. Bepaalt de route van het gesprek. */
export type GlassKind = 'Windscreen' | 'SideWindow' | 'RearWindow' | 'Other' | 'NotADamageCall';

/** Waar het gesprek op uitkomt. */
export type IntakeRoute = 'Undetermined' | 'Urgent' | 'Callback';

/** Eén alarmcentrale uit `GET /api/lookups/alarm-centers`. */
export interface AlarmCenterOption {
  id: string;
  name: string | null;
  /** Telefoonnummer van de centrale; het scherm vult daarmee het contactnummer voor. */
  phone: string | null;
}

export interface IntakeCreateResponse {
  number: number;
  id: string;
  /** Altijd 501 "Nog niet toegekend" bij een nieuwe melding. */
  statusValue: number;
  workflowName: string;
}

export interface IntakeTriageRequest {
  byAlarmCenter: boolean | null;
  alarmCenterId?: string | null;
  alarmContactName?: string | null;
  alarmContactPhone?: string | null;
  glass: GlassKind | null;
  /** `null` = niet gevraagd. Alleen van belang bij een zijruit of achterruit. */
  canWaitUntilNextWorkday: boolean | null;
  /**
   * "Spoedmelding aanmaken" vanaf de terugbelroute (legacy `ForceUrgency`): maakt
   * van deze melding een spoedmelding, ongeacht ruitsoort en kan-het-wachten.
   */
  forceUrgent?: boolean | null;
}

export interface IntakeAddressDto {
  zipcode?: string | null;
  houseNumber?: string | null;
  street?: string | null;
  city?: string | null;
}

export interface IntakeDetailsRequest {
  /** ⚠ Waar de hersteller naartoe moet — niet het klantadres. */
  repairLocation?: IntakeAddressDto | null;
  customerAddressSameAsRepair: boolean;
  customerAddress?: IntakeAddressDto | null;
  customerName?: string | null;
  customerPhone?: string | null;
  customerEmail?: string | null;
  license?: string | null;
  manufacturer?: string | null;
  repairTypeId?: string | null;
  /** Drie toestanden: ja / nee / onbekend-of-niet-gevraagd. */
  isLease?: boolean | null;
  isInsured?: boolean | null;
  insurerId?: string | null;
  remarks?: string | null;
}

/**
 * `GET /api/intakes/{nummer}/callcenter` — het lopende gesprek zoals het op de
 * melding staat. Spiegelt `IntakeTriageRequest` + `IntakeDetailsRequest`.
 *
 * ⚠ Bestaat omdat `patchIntakeDetails` de HELE veldset wegschrijft. Een scherm
 * dat op een bestaand meldingnummer opent zonder eerst dit te laden, wist bij de
 * eerste opslag de klant, het adres en het kenteken — zonder foutmelding, want
 * technisch is dat een geldige opslag.
 */
export interface IntakeCallCenterSnapshot {
  number: number;
  /** `WF_CaseState.StatusValue`; 501 = het gesprek loopt nog. */
  statusValue: number | null;
  createdAt: string | null;
  route: IntakeRoute;

  /** `null` = de triage is nog niet vastgelegd, en dat is iets anders dan "nee". */
  byAlarmCenter: boolean | null;
  alarmCenterId: string | null;
  alarmCenterName: string | null;
  alarmContactName: string | null;
  alarmContactPhone: string | null;
  /** `'Unknown'` zolang de triage niet gedaan is. */
  glass: GlassKind | 'Unknown';
  canWaitUntilNextWorkday: boolean | null;
  /** Is vanaf de terugbelroute alsnog een spoedmelding aangemaakt? */
  forceUrgent?: boolean;
  /**
   * Is het terugbelverzoek al verzonden (blob `Status = "FinishedCallback"`, zoals
   * legacy)? De status blijft 501; dit veld is het enige onderscheid.
   */
  callbackSent?: boolean;
  /** Wanneer het terugbelverzoek verzonden is (lokale tijd); `null` als onbekend. */
  callbackSentAt?: string | null;

  /** ⚠ Waar de hersteller naartoe moet — niet het klantadres. */
  repairLocation: IntakeAddressDto | null;
  customerAddress: IntakeAddressDto | null;

  customerName: string | null;
  customerPhone: string | null;
  customerEmail: string | null;

  license: string | null;
  manufacturer: string | null;
  repairTypeId: string | null;

  isLease: boolean | null;
  isInsured: boolean | null;
  insurerId: string | null;
  insurerName: string | null;

  remarks: string | null;
}

/** Eén geweigerd veld, met de reden-code waar de FE een Nederlandse tekst bij maakt. */
export interface IntakeUpdateProblem {
  field: string | null;
  reason: string;
}

export interface IntakeUpdateResult {
  ok: boolean;
  problems: IntakeUpdateProblem[];
  route: IntakeRoute | null;
  /** Vraagt deze ruitsoort om de kan-het-wachten-vraag? */
  asksCanWait: boolean | null;
}

export interface RepairerCandidate {
  id: string;
  /** ⚠ De vestigingsplaats zit hierin — er is geen apart plaats-veld. */
  name: string;
  region: string | null;
  phone: string | null;
  phoneAfterHours: string | null;
  /** Vakantie-/weekendmelding; vrije tekst van de hersteller zelf. */
  availabilityNote: string | null;
  weekOfficeHours: string | null;
  replacementRepairerId: string | null;
  activeRegionCount: number | null;
}

export type RepairerMatchOutcome =
  | 'Found'
  | 'NoRepairerForZipcode'
  | 'NoZipcode'
  /** De ketentoets kon niet worden uitgevoerd — géén lege lijst tonen. */
  | 'ChainUndetermined';

export type ChainOutcome = 'Unknown' | 'InChain' | 'NotInChain';

export interface RepairerMatchResult {
  outcome: RepairerMatchOutcome;
  repairer: RepairerCandidate | null;
  searchedZipNumber: string | null;
  chain: ChainOutcome;
}

export interface RepairerSearchResult {
  items: RepairerCandidate[];
  chain: ChainOutcome;
  /** `true` → er waren meer treffers dan er zijn meegestuurd. */
  hasMore: boolean;
}

/** Wat er met één bericht gebeurd is. */
export interface NotificationResult {
  channel: 'email' | 'sms' | 'case_message';
  sent: boolean;
  reason: string | null;
}

export interface IntakeOutcomeResult {
  ok: boolean;
  statusValue: number | null;
  notifications: NotificationResult[];
  /** De status is gezet, maar niet alles is verzonden. */
  hasDeliveryProblems: boolean;
  reason: string | null;
  /** Alleen bij een terugbelverzoek: wanneer het verzonden is (lokale tijd). */
  callbackSentAt?: string | null;
}

export interface IntakeOutcomeRequest {
  repairerId?: string | null;
  note?: string | null;
}

/* ─── Configuratie-overzicht (system-only) ──────────────────────────────── */

/** De vastgelegde statuswaarden van één instelling. */
export type ConfigurationStatus =
  /** Verplicht volgens het manifest, staat niet in de configuratie. */
  | 'missing'
  /** Aanwezig maar leeg of enkel witruimte — faalt identiek aan ontbreken. */
  | 'empty'
  /** Gevuld en omgevingsgebonden: nalopen bij een deploy. */
  | 'review'
  /** Gevuld en niet omgevingsgebonden. */
  | 'set'
  /** Optioneel of voorwaardelijk en niet gezet. */
  | 'not_needed'
  /** Staat in de configuratie maar niet in het manifest — het manifest loopt achter. */
  | 'unknown';

export type ConfigurationRequirement = 'required' | 'optional' | 'conditional';

/** Eén instelling: wat hij betekent plus hoe hij er op deze omgeving bij staat. */
export interface ConfigurationRow {
  key: string;
  group: string;
  requirement: ConfigurationRequirement;
  /** Nederlandse voorwaarde-tekst; alleen gevuld bij `conditional`. */
  condition: string | null;
  /** ⚠ Bij `true` is `value` ALTIJD `null` — het BE stuurt de waarde niet mee,
   *  ook geen lengte, hash of prefix. Toon dus nooit een placeholder die de
   *  indruk wekt dat de waarde er wel is. */
  secret: boolean;
  /** Hoort per omgeving te verschillen; dit is de deploy-lijst. */
  perEnvironment: boolean;
  description: string;
  status: ConfigurationStatus;
  /** Alleen gevuld wanneer `secret` `false` is. */
  value: string | null;
  /** Alleen bij een connectiereeks: server, catalogus en gebruiker — nooit het wachtwoord. */
  catalog: string | null;
}

/** De informatieve vergelijking met de configuratie van de oude applicatie. */
export interface ConfigurationLegacySection {
  /** `false` → toon de sectie niet, maar wél `reason`. Dit is géén fout. */
  available: boolean;
  reason: string | null;
  sharedKeys: string[];
  legacyOnlyKeys: string[];
}

export interface ConfigurationOverview {
  rows: ConfigurationRow[];
  /** Aantal regels per status; elke status komt voor, ook met 0. */
  counts: Record<ConfigurationStatus, number>;
  groups: string[];
  legacy: ConfigurationLegacySection;
}

// ─── S021.7 — Ondertekenen in de monteur-app (EPIC-021) ─────────────────────

/**
 * Story `fe-be-monteur-s7-ondertekenen` (S021.7) §2.1 — `GET /api/dossiers/{id}/signing/status`.
 * Voedt de tegel "Ondertekenen" (PB-5). `configured: false` = geen enkel tekendocument
 * ingericht in de keten van de hersteller → de tegel is niet zichtbaar (PB-8).
 */
export interface SigningStatus {
  configured: boolean;
  signed: boolean;
  signedCount: number;
  /** Lokale tijd, ISO zonder offset. */
  lastSignedAt: string | null;
  signedBy: string | null;
}

/** Eén tekendocument uit de inrichting (`UA_SignatureDocuments`), zoals het formulier hem toont. */
export interface SigningDocument {
  /** De id van de inrichtingsrij (legacy noemt dat veld `ReportID`). */
  id: string;
  /** Lege naam → `""`; de FE toont dan "Document". */
  name: string;
  /** Voorgeselecteerd volgens de legacy-conditie. */
  selected: boolean;
}

/** `GET /api/dossiers/{id}/signing/documents` — de documenten plus de formulier-defaults. */
export interface SigningDocumentsResponse {
  documents: SigningDocument[];
  defaults: {
    email: string;
    signedBy: string;
    mailToCustomer: boolean;
  };
}

/** `POST /api/dossiers/{id}/signing/preview` (CSRF) — antwoord is een PDF (`Blob`). */
export interface SigningPreviewRequest {
  documentIds: string[];
  /** `data:image/png;base64,…` (400×200); weglaten = voorbeeld zonder handtekening (PB-7). */
  signature?: string;
  /** Alleen in het geheugen gebruikt, zodat voorbeeld en getekend document gelijk zijn (PB-20). */
  signedBy?: string;
  email?: string;
}

/**
 * `POST /api/dossiers/{id}/signing` (CSRF). ⚠ Precies deze sleutels: de monteur-allowlist
 * (`[FitterAccess]`, §3) weigert elke andere key met 403 `fitter_only_field_not_allowed`.
 * Er is bewust GEEN `phone` (PB-6).
 */
export interface SignRequest {
  version: number;
  /** UUID; behouden bij een herhaling na een onbekende uitkomst (idempotentie, PB-16). */
  clientRequestId: string;
  documentIds: string[];
  signature: string;
  signedBy: string;
  email?: string;
  mailToCustomer: boolean;
}

export interface SignResponse {
  version: number;
  /** `true` = deze `clientRequestId` was al verwerkt; er is niets opnieuw opgeslagen of gemaild. */
  replayed: boolean;
  attachments: Array<{ id: string; name: string }>;
  dossierFieldsUpdated: boolean;
  mailed: boolean;
  /** Bv. `mail_failed`, `mail_sender_missing`, `mail_sender_invalid`; `null` = geen mailfout. */
  mailError: string | null;
}

// ─── Story `fe-be-monteur-s6-onderdelen` (EPIC-021, S021.6) — gebruikte onderdelen ───

/** Eén onderdeel uit de checklist van de hersteller van het dossier (`UA_MobilePart`). */
export interface UsedPartItem {
  id: string;
  /** Getrimd; leeg → `""` (de FE toont dan "Onderdeel"). */
  name: string;
  /** Er staat een koppeling op dit dossier. */
  used: boolean;
  /** Niet meer in de lijst (gearchiveerd/verwijderd, of van een andere hersteller); alleen zichtbaar als het aanstaat. */
  archived: boolean;
}

/** Waarom er niet afgevinkt kan worden (`null` = het kan wel). */
export type UsedPartsEditBlockedReason = 'not_owner' | 'locked' | 'lock_state_unknown' | 'no_repairer';

/**
 * GET/PATCH `/api/dossiers/{id}/used-parts` (§2.1): de volledige stand. Geen `version` — er
 * wordt niets in het dossier zelf geschreven (PB-4/PB-12).
 */
export interface DossierUsedParts {
  /** ≥ 1 onderdeel in `items` (actief van de hersteller, of aangevinkt op dit dossier). */
  configured: boolean;
  items: UsedPartItem[];
  noPartsUsed: boolean;
  /** `false` = migratie 114 nog niet gedraaid: de schakelaar "Geen onderdelen gebruikt" verbergen. */
  noPartsUsedSupported: boolean;
  /** ≥ 1 onderdeel aan, of de vlag aan (zonder lijst ook `true`). */
  complete: boolean;
  canEdit: boolean;
  editBlockedReason: UsedPartsEditBlockedReason | null;
  /** De wire-reden van het slot (`invoiced`, `archived`, …), alleen bij `locked`. */
  lockReason: string | null;
  repairerName: string | null;
}

/** Eén wijziging in een PATCH: dit onderdeel aan of uit. */
export interface UsedPartChange {
  partId: string;
  used: boolean;
}

/**
 * De body van PATCH `/api/dossiers/{id}/used-parts`: een WIJZIGING, geen vervanging (PB-4).
 * ⚠ Precies deze twee sleutels (monteur-allowlist); minstens één van beide.
 */
export interface UsedPartsPatchRequest {
  items?: UsedPartChange[];
  noPartsUsed?: boolean;
}

/** Eén onderdeel in Inrichting → Onderdelenlijst. */
export interface ChecklistPart {
  id: string;
  name: string;
  sortOrder: number | null;
  archived: boolean;
  /** Aantal dossiers waarop het onderdeel aanstaat. */
  usageCount: number;
  version: number;
}

/** GET `/api/inrichting/checklist-parts` (en het antwoord van een herordening). */
export interface ChecklistPartsList {
  repairerName: string | null;
  /** Actief vóór gearchiveerd, dan in de volgorde van de monteur. */
  items: ChecklistPart[];
}

/** DELETE `/api/inrichting/checklist-parts/{id}`: verwijderd, of gearchiveerd omdat het in gebruik is. */
export interface ChecklistPartDeleteResult {
  deleted: boolean;
  archived: boolean;
  usageCount: number;
}
